/**
 * Ajan modu eylem yürütücüleri.
 *
 * Backend'deki AgentActionCatalog (gts_dotnetapi/GTS.Business/Managers/Reports/Ai/
 * AgentActionCatalog.cs) ile birebir eşleşir: AI yalnızca katalogdaki eylemleri
 * çağırabilir, YÜRÜTME burada kullanıcının kendi oturumu/yetkisiyle yapılır —
 * ajan kullanıcının yetkisini aşamaz. risk=write eylemler AiChatWindow'da onay
 * kartından geçmeden çalıştırılmaz.
 *
 * Yeni modül eklemek = backend kataloğuna eylem tanımı + buraya executor eklemek.
 */
import {
  accountingApi,
  authApi,
  einvoiceApi,
  mainApi,
  remoteApi,
  trendyolMpApi,
  n11Api,
  hepsiburadaApi,
  pazaramaApi,
  pttavmApi,
  hepsijetApi,
} from '../../../config/apiClient';
import { ACCOUNTING_API, EINVOICE_API, API_BASE_URL } from '../../../config/api';
import { BRAND } from '../../../config/brand';
import { getBranches } from '../../../services/migration/branchDataService';
import {
  requestInventoryFilters,
  requestCheckNotesFilters,
  requestWaybillFilters,
  requestOrderFilters,
  requestOfferFilters,
  requestCurrencyPage,
  requestEndOfDayFilters,
  requestQrOrdersFilters,
  requestCourierTracking,
  requestImageLibrary,
  requestPersonnelFilters,
  waitForInvoiceFormApi,
  getSalesScreensPageApi,
  waitForSalesScreensPageApi,
  waitForQuickSaleSettingsApi,
  type SalesScreensPageType,
  type QuickSaleSettingsFields,
  type InventoryFilterRequest,
  type CheckNotesFilterRequest,
  type WaybillFilterRequest,
  type OrderFilterRequest,
  type OfferFilterRequest,
  type CurrencyPageRequest,
  type InvoiceFormFields,
  type InvoiceFormItemInput,
  type InvoiceFormType,
} from './uiBridge';
import CurrencyService, { type CurrencyRate } from '../../../services/CurrencyService';
import salesService from '../../../services/salesService';
import { restaurantScreenApi, endOfDayReportApi } from '../../../services/restaurantApiService';
import { fetchEndOfDayDataset } from '../../reports/services/endOfDayDataService';
import {
  ImageLibraryFolderService,
  ImageLibraryImageService,
} from '../../accounting/inventory/services/imageLibraryService';
// İK modülleri: fonksiyon adları executor adlarıyla çakıştığı için namespace import
import * as personnelApi from '../../../services/migration/personnelDataService';
import * as personnelTxApi from '../../../services/migration/personnelTransactionService';
import * as shiftApi from '../../../services/migration/shiftDataService';
// Şube modülü: createBranch/updateBranch vb. executor adlarıyla çakışır → namespace import
import * as branchApi from '../../../services/migration/branchDataService';
import * as branchAssignApi from '../../../services/migration/branchPersonnelAssignmentDataService';
// AI Plan Modu: plan CRUD eylemleri (createAiPlan vb.) — panel notifyAiPlanChanged ile tazelenir
import aiPlanService, { notifyAiPlanChanged } from '../../../services/aiPlanService';
// PYS: createProject/getProjectSummary servis adları executor adlarıyla çakışır → namespace import
import * as projectApi from '../../../services/projectService';

/** Yürütücülere geçen bağlam — pencere sahibi (AiChatWindow) sağlar. */
export interface AgentActionContext {
  /** react-router navigate (FAB MainLayout içinde, Router kapsamında) */
  navigate: (path: string) => void;
  /** Multi-tenant: yazma eylemlerinin payload'ına eklenir (backend RequireCompanyId zaten header'dan da doğrular). */
  companyId?: string;
  /** createdBy/deletedBy denetim alanları için oturum sahibinin e-postası. */
  userEmail?: string;
  /** Oturum sahibinin uid'si — AI plan eylemleri (createAiPlan vb.) plan sahipliği için ister. */
  userId?: string;
}

export interface AgentActionExecutor {
  /** Eylemi çalıştırır; dönen değer JSON'lanıp backend'e functionResponse olarak gider. */
  execute: (args: Record<string, any>, ctx: AgentActionContext) => Promise<Record<string, any>>;
  /** Onay kartı / durum satırı için kısa Türkçe açıklama. */
  describe: (args: Record<string, any>) => string;
}

/**
 * pageId → route eşlemesi. Anahtarlar backend AgentActionCatalog.NavigablePages
 * ile birebir aynıdır (whitelist iki uçta da doğrulanır).
 */
const PAGE_ROUTES: Record<string, { path: string; label: string }> = {
  dashboard: { path: '/dashboard', label: 'Dashboard' },
  inventory: { path: '/stok', label: 'Stok / Ürünler' },
  customers: { path: '/cari', label: 'Cari Hesaplar' },
  bank: { path: '/banka', label: 'Banka Hesapları' },
  cashbox: { path: '/kasa', label: 'Kasalar' },
  warehouse: { path: '/depo', label: 'Depolar' },
  invoice: { path: '/fatura/satis', label: 'Faturalar' },
  'e-invoice': { path: '/e-fatura', label: 'E-Fatura' },
  offers: { path: '/teklifler', label: 'Teklifler' },
  orders: { path: '/siparisler', label: 'Siparişler' },
  waybills: { path: '/irsaliyeler', label: 'İrsaliyeler' },
  'checks-notes': { path: '/cek-senet', label: 'Çek / Senet' },
  currency: { path: '/doviz', label: 'Döviz' },
  'quick-sale': { path: '/hizli-satis', label: 'Hızlı Satış' },
  'restaurant-management': { path: '/restoran', label: 'Restoran Yönetimi' },
  'order-management': { path: '/siparis-yonetimi', label: 'Paket Sipariş Yönetimi' },
  personnel: { path: '/personnel', label: 'Personeller' },
  branches: { path: '/branches', label: 'Şubeler' },
  reports: { path: '/reports', label: 'Raporlar' },
  'end-of-day': { path: '/gun-sonu', label: 'Gün Sonu Raporu' },
  'end-of-day-list': { path: '/gun-sonlari', label: 'Kayıtlı Gün Sonları' },
  'qr-menu-management': { path: '/qr-menu-management', label: 'QR Menü Yönetimi' },
  'qr-orders': { path: '/qr-menu/orders', label: 'QR Menü Siparişleri' },
  'qr-tables': { path: '/qr-menu/tables', label: 'QR Menü Masaları' },
  'kitchen-screen-selection': { path: '/mutfak-ekrani-secimi', label: 'Mutfak Ekranı Seçimi' },
  'kiosk-menu': { path: '/kiosk-menu', label: 'Kiosk Yönetimi' },
  'kiosk-products': { path: '/kiosk-products', label: 'Kiosk Ürünleri' },
  courier: { path: '/kurye', label: 'Kurye Yönetimi' },
  integrations: { path: '/entegrasyonlar', label: 'Entegrasyonlar' },
  'image-library': { path: '/resim-kutuphanesi', label: 'Resim Kütüphanesi' },
  shifts: { path: '/shifts', label: 'Vardiyalar' },
  projects: { path: '/projeler', label: 'Projeler' },
};

// ═══════════════════════════════════════════════════════════════════════════
// Ortak yardımcılar
// ═══════════════════════════════════════════════════════════════════════════

/** API yanıtlarındaki { data: ... } sargısını açar (bazı endpoint'ler sarmalar, bazıları sarmaz). */
const unwrap = (resp: any): any => resp?.data?.data ?? resp?.data ?? resp;

/** unwrap + dizi garantisi. */
const listOf = (resp: any): any[] => {
  const d = unwrap(resp);
  return Array.isArray(d) ? d : [];
};

const str = (v: any): string => (v === undefined || v === null ? '' : String(v).trim());

const toIntId = (v: any): number | null => {
  const n = parseInt(String(v), 10);
  return Number.isFinite(n) && !isNaN(n) ? n : null;
};

const clampLimit = (v: any, def: number, max: number): number =>
  Math.min(max, Math.max(1, Number(v) || def));

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * İşlem tarihini Türkiye yerel saatiyle (timezone eki OLMADAN) üretir.
 * toISOString() UTC'ye çevirip 3 saat geriye kaydırıyordu; backend cari hareket
 * tarihlerinde UTC+3 yerel saat konvansiyonu kullanır (bkz. MakePaymentModal).
 */
const localNowIso = (): string => {
  const n = new Date();
  return `${n.getFullYear()}-${pad2(n.getMonth() + 1)}-${pad2(n.getDate())}T${pad2(n.getHours())}:${pad2(n.getMinutes())}:${pad2(n.getSeconds())}`;
};

/** Belge no verilmezse AI işlemleri için izlenebilir bir numara üretir (yerel saat).
 *  prefix: banka transferlerinde UI konvansiyonuna uygun BBT/BCT gibi önekler için. */
const genDocumentNo = (prefix: string = 'AI'): string => {
  const n = new Date();
  const d = `${n.getFullYear()}${pad2(n.getMonth() + 1)}${pad2(n.getDate())}`;
  const t = `${pad2(n.getHours())}${pad2(n.getMinutes())}${pad2(n.getSeconds())}`;
  return `${prefix}-${d}-${t}`;
};

const requireCompanyId = (ctx: AgentActionContext): string => {
  if (ctx.companyId) return ctx.companyId;
  try {
    const stored = localStorage.getItem('userDetails');
    const cid = stored ? JSON.parse(stored)?.companyId : null;
    if (cid) return cid;
  } catch {
    // parse hatası — aşağıda fırlat
  }
  throw new Error('Şirket bilgisi (companyId) bulunamadı — oturumu yenileyin.');
};

const actorEmail = (ctx: AgentActionContext): string => ctx.userEmail || 'AI Asistan';

/** Kompakt cari özeti — token maliyeti düşük kalsın diye yalnızca temel alanlar. */
const mapAccount = (a: any) => {
  const debit = Number(a.debitBalance ?? a.DebitBalance ?? a.totalDebit ?? 0) || 0;
  const credit = Number(a.creditBalance ?? a.CreditBalance ?? a.totalCredit ?? 0) || 0;
  return {
    id: a.id ?? a.Id,
    companyName: a.companyName ?? a.CompanyName ?? '',
    authorizedPerson: a.authorizedPerson ?? a.AuthorizedPerson ?? '',
    taxNumber: a.taxNumber ?? a.TaxNumber ?? '',
    phone: a.phone ?? a.Phone ?? '',
    email: a.email ?? a.Email ?? '',
    city: a.city ?? a.City ?? '',
    accountCode: a.accountCode ?? a.AccountCode ?? '',
    debitBalance: debit,
    creditBalance: credit,
    /** Pozitif = cari bize borçlu (alacak), negatif = biz cariye borçluyuz. */
    balance: credit - debit,
    isActive: (a.isActive ?? a.IsActive ?? true) !== false,
  };
};

const mapTransaction = (t: any) => ({
  id: t.id ?? t.Id,
  date: t.date ?? t.Date ?? t.transactionDate ?? t.createdAt ?? null,
  transactionType: t.transactionType ?? t.TransactionType ?? '',
  amount: Number(t.amount ?? t.Amount ?? 0) || 0,
  currency: t.currency ?? t.Currency ?? 'TRY',
  paymentMethod: t.paymentMethod ?? t.PaymentMethod ?? null,
  description: t.description ?? t.Description ?? '',
  documentNo: t.documentNo ?? t.DocumentNo ?? '',
  accountName: t.accountName ?? t.AccountName ?? undefined,
});

const mapGroup = (g: any) => ({
  id: g.id ?? g.Id,
  name: g.name ?? g.Name ?? '',
  parentId: g.parentId ?? g.ParentId ?? null,
  level: g.level ?? g.Level ?? 1,
});

const getAccount = async (accountId: number): Promise<any | null> => {
  const resp = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_ID(accountId));
  const acc = unwrap(resp);
  return acc && (acc.id ?? acc.Id) !== undefined ? acc : acc || null;
};

const accountNameOf = (a: any): string => str(a?.companyName ?? a?.CompanyName);

const isGeneralAccount = (a: any): boolean => accountNameOf(a) === 'Genel Cari';

const fetchGroups = async (): Promise<any[]> =>
  listOf(await accountingApi.get(ACCOUNTING_API.ACCOUNT_GROUP_BY_COMPANY));

const findGroupByName = (groups: any[], name: string): any | null =>
  groups.find((g) => str(g.name ?? g.Name).toLowerCase() === name.trim().toLowerCase()) ?? null;

/** Kasa adını id'den çözer (tahsilat/ödeme payload'ı ad da taşır). */
const resolveCashboxName = async (cashboxId: any): Promise<string | null> => {
  const boxes = listOf(await accountingApi.get(ACCOUNTING_API.CASHBOX_BY_COMPANY));
  const box = boxes.find((b) => String(b.id ?? b.Id) === String(cashboxId));
  return box ? str(box.name ?? box.Name) : null;
};

/** Banka hesabı adını id'den çözer. */
const resolveBankAccountName = async (bankAccountId: any): Promise<string | null> => {
  const accounts = listOf(await accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY));
  const acc = accounts.find((b) => String(b.id ?? b.Id) === String(bankAccountId));
  return acc ? str(acc.bankName ?? acc.BankName ?? acc.name ?? acc.Name) : null;
};

/** Sayı veya Türk formatlı string bakiyeyi ("1.234,56") sayıya çevirir. */
const parseAmountLoose = (v: any): number => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const s = str(v);
  if (!s) return 0;
  const direct = Number(s);
  if (Number.isFinite(direct)) return direct;
  const tr = Number(s.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(tr) ? tr : 0;
};

const fmtAmount = (v: any, currency?: any): string =>
  `${Number(v ?? 0).toLocaleString('tr-TR')} ${str(currency) || 'TRY'}`;

// ═══════════════════════════════════════════════════════════════════════════
// Genel eylemler
// ═══════════════════════════════════════════════════════════════════════════

const navigateToPage: AgentActionExecutor = {
  describe: (args) => {
    const page = PAGE_ROUTES[String(args.pageId ?? '')];
    return page ? `"${page.label}" sayfasına git` : 'Sayfaya git';
  },
  execute: async (args, ctx) => {
    const pageId = String(args.pageId ?? '');
    const page = PAGE_ROUTES[pageId];
    if (!page) {
      return { success: false, error: `Bilinmeyen sayfa: ${pageId}` };
    }
    ctx.navigate(page.path);
    return { success: true, navigatedTo: page.label, path: page.path };
  },
};

const searchProduct: AgentActionExecutor = {
  describe: (args) => `Ürün ara: "${String(args.query ?? '')}"`,
  execute: async (args) => {
    const query = String(args.query ?? '').trim();
    const limit = clampLimit(args.limit, 5, 20);
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEMS_PAGED, {
      pageNumber: 1,
      pageSize: limit,
      searchTerm: query || undefined,
      categoryId: str(args.categoryId) || undefined,
      brandId: str(args.brandId) || undefined,
      warehouseId: str(args.warehouseId) || undefined,
      isActive: args.includePassive === true ? undefined : true,
      excludeVariantChildren: true,
    });

    const data = response.data || {};
    const items: any[] = data.items || data.Items || [];
    const totalCount = data.totalCount || data.TotalCount || 0;

    return {
      success: true,
      totalCount,
      // Kompakt sonuç: token maliyeti düşük kalsın diye yalnızca temel alanlar
      products: items.map((i: any) => {
        const stocks: any[] = i.warehouseStocks || i.WarehouseStocks || [];
        const totalStock = stocks.reduce(
          (sum, ws) => sum + (Number(ws.quantity ?? ws.Quantity) || 0),
          0
        );
        return {
          id: i.id || i.Id || '',
          name: i.name || i.Name || '',
          stockCode: i.stockCode || i.StockCode || '',
          barcode: i.barcode || i.Barcode || '',
          unit: i.unit || i.Unit || 'adet',
          salePrice: Number(i.salePrice ?? i.SalePrice) || 0,
          purchasePrice: Number(i.purchasePrice ?? i.PurchasePrice) || 0,
          vatRate: Number(i.vatRate ?? i.VatRate) || 0,
          isActive: (i.isActive ?? i.IsActive ?? true) !== false,
          stockQuantity: totalStock,
        };
      }),
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// UI filtre köprüsü — Ürün ve Stok SAYFASININ filtrelerini doldurur
// ═══════════════════════════════════════════════════════════════════════════

/** Ada göre eşleştirme: önce tam eşleşme (TR harf duyarsız), sonra "içerir". */
const matchByName = <T extends { id: string; name: string }>(rows: T[], query: string): T | null => {
  const q = query.trim().toLocaleLowerCase('tr');
  if (!q) return null;
  return (
    rows.find((r) => r.name.toLocaleLowerCase('tr') === q) ??
    rows.find((r) => r.name.toLocaleLowerCase('tr').includes(q)) ??
    null
  );
};

const applyInventoryFilters: AgentActionExecutor = {
  describe: (args) => {
    const parts: string[] = [];
    if (args.clearAll === true) parts.push('filtreleri temizle');
    if (str(args.searchText)) parts.push(`ara: "${str(args.searchText)}"`);
    if (str(args.categoryName)) parts.push(`kategori: ${str(args.categoryName)}`);
    if (str(args.brandName)) parts.push(`marka: ${str(args.brandName)}`);
    if (str(args.warehouseName)) parts.push(`depo: ${str(args.warehouseName)}`);
    return `Stok sayfasında filtrele${parts.length > 0 ? ` (${parts.join(', ')})` : ''}`;
  },
  execute: async (args, ctx) => {
    const req: InventoryFilterRequest = {};
    const applied: string[] = [];

    if (args.clearAll === true) {
      req.clearAll = true;
      applied.push('tüm filtreler temizlendi');
    }

    if (args.searchText !== undefined && args.searchText !== null) {
      req.searchText = str(args.searchText);
      applied.push(req.searchText ? `arama: "${req.searchText}"` : 'arama kutusu temizlendi');
    }

    if (str(args.categoryName)) {
      const rows = (await fetchCategories()).map((c: any) => ({
        id: str(c.id ?? c.Id),
        name: str(c.name ?? c.Name),
      }));
      const match = matchByName(rows, str(args.categoryName));
      if (!match) {
        return {
          success: false,
          error: `Kategori bulunamadı: "${str(args.categoryName)}" — mevcut kategoriler için listCategories kullan.`,
        };
      }
      req.categoryId = match.id;
      applied.push(`kategori: ${match.name}`);
    }

    if (str(args.brandName)) {
      const rows = (await fetchBrands()).map((b: any) => ({
        id: str(b.id ?? b.Id),
        name: str(b.name ?? b.Name),
      }));
      const match = matchByName(rows, str(args.brandName));
      if (!match) {
        return {
          success: false,
          error: `Marka bulunamadı: "${str(args.brandName)}" — mevcut markalar için listBrands kullan.`,
        };
      }
      req.brandId = match.id;
      applied.push(`marka: ${match.name}`);
    }

    if (str(args.warehouseName)) {
      const rows = await fetchWarehouses();
      const match = matchByName(rows, str(args.warehouseName));
      if (!match) {
        return {
          success: false,
          error: `Depo bulunamadı: "${str(args.warehouseName)}" — mevcut depolar için listWarehouses kullan.`,
        };
      }
      req.warehouseId = match.id;
      applied.push(`depo: ${match.name}`);
    }

    if (args.vatRate !== undefined && args.vatRate !== null && args.vatRate !== '') {
      const rate = Number(args.vatRate);
      if (Number.isNaN(rate)) return { success: false, error: 'vatRate sayısal olmalı (ör. 20).' };
      req.vatRate = rate;
      applied.push(`KDV: %${rate}`);
    }

    if (typeof args.onlyActive === 'boolean') {
      req.onlyActive = args.onlyActive;
      applied.push(args.onlyActive ? 'yalnızca aktif ürünler' : 'pasif ürünler de gösteriliyor');
    }

    if (typeof args.hideZeroPrice === 'boolean') {
      req.hideZeroPrice = args.hideZeroPrice;
      applied.push(args.hideZeroPrice ? 'fiyatı 0 olanlar gizli' : 'fiyatı 0 olanlar gösteriliyor');
    }

    if (applied.length === 0) {
      return {
        success: false,
        error:
          'Uygulanacak filtre verilmedi — searchText, categoryName, brandName, warehouseName, vatRate, onlyActive, hideZeroPrice veya clearAll parametrelerinden en az birini gönder.',
      };
    }

    ctx.navigate('/stok');
    requestInventoryFilters(req);

    return {
      success: true,
      applied,
      note:
        'Ürün ve Stok sayfası açıldı ve filtreler sayfaya uygulandı — sonuç listesi kullanıcının ekranında, kayıtlar üzerinde kendisi işlem yapabilir. Ürün verisine (id/fiyat/stok) ihtiyacın varsa searchProduct kullan.',
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Cari Hesaplar — OKUMA eylemleri
// ═══════════════════════════════════════════════════════════════════════════

const searchAccount: AgentActionExecutor = {
  describe: (args) => {
    const q = str(args.query);
    return q ? `Cari ara: "${q}"` : 'Carileri listele';
  },
  execute: async (args) => {
    const limit = clampLimit(args.limit, 10, 50);
    const includePassive = args.includePassive === true;
    const url = ACCOUNTING_API.ACCOUNT_BY_COMPANY_PAGED(
      1,
      limit,
      str(args.query) || undefined,
      undefined,
      'companyName',
      'asc',
      !includePassive,
      null
    );
    const response = await accountingApi.get(url);
    const body = response.data || {};
    const items: any[] = body.data || [];
    return {
      success: true,
      totalCount: body.pagination?.totalCount ?? items.length,
      accounts: items.map(mapAccount),
    };
  },
};

const getAccountDetails: AgentActionExecutor = {
  describe: (args) => `Cari detayını getir (id: ${args.accountId})`,
  execute: async (args) => {
    const id = toIntId(args.accountId);
    if (id === null) return { success: false, error: 'Geçersiz accountId.' };
    const acc = await getAccount(id);
    if (!acc) return { success: false, error: `Cari bulunamadı (id: ${id}).` };
    return {
      success: true,
      account: {
        ...mapAccount(acc),
        taxOffice: acc.taxOffice ?? acc.TaxOffice ?? '',
        mobilePhone: acc.mobilePhone ?? acc.MobilePhone ?? '',
        district: acc.district ?? acc.District ?? '',
        address: acc.address ?? acc.Address ?? '',
        notes: acc.notes ?? acc.Notes ?? '',
        riskLimitAmount: acc.riskLimitAmount ?? acc.RiskLimitAmount ?? null,
        paymentDueDays: acc.paymentDueDays ?? acc.PaymentDueDays ?? null,
      },
    };
  },
};

const listAccountTransactions: AgentActionExecutor = {
  describe: (args) => `Cari hareketlerini listele (id: ${args.accountId})`,
  execute: async (args) => {
    const id = toIntId(args.accountId);
    if (id === null) return { success: false, error: 'Geçersiz accountId.' };
    const limit = clampLimit(args.limit, 20, 100);
    const url = ACCOUNTING_API.TRANSACTIONS_BY_ACCOUNT_PAGED(id, 1, limit, {
      sortColumn: 'date',
      sortDirection: 'desc',
      startDate: str(args.startDate) || undefined,
      endDate: str(args.endDate) || undefined,
      transactionType: str(args.transactionType) || undefined,
    });
    const response = await accountingApi.get(url);
    const body = response.data || {};
    const items: any[] = body.data || [];
    return {
      success: true,
      totalCount: body.pagination?.totalCount ?? items.length,
      transactions: items.map(mapTransaction),
    };
  },
};

const listCompanyTransactions: AgentActionExecutor = {
  describe: () => 'Şirket geneli cari hareket raporunu getir',
  execute: async (args) => {
    const limit = clampLimit(args.limit, 20, 100);
    const params = new URLSearchParams({ page: '1', pageSize: String(limit) });
    if (str(args.searchTerm)) params.append('searchTerm', str(args.searchTerm));
    if (str(args.transactionType)) params.append('transactionType', str(args.transactionType));
    if (str(args.startDate)) params.append('startDate', str(args.startDate));
    if (str(args.endDate)) params.append('endDate', str(args.endDate));
    params.append('sortColumn', 'date');
    params.append('sortDirection', 'desc');
    const response = await accountingApi.get(
      `${ACCOUNTING_API.TRANSACTIONS_BY_COMPANY}/paged?${params.toString()}`
    );
    const body = response.data || {};
    const items: any[] = body.data || [];
    return {
      success: true,
      totalCount: body.pagination?.totalCount ?? items.length,
      transactions: items.map(mapTransaction),
    };
  },
};

const listTransactionLogs: AgentActionExecutor = {
  describe: () => 'Cari işlem loglarını listele',
  execute: async (args) => {
    const limit = clampLimit(args.limit, 20, 100);
    const logs = listOf(await accountingApi.get(ACCOUNTING_API.TRANSACTION_LOGS_BY_COMPANY));
    return {
      success: true,
      totalCount: logs.length,
      logs: logs.slice(0, limit).map((l: any) => ({
        id: l.id ?? l.Id,
        logType: l.logType ?? l.LogType ?? l.type ?? '',
        transactionId: l.transactionId ?? l.TransactionId ?? null,
        reason: l.reason ?? l.Reason ?? '',
        performedBy: l.deletedBy ?? l.DeletedBy ?? l.changedBy ?? l.ChangedBy ?? l.createdBy ?? '',
        date: l.createdAt ?? l.CreatedAt ?? l.logDate ?? null,
        description: l.description ?? l.Description ?? '',
      })),
    };
  },
};

const listAccountGroups: AgentActionExecutor = {
  describe: () => 'Cari gruplarını listele',
  execute: async () => {
    const groups = await fetchGroups();
    return { success: true, groups: groups.map(mapGroup) };
  },
};

const listShippingAddresses: AgentActionExecutor = {
  describe: (args) => `Sevk adreslerini listele (cari id: ${args.accountId})`,
  execute: async (args) => {
    const id = toIntId(args.accountId);
    if (id === null) return { success: false, error: 'Geçersiz accountId.' };
    const addresses = listOf(await accountingApi.get(ACCOUNTING_API.SHIPPING_ADDRESSES(id)));
    return {
      success: true,
      addresses: addresses.map((a: any) => ({
        id: a.id ?? a.Id,
        country: a.country ?? a.Country ?? '',
        city: a.city ?? a.City ?? '',
        district: a.district ?? a.District ?? '',
        address: a.address ?? a.Address ?? '',
        authorizedPerson: a.authorizedPerson ?? a.AuthorizedPerson ?? '',
        phone: a.phone ?? a.Phone ?? '',
      })),
    };
  },
};

const listCashboxes: AgentActionExecutor = {
  describe: () => 'Kasaları listele',
  execute: async () => {
    const boxes = listOf(await accountingApi.get(ACCOUNTING_API.CASHBOX_BY_COMPANY));
    return {
      success: true,
      cashboxes: boxes.map((b: any) => ({
        id: b.id ?? b.Id,
        name: b.name ?? b.Name ?? '',
        currency: b.currency ?? b.Currency ?? 'TRY',
        balance: parseAmountLoose(b.balance ?? b.Balance),
        isDefault: (b.isDefault ?? b.IsDefault) === true,
        hasTransactions: (b.hasTransactions ?? b.HasTransactions) === true,
        isActive: (b.isActive ?? b.IsActive ?? true) !== false,
      })),
    };
  },
};

const listBankAccounts: AgentActionExecutor = {
  describe: () => 'Banka hesaplarını listele',
  execute: async () => {
    const accounts = listOf(await accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY));
    return {
      success: true,
      bankAccounts: accounts.map((b: any) => ({
        id: b.id ?? b.Id,
        bankName: b.bankName ?? b.BankName ?? '',
        accountNumber: b.accountNumber ?? b.AccountNumber ?? '',
        balance: Number(b.balance ?? b.Balance ?? 0) || 0,
        currency: b.currency ?? b.Currency ?? 'TRY',
        isPosAccount: (b.isPosAccount ?? b.IsPosAccount) === true,
        isCheckAccount: (b.isCheckAccount ?? b.IsCheckAccount) === true,
        // Alt hesaplar: banka içi virman ve alt hesaba yatırma/çekme için id gerekir
        subAccounts: (Array.isArray(b.subAccounts) ? b.subAccounts : []).map((s: any) => ({
          id: s.id ?? s.Id,
          accountName: s.accountName ?? s.AccountName ?? '',
          balance: Number(s.balance ?? s.Balance ?? 0) || 0,
          isPosAccount: (s.isPosAccount ?? s.IsPosAccount) === true,
          isCheckAccount: (s.isCheckAccount ?? s.IsCheckAccount) === true,
        })),
      })),
    };
  },
};

const listCreditCards: AgentActionExecutor = {
  describe: () => 'Kredi kartlarını listele',
  execute: async () => {
    const cards = listOf(await accountingApi.get(ACCOUNTING_API.CREDIT_CARDS));
    return {
      success: true,
      creditCards: cards.map((c: any) => ({
        id: c.id ?? c.Id,
        name: c.name ?? c.Name ?? c.cardName ?? c.CardName ?? '',
        bankName: c.bankName ?? c.BankName ?? '',
        cardLimit: Number(c.cardLimit ?? c.CardLimit ?? c.limit ?? 0) || 0,
        currentDebt: Number(c.currentDebt ?? c.CurrentDebt ?? 0) || 0,
      })),
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Cari Hesaplar — YAZMA eylemleri (AiChatWindow onay kartından sonra çalışır)
// ═══════════════════════════════════════════════════════════════════════════

const createAccount: AgentActionExecutor = {
  describe: (args) => `Yeni cari oluştur: "${str(args.companyName)}"`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const name = str(args.companyName);
    if (!name) return { success: false, error: 'Cari adı (companyName) boş olamaz.' };

    // Duplicate koruması — UI'daki BulkAdd davranışıyla aynı
    const existing = listOf(await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY));
    if (existing.some((a) => accountNameOf(a).toLowerCase() === name.toLowerCase())) {
      return { success: false, error: `"${name}" adında bir cari zaten mevcut.` };
    }

    const groups = await fetchGroups();
    const wantedGroup = str(args.groupName) || 'Genel Cari Grubu';
    const group = findGroupByName(groups, wantedGroup);
    if (!group) {
      return {
        success: false,
        error: `"${wantedGroup}" adında cari grubu bulunamadı. listAccountGroups ile mevcut grupları kontrol edin.`,
      };
    }

    const payload = {
      companyId,
      companyName: name,
      groupFirebaseId: group.firebaseId || String(group.id),
      authorizedPerson: str(args.authorizedPerson),
      phone: str(args.phone),
      mobilePhone: str(args.mobilePhone),
      email: str(args.email),
      website: '',
      postalCode: '',
      taxOffice: str(args.taxOffice),
      taxNumber: str(args.taxNumber) || '11111111111',
      tradeRegisterNumber: '',
      country: 'Türkiye',
      city: str(args.city),
      district: str(args.district),
      address: str(args.address),
      accountCode: '',
      notes: str(args.notes),
      branchId: null,
      riskLimitAmount: null,
      riskLimitCurrency: 'TRY',
      paymentDueDays: 30,
      riskLimitAction: null,
      preventNegativeBalance: false,
      isActive: true,
      createdAt: new Date().toISOString(),
      createdBy: actorEmail(ctx),
    };

    const response = await accountingApi.post(ACCOUNTING_API.ACCOUNTS, payload);
    const created = unwrap(response);
    return {
      success: true,
      accountId: created?.id ?? created?.Id ?? null,
      companyName: name,
      group: str(group.name ?? group.Name),
    };
  },
};

const updateAccount: AgentActionExecutor = {
  describe: (args) => `Cariyi güncelle (id: ${args.accountId})`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const id = toIntId(args.accountId);
    if (id === null) return { success: false, error: 'Geçersiz accountId.' };

    const current = await getAccount(id);
    if (!current) return { success: false, error: `Cari bulunamadı (id: ${id}).` };

    const editableFields = [
      'companyName',
      'authorizedPerson',
      'taxNumber',
      'taxOffice',
      'phone',
      'mobilePhone',
      'email',
      'city',
      'district',
      'address',
      'notes',
    ];
    const changes: Record<string, string> = {};
    for (const field of editableFields) {
      if (args[field] !== undefined && args[field] !== null) changes[field] = str(args[field]);
    }
    if (Object.keys(changes).length === 0) {
      return { success: false, error: 'Değiştirilecek en az bir alan verilmelidir.' };
    }

    // Mevcut kayıt üzerine yalnızca verilen alanlar yazılır; update endpoint'i tam payload bekler.
    const payload = {
      ...current,
      ...changes,
      id,
      companyId,
      updatedAt: new Date().toISOString(),
    };
    await accountingApi.post(ACCOUNTING_API.ACCOUNT_UPDATE(id), payload);
    return { success: true, accountId: id, updatedFields: Object.keys(changes) };
  },
};

const deleteAccount: AgentActionExecutor = {
  describe: (args) => `Cariyi KALICI olarak sil (id: ${args.accountId})`,
  execute: async (args, ctx) => {
    requireCompanyId(ctx);
    const id = toIntId(args.accountId);
    if (id === null) return { success: false, error: 'Geçersiz accountId.' };
    const acc = await getAccount(id);
    if (!acc) return { success: false, error: `Cari bulunamadı (id: ${id}).` };
    if (isGeneralAccount(acc)) {
      return { success: false, error: '"Genel Cari" hesabı silinemez (sistem hesabı).' };
    }
    await accountingApi.post(ACCOUNTING_API.ACCOUNT_DELETE(id));
    return { success: true, accountId: id, companyName: accountNameOf(acc), deleted: true };
  },
};

const deactivateAccount: AgentActionExecutor = {
  describe: (args) => `Cariyi pasife al (id: ${args.accountId})`,
  execute: async (args, ctx) => {
    requireCompanyId(ctx);
    const id = toIntId(args.accountId);
    if (id === null) return { success: false, error: 'Geçersiz accountId.' };
    const acc = await getAccount(id);
    if (!acc) return { success: false, error: `Cari bulunamadı (id: ${id}).` };
    if (isGeneralAccount(acc)) {
      return { success: false, error: '"Genel Cari" hesabı pasife alınamaz (sistem hesabı).' };
    }
    await accountingApi.post(ACCOUNTING_API.ACCOUNT_DEACTIVATE(id));
    return { success: true, accountId: id, companyName: accountNameOf(acc), isActive: false };
  },
};

const activateAccount: AgentActionExecutor = {
  describe: (args) => `Cariyi aktifleştir (id: ${args.accountId})`,
  execute: async (args, ctx) => {
    requireCompanyId(ctx);
    const id = toIntId(args.accountId);
    if (id === null) return { success: false, error: 'Geçersiz accountId.' };
    await accountingApi.post(ACCOUNTING_API.ACCOUNT_ACTIVATE(id));
    return { success: true, accountId: id, isActive: true };
  },
};

const bulkCreateAccounts: AgentActionExecutor = {
  describe: (args) => {
    const count = str(args.names).split(/[,;\n]/).filter((s) => s.trim()).length;
    return `${count} cari hesabı toplu oluştur`;
  },
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const names = Array.from(
      new Set(
        str(args.names)
          .split(/[,;\n]/)
          .map((s) => s.trim())
          .filter(Boolean)
      )
    );
    if (names.length === 0) return { success: false, error: 'Oluşturulacak cari adı verilmedi.' };

    const groups = await fetchGroups();
    const wantedGroup = str(args.groupName) || 'Genel Cari Grubu';
    const group = findGroupByName(groups, wantedGroup);
    if (!group) {
      return { success: false, error: `"${wantedGroup}" adında cari grubu bulunamadı.` };
    }

    const existing = listOf(await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY));
    const existingNames = new Set(existing.map((a) => accountNameOf(a).toLowerCase()));

    const created: string[] = [];
    const skipped: string[] = [];
    for (const name of names) {
      if (existingNames.has(name.toLowerCase())) {
        skipped.push(name);
        continue;
      }
      await accountingApi.post(ACCOUNTING_API.ACCOUNTS, {
        companyId,
        companyName: name,
        groupFirebaseId: group.firebaseId || String(group.id),
        authorizedPerson: 'Yetkili',
        phone: '',
        mobilePhone: '',
        email: '',
        website: '',
        postalCode: '',
        taxOffice: '',
        taxNumber: '11111111111',
        tradeRegisterNumber: '',
        country: 'Türkiye',
        city: '',
        district: '',
        address: '',
        accountCode: '',
        notes: 'AI Asistan ile toplu eklendi',
        branchId: null,
        riskLimitAmount: 0,
        riskLimitCurrency: 'TRY',
        paymentDueDays: 30,
        riskLimitAction: 'işlem yaptır',
        preventNegativeBalance: false,
        createdBy: actorEmail(ctx),
      });
      created.push(name);
      existingNames.add(name.toLowerCase());
    }
    return { success: true, createdCount: created.length, skippedCount: skipped.length, created, skipped };
  },
};

/** Tahsilat/ödeme ortak doğrulaması + payload iskeleti. */
const buildMoneyTransactionPayload = async (
  args: Record<string, any>,
  ctx: AgentActionContext,
  kind: 'collection' | 'payment'
): Promise<{ error?: string; payload?: Record<string, any>; accountName?: string }> => {
  const companyId = requireCompanyId(ctx);
  const id = toIntId(args.accountId);
  if (id === null) return { error: 'Geçersiz accountId.' };
  const amount = Number(args.amount);
  if (!Number.isFinite(amount) || amount <= 0) return { error: 'Tutar pozitif bir sayı olmalıdır.' };

  const method = str(args.paymentMethod);
  const needsCashbox = method === 'cash';
  const needsBank =
    kind === 'collection'
      ? ['bank_transfer', 'pos', 'check', 'credit_card'].includes(method)
      : method === 'bank_transfer';
  const needsCreditCard = kind === 'payment' && method === 'credit_card';

  if (needsCashbox && !args.cashboxId) {
    return { error: 'Nakit işlem için cashboxId zorunludur — listCashboxes ile kasaları listeleyin.' };
  }
  if (needsBank && !args.bankAccountId) {
    return { error: 'Bu ödeme yöntemi için bankAccountId zorunludur — listBankAccounts ile hesapları listeleyin.' };
  }
  if (needsCreditCard && !args.creditCardId) {
    return { error: 'Kredi kartı ödemesi için creditCardId zorunludur — listCreditCards ile kartları listeleyin.' };
  }

  const acc = await getAccount(id);
  if (!acc) return { error: `Cari bulunamadı (id: ${id}).` };
  if (isGeneralAccount(acc)) {
    return { error: '"Genel Cari" üzerinde tahsilat/ödeme yapılamaz (sistem hesabı).' };
  }
  const accountName = accountNameOf(acc);

  const cashboxId = needsCashbox ? String(args.cashboxId) : null;
  const bankAccountId = needsBank ? String(args.bankAccountId) : null;
  const cashboxName = cashboxId ? await resolveCashboxName(cashboxId) : null;
  const bankAccountName = bankAccountId ? await resolveBankAccountName(bankAccountId) : null;
  if (cashboxId && !cashboxName) return { error: `Kasa bulunamadı (id: ${cashboxId}).` };
  if (bankAccountId && !bankAccountName) return { error: `Banka hesabı bulunamadı (id: ${bankAccountId}).` };

  return {
    accountName,
    payload: {
      companyId,
      accountId: id,
      accountName,
      amount,
      currency: str(args.currency) || 'TRY',
      paymentMethod: method,
      cashboxId,
      cashboxName,
      bankAccountId,
      bankSubAccountId: null,
      bankAccountName,
      description:
        str(args.description) ||
        (kind === 'collection'
          ? `${accountName} carisinden tahsilat (AI Asistan)`
          : `${accountName} carisine ödeme (AI Asistan)`),
      documentNo: str(args.documentNo) || genDocumentNo(),
      date: localNowIso(), // Türkiye yerel saati — UTC gönderilirse hareket 3 saat geride kaydolur
      createdBy: actorEmail(ctx),
      branchId: null,
    },
  };
};

const collectPayment: AgentActionExecutor = {
  describe: (args) =>
    `Cariden tahsilat: ${fmtAmount(args.amount, args.currency)} (${str(args.paymentMethod)})`,
  execute: async (args, ctx) => {
    const built = await buildMoneyTransactionPayload(args, ctx, 'collection');
    if (built.error || !built.payload) return { success: false, error: built.error };
    const response = await accountingApi.post(ACCOUNTING_API.TRANSACTION_COLLECTION, built.payload);
    const result = unwrap(response);
    return {
      success: true,
      transactionId: result?.id ?? result?.Id ?? null,
      accountName: built.accountName,
      amount: built.payload.amount,
      paymentMethod: built.payload.paymentMethod,
      documentNo: built.payload.documentNo,
    };
  },
};

const makePayment: AgentActionExecutor = {
  describe: (args) =>
    `Cariye ödeme: ${fmtAmount(args.amount, args.currency)} (${str(args.paymentMethod)})`,
  execute: async (args, ctx) => {
    const built = await buildMoneyTransactionPayload(args, ctx, 'payment');
    if (built.error || !built.payload) return { success: false, error: built.error };
    const payload: Record<string, any> = {
      ...built.payload,
      creditCardId:
        str(args.paymentMethod) === 'credit_card' ? toIntId(args.creditCardId) : null,
    };
    const response = await accountingApi.post(ACCOUNTING_API.TRANSACTION_PAYMENT, payload);
    const result = unwrap(response);
    return {
      success: true,
      transactionId: result?.id ?? result?.Id ?? null,
      accountName: built.accountName,
      amount: payload.amount,
      paymentMethod: payload.paymentMethod,
      documentNo: payload.documentNo,
    };
  },
};

const debitAccount: AgentActionExecutor = {
  describe: (args) => `Cariyi borçlandır: ${fmtAmount(args.amount, args.currency)}`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const id = toIntId(args.accountId);
    if (id === null) return { success: false, error: 'Geçersiz accountId.' };
    const amount = Number(args.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      return { success: false, error: 'Tutar pozitif bir sayı olmalıdır.' };
    }
    const acc = await getAccount(id);
    if (!acc) return { success: false, error: `Cari bulunamadı (id: ${id}).` };
    if (isGeneralAccount(acc)) {
      return { success: false, error: '"Genel Cari" borçlandırılamaz (sistem hesabı).' };
    }
    const accountName = accountNameOf(acc);
    const payload = {
      companyId,
      accountId: id,
      accountName,
      amount,
      currency: str(args.currency) || 'TRY',
      description: str(args.description) || `${accountName} carisi borçlandırma (AI Asistan)`,
      documentNo: str(args.documentNo) || genDocumentNo(),
      date: localNowIso(), // Türkiye yerel saati — UTC gönderilirse hareket 3 saat geride kaydolur
      createdBy: actorEmail(ctx),
      branchId: null,
    };
    const response = await accountingApi.post(ACCOUNTING_API.TRANSACTION_DEBIT, payload);
    const result = unwrap(response);
    return {
      success: true,
      transactionId: result?.id ?? result?.Id ?? null,
      accountName,
      amount,
      documentNo: payload.documentNo,
    };
  },
};

const deleteTransaction: AgentActionExecutor = {
  describe: (args) => `Cari işlemini sil (id: ${args.transactionId})`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const id = toIntId(args.transactionId);
    if (id === null) return { success: false, error: 'Geçersiz transactionId.' };
    await accountingApi.post(ACCOUNTING_API.TRANSACTION_DELETE(id), {
      companyId,
      reason: str(args.reason) || 'AI Asistan üzerinden silindi',
      deletedBy: actorEmail(ctx),
    });
    return { success: true, transactionId: id, deleted: true };
  },
};

const changeTransactionPaymentMethod: AgentActionExecutor = {
  describe: (args) =>
    `İşlem ödeme yöntemini değiştir (id: ${args.transactionId} → ${str(args.newPaymentMethod)})`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const id = toIntId(args.transactionId);
    if (id === null) return { success: false, error: 'Geçersiz transactionId.' };

    const method = str(args.newPaymentMethod);
    if (method === 'cash' && !args.newCashboxId) {
      return { success: false, error: 'Nakde çevirmek için newCashboxId zorunludur (listCashboxes).' };
    }
    if ((method === 'pos' || method === 'bank_transfer') && !args.newBankAccountId) {
      return { success: false, error: 'POS/havaleye çevirmek için newBankAccountId zorunludur (listBankAccounts).' };
    }

    const cashboxId = method === 'cash' ? String(args.newCashboxId) : null;
    const bankAccountId = method !== 'cash' ? String(args.newBankAccountId) : null;
    const cashboxName = cashboxId ? await resolveCashboxName(cashboxId) : null;
    const bankAccountName = bankAccountId ? await resolveBankAccountName(bankAccountId) : null;
    if (cashboxId && !cashboxName) return { success: false, error: `Kasa bulunamadı (id: ${cashboxId}).` };
    if (bankAccountId && !bankAccountName) {
      return { success: false, error: `Banka hesabı bulunamadı (id: ${bankAccountId}).` };
    }

    await accountingApi.post(ACCOUNTING_API.TRANSACTION_CHANGE_PAYMENT_METHOD(id), {
      companyId,
      newPaymentMethod: method,
      cashboxId,
      cashboxName,
      bankAccountId,
      bankSubAccountId: null,
      bankAccountName,
      reason: str(args.reason) || 'AI Asistan üzerinden değiştirildi',
      changedBy: actorEmail(ctx),
    });
    return { success: true, transactionId: id, newPaymentMethod: method };
  },
};

const transferBetweenAccounts: AgentActionExecutor = {
  describe: (args) =>
    `Cariden cariye transfer: ${fmtAmount(args.amount)} (${args.sourceAccountId} → ${args.targetAccountId})`,
  execute: async (args, ctx) => {
    requireCompanyId(ctx);
    const sourceId = toIntId(args.sourceAccountId);
    const targetId = toIntId(args.targetAccountId);
    if (sourceId === null || targetId === null) {
      return { success: false, error: 'Geçersiz kaynak/hedef accountId.' };
    }
    if (sourceId === targetId) {
      return { success: false, error: 'Kaynak ve hedef cari aynı olamaz.' };
    }
    const amount = Number(args.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      return { success: false, error: 'Tutar pozitif bir sayı olmalıdır.' };
    }
    const [source, target] = await Promise.all([getAccount(sourceId), getAccount(targetId)]);
    if (!source) return { success: false, error: `Kaynak cari bulunamadı (id: ${sourceId}).` };
    if (!target) return { success: false, error: `Hedef cari bulunamadı (id: ${targetId}).` };

    await accountingApi.post(ACCOUNTING_API.ACCOUNT_TRANSFER, {
      sourceAccountId: sourceId,
      targetAccountId: targetId,
      amount,
      description:
        str(args.description) ||
        `Mahsup Fişi: ${accountNameOf(source)} → ${accountNameOf(target)} (AI Asistan)`,
      documentNo: genDocumentNo(),
      createdBy: actorEmail(ctx),
    });
    return {
      success: true,
      sourceAccount: accountNameOf(source),
      targetAccount: accountNameOf(target),
      amount,
    };
  },
};

const createAccountGroup: AgentActionExecutor = {
  describe: (args) => `Cari grubu oluştur: "${str(args.name)}"`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const name = str(args.name);
    if (!name) return { success: false, error: 'Grup adı boş olamaz.' };

    const groups = await fetchGroups();
    if (findGroupByName(groups, name)) {
      return { success: false, error: `"${name}" adında bir grup zaten mevcut.` };
    }

    let parentId: number | null = null;
    let level = 1;
    if (args.parentGroupId !== undefined && args.parentGroupId !== null) {
      const parentIdNum = toIntId(args.parentGroupId);
      const parent = groups.find((g) => (g.id ?? g.Id) === parentIdNum);
      if (!parent) {
        return { success: false, error: `Üst grup bulunamadı (id: ${args.parentGroupId}).` };
      }
      const parentLevel = Number(parent.level ?? parent.Level ?? 1);
      if (parentLevel >= 3) {
        return { success: false, error: 'En fazla 3 seviye grup açılabilir; bu grubun altına grup eklenemez.' };
      }
      parentId = parentIdNum;
      level = parentLevel + 1;
    }

    const response = await accountingApi.post(ACCOUNTING_API.ACCOUNT_GROUPS, {
      companyId,
      name,
      parentId,
      level,
      branchId: null,
      createdAt: new Date().toISOString(),
      createdBy: actorEmail(ctx),
    });
    const created = unwrap(response);
    return { success: true, groupId: created?.id ?? created?.Id ?? null, name, level };
  },
};

const deleteAccountGroup: AgentActionExecutor = {
  describe: (args) => `Cari grubunu sil (id: ${args.groupId})`,
  execute: async (args, ctx) => {
    requireCompanyId(ctx);
    const id = toIntId(args.groupId);
    if (id === null) return { success: false, error: 'Geçersiz groupId.' };

    const groups = await fetchGroups();
    const group = groups.find((g) => (g.id ?? g.Id) === id);
    if (!group) return { success: false, error: `Grup bulunamadı (id: ${id}).` };
    const groupName = str(group.name ?? group.Name);
    if (groupName === 'Genel Cari Grubu') {
      return { success: false, error: '"Genel Cari Grubu" silinemez (sistem grubu).' };
    }
    const hasChildren = groups.some((g) => (g.parentId ?? g.ParentId) === id);
    if (hasChildren) {
      return { success: false, error: 'Alt grubu olan grup silinemez; önce alt grupları silin.' };
    }

    await accountingApi.post(ACCOUNTING_API.ACCOUNT_GROUP_DELETE(id));
    return { success: true, groupId: id, name: groupName, deleted: true };
  },
};

const addShippingAddress: AgentActionExecutor = {
  describe: (args) => `Sevk adresi ekle (cari id: ${args.accountId}, ${str(args.city)})`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const id = toIntId(args.accountId);
    if (id === null) return { success: false, error: 'Geçersiz accountId.' };
    const city = str(args.city);
    const address = str(args.address);
    if (!city || !address) {
      return { success: false, error: 'Şehir (city) ve açık adres (address) zorunludur.' };
    }
    const acc = await getAccount(id);
    if (!acc) return { success: false, error: `Cari bulunamadı (id: ${id}).` };
    if (isGeneralAccount(acc)) {
      return { success: false, error: '"Genel Cari" hesabına sevk adresi eklenemez.' };
    }

    const response = await accountingApi.post(ACCOUNTING_API.SHIPPING_ADDRESSES(id), {
      companyId,
      accountId: id,
      country: str(args.country) || 'Türkiye',
      city,
      district: str(args.district),
      address,
      postalCode: '',
      authorizedPerson: str(args.authorizedPerson),
      phone: str(args.phone),
      mobilePhone: '',
      email: '',
    });
    const created = unwrap(response);
    return {
      success: true,
      accountId: id,
      addressId: created?.id ?? created?.Id ?? null,
      city,
    };
  },
};

const deleteShippingAddress: AgentActionExecutor = {
  describe: (args) => `Sevk adresini sil (cari id: ${args.accountId}, adres id: ${args.addressId})`,
  execute: async (args, ctx) => {
    requireCompanyId(ctx);
    const accountId = toIntId(args.accountId);
    const addressId = toIntId(args.addressId);
    if (accountId === null || addressId === null) {
      return { success: false, error: 'Geçersiz accountId/addressId.' };
    }
    await accountingApi.post(ACCOUNTING_API.SHIPPING_ADDRESS_DELETE(accountId, addressId));
    return { success: true, accountId, addressId, deleted: true };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Banka Modülü — yardımcılar
// (Silme eylemleri BİLİNÇLİ olarak yok: banka hesabı/hareketi, kredi kartı,
//  çek defteri ve kredi silme yalnızca UI'dan yapılabilir.)
// ═══════════════════════════════════════════════════════════════════════════

const fetchBankAccounts = async (): Promise<any[]> =>
  listOf(await accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY));

const findBankAccount = (accounts: any[], id: number): any | null =>
  accounts.find((a) => Number(a.id ?? a.Id) === id) ?? null;

const subAccountsOf = (account: any): any[] =>
  Array.isArray(account?.subAccounts) ? account.subAccounts : [];

const findSubAccount = (account: any, subId: number): any | null =>
  subAccountsOf(account).find((s) => Number(s.id ?? s.Id) === subId) ?? null;

const bankLabel = (account: any): string =>
  `${str(account?.bankName ?? account?.BankName)} (${str(account?.accountNumber ?? account?.AccountNumber)})`;

const balanceOf = (accountOrSub: any): number =>
  Number(accountOrSub?.balance ?? accountOrSub?.Balance ?? 0) || 0;

const currencyOf = (accountOrSub: any): string =>
  str(accountOrSub?.currency ?? accountOrSub?.Currency) || 'TRY';

/** Banka/kart hareketi kompakt özeti (token maliyeti düşük kalsın). */
const mapBankTx = (t: any) => ({
  id: t.id ?? t.Id,
  date: t.date ?? t.Date ?? t.transactionDate ?? t.createdAt ?? null,
  transactionType: t.transactionType ?? t.TransactionType ?? t.type ?? '',
  amount: Number(t.amount ?? t.Amount ?? 0) || 0,
  currency: t.currency ?? t.Currency ?? 'TRY',
  description: t.description ?? t.Description ?? '',
  documentNo: t.documentNo ?? t.DocumentNo ?? '',
});

const mapLoan = (l: any) => ({
  id: l.id ?? l.Id,
  name: l.name ?? l.Name ?? '',
  loanNumber: l.loanNumber ?? l.LoanNumber ?? '',
  bankAccountName: l.bankAccountName ?? l.BankAccountName ?? '',
  principalAmount: Number(l.principalAmount ?? l.PrincipalAmount ?? 0) || 0,
  totalPayback: Number(l.totalPayback ?? l.TotalPayback ?? 0) || 0,
  remainingAmount: Number(l.remainingAmount ?? l.RemainingAmount ?? 0) || 0,
  paidInstallments: Number(l.paidInstallments ?? l.PaidInstallments ?? 0) || 0,
  totalInstallments: Number(l.totalInstallments ?? l.TotalInstallments ?? l.installmentCount ?? 0) || 0,
  status: l.status ?? l.Status ?? '',
  currency: l.currency ?? l.Currency ?? 'TRY',
});

/** YYYY-MM-DD alır, gün kayması olmasın diye öğlen 12:00'lı yerel ISO döndürür. */
const dateNoonIso = (d: string): string => `${d}T12:00:00`;

// ═══════════════════════════════════════════════════════════════════════════
// Banka Modülü — OKUMA eylemleri
// ═══════════════════════════════════════════════════════════════════════════

const getBankSummary: AgentActionExecutor = {
  describe: () => 'Banka özetini getir',
  execute: async () => {
    const data = unwrap(await accountingApi.get(ACCOUNTING_API.BANK_SUMMARY));
    return { success: true, summary: data };
  },
};

const listBankTransactions: AgentActionExecutor = {
  describe: () => 'Banka hareketlerini listele',
  execute: async (args) => {
    const limit = clampLimit(args.limit, 20, 100);
    const response = await accountingApi.get(
      ACCOUNTING_API.BANK_ACCOUNT_COMPANY_TRANSACTIONS_PAGED({
        page: 1,
        pageSize: limit,
        searchTerm: str(args.searchTerm) || undefined,
        bankAccountId: toIntId(args.bankAccountId) ?? undefined,
        transactionType: str(args.transactionType) || undefined,
        startDate: str(args.startDate) || undefined,
        endDate: str(args.endDate) || undefined,
      })
    );
    const body = response?.data ?? {};
    const rows: any[] = Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : [];
    return {
      success: true,
      totalCount: Number(body?.totalCount ?? body?.pagination?.totalCount ?? rows.length) || rows.length,
      transactions: rows.slice(0, limit).map((t: any) => ({
        ...mapBankTx(t),
        bankAccountName: t.bankAccountName ?? t.BankAccountName ?? '',
      })),
    };
  },
};

const listBankAccountTransactions: AgentActionExecutor = {
  describe: (args) => `Banka hesabı hareketlerini listele (#${args.subAccountId ?? args.bankAccountId})`,
  execute: async (args) => {
    const subId = toIntId(args.subAccountId);
    const bankId = toIntId(args.bankAccountId);
    if (!bankId && !subId) return { success: false, error: 'bankAccountId zorunludur (listBankAccounts).' };
    const url = subId
      ? ACCOUNTING_API.BANK_SUB_ACCOUNT_TRANSACTIONS(subId)
      : ACCOUNTING_API.BANK_ACCOUNT_TRANSACTIONS(bankId!);
    const limit = clampLimit(args.limit, 20, 100);
    const rows = listOf(await accountingApi.get(url));
    return {
      success: true,
      totalCount: rows.length,
      transactions: rows.slice(0, limit).map(mapBankTx),
    };
  },
};

const listBankTransactionLogs: AgentActionExecutor = {
  describe: () => 'Banka işlem loglarını listele',
  execute: async (args) => {
    const limit = clampLimit(args.limit, 20, 100);
    const rows = listOf(await accountingApi.get(ACCOUNTING_API.BANK_TRANSACTION_LOGS_BY_COMPANY));
    return {
      success: true,
      logs: rows.slice(0, limit).map((l: any) => ({
        id: l.id ?? l.Id,
        action: l.action ?? l.Action ?? l.logType ?? l.LogType ?? '',
        transactionType: l.transactionType ?? l.TransactionType ?? '',
        amount: Number(l.amount ?? l.Amount ?? 0) || 0,
        description: l.description ?? l.Description ?? '',
        performedBy: l.deletedBy ?? l.DeletedBy ?? l.createdBy ?? l.CreatedBy ?? '',
        date: l.createdAt ?? l.CreatedAt ?? l.date ?? l.Date ?? null,
      })),
    };
  },
};

const listCreditCardTransactions: AgentActionExecutor = {
  describe: (args) => `Kredi kartı hareketlerini listele (#${args.creditCardId})`,
  execute: async (args) => {
    const cardId = toIntId(args.creditCardId);
    if (!cardId) return { success: false, error: 'creditCardId zorunludur (listCreditCards).' };
    const limit = clampLimit(args.limit, 20, 100);
    const response = await accountingApi.get(
      ACCOUNTING_API.CREDIT_CARD_TRANSACTIONS(cardId, { page: 1, pageSize: limit })
    );
    const body = response?.data ?? {};
    const rows: any[] = Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : [];
    return {
      success: true,
      transactions: rows.slice(0, limit).map((t: any) => ({
        ...mapBankTx(t),
        // USAGE = harcama, PAYMENT = ödeme; ACCOUNT_PAYMENT referansı cariye bağlıdır
        referenceType: t.referenceType ?? t.ReferenceType ?? '',
        usedAmountAfter: Number(t.usedAmountAfter ?? t.UsedAmountAfter ?? 0) || 0,
      })),
    };
  },
};

const listCheckBooks: AgentActionExecutor = {
  describe: () => 'Çek defterlerini listele',
  execute: async () => {
    const books = listOf(await accountingApi.get(ACCOUNTING_API.CHECK_BOOK_BY_COMPANY));
    return {
      success: true,
      checkBooks: books.map((b: any) => ({
        id: b.id ?? b.Id,
        serialNumber: b.serialNumber ?? b.SerialNumber ?? '',
        bankAccountId: b.bankAccountId ?? b.BankAccountId ?? null,
        bankName: b.bankName ?? b.BankName ?? '',
        startNumber: b.startNumber ?? b.StartNumber ?? null,
        endNumber: b.endNumber ?? b.EndNumber ?? null,
        totalLeaves: Number(b.totalLeaves ?? b.TotalLeaves ?? 0) || 0,
        usedLeaves: Number(b.usedLeaves ?? b.UsedLeaves ?? 0) || 0,
        nextAvailableNumber: b.nextAvailableNumber ?? b.NextAvailableNumber ?? null,
        currency: b.currency ?? b.Currency ?? 'TRY',
        isActive: (b.isActive ?? b.IsActive ?? true) !== false,
      })),
    };
  },
};

const listCheckBookLeaves: AgentActionExecutor = {
  describe: (args) => `Çek defteri yapraklarını listele (#${args.checkBookId})`,
  execute: async (args) => {
    const bookId = toIntId(args.checkBookId);
    if (!bookId) return { success: false, error: 'checkBookId zorunludur (listCheckBooks).' };
    // Defter bilgisi + kullanılan yapraklara bağlı çekler (CheckNote) birlikte döner
    const books = listOf(await accountingApi.get(ACCOUNTING_API.CHECK_BOOK_BY_COMPANY));
    const book = books.find((b: any) => Number(b.id ?? b.Id) === bookId) ?? null;
    if (!book) return { success: false, error: `Çek defteri bulunamadı (id: ${bookId}).` };
    const notes = listOf(
      await accountingApi.get(`${API_BASE_URL}/api/accounting/CheckNote/by-checkbook/${bookId}`)
    );
    return {
      success: true,
      checkBook: {
        id: book.id ?? book.Id,
        serialNumber: book.serialNumber ?? book.SerialNumber ?? '',
        startNumber: book.startNumber ?? book.StartNumber ?? null,
        endNumber: book.endNumber ?? book.EndNumber ?? null,
        usedLeaves: Number(book.usedLeaves ?? book.UsedLeaves ?? 0) || 0,
        nextAvailableNumber: book.nextAvailableNumber ?? book.NextAvailableNumber ?? null,
      },
      usedChecks: notes.map((n: any) => ({
        id: n.id ?? n.Id,
        checkNumber: n.checkNumber ?? n.CheckNumber ?? n.documentNo ?? '',
        amount: Number(n.amount ?? n.Amount ?? 0) || 0,
        dueDate: n.dueDate ?? n.DueDate ?? null,
        status: n.status ?? n.Status ?? '',
        accountName: n.accountName ?? n.AccountName ?? '',
      })),
    };
  },
};

const listLoans: AgentActionExecutor = {
  describe: () => 'Kredileri listele',
  execute: async (args) => {
    const limit = clampLimit(args.limit, 20, 100);
    const response = await accountingApi.get(
      ACCOUNTING_API.BANK_LOANS({
        page: 1,
        pageSize: limit,
        status: str(args.status) || undefined,
        search: str(args.search) || undefined,
      })
    );
    const body = response?.data ?? {};
    const rows: any[] = Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : [];
    return {
      success: true,
      totalCount: Number(body?.totalCount ?? rows.length) || rows.length,
      loans: rows.slice(0, limit).map(mapLoan),
    };
  },
};

const getLoanDetail: AgentActionExecutor = {
  describe: (args) => `Kredi detayını getir (#${args.loanId})`,
  execute: async (args) => {
    const loanId = toIntId(args.loanId);
    if (!loanId) return { success: false, error: 'loanId zorunludur (listLoans).' };
    const loan = unwrap(await accountingApi.get(ACCOUNTING_API.BANK_LOAN_BY_ID(loanId)));
    if (!loan) return { success: false, error: `Kredi bulunamadı (id: ${loanId}).` };
    const installments: any[] = Array.isArray(loan.installments ?? loan.Installments)
      ? (loan.installments ?? loan.Installments)
      : [];
    const deductions: any[] = Array.isArray(loan.deductions ?? loan.Deductions)
      ? (loan.deductions ?? loan.Deductions)
      : [];
    return {
      success: true,
      loan: mapLoan(loan),
      installments: installments.map((i: any) => ({
        id: i.id ?? i.Id,
        installmentNo: i.installmentNo ?? i.InstallmentNo,
        dueDate: i.dueDate ?? i.DueDate ?? null,
        amount: Number(i.amount ?? i.Amount ?? 0) || 0,
        status: i.status ?? i.Status ?? '', // PAID | PENDING
        paidAt: i.paidAt ?? i.PaidAt ?? null,
        lateInterestAmount: Number(i.lateInterestAmount ?? i.LateInterestAmount ?? 0) || 0,
      })),
      deductions: deductions.map((d: any) => ({
        name: d.name ?? d.Name ?? '',
        amount: Number(d.amount ?? d.Amount ?? 0) || 0,
      })),
    };
  },
};

const listLoanPayments: AgentActionExecutor = {
  describe: () => 'Kredi ödemelerini listele',
  execute: async (args) => {
    const limit = clampLimit(args.limit, 20, 100);
    const response = await accountingApi.get(
      ACCOUNTING_API.BANK_LOAN_PAYMENTS({
        page: 1,
        pageSize: limit,
        loanId: toIntId(args.loanId) ?? undefined,
        dateFrom: str(args.startDate) || undefined,
        dateTo: str(args.endDate) || undefined,
      })
    );
    const body = response?.data ?? {};
    const rows: any[] = Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : [];
    return {
      success: true,
      payments: rows.slice(0, limit).map((p: any) => ({
        id: p.id ?? p.Id,
        loanName: p.loanName ?? p.LoanName ?? '',
        installmentNo: p.installmentNo ?? p.InstallmentNo ?? null,
        amount: Number(p.amount ?? p.Amount ?? 0) || 0,
        lateInterestAmount: Number(p.lateInterestAmount ?? p.LateInterestAmount ?? 0) || 0,
        paymentSource: p.paymentSource ?? p.PaymentSource ?? '',
        date: p.date ?? p.Date ?? p.paidAt ?? p.createdAt ?? null,
        paidBy: p.paidBy ?? p.PaidBy ?? '',
      })),
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Banka Modülü — YAZMA eylemleri (onay kartı gerektirir; silme eylemi YOK)
// ═══════════════════════════════════════════════════════════════════════════

const IBAN_REGEX = /^TR\d{24}$/;

const validateIban = (raw: any): { iban: string | null; error?: string } => {
  const iban = str(raw).replace(/\s/g, '').toUpperCase();
  if (!iban) return { iban: null };
  if (!IBAN_REGEX.test(iban)) {
    return { iban: null, error: 'Geçersiz IBAN: TR ile başlayan, boşluksuz 26 haneli format bekleniyor.' };
  }
  return { iban };
};

const validateAccountNumber = (raw: any): { accountNumber: string | null; error?: string } => {
  const accountNumber = str(raw);
  if (accountNumber.replace(/\D/g, '').length < 5) {
    return { accountNumber: null, error: 'Hesap numarası en az 5 rakam içermelidir.' };
  }
  return { accountNumber };
};

const createBankAccount: AgentActionExecutor = {
  describe: (args) =>
    `Yeni banka hesabı: "${str(args.bankName)}" (${str(args.accountNumber)})${str(args.companyBranchName) ? ` — ${str(args.companyBranchName)} şubesi` : ''}`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const bankName = str(args.bankName);
    if (!bankName) return { success: false, error: 'bankName zorunludur.' };
    const { accountNumber, error: accErr } = validateAccountNumber(args.accountNumber);
    if (accErr) return { success: false, error: accErr };
    const { iban, error: ibanErr } = validateIban(args.iban);
    if (ibanErr) return { success: false, error: ibanErr };

    // Şirket şubesi ataması (GTS şubesi) — bankanın kendi şubesi olan branchName'den farklıdır
    let branchId: string | null = null;
    const companyBranchName = str(args.companyBranchName);
    if (companyBranchName) {
      const resolved = await resolveBranchByName(ctx, companyBranchName);
      if ('error' in resolved) return resolved.error;
      branchId = resolved.branchId;
    }

    const payload = {
      companyId,
      bankName,
      accountNumber,
      branchName: str(args.branchName) || null,
      branchId,
      authorizedPerson: str(args.authorizedPerson) || null,
      authorizedPhone: str(args.authorizedPhone) || null,
      iban,
      currency: str(args.currency) || 'TRY',
      isPosAccount: args.isPosAccount === true,
      isCheckAccount: args.isCheckAccount === true,
      createdBy: actorEmail(ctx),
    };
    const created = unwrap(await accountingApi.post(ACCOUNTING_API.BANK_ACCOUNTS, payload));
    return {
      success: true,
      bankAccountId: created?.id ?? created?.Id ?? null,
      bankName,
      accountNumber,
      ...(companyBranchName ? { companyBranchName } : {}),
    };
  },
};

const updateBankAccount: AgentActionExecutor = {
  describe: (args) => `Banka hesabını güncelle (#${args.bankAccountId})`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const id = toIntId(args.bankAccountId);
    if (!id) return { success: false, error: 'bankAccountId zorunludur (listBankAccounts).' };
    const current = findBankAccount(await fetchBankAccounts(), id);
    if (!current) return { success: false, error: `Banka hesabı bulunamadı (id: ${id}).` };

    if (args.accountNumber !== undefined) {
      const { error } = validateAccountNumber(args.accountNumber);
      if (error) return { success: false, error };
    }
    if (args.iban !== undefined) {
      const { error } = validateIban(args.iban);
      if (error) return { success: false, error };
    }

    // Şirket şubesi ataması: companyBranchName verilirse değişir, verilmezse korunur
    let branchId: string | null = current.branchId ?? null;
    const companyBranchName = str(args.companyBranchName);
    if (companyBranchName) {
      const resolved = await resolveBranchByName(ctx, companyBranchName);
      if ('error' in resolved) return resolved.error;
      branchId = resolved.branchId;
    }

    // Yalnızca verilen alanlar değişir; update endpoint'i tam payload bekler
    const payload = {
      id,
      companyId,
      bankName: str(args.bankName) || str(current.bankName),
      accountNumber: str(args.accountNumber) || str(current.accountNumber),
      branchName: args.branchName !== undefined ? str(args.branchName) || null : (current.branchName ?? null),
      branchId,
      authorizedPerson:
        args.authorizedPerson !== undefined ? str(args.authorizedPerson) || null : (current.authorizedPerson ?? null),
      authorizedPhone:
        args.authorizedPhone !== undefined ? str(args.authorizedPhone) || null : (current.authorizedPhone ?? null),
      iban: args.iban !== undefined ? str(args.iban).replace(/\s/g, '').toUpperCase() || null : (current.iban ?? null),
      currency: currencyOf(current),
      isPosAccount: args.isPosAccount !== undefined ? args.isPosAccount === true : current.isPosAccount === true,
      isCheckAccount: args.isCheckAccount !== undefined ? args.isCheckAccount === true : current.isCheckAccount === true,
      updatedBy: actorEmail(ctx),
    };
    await accountingApi.post(ACCOUNTING_API.BANK_ACCOUNT_UPDATE(id), payload);
    return { success: true, bankAccountId: id, bankName: payload.bankName };
  },
};

const createBankSubAccount: AgentActionExecutor = {
  describe: (args) => `Yeni alt hesap: "${str(args.accountName)}" (ana hesap #${args.parentBankAccountId})`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const parentId = toIntId(args.parentBankAccountId);
    if (!parentId) return { success: false, error: 'parentBankAccountId zorunludur (listBankAccounts).' };
    const accountName = str(args.accountName);
    if (!accountName) return { success: false, error: 'accountName zorunludur.' };
    const { accountNumber, error: accErr } = validateAccountNumber(args.accountNumber);
    if (accErr) return { success: false, error: accErr };
    const { iban, error: ibanErr } = validateIban(args.iban);
    if (ibanErr) return { success: false, error: ibanErr };

    const parent = findBankAccount(await fetchBankAccounts(), parentId);
    if (!parent) return { success: false, error: `Ana banka hesabı bulunamadı (id: ${parentId}).` };

    const payload = {
      companyId,
      parentBankAccountId: parentId,
      accountName,
      accountNumber,
      iban,
      currency: str(args.currency) || currencyOf(parent),
      isPosAccount: args.isPosAccount === true,
      isCheckAccount: args.isCheckAccount === true,
      createdBy: actorEmail(ctx),
    };
    const created = unwrap(await accountingApi.post(ACCOUNTING_API.BANK_SUB_ACCOUNT, payload));
    return {
      success: true,
      subAccountId: created?.id ?? created?.Id ?? null,
      accountName,
      parentBank: bankLabel(parent),
    };
  },
};

const updateBankSubAccount: AgentActionExecutor = {
  describe: (args) => `Alt hesabı güncelle (#${args.subAccountId})`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const subId = toIntId(args.subAccountId);
    if (!subId) return { success: false, error: 'subAccountId zorunludur (listBankAccounts).' };

    const accounts = await fetchBankAccounts();
    const parent = accounts.find((a) => findSubAccount(a, subId)) ?? null;
    const current = parent ? findSubAccount(parent, subId) : null;
    if (!parent || !current) return { success: false, error: `Alt hesap bulunamadı (id: ${subId}).` };

    if (args.accountNumber !== undefined) {
      const { error } = validateAccountNumber(args.accountNumber);
      if (error) return { success: false, error };
    }
    if (args.iban !== undefined) {
      const { error } = validateIban(args.iban);
      if (error) return { success: false, error };
    }

    const payload = {
      id: subId,
      companyId,
      parentBankAccountId: Number(parent.id ?? parent.Id),
      accountName: str(args.accountName) || str(current.accountName),
      accountNumber: str(args.accountNumber) || str(current.accountNumber),
      iban: args.iban !== undefined ? str(args.iban).replace(/\s/g, '').toUpperCase() || null : (current.iban ?? null),
      currency: currencyOf(current) || currencyOf(parent),
      isPosAccount: args.isPosAccount !== undefined ? args.isPosAccount === true : current.isPosAccount === true,
      isCheckAccount: args.isCheckAccount !== undefined ? args.isCheckAccount === true : current.isCheckAccount === true,
      updatedBy: actorEmail(ctx),
    };
    await accountingApi.post(ACCOUNTING_API.BANK_SUB_ACCOUNT_UPDATE(subId), payload);
    return { success: true, subAccountId: subId, accountName: payload.accountName };
  },
};

const bankDeposit: AgentActionExecutor = {
  describe: (args) => `Bankaya para yatır: ${fmtAmount(args.amount)} (hesap #${args.bankAccountId})`,
  execute: async (args, ctx) => {
    const bankId = toIntId(args.bankAccountId);
    if (!bankId) return { success: false, error: 'bankAccountId zorunludur (listBankAccounts).' };
    const amount = Number(args.amount);
    if (!Number.isFinite(amount) || amount <= 0) return { success: false, error: 'amount pozitif olmalıdır.' };
    const subId = toIntId(args.subAccountId);

    const account = findBankAccount(await fetchBankAccounts(), bankId);
    if (!account) return { success: false, error: `Banka hesabı bulunamadı (id: ${bankId}).` };
    if (subId && !findSubAccount(account, subId)) {
      return { success: false, error: `Alt hesap bulunamadı (id: ${subId}).` };
    }

    await accountingApi.post(ACCOUNTING_API.BANK_ACCOUNT_DEPOSIT(bankId), {
      bankSubAccountId: subId ?? null,
      amount,
      description: str(args.description) || `${bankLabel(account)} hesabına para yatırma (AI Asistan)`,
      createdBy: actorEmail(ctx),
    });
    return { success: true, bankAccount: bankLabel(account), amount, subAccountId: subId ?? null };
  },
};

const bankWithdraw: AgentActionExecutor = {
  describe: (args) => `Bankadan para çek: ${fmtAmount(args.amount)} (hesap #${args.bankAccountId})`,
  execute: async (args, ctx) => {
    const bankId = toIntId(args.bankAccountId);
    if (!bankId) return { success: false, error: 'bankAccountId zorunludur (listBankAccounts).' };
    const amount = Number(args.amount);
    if (!Number.isFinite(amount) || amount <= 0) return { success: false, error: 'amount pozitif olmalıdır.' };
    const subId = toIntId(args.subAccountId);

    const account = findBankAccount(await fetchBankAccounts(), bankId);
    if (!account) return { success: false, error: `Banka hesabı bulunamadı (id: ${bankId}).` };
    const source = subId ? findSubAccount(account, subId) : account;
    if (!source) return { success: false, error: `Alt hesap bulunamadı (id: ${subId}).` };
    const balance = balanceOf(source);
    if (amount > balance) {
      return {
        success: false,
        error: `Yetersiz bakiye: çekilmek istenen ${fmtAmount(amount, currencyOf(source))}, mevcut bakiye ${fmtAmount(balance, currencyOf(source))}.`,
      };
    }

    await accountingApi.post(ACCOUNTING_API.BANK_ACCOUNT_WITHDRAW(bankId), {
      bankSubAccountId: subId ?? null,
      amount,
      description: str(args.description) || `${bankLabel(account)} hesabından para çekme (AI Asistan)`,
      createdBy: actorEmail(ctx),
    });
    return { success: true, bankAccount: bankLabel(account), amount, remainingBalance: balance - amount };
  },
};

const bankToBankTransfer: AgentActionExecutor = {
  describe: (args) =>
    `Bankadan bankaya transfer: ${fmtAmount(args.amount)} (#${args.sourceBankAccountId} → #${args.targetBankAccountId})`,
  execute: async (args, ctx) => {
    const sourceId = toIntId(args.sourceBankAccountId);
    const targetId = toIntId(args.targetBankAccountId);
    if (!sourceId || !targetId) {
      return { success: false, error: 'sourceBankAccountId ve targetBankAccountId zorunludur (listBankAccounts).' };
    }
    const amount = Number(args.amount);
    if (!Number.isFinite(amount) || amount <= 0) return { success: false, error: 'amount pozitif olmalıdır.' };
    const sourceSubId = toIntId(args.sourceSubAccountId);
    const targetSubId = toIntId(args.targetSubAccountId);
    if (sourceId === targetId && (sourceSubId ?? null) === (targetSubId ?? null)) {
      return { success: false, error: 'Kaynak ve hedef hesap aynı olamaz. Aynı banka içi aktarım için bankVirman kullanın.' };
    }

    const accounts = await fetchBankAccounts();
    const source = findBankAccount(accounts, sourceId);
    const target = findBankAccount(accounts, targetId);
    if (!source) return { success: false, error: `Kaynak banka hesabı bulunamadı (id: ${sourceId}).` };
    if (!target) return { success: false, error: `Hedef banka hesabı bulunamadı (id: ${targetId}).` };
    const sourceLeg = sourceSubId ? findSubAccount(source, sourceSubId) : source;
    if (!sourceLeg) return { success: false, error: `Kaynak alt hesap bulunamadı (id: ${sourceSubId}).` };
    if (targetSubId && !findSubAccount(target, targetSubId)) {
      return { success: false, error: `Hedef alt hesap bulunamadı (id: ${targetSubId}).` };
    }
    if (currencyOf(source) !== currencyOf(target)) {
      return {
        success: false,
        error: `Para birimi uyuşmazlığı: transfer yalnızca aynı para birimindeki hesaplar arasında yapılabilir (${currencyOf(source)} → ${currencyOf(target)}).`,
      };
    }
    const balance = balanceOf(sourceLeg);
    if (amount > balance) {
      return { success: false, error: `Yetersiz bakiye: mevcut ${fmtAmount(balance, currencyOf(source))}.` };
    }

    await accountingApi.post(ACCOUNTING_API.BANK_ACCOUNT_TRANSFER, {
      sourceBankAccountId: sourceId,
      sourceBankSubAccountId: sourceSubId ?? null,
      targetBankAccountId: targetId,
      targetBankSubAccountId: targetSubId ?? null,
      amount,
      description: str(args.description) || `${bankLabel(source)} → ${bankLabel(target)} transfer (AI Asistan)`,
      documentNo: genDocumentNo('BBT'),
      createdBy: actorEmail(ctx),
    });
    return { success: true, source: bankLabel(source), target: bankLabel(target), amount };
  },
};

const bankToCashTransfer: AgentActionExecutor = {
  describe: (args) => `Bankadan kasaya aktar: ${fmtAmount(args.amount)} (banka #${args.bankAccountId} → kasa #${args.cashboxId})`,
  execute: async (args, ctx) => {
    const bankId = toIntId(args.bankAccountId);
    const cashboxId = toIntId(args.cashboxId);
    if (!bankId || !cashboxId) {
      return { success: false, error: 'bankAccountId (listBankAccounts) ve cashboxId (listCashboxes) zorunludur.' };
    }
    const amount = Number(args.amount);
    if (!Number.isFinite(amount) || amount <= 0) return { success: false, error: 'amount pozitif olmalıdır.' };
    const subId = toIntId(args.subAccountId);

    const account = findBankAccount(await fetchBankAccounts(), bankId);
    if (!account) return { success: false, error: `Banka hesabı bulunamadı (id: ${bankId}).` };
    const sourceLeg = subId ? findSubAccount(account, subId) : account;
    if (!sourceLeg) return { success: false, error: `Alt hesap bulunamadı (id: ${subId}).` };

    const cashboxes = listOf(await accountingApi.get(ACCOUNTING_API.CASHBOX_BY_COMPANY));
    const cashbox = cashboxes.find((c: any) => Number(c.id ?? c.Id) === cashboxId) ?? null;
    if (!cashbox) return { success: false, error: `Kasa bulunamadı (id: ${cashboxId}).` };
    const cashboxCurrency = str(cashbox.currency ?? cashbox.Currency) || 'TRY';
    if (currencyOf(account) !== cashboxCurrency) {
      return {
        success: false,
        error: `Para birimi uyuşmazlığı: banka ${currencyOf(account)}, kasa ${cashboxCurrency} — aktarım yalnızca aynı para biriminde yapılabilir.`,
      };
    }
    const balance = balanceOf(sourceLeg);
    if (amount > balance) {
      return { success: false, error: `Yetersiz bakiye: mevcut ${fmtAmount(balance, currencyOf(account))}.` };
    }

    const cashboxName = str(cashbox.name ?? cashbox.Name);
    await accountingApi.post(ACCOUNTING_API.BANK_ACCOUNT_TRANSFER_TO_CASH, {
      bankAccountId: bankId,
      bankSubAccountId: subId ?? null,
      cashboxId,
      amount,
      description: str(args.description) || `${bankLabel(account)} → ${cashboxName} kasasına aktarım (AI Asistan)`,
      documentNo: genDocumentNo('BCT'),
      createdBy: actorEmail(ctx),
    });
    return { success: true, source: bankLabel(account), targetCashbox: cashboxName, amount };
  },
};

const bankVirman: AgentActionExecutor = {
  describe: (args) => `Banka içi virman: ${fmtAmount(args.amount)} (hesap #${args.bankAccountId})`,
  execute: async (args, ctx) => {
    const bankId = toIntId(args.bankAccountId);
    if (!bankId) return { success: false, error: 'bankAccountId zorunludur (listBankAccounts).' };
    const amount = Number(args.amount);
    if (!Number.isFinite(amount) || amount <= 0) return { success: false, error: 'amount pozitif olmalıdır.' };
    const sourceSubId = toIntId(args.sourceSubAccountId);
    const targetSubId = toIntId(args.targetSubAccountId);
    if ((sourceSubId ?? null) === (targetSubId ?? null)) {
      return { success: false, error: 'Kaynak ve hedef aynı olamaz (biri ana hesap, diğeri alt hesap olmalı veya iki farklı alt hesap).' };
    }

    const account = findBankAccount(await fetchBankAccounts(), bankId);
    if (!account) return { success: false, error: `Banka hesabı bulunamadı (id: ${bankId}).` };
    const sourceLeg = sourceSubId ? findSubAccount(account, sourceSubId) : account;
    const targetLeg = targetSubId ? findSubAccount(account, targetSubId) : account;
    if (!sourceLeg) return { success: false, error: `Kaynak alt hesap bulunamadı (id: ${sourceSubId}).` };
    if (!targetLeg) return { success: false, error: `Hedef alt hesap bulunamadı (id: ${targetSubId}).` };
    const balance = balanceOf(sourceLeg);
    if (amount > balance) {
      return { success: false, error: `Yetersiz bakiye: mevcut ${fmtAmount(balance, currencyOf(account))}.` };
    }

    await accountingApi.post(ACCOUNTING_API.BANK_ACCOUNT_VIRMAN, {
      sourceBankAccountId: bankId,
      sourceBankSubAccountId: sourceSubId ?? null,
      targetBankAccountId: bankId,
      targetBankSubAccountId: targetSubId ?? null,
      amount,
      description: str(args.description) || `${bankLabel(account)} banka içi virman (AI Asistan)`,
      documentNo: genDocumentNo('VRM'),
      createdBy: actorEmail(ctx),
    });
    return { success: true, bankAccount: bankLabel(account), amount };
  },
};

// — Kredi Kartı (payload'lar PascalCase: backend CreditCard controller böyle bekler) —

const fetchCreditCards = async (): Promise<any[]> =>
  listOf(await accountingApi.get(ACCOUNTING_API.CREDIT_CARDS));

const findCreditCard = (cards: any[], id: number): any | null =>
  cards.find((c) => Number(c.id ?? c.Id) === id) ?? null;

const cardNameOf = (c: any): string => str(c?.accountName ?? c?.AccountName ?? c?.name ?? c?.Name);
const cardLimitOf = (c: any): number => Number(c?.creditLimit ?? c?.CreditLimit ?? c?.cardLimit ?? 0) || 0;
const cardDebtOf = (c: any): number => Number(c?.usedAmount ?? c?.UsedAmount ?? c?.currentDebt ?? c?.CurrentDebt ?? 0) || 0;

const createCreditCard: AgentActionExecutor = {
  describe: (args) => `Yeni kredi kartı: "${str(args.accountName)}" (limit ${fmtAmount(args.creditLimit, args.currency)})`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const accountName = str(args.accountName);
    if (!accountName) return { success: false, error: 'accountName zorunludur.' };
    const cardNumber = str(args.cardNumber).replace(/\s/g, '');
    if (!cardNumber || cardNumber.length > 16) {
      return { success: false, error: 'cardNumber zorunludur ve en fazla 16 hane olmalıdır.' };
    }
    const creditLimit = Number(args.creditLimit);
    if (!Number.isFinite(creditLimit) || creditLimit < 0) {
      return { success: false, error: 'creditLimit 0 veya pozitif olmalıdır.' };
    }
    const clampDay = (v: any, def: number) => Math.min(31, Math.max(1, toIntId(v) ?? def));

    const created = unwrap(
      await accountingApi.post(ACCOUNTING_API.CREDIT_CARDS, {
        CompanyId: companyId,
        AccountName: accountName,
        CardNumber: cardNumber,
        CreditLimit: creditLimit,
        BankAccountId: toIntId(args.bankAccountId) ?? null,
        BranchId: null,
        Description: str(args.description) || null,
        StatementDay: clampDay(args.statementDay, 1),
        DueDay: clampDay(args.dueDay, 10),
        MinPaymentAmount: null, // boş bırakılırsa backend %20 hesaplar
        Currency: str(args.currency) || 'TRY',
        IsActive: true,
        IsBranchOnly: false,
        Notes: null,
        CreatedBy: actorEmail(ctx),
        UpdatedBy: actorEmail(ctx),
      })
    );
    return { success: true, creditCardId: created?.id ?? created?.Id ?? null, accountName, creditLimit };
  },
};

const updateCreditCard: AgentActionExecutor = {
  describe: (args) => `Kredi kartını güncelle (#${args.creditCardId})`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const id = toIntId(args.creditCardId);
    if (!id) return { success: false, error: 'creditCardId zorunludur (listCreditCards).' };
    const current = findCreditCard(await fetchCreditCards(), id);
    if (!current) return { success: false, error: `Kredi kartı bulunamadı (id: ${id}).` };

    const clampDay = (v: any, cur: number) => (v === undefined ? cur : Math.min(31, Math.max(1, toIntId(v) ?? cur)));
    const newLimit = args.creditLimit !== undefined ? Number(args.creditLimit) : cardLimitOf(current);
    if (!Number.isFinite(newLimit) || newLimit < 0) return { success: false, error: 'creditLimit 0 veya pozitif olmalıdır.' };

    await accountingApi.post(ACCOUNTING_API.CREDIT_CARD_UPDATE(id), {
      Id: id,
      CompanyId: companyId,
      AccountName: str(args.accountName) || cardNameOf(current),
      CardNumber: str(current.cardNumber ?? current.CardNumber),
      CreditLimit: newLimit,
      BankAccountId: current.bankAccountId ?? current.BankAccountId ?? null,
      BranchId: current.branchId ?? current.BranchId ?? null,
      Description: args.description !== undefined ? str(args.description) || null : (current.description ?? null),
      StatementDay: clampDay(args.statementDay, Number(current.statementDay ?? current.StatementDay ?? 1) || 1),
      DueDay: clampDay(args.dueDay, Number(current.dueDay ?? current.DueDay ?? 10) || 10),
      MinPaymentAmount: current.minPaymentAmount ?? current.MinPaymentAmount ?? null,
      Currency: str(current.currency ?? current.Currency) || 'TRY',
      IsActive: (current.isActive ?? current.IsActive ?? true) !== false,
      IsBranchOnly: (current.isBranchOnly ?? current.IsBranchOnly) === true,
      Notes: current.notes ?? current.Notes ?? null,
      UpdatedBy: actorEmail(ctx),
    });
    return { success: true, creditCardId: id, accountName: str(args.accountName) || cardNameOf(current) };
  },
};

const creditCardAddUsage: AgentActionExecutor = {
  describe: (args) => `Kredi kartına harcama işle: ${fmtAmount(args.amount)} (kart #${args.creditCardId})`,
  execute: async (args, ctx) => {
    const id = toIntId(args.creditCardId);
    if (!id) return { success: false, error: 'creditCardId zorunludur (listCreditCards).' };
    const amount = Number(args.amount);
    if (!Number.isFinite(amount) || amount <= 0) return { success: false, error: 'amount pozitif olmalıdır.' };
    const card = findCreditCard(await fetchCreditCards(), id);
    if (!card) return { success: false, error: `Kredi kartı bulunamadı (id: ${id}).` };
    const available = cardLimitOf(card) - cardDebtOf(card);
    if (amount > available) {
      return {
        success: false,
        error: `Kart limiti yetersiz: kullanılabilir limit ${fmtAmount(available)}, istenen harcama ${fmtAmount(amount)}.`,
      };
    }

    await accountingApi.post(ACCOUNTING_API.CREDIT_CARD_ADD_USAGE(id), {
      Amount: amount,
      Description: str(args.description) || `${cardNameOf(card)} kartı harcama (AI Asistan)`,
      DocumentNo: str(args.documentNo) || null,
      Date: localNowIso(),
      UpdatedBy: actorEmail(ctx),
    });
    return { success: true, card: cardNameOf(card), amount, remainingLimit: available - amount };
  },
};

const creditCardPayment: AgentActionExecutor = {
  describe: (args) => `Kredi kartı borcu öde: ${fmtAmount(args.amount)} (kart #${args.creditCardId}, kaynak: ${str(args.paymentSource)})`,
  execute: async (args, ctx) => {
    const id = toIntId(args.creditCardId);
    if (!id) return { success: false, error: 'creditCardId zorunludur (listCreditCards).' };
    const amount = Number(args.amount);
    if (!Number.isFinite(amount) || amount <= 0) return { success: false, error: 'amount pozitif olmalıdır.' };
    const source = str(args.paymentSource);
    const cashboxId = toIntId(args.cashboxId);
    const bankAccountId = toIntId(args.bankAccountId);
    if (source === 'cashbox' && !cashboxId) {
      return { success: false, error: 'Kasadan ödeme için cashboxId zorunludur (listCashboxes).' };
    }
    if (source === 'bank' && !bankAccountId) {
      return { success: false, error: 'Bankadan ödeme için bankAccountId zorunludur (listBankAccounts).' };
    }

    const card = findCreditCard(await fetchCreditCards(), id);
    if (!card) return { success: false, error: `Kredi kartı bulunamadı (id: ${id}).` };
    const debt = cardDebtOf(card);
    if (amount > debt) {
      return { success: false, error: `Ödeme tutarı kart borcunu aşamaz: mevcut borç ${fmtAmount(debt)}.` };
    }

    await accountingApi.post(ACCOUNTING_API.CREDIT_CARD_PAYMENT(id), {
      Amount: amount,
      Description: str(args.description) || `${cardNameOf(card)} kartı borç ödemesi (AI Asistan)`,
      Date: localNowIso(),
      PaymentSource: source,
      CashboxId: source === 'cashbox' ? cashboxId : null,
      BankAccountId: source === 'bank' ? bankAccountId : null,
      UpdatedBy: actorEmail(ctx),
    });
    return { success: true, card: cardNameOf(card), amount, remainingDebt: debt - amount };
  },
};

// — Çek Defteri —

const createCheckBook: AgentActionExecutor = {
  describe: (args) => `Yeni çek defteri: "${str(args.serialNumber)}" (${args.startNumber}-${args.endNumber})`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const bankId = toIntId(args.bankAccountId);
    if (!bankId) return { success: false, error: 'bankAccountId zorunludur (listBankAccounts).' };
    const serialNumber = str(args.serialNumber);
    if (!serialNumber) return { success: false, error: 'serialNumber zorunludur.' };
    const startNumber = toIntId(args.startNumber);
    const endNumber = toIntId(args.endNumber);
    if (!startNumber || !endNumber || startNumber >= endNumber) {
      return { success: false, error: 'startNumber, endNumber\'dan küçük olmalıdır (yaprak sayısı = aralık).' };
    }
    const subId = toIntId(args.subAccountId);

    const account = findBankAccount(await fetchBankAccounts(), bankId);
    if (!account) return { success: false, error: `Banka hesabı bulunamadı (id: ${bankId}).` };
    const holder = subId ? findSubAccount(account, subId) : account;
    if (!holder) return { success: false, error: `Alt hesap bulunamadı (id: ${subId}).` };
    if (!((holder.isCheckAccount ?? holder.IsCheckAccount) === true)) {
      return {
        success: false,
        error: `Seçilen hesap "çek hesabı" olarak işaretli değil — çek defteri yalnızca çek hesabına açılabilir (${bankLabel(account)}).`,
      };
    }

    const created = unwrap(
      await accountingApi.post(ACCOUNTING_API.CHECK_BOOKS, {
        companyId,
        bankAccountId: bankId,
        subAccountId: subId ?? null,
        serialNumber,
        startNumber,
        endNumber,
        currency: currencyOf(holder) || currencyOf(account),
        description: str(args.description) || null,
        createdBy: actorEmail(ctx),
      })
    );
    return {
      success: true,
      checkBookId: created?.id ?? created?.Id ?? null,
      serialNumber,
      totalLeaves: endNumber - startNumber + 1,
    };
  },
};

const updateCheckBook: AgentActionExecutor = {
  describe: (args) => `Çek defterini güncelle (#${args.checkBookId})`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const id = toIntId(args.checkBookId);
    if (!id) return { success: false, error: 'checkBookId zorunludur (listCheckBooks).' };
    const books = listOf(await accountingApi.get(ACCOUNTING_API.CHECK_BOOK_BY_COMPANY));
    const current = books.find((b: any) => Number(b.id ?? b.Id) === id) ?? null;
    if (!current) return { success: false, error: `Çek defteri bulunamadı (id: ${id}).` };

    const usedLeaves = Number(current.usedLeaves ?? current.UsedLeaves ?? 0) || 0;
    const wantsNumberChange = args.startNumber !== undefined || args.endNumber !== undefined;
    if (wantsNumberChange && usedLeaves > 0) {
      return {
        success: false,
        error: `Numara aralığı değiştirilemez: defterde ${usedLeaves} yaprak kullanılmış. Yalnızca seri no / açıklama / aktiflik güncellenebilir.`,
      };
    }
    const startNumber = toIntId(args.startNumber) ?? Number(current.startNumber ?? current.StartNumber);
    const endNumber = toIntId(args.endNumber) ?? Number(current.endNumber ?? current.EndNumber);
    if (wantsNumberChange && startNumber >= endNumber) {
      return { success: false, error: 'startNumber, endNumber\'dan küçük olmalıdır.' };
    }

    await accountingApi.post(ACCOUNTING_API.CHECK_BOOK_UPDATE(id), {
      companyId,
      serialNumber: str(args.serialNumber) || str(current.serialNumber ?? current.SerialNumber),
      description: args.description !== undefined ? str(args.description) || null : (current.description ?? null),
      isActive: args.isActive !== undefined ? args.isActive === true : (current.isActive ?? current.IsActive ?? true) !== false,
      startNumber,
      endNumber,
      updatedBy: actorEmail(ctx),
    });
    return { success: true, checkBookId: id };
  },
};

// — Krediler (payload'lar PascalCase; CompanyId body'ye KONMAZ — X-Company-Id header'dan çözülür) —

const createLoan: AgentActionExecutor = {
  describe: (args) =>
    `Yeni kredi: "${str(args.name)}" — anapara ${fmtAmount(args.principalAmount)}, geri ödeme ${fmtAmount(args.totalPayback)}, ${args.installmentCount} taksit`,
  execute: async (args, ctx) => {
    const bankId = toIntId(args.bankAccountId);
    if (!bankId) return { success: false, error: 'bankAccountId zorunludur (listBankAccounts).' };
    const name = str(args.name);
    if (!name) return { success: false, error: 'name zorunludur.' };
    const principal = Number(args.principalAmount);
    const totalPayback = Number(args.totalPayback);
    if (!Number.isFinite(principal) || principal <= 0) return { success: false, error: 'principalAmount pozitif olmalıdır.' };
    if (!Number.isFinite(totalPayback) || totalPayback < principal) {
      return { success: false, error: 'totalPayback anaparadan küçük olamaz (toplam faiz negatif olamaz).' };
    }
    const count = toIntId(args.installmentCount) ?? 0;
    if (count < 1 || count > 360) return { success: false, error: 'installmentCount 1-360 arasında olmalıdır.' };
    const firstDue = str(args.firstDueDate);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(firstDue)) {
      return { success: false, error: 'firstDueDate YYYY-MM-DD formatında olmalıdır.' };
    }
    const target = str(args.disbursementTarget) || 'NONE';
    const disbBankId = toIntId(args.disbursementBankAccountId);
    const disbCashboxId = toIntId(args.disbursementCashboxId);
    if (target === 'BANK' && !disbBankId) {
      return { success: false, error: 'disbursementTarget=BANK için disbursementBankAccountId zorunludur.' };
    }
    if (target === 'CASHBOX' && !disbCashboxId) {
      return { success: false, error: 'disbursementTarget=CASHBOX için disbursementCashboxId zorunludur.' };
    }

    // Eşit tutarlı aylık taksit planı: son taksit yuvarlama farkını kapatır
    const base = Math.floor((totalPayback / count) * 100) / 100;
    const [y, m, d] = firstDue.split('-').map(Number);
    const installments = Array.from({ length: count }, (_, i) => {
      const due = new Date(y, m - 1 + i, d, 12, 0, 0);
      const amount = i === count - 1 ? Math.round((totalPayback - base * (count - 1)) * 100) / 100 : base;
      return {
        InstallmentNo: i + 1,
        DueDate: `${due.getFullYear()}-${pad2(due.getMonth() + 1)}-${pad2(due.getDate())}T12:00:00`,
        Amount: amount,
        PrincipalPart: null,
        InterestPart: null,
      };
    });

    const loanDate = str(args.loanDate);
    const created = unwrap(
      await accountingApi.post(ACCOUNTING_API.BANK_LOAN_CREATE, {
        BankAccountId: bankId,
        Name: name,
        LoanNumber: str(args.loanNumber) || null,
        PrincipalAmount: principal,
        TotalPayback: totalPayback,
        LoanDate: /^\d{4}-\d{2}-\d{2}$/.test(loanDate) ? dateNoonIso(loanDate) : localNowIso(),
        Notes: str(args.notes) || null,
        Deductions: [],
        Installments: installments,
        DisbursementTarget: target,
        DisbursementBankAccountId: target === 'BANK' ? disbBankId : null,
        DisbursementCashboxId: target === 'CASHBOX' ? disbCashboxId : null,
        CreatedBy: actorEmail(ctx),
      })
    );
    return {
      success: true,
      loanId: created?.id ?? created?.Id ?? null,
      name,
      installmentCount: count,
      installmentAmount: base,
    };
  },
};

const updateLoan: AgentActionExecutor = {
  describe: (args) => `Krediyi güncelle (#${args.loanId})`,
  execute: async (args, ctx) => {
    const id = toIntId(args.loanId);
    if (!id) return { success: false, error: 'loanId zorunludur (listLoans).' };
    const current = unwrap(await accountingApi.get(ACCOUNTING_API.BANK_LOAN_BY_ID(id)));
    if (!current) return { success: false, error: `Kredi bulunamadı (id: ${id}).` };

    await accountingApi.post(ACCOUNTING_API.BANK_LOAN_UPDATE(id), {
      Id: id,
      Name: str(args.name) || str(current.name ?? current.Name) || null,
      LoanNumber: args.loanNumber !== undefined ? str(args.loanNumber) || null : (current.loanNumber ?? current.LoanNumber ?? null),
      Notes: args.notes !== undefined ? str(args.notes) || null : (current.notes ?? current.Notes ?? null),
      UpdatedBy: actorEmail(ctx),
    });
    return { success: true, loanId: id };
  },
};

const payLoanInstallment: AgentActionExecutor = {
  describe: (args) =>
    `Kredi taksidi öde (kredi #${args.loanId}${args.installmentNo ? `, taksit ${args.installmentNo}` : ', sıradaki taksit'}, kaynak: ${str(args.paymentSource)})`,
  execute: async (args, ctx) => {
    const loanId = toIntId(args.loanId);
    if (!loanId) return { success: false, error: 'loanId zorunludur (listLoans).' };
    const source = str(args.paymentSource);
    const cashboxId = toIntId(args.cashboxId);
    const bankAccountId = toIntId(args.bankAccountId);
    if (source === 'cashbox' && !cashboxId) {
      return { success: false, error: 'Kasadan ödeme için cashboxId zorunludur (listCashboxes).' };
    }
    if (source === 'bank' && !bankAccountId) {
      return { success: false, error: 'Bankadan ödeme için bankAccountId zorunludur (listBankAccounts).' };
    }

    const loan = unwrap(await accountingApi.get(ACCOUNTING_API.BANK_LOAN_BY_ID(loanId)));
    if (!loan) return { success: false, error: `Kredi bulunamadı (id: ${loanId}).` };
    const installments: any[] = Array.isArray(loan.installments ?? loan.Installments)
      ? (loan.installments ?? loan.Installments)
      : [];
    const wantedNo = toIntId(args.installmentNo);
    const pending = installments
      .filter((i) => str(i.status ?? i.Status).toUpperCase() === 'PENDING')
      .sort((a, b) => Number(a.installmentNo ?? a.InstallmentNo) - Number(b.installmentNo ?? b.InstallmentNo));
    const installment = wantedNo
      ? installments.find((i) => Number(i.installmentNo ?? i.InstallmentNo) === wantedNo) ?? null
      : pending[0] ?? null;
    if (!installment) {
      return {
        success: false,
        error: wantedNo ? `Taksit bulunamadı (no: ${wantedNo}).` : 'Bekleyen taksit yok — kredinin tüm taksitleri ödenmiş.',
      };
    }
    if (str(installment.status ?? installment.Status).toUpperCase() !== 'PENDING') {
      return { success: false, error: `Taksit ${wantedNo} zaten ödenmiş — yalnızca bekleyen taksitler ödenebilir.` };
    }

    const lateInterest = Number(args.lateInterestAmount);
    await accountingApi.post(
      ACCOUNTING_API.BANK_LOAN_INSTALLMENT_PAY(Number(installment.id ?? installment.Id)),
      {
        PaymentSource: source,
        CashboxId: source === 'cashbox' ? cashboxId : null,
        BankAccountId: source === 'bank' ? bankAccountId : null,
        Date: localNowIso(),
        LateInterestAmount: Number.isFinite(lateInterest) && lateInterest > 0 ? lateInterest : null,
        Description: str(args.description) || `${str(loan.name ?? loan.Name)} kredisi taksit ödemesi (AI Asistan)`,
        PaidBy: actorEmail(ctx),
      }
    );
    return {
      success: true,
      loan: str(loan.name ?? loan.Name),
      installmentNo: Number(installment.installmentNo ?? installment.InstallmentNo),
      amount: Number(installment.amount ?? installment.Amount ?? 0) || 0,
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Kasa Modülü — yardımcılar
// (Silme eylemleri BİLİNÇLİ olarak yok: kasa ve kasa hareketi silme
//  yalnızca UI'dan yapılabilir.)
// ═══════════════════════════════════════════════════════════════════════════

const fetchCashboxes = async (): Promise<any[]> =>
  listOf(await accountingApi.get(ACCOUNTING_API.CASHBOX_BY_COMPANY));

const findCashbox = (boxes: any[], id: number): any | null =>
  boxes.find((b) => Number(b.id ?? b.Id) === id) ?? null;

const cashboxNameOf = (b: any): string => str(b?.name ?? b?.Name);
const cashboxBalanceOf = (b: any): number => parseAmountLoose(b?.balance ?? b?.Balance);
const cashboxCurrencyOf = (b: any): string => str(b?.currency ?? b?.Currency) || 'TRY';
const isDefaultCashbox = (b: any): boolean => (b?.isDefault ?? b?.IsDefault) === true;

// ═══════════════════════════════════════════════════════════════════════════
// Kasa Modülü — OKUMA eylemleri
// ═══════════════════════════════════════════════════════════════════════════

const getCashboxSummary: AgentActionExecutor = {
  describe: () => 'Kasa özetini getir',
  execute: async (args) => {
    const p = new URLSearchParams();
    const cashboxId = toIntId(args.cashboxId);
    if (cashboxId) p.append('cashboxId', String(cashboxId));
    const startDate = str(args.startDate);
    const endDate = str(args.endDate);
    if (/^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
      p.append('startDate', new Date(`${startDate}T00:00:00`).toISOString());
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
      p.append('endDate', new Date(`${endDate}T23:59:59.999`).toISOString());
    }
    const qs = p.toString();
    const url = qs ? `${ACCOUNTING_API.CASHBOX_DAILY_SUMMARY}?${qs}` : ACCOUNTING_API.CASHBOX_DAILY_SUMMARY;
    const data = unwrap(await accountingApi.get(url));
    return {
      success: true,
      summary: {
        previousBalance: data?.previousBalance ?? null,
        period: data?.period ?? null,
        currentBalance: parseAmountLoose(data?.currentBalance),
      },
    };
  },
};

const listCashboxTransactions: AgentActionExecutor = {
  describe: () => 'Kasa hareketlerini listele',
  execute: async (args) => {
    const limit = clampLimit(args.limit, 20, 100);
    const cashboxId = toIntId(args.cashboxId);
    const startDate = str(args.startDate);
    const endDate = str(args.endDate);
    const response = await accountingApi.get(
      ACCOUNTING_API.CASHBOX_COMPANY_TRANSACTIONS_PAGED({
        page: 1,
        pageSize: limit,
        searchTerm: str(args.searchTerm) || undefined,
        cashboxId: cashboxId ? String(cashboxId) : undefined,
        type: str(args.type) || undefined,
        startDate: /^\d{4}-\d{2}-\d{2}$/.test(startDate)
          ? new Date(`${startDate}T00:00:00`).toISOString()
          : undefined,
        endDate: /^\d{4}-\d{2}-\d{2}$/.test(endDate)
          ? new Date(`${endDate}T23:59:59.999`).toISOString()
          : undefined,
      })
    );
    const body = response?.data ?? {};
    const rows: any[] = Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : [];
    return {
      success: true,
      totalCount: Number(body?.totalCount ?? rows.length) || rows.length,
      transactions: rows.slice(0, limit).map((t: any) => ({
        id: t.id ?? t.Id,
        cashboxName: t.cashbox?.name ?? t.cashboxName ?? t.CashboxName ?? '',
        type: t.type ?? t.Type ?? '', // INCOME | EXPENSE
        transactionType: t.transactionType ?? t.TransactionType ?? '',
        amount: parseAmountLoose(t.amount ?? t.Amount),
        currency: t.currency ?? t.Currency ?? 'TRY',
        description: t.description ?? t.Description ?? '',
        accountName: t.accountName ?? t.AccountName ?? '',
        date: t.date ?? t.Date ?? t.createdAt ?? null,
        createdBy: t.createdBy ?? t.CreatedBy ?? '',
      })),
    };
  },
};

const listCashboxTransactionLogs: AgentActionExecutor = {
  describe: () => 'Kasa işlem loglarını listele',
  execute: async (args) => {
    const limit = clampLimit(args.limit, 20, 100);
    const rows = listOf(await accountingApi.get(ACCOUNTING_API.CASHBOX_TRANSACTION_LOGS_BY_COMPANY));
    return {
      success: true,
      logs: rows.slice(0, limit).map((l: any) => ({
        id: l.id ?? l.Id,
        transactionId: l.transactionId ?? l.TransactionId ?? null,
        type: l.type ?? l.Type ?? '',
        transactionType: l.transactionType ?? l.TransactionType ?? '',
        amount: parseAmountLoose(l.amount ?? l.Amount),
        description: l.description ?? l.Description ?? '',
        reason: l.reason ?? l.Reason ?? '',
        deletedBy: l.deletedBy ?? l.DeletedBy ?? '',
        deletedAt: l.deletedAt ?? l.DeletedAt ?? null,
        transactionDate: l.date ?? l.Date ?? null,
      })),
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Kasa Modülü — YAZMA eylemleri (onay kartı gerektirir; silme eylemi YOK)
// ═══════════════════════════════════════════════════════════════════════════

const createCashbox: AgentActionExecutor = {
  describe: (args) =>
    `Yeni kasa oluştur: "${str(args.name)}"${str(args.branchName) ? ` (${str(args.branchName)} şubesi)` : ''}`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const name = str(args.name);
    if (!name) return { success: false, error: 'name zorunludur.' };
    const boxes = await fetchCashboxes();
    if (boxes.some((b) => cashboxNameOf(b).toLocaleLowerCase('tr') === name.toLocaleLowerCase('tr'))) {
      return { success: false, error: `"${name}" adında bir kasa zaten var.` };
    }

    // Şube ataması: branchName verilirse Firebase şube id'sine çözülür
    let branchId: string | null = null;
    const branchName = str(args.branchName);
    if (branchName) {
      const resolved = await resolveBranchByName(ctx, branchName);
      if ('error' in resolved) return resolved.error;
      branchId = resolved.branchId;
    }

    const created = unwrap(
      await accountingApi.post(ACCOUNTING_API.CASHBOXES, {
        companyId,
        name,
        description: str(args.description),
        currency: str(args.currency) || 'TRY',
        branchId,
        isDefault: false,
        createdBy: actorEmail(ctx),
      })
    );
    return {
      success: true,
      cashboxId: created?.id ?? created?.Id ?? null,
      name,
      ...(branchName ? { branchName } : {}),
    };
  },
};

const updateCashbox: AgentActionExecutor = {
  describe: (args) => `Kasayı güncelle (#${args.cashboxId})`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const id = toIntId(args.cashboxId);
    if (!id) return { success: false, error: 'cashboxId zorunludur (listCashboxes).' };
    const current = findCashbox(await fetchCashboxes(), id);
    if (!current) return { success: false, error: `Kasa bulunamadı (id: ${id}).` };
    if (isDefaultCashbox(current)) {
      return { success: false, error: 'Varsayılan kasa (Genel Kasa) güncellenemez.' };
    }
    if ((current.hasTransactions ?? current.HasTransactions) === true) {
      return { success: false, error: 'Hareket geçmişi bulunan kasa güncellenemez.' };
    }

    // Şube ataması: branchName verilirse değişir, verilmezse mevcut atama korunur
    let branchId: string | null = current.branchId ?? current.BranchId ?? null;
    const branchName = str(args.branchName);
    if (branchName) {
      const resolved = await resolveBranchByName(ctx, branchName);
      if ('error' in resolved) return resolved.error;
      branchId = resolved.branchId;
    }

    const name = str(args.name) || cashboxNameOf(current);
    await accountingApi.post(ACCOUNTING_API.CASHBOX_UPDATE(id), {
      id,
      companyId,
      name,
      description: args.description !== undefined ? str(args.description) : str(current.description ?? current.Description),
      currency: str(args.currency) || cashboxCurrencyOf(current),
      branchId,
      isActive: (current.isActive ?? current.IsActive ?? true) !== false,
      isDefault: isDefaultCashbox(current),
      updatedBy: actorEmail(ctx),
    });
    return { success: true, cashboxId: id, name, ...(branchName ? { branchName } : {}) };
  },
};

const cashboxDeposit: AgentActionExecutor = {
  describe: (args) => `Kasaya para yatır: ${fmtAmount(args.amount)} (kasa #${args.cashboxId})`,
  execute: async (args, ctx) => {
    const id = toIntId(args.cashboxId);
    if (!id) return { success: false, error: 'cashboxId zorunludur (listCashboxes).' };
    const amount = Number(args.amount);
    if (!Number.isFinite(amount) || amount <= 0) return { success: false, error: 'amount pozitif olmalıdır.' };
    const cashbox = findCashbox(await fetchCashboxes(), id);
    if (!cashbox) return { success: false, error: `Kasa bulunamadı (id: ${id}).` };

    await accountingApi.post(ACCOUNTING_API.CASHBOX_DEPOSIT(id), {
      amount,
      description: str(args.description) || `Kasaya para yatırma: ${cashboxNameOf(cashbox)} (AI Asistan)`,
      createdBy: actorEmail(ctx),
    });
    return { success: true, cashbox: cashboxNameOf(cashbox), amount };
  },
};

const cashboxWithdraw: AgentActionExecutor = {
  describe: (args) => `Kasadan para çek: ${fmtAmount(args.amount)} (kasa #${args.cashboxId})`,
  execute: async (args, ctx) => {
    const id = toIntId(args.cashboxId);
    if (!id) return { success: false, error: 'cashboxId zorunludur (listCashboxes).' };
    const amount = Number(args.amount);
    if (!Number.isFinite(amount) || amount <= 0) return { success: false, error: 'amount pozitif olmalıdır.' };
    const cashbox = findCashbox(await fetchCashboxes(), id);
    if (!cashbox) return { success: false, error: `Kasa bulunamadı (id: ${id}).` };
    const balance = cashboxBalanceOf(cashbox);
    if (amount > balance) {
      return {
        success: false,
        error: `Yetersiz bakiye: çekilmek istenen ${fmtAmount(amount, cashboxCurrencyOf(cashbox))}, mevcut bakiye ${fmtAmount(balance, cashboxCurrencyOf(cashbox))}.`,
      };
    }

    await accountingApi.post(ACCOUNTING_API.CASHBOX_WITHDRAW(id), {
      amount,
      description: str(args.description) || `Kasadan para çekme: ${cashboxNameOf(cashbox)} (AI Asistan)`,
      createdBy: actorEmail(ctx),
    });
    return { success: true, cashbox: cashboxNameOf(cashbox), amount, remainingBalance: balance - amount };
  },
};

const cashboxToCashboxTransfer: AgentActionExecutor = {
  describe: (args) =>
    `Kasadan kasaya transfer: ${fmtAmount(args.amount)} (#${args.sourceCashboxId} → #${args.targetCashboxId})`,
  execute: async (args, ctx) => {
    const sourceId = toIntId(args.sourceCashboxId);
    const targetId = toIntId(args.targetCashboxId);
    if (!sourceId || !targetId) {
      return { success: false, error: 'sourceCashboxId ve targetCashboxId zorunludur (listCashboxes).' };
    }
    if (sourceId === targetId) return { success: false, error: 'Kaynak ve hedef kasa aynı olamaz.' };
    const amount = Number(args.amount);
    if (!Number.isFinite(amount) || amount <= 0) return { success: false, error: 'amount pozitif olmalıdır.' };

    const boxes = await fetchCashboxes();
    const source = findCashbox(boxes, sourceId);
    const target = findCashbox(boxes, targetId);
    if (!source) return { success: false, error: `Kaynak kasa bulunamadı (id: ${sourceId}).` };
    if (!target) return { success: false, error: `Hedef kasa bulunamadı (id: ${targetId}).` };
    if (cashboxCurrencyOf(source) !== cashboxCurrencyOf(target)) {
      return {
        success: false,
        error: `Para birimi uyuşmazlığı: transfer yalnızca aynı para birimindeki kasalar arasında yapılabilir (${cashboxCurrencyOf(source)} → ${cashboxCurrencyOf(target)}).`,
      };
    }
    const balance = cashboxBalanceOf(source);
    if (amount > balance) {
      return { success: false, error: `Yetersiz bakiye: mevcut ${fmtAmount(balance, cashboxCurrencyOf(source))}.` };
    }

    await accountingApi.post(ACCOUNTING_API.CASHBOX_TRANSFER, {
      sourceCashboxId: sourceId,
      targetCashboxId: targetId,
      amount,
      description: str(args.description) || `${cashboxNameOf(source)} → ${cashboxNameOf(target)} kasa transferi (AI Asistan)`,
      documentNo: genDocumentNo('KT'),
      createdBy: actorEmail(ctx),
    });
    return { success: true, source: cashboxNameOf(source), target: cashboxNameOf(target), amount };
  },
};

const cashboxToBankTransfer: AgentActionExecutor = {
  describe: (args) => `Kasadan bankaya aktar: ${fmtAmount(args.amount)} (kasa #${args.cashboxId} → banka #${args.bankAccountId})`,
  execute: async (args, ctx) => {
    const cashboxId = toIntId(args.cashboxId);
    const bankId = toIntId(args.bankAccountId);
    if (!cashboxId || !bankId) {
      return { success: false, error: 'cashboxId (listCashboxes) ve bankAccountId (listBankAccounts) zorunludur.' };
    }
    const amount = Number(args.amount);
    if (!Number.isFinite(amount) || amount <= 0) return { success: false, error: 'amount pozitif olmalıdır.' };
    const subId = toIntId(args.subAccountId);

    const cashbox = findCashbox(await fetchCashboxes(), cashboxId);
    if (!cashbox) return { success: false, error: `Kasa bulunamadı (id: ${cashboxId}).` };
    const account = findBankAccount(await fetchBankAccounts(), bankId);
    if (!account) return { success: false, error: `Banka hesabı bulunamadı (id: ${bankId}).` };
    const targetLeg = subId ? findSubAccount(account, subId) : account;
    if (!targetLeg) return { success: false, error: `Alt hesap bulunamadı (id: ${subId}).` };
    if (cashboxCurrencyOf(cashbox) !== currencyOf(account)) {
      return {
        success: false,
        error: `Para birimi uyuşmazlığı: kasa ${cashboxCurrencyOf(cashbox)}, banka ${currencyOf(account)} — aktarım yalnızca aynı para biriminde yapılabilir.`,
      };
    }
    const balance = cashboxBalanceOf(cashbox);
    if (amount > balance) {
      return { success: false, error: `Yetersiz kasa bakiyesi: mevcut ${fmtAmount(balance, cashboxCurrencyOf(cashbox))}.` };
    }

    await accountingApi.post(ACCOUNTING_API.CASHBOX_BANK_TRANSFER, {
      cashboxId,
      bankAccountId: bankId,
      bankSubAccountId: subId ?? null,
      amount,
      description: str(args.description) || `${cashboxNameOf(cashbox)} → ${bankLabel(account)} transfer (AI Asistan)`,
      documentNo: genDocumentNo('KBT'),
      createdBy: actorEmail(ctx),
    });
    return { success: true, source: cashboxNameOf(cashbox), target: bankLabel(account), amount };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Stok (Inventory) Modülü — yardımcılar
// (Silme eylemleri BİLİNÇLİ olarak yok. Ürün id'leri Guid STRING'dir.)
// ═══════════════════════════════════════════════════════════════════════════

/** Virgül/yeni satırla ayrılmış Guid listesi ayrıştırır. */
const parseIdList = (raw: any): string[] =>
  str(raw)
    .split(/[,\n;]/)
    .map((s) => s.trim())
    .filter(Boolean);

const getProduct = async (productId: string): Promise<any | null> => {
  try {
    const resp = await accountingApi.get(ACCOUNTING_API.INVENTORY_ITEM_BY_ID(productId));
    return resp?.data ?? null;
  } catch {
    return null;
  }
};

const productNameOf = (p: any): string => str(p?.name ?? p?.Name);

/** Ürünün depo bazlı stok kayıtlarını normalize eder. */
const warehouseStocksOf = (p: any): any[] => {
  const raw = p?.warehouseStocks ?? p?.WarehouseStocks;
  return Array.isArray(raw) ? raw : [];
};

const totalStockOf = (p: any): number =>
  warehouseStocksOf(p).reduce((sum, ws) => sum + (Number(ws.quantity ?? ws.Quantity) || 0), 0);

const fetchWarehouses = async (): Promise<any[]> => {
  const rows = listOf(await accountingApi.get(ACCOUNTING_API.INVENTORY_WAREHOUSES));
  // Backend sürümüne göre camelCase/PascalCase gelebilir — normalize et
  return rows.map((w: any) => ({
    id: str(w.id ?? w.Id),
    name: str(w.name ?? w.Name),
    address: str(w.address ?? w.Address),
    description: str(w.description ?? w.Description),
    isDefault: (w.isDefault ?? w.IsDefault) === true,
    isActive: (w.isActive ?? w.IsActive ?? true) !== false,
    branchesId: w.branchesId ?? w.BranchesId ?? null,
    warehouseType: str(w.warehouseType ?? w.WarehouseType) || 'Normal',
    locationModel: w.locationModel ?? w.LocationModel ?? undefined,
    defaultRoutingStrategy: w.defaultRoutingStrategy ?? w.DefaultRoutingStrategy ?? undefined,
  }));
};

const fetchCategories = async (): Promise<any[]> =>
  listOf(await accountingApi.get(ACCOUNTING_API.INVENTORY_CATEGORIES));

const fetchBrands = async (): Promise<any[]> =>
  listOf(await accountingApi.get(ACCOUNTING_API.INVENTORY_BRANDS));

/** Ürünün şube fiyatları (404 = kayıt yok → boş liste). */
const fetchBranchPrices = async (productId: string): Promise<any[]> => {
  try {
    const resp = await accountingApi.get(`${API_BASE_URL}/api/InventoryBranchPrice/item/${productId}`);
    return Array.isArray(resp?.data) ? resp.data : [];
  } catch (err: any) {
    if (err?.response?.status === 404) return [];
    throw err;
  }
};

/** Bir şubenin tüm ürün fiyatları — ürün bilgisiyle zenginleştirilmiş (404 = kayıt yok → boş liste). */
const fetchBranchPricesByBranch = async (branchId: string): Promise<any[]> => {
  try {
    const resp = await accountingApi.get(`${API_BASE_URL}/api/InventoryBranchPrice/branch/${branchId}`);
    return Array.isArray(resp?.data) ? resp.data : [];
  } catch (err: any) {
    if (err?.response?.status === 404) return [];
    throw err;
  }
};

const rnd = (min: number, max: number): number => Math.floor(Math.random() * (max - min + 1)) + min;

// ═══════════════════════════════════════════════════════════════════════════
// Stok Modülü — OKUMA eylemleri
// ═══════════════════════════════════════════════════════════════════════════

const getProductDetails: AgentActionExecutor = {
  describe: (args) => `Ürün detayını getir (${str(args.productId).slice(0, 8)}…)`,
  execute: async (args) => {
    const productId = str(args.productId);
    if (!productId) return { success: false, error: 'productId zorunludur (searchProduct).' };
    const p = await getProduct(productId);
    if (!p) return { success: false, error: `Ürün bulunamadı (id: ${productId}).` };
    return {
      success: true,
      product: {
        id: p.id ?? p.Id,
        name: productNameOf(p),
        stockCode: p.stockCode ?? p.StockCode ?? '',
        barcode: p.barcode ?? p.Barcode ?? '',
        unit: p.unit ?? p.Unit ?? 'adet',
        salePrice: Number(p.salePrice ?? p.SalePrice) || 0,
        purchasePrice: Number(p.purchasePrice ?? p.PurchasePrice) || 0,
        listPrice: Number(p.listPrice ?? p.ListPrice) || 0,
        vatRate: Number(p.vatRate ?? p.VatRate) || 0,
        vatStatus: p.vatStatus ?? p.VatStatus ?? '',
        departmentNo: p.departmentNo ?? p.DepartmentNo ?? null,
        categoryId: p.categoryId ?? p.CategoryId ?? null,
        brandId: p.brandId ?? p.BrandId ?? null,
        description: p.description ?? p.Description ?? '',
        canBeNegative: (p.canBeNegative ?? p.CanBeNegative) === true,
        isActive: (p.isActive ?? p.IsActive ?? true) !== false,
        isEcommerce: (p.isEcommerce ?? p.IsEcommerce) === true,
        useBranchPricing: (p.useBranchPricing ?? p.UseBranchPricing) === true,
        totalStock: totalStockOf(p),
        warehouseStocks: warehouseStocksOf(p).map((ws: any) => ({
          warehouseId: ws.warehouseId ?? ws.WarehouseId,
          warehouseName: ws.warehouseName ?? ws.WarehouseName ?? '',
          quantity: Number(ws.quantity ?? ws.Quantity) || 0,
        })),
      },
    };
  },
};

const listCategories: AgentActionExecutor = {
  describe: () => 'Kategorileri listele',
  execute: async () => {
    const rows = await fetchCategories();
    return {
      success: true,
      categories: rows.map((c: any) => ({
        id: c.id ?? c.Id,
        name: c.name ?? c.Name ?? '',
        parentId: c.parentId ?? c.ParentId ?? null,
        level: Number(c.level ?? c.Level ?? 1) || 1,
        isEcommerceEnabled: (c.isEcommerceEnabled ?? c.IsEcommerceEnabled) === true,
        itemCount: Number(c.itemCount ?? c.ItemCount ?? 0) || 0,
        isActive: (c.isActive ?? c.IsActive ?? true) !== false,
      })),
    };
  },
};

const listBrands: AgentActionExecutor = {
  describe: () => 'Markaları listele',
  execute: async () => {
    const rows = await fetchBrands();
    return {
      success: true,
      brands: rows.map((b: any) => ({
        id: b.id ?? b.Id,
        name: b.name ?? b.Name ?? '',
        description: b.description ?? b.Description ?? '',
        website: b.website ?? b.Website ?? '',
        isActive: (b.isActive ?? b.IsActive ?? true) !== false,
      })),
    };
  },
};

const listWarehouses: AgentActionExecutor = {
  describe: () => 'Depoları listele',
  execute: async () => {
    const warehouses = await fetchWarehouses();
    return {
      success: true,
      warehouses: warehouses.map((w) => ({
        id: w.id,
        name: w.name,
        isDefault: w.isDefault,
        warehouseType: w.warehouseType, // Normal | Smart (WMS)
        isActive: w.isActive,
      })),
    };
  },
};

const listStockMovements: AgentActionExecutor = {
  describe: () => 'Stok hareketlerini listele',
  execute: async (args) => {
    const limit = clampLimit(args.limit, 20, 100);
    const startDate = str(args.startDate);
    const endDate = str(args.endDate);
    const response = await accountingApi.get(
      ACCOUNTING_API.STOCK_MOVEMENTS_PAGED({
        page: 1,
        pageSize: limit,
        search: str(args.searchTerm) || undefined,
        movementType: str(args.movementType) || undefined,
        direction: str(args.direction) || undefined,
        productId: str(args.productId) || undefined,
        warehouseId: str(args.warehouseId) || undefined,
        categoryId: str(args.categoryId) || undefined,
        startDate: /^\d{4}-\d{2}-\d{2}$/.test(startDate)
          ? new Date(`${startDate}T00:00:00`).toISOString()
          : undefined,
        endDate: /^\d{4}-\d{2}-\d{2}$/.test(endDate)
          ? new Date(`${endDate}T23:59:59.999`).toISOString()
          : undefined,
      })
    );
    const body = response?.data ?? {};
    const rows: any[] = Array.isArray(body?.data) ? body.data : [];
    return {
      success: true,
      totalCount: Number(body?.totalCount ?? rows.length) || rows.length,
      movements: rows.slice(0, limit).map((m: any) => ({
        id: m.id ?? m.Id,
        productName: m.itemName ?? m.productName ?? m.ProductName ?? '',
        movementType: m.movementType ?? m.MovementType ?? '',
        quantity: Number(m.quantity ?? m.Quantity) || 0,
        previousQuantity: Number(m.previousQuantity ?? m.PreviousQuantity ?? 0) || 0,
        newQuantity: Number(m.newQuantity ?? m.NewQuantity ?? 0) || 0,
        unitPrice: Number(m.unitPrice ?? m.UnitPrice ?? 0) || 0,
        totalAmount: Number(m.totalAmount ?? m.TotalAmount ?? 0) || 0,
        warehouseName: m.warehouseName ?? m.WarehouseName ?? '',
        accountName: m.accountName ?? m.AccountName ?? '',
        description: m.description ?? m.Description ?? '',
        date: m.date ?? m.Date ?? m.createdAt ?? null,
      })),
    };
  },
};

const listPriceChangeLogs: AgentActionExecutor = {
  describe: () => 'Fiyat değişim loglarını listele',
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const limit = clampLimit(args.limit, 20, 100);
    const productId = str(args.productId);
    const startDate = str(args.startDate);
    const endDate = str(args.endDate);
    const url = productId
      ? `${API_BASE_URL}/api/Inventory/price-change-logs/${companyId}/${productId}`
      : `${API_BASE_URL}/api/Inventory/price-change-logs/${companyId}`;
    const response = await accountingApi.get(url, {
      params: {
        page: 1,
        pageSize: limit,
        searchTerm: str(args.searchTerm) || undefined,
        startDate: /^\d{4}-\d{2}-\d{2}$/.test(startDate)
          ? new Date(`${startDate}T00:00:00`).toISOString()
          : undefined,
        endDate: /^\d{4}-\d{2}-\d{2}$/.test(endDate)
          ? new Date(`${endDate}T23:59:59.999`).toISOString()
          : undefined,
        sortColumn: 'createdAt',
        sortDirection: 'desc',
      },
    });
    const body = response?.data ?? {};
    const rows: any[] = Array.isArray(body?.data) ? body.data : Array.isArray(body?.Data) ? body.Data : [];
    return {
      success: true,
      totalCount: Number(body?.totalCount ?? body?.TotalCount ?? rows.length) || rows.length,
      logs: rows.slice(0, limit).map((l: any) => ({
        productName: l.productName ?? l.ProductName ?? '',
        stockCode: l.stockCode ?? l.StockCode ?? '',
        priceType: l.priceType ?? l.PriceType ?? '',
        oldValue: Number(l.oldValue ?? l.OldValue ?? 0) || 0,
        newValue: Number(l.newValue ?? l.NewValue ?? 0) || 0,
        currency: l.currency ?? l.Currency ?? 'TRY',
        userName: l.userName ?? l.UserName ?? '',
        source: l.source ?? l.Source ?? '',
        branchName: l.branchName ?? l.BranchName ?? '',
        date: l.createdAt ?? l.CreatedAt ?? null,
      })),
    };
  },
};

const listBranches: AgentActionExecutor = {
  describe: () => 'Şubeleri listele',
  execute: async (_args, ctx) => {
    const branches = await getBranches(ctx.companyId || '');
    return {
      success: true,
      branches: branches.map((b) => ({
        id: b.id, // kanonik şube id'si — şube fiyatı işlemlerinde bu kullanılır
        name: b.name,
        city: b.city ?? '',
        address: b.address ?? '',
        phone: b.phone ?? '',
        email: b.email ?? '',
        isHeadquarter: b.isHeadquarter === true,
        isActive: b.isActive !== false,
        isFranchise: b.isFranchise === true,
        hasFirmInfo: !!str(b.firmName), // franchise firma bilgisi girilmiş mi
      })),
    };
  },
};

const listBranchPrices: AgentActionExecutor = {
  describe: (args) => `Şube fiyatlarını listele (ürün ${str(args.productId).slice(0, 8)}…)`,
  execute: async (args) => {
    const productId = str(args.productId);
    if (!productId) return { success: false, error: 'productId zorunludur (searchProduct).' };
    const product = await getProduct(productId);
    if (!product) return { success: false, error: `Ürün bulunamadı (id: ${productId}).` };
    const prices = await fetchBranchPrices(productId);
    return {
      success: true,
      productName: productNameOf(product),
      useBranchPricing: (product.useBranchPricing ?? product.UseBranchPricing) === true,
      defaultSalePrice: Number(product.salePrice ?? product.SalePrice) || 0,
      branchPrices: prices.map((bp: any) => ({
        id: bp.id ?? bp.Id,
        branchId: bp.branchId ?? bp.BranchId ?? '',
        branchName: bp.branchName ?? bp.BranchName ?? '',
        salePrice: Number(bp.salePrice ?? bp.SalePrice) || 0,
        listPrice: Number(bp.listPrice ?? bp.ListPrice ?? 0) || 0,
        packagePrice: Number(bp.packagePrice ?? bp.PackagePrice ?? 0) || 0,
        currency: bp.saleCurrency ?? bp.SaleCurrency ?? 'TRY',
      })),
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Stok Modülü — YAZMA eylemleri (onay kartı gerektirir; silme eylemi YOK)
// ═══════════════════════════════════════════════════════════════════════════

const createProduct: AgentActionExecutor = {
  describe: (args) => `Yeni ürün oluştur: "${str(args.name)}" (${fmtAmount(args.salePrice)})`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const name = str(args.name);
    if (!name) return { success: false, error: 'name zorunludur.' };
    const salePrice = Number(args.salePrice);
    if (!Number.isFinite(salePrice) || salePrice < 0) return { success: false, error: 'salePrice 0 veya pozitif olmalıdır.' };

    const initialQuantity = Number(args.initialQuantity) || 0;
    const warehouseId = str(args.warehouseId);
    if (initialQuantity > 0 && !warehouseId) {
      return { success: false, error: 'Başlangıç stoğu için warehouseId zorunludur (listWarehouses).' };
    }

    // Barkod verilmişse duplicate kontrolü (UI konvansiyonu)
    const givenBarcode = str(args.barcode);
    if (givenBarcode) {
      const dup = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEMS_PAGED, {
        pageNumber: 1,
        pageSize: 5,
        searchTerm: givenBarcode,
      });
      const dupItems: any[] = dup?.data?.items ?? [];
      if (dupItems.some((i: any) => str(i.barcode ?? i.Barcode) === givenBarcode)) {
        return { success: false, error: `"${givenBarcode}" barkodu başka bir üründe zaten kayıtlı.` };
      }
    }

    // Stok kodu / barkod otomatik üretimi (UI ile aynı konvansiyon)
    const stockCode =
      str(args.stockCode) || `${name.slice(0, 3).toLocaleUpperCase('tr')}${rnd(1000, 9999)}`;
    const barcode = givenBarcode || `2${Date.now().toString().slice(-10)}${rnd(100, 999)}`;
    const vatRate = args.vatRate !== undefined ? Number(args.vatRate) : 20;
    if (!Number.isFinite(vatRate) || vatRate < 0 || vatRate > 100) {
      return { success: false, error: 'vatRate 0-100 arasında olmalıdır.' };
    }

    const created = unwrap(
      await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_CREATE, {
        companyId,
        stockCode,
        name,
        categoryId: str(args.categoryId) || null,
        brandId: str(args.brandId) || null,
        purchasePrice: args.purchasePrice !== undefined ? Number(args.purchasePrice) : null,
        purchaseCurrency: 'TRY',
        salePrice,
        saleCurrency: 'TRY',
        unit: str(args.unit) || 'adet',
        canBeNegative: args.canBeNegative !== undefined ? args.canBeNegative === true : true,
        barcode,
        vatRate,
        vatStatus: str(args.vatStatus) || 'excluded',
        description: str(args.description) || 'AI Asistan ile eklendi.',
        departmentNo: toIntId(args.departmentNo) ?? null,
        isActive: true,
      })
    );
    const productId = created?.id ?? created?.Id ?? null;

    // Başlangıç stoğu ayrı çağrıyla eklenir (backend create'te stok almaz)
    let stockNote: string | undefined;
    if (productId && initialQuantity > 0) {
      try {
        await accountingApi.post(ACCOUNTING_API.INVENTORY_WAREHOUSE_ADD_STOCK, {
          companyId,
          productId,
          warehouseId,
          quantity: initialQuantity,
        });
      } catch {
        stockNote = 'Ürün oluşturuldu ancak başlangıç stoğu eklenemedi — setProductStock ile tekrar deneyin.';
      }
    }

    return { success: true, productId, name, stockCode, barcode, initialQuantity, note: stockNote };
  },
};

const updateProduct: AgentActionExecutor = {
  describe: (args) => `Ürünü güncelle (${str(args.productId).slice(0, 8)}…)`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const productId = str(args.productId);
    if (!productId) return { success: false, error: 'productId zorunludur (searchProduct).' };
    const current = await getProduct(productId);
    if (!current) return { success: false, error: `Ürün bulunamadı (id: ${productId}).` };

    const pick = (argVal: any, curCamel: any, curPascal: any) =>
      argVal !== undefined ? argVal : (curCamel ?? curPascal ?? null);

    const name = str(args.name) || productNameOf(current);
    // Tam DTO gönderilir; resimler mutlaka mevcut değerle korunur (UI kuralı)
    const imageUrls = current.imageUrls ?? current.ImageUrls ?? null;
    await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_UPDATE(productId), {
      id: productId,
      companyId,
      name,
      stockCode: str(args.stockCode) || str(current.stockCode ?? current.StockCode),
      barcode: str(args.barcode) || str(current.barcode ?? current.Barcode),
      categoryId: args.categoryId !== undefined ? str(args.categoryId) || null : (current.categoryId ?? current.CategoryId ?? null),
      brandId: args.brandId !== undefined ? str(args.brandId) || null : (current.brandId ?? current.BrandId ?? null),
      canBeNegative:
        args.canBeNegative !== undefined ? args.canBeNegative === true : (current.canBeNegative ?? current.CanBeNegative) === true,
      isVariantParent: (current.isVariantParent ?? current.IsVariantParent) === true,
      parentItemId: current.parentItemId ?? current.ParentItemId ?? null,
      unit: str(args.unit) || str(current.unit ?? current.Unit) || 'adet',
      purchasePrice: pick(args.purchasePrice !== undefined ? Number(args.purchasePrice) : undefined, current.purchasePrice, current.PurchasePrice),
      purchaseCurrency: current.purchaseCurrency ?? current.PurchaseCurrency ?? 'TRY',
      salePrice: pick(args.salePrice !== undefined ? Number(args.salePrice) : undefined, current.salePrice, current.SalePrice),
      saleCurrency: current.saleCurrency ?? current.SaleCurrency ?? 'TRY',
      listPrice: pick(args.listPrice !== undefined ? Number(args.listPrice) : undefined, current.listPrice, current.ListPrice),
      listCurrency: current.listCurrency ?? current.ListCurrency ?? 'TRY',
      vatRate: pick(args.vatRate !== undefined ? Number(args.vatRate) : undefined, current.vatRate, current.VatRate),
      vatStatus: str(args.vatStatus) || str(current.vatStatus ?? current.VatStatus) || 'excluded',
      sctRate: current.sctRate ?? current.SctRate ?? null,
      sctStatus: current.sctStatus ?? current.SctStatus ?? null,
      description: args.description !== undefined ? str(args.description) : str(current.description ?? current.Description),
      desi: current.desi ?? current.Desi ?? null,
      branchesId: current.branchesId ?? current.BranchesId ?? null,
      departmentNo: args.departmentNo !== undefined ? toIntId(args.departmentNo) : (current.departmentNo ?? current.DepartmentNo ?? null),
      itemType: current.itemType ?? current.ItemType ?? null,
      isActive: (current.isActive ?? current.IsActive ?? true) !== false,
      imageUrl: current.imageUrl ?? current.ImageUrl ?? (Array.isArray(imageUrls) ? imageUrls[0] : null) ?? null,
      imageUrls,
    });
    return { success: true, productId, name };
  },
};

const updateProductPrice: AgentActionExecutor = {
  describe: (args) => `Ürün fiyatını güncelle: ${fmtAmount(args.salePrice)} (${str(args.productId).slice(0, 8)}…)`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const productId = str(args.productId);
    if (!productId) return { success: false, error: 'productId zorunludur (searchProduct).' };
    const salePrice = Number(args.salePrice);
    if (!Number.isFinite(salePrice) || salePrice < 0) return { success: false, error: 'salePrice 0 veya pozitif olmalıdır.' };

    // DİKKAT: bu uç PascalCase payload bekler (fiyat değişiklik logu için audit alanları dahil)
    await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_UPDATE_PRICE, {
      CompanyId: companyId,
      Id: productId,
      SalePrice: salePrice,
      ListPrice: args.listPrice !== undefined ? Number(args.listPrice) : null,
      UserId: null,
      UserName: actorEmail(ctx),
    });
    return { success: true, productId, salePrice };
  },
};

const setProductStock: AgentActionExecutor = {
  describe: (args) => `Stok ayarla: ${args.quantity} (ürün ${str(args.productId).slice(0, 8)}…)`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const productId = str(args.productId);
    const warehouseId = str(args.warehouseId);
    if (!productId || !warehouseId) {
      return { success: false, error: 'productId (searchProduct) ve warehouseId (listWarehouses) zorunludur.' };
    }
    const quantity = Number(args.quantity);
    if (!Number.isFinite(quantity) || quantity < 0) return { success: false, error: 'quantity 0 veya pozitif olmalıdır.' };

    const product = await getProduct(productId);
    if (!product) return { success: false, error: `Ürün bulunamadı (id: ${productId}).` };
    const existing = warehouseStocksOf(product).find(
      (ws) => str(ws.warehouseId ?? ws.WarehouseId) === warehouseId
    );

    if (existing) {
      // Mevcut depo kaydı: mutlak değere ayarla
      await accountingApi.post(ACCOUNTING_API.INVENTORY_WAREHOUSE_STOCK_UPDATE, {
        companyId,
        productId,
        warehouseId,
        quantityChange: quantity, // backend semantiği: mutlak yeni miktar
      });
    } else if (quantity > 0) {
      // Depoda kayıt yok: yeni stok kaydı aç
      await accountingApi.post(ACCOUNTING_API.INVENTORY_WAREHOUSE_ADD_STOCK, {
        companyId,
        productId,
        warehouseId,
        quantity,
      });
    }

    return {
      success: true,
      product: productNameOf(product),
      warehouseId,
      previousQuantity: existing ? Number(existing.quantity ?? existing.Quantity) || 0 : 0,
      newQuantity: quantity,
    };
  },
};

const stockMovement: AgentActionExecutor = {
  describe: (args) =>
    `Stok ${str(args.direction) === 'OUT' ? 'çıkışı' : 'girişi'}: ${args.quantity} (ürün ${str(args.productId).slice(0, 8)}…)`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const productId = str(args.productId);
    const warehouseId = str(args.warehouseId);
    if (!productId || !warehouseId) {
      return { success: false, error: 'productId (searchProduct) ve warehouseId (listWarehouses) zorunludur.' };
    }
    const direction = str(args.direction);
    const quantity = Number(args.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) return { success: false, error: 'quantity pozitif olmalıdır.' };

    const product = await getProduct(productId);
    if (!product) return { success: false, error: `Ürün bulunamadı (id: ${productId}).` };
    if (direction === 'OUT') {
      const stock = warehouseStocksOf(product).find(
        (ws) => str(ws.warehouseId ?? ws.WarehouseId) === warehouseId
      );
      const available = stock ? Number(stock.quantity ?? stock.Quantity) || 0 : 0;
      if (quantity > available) {
        return {
          success: false,
          error: `Yetersiz stok: depoda ${available} ${str(product.unit ?? product.Unit) || 'adet'} var, ${quantity} çıkış istendi.`,
        };
      }
    }

    const resp = await accountingApi.post(ACCOUNTING_API.STOCK_MOVEMENT_CREATE_MANUAL, {
      companyId,
      productId,
      warehouseId,
      direction,
      quantity,
      unitPrice: Number(args.unitPrice) || 0,
      referenceNumber: genDocumentNo('STK'),
      description:
        str(args.description) ||
        `${productNameOf(product)} — manuel stok ${direction === 'OUT' ? 'çıkışı' : 'girişi'} (AI Asistan)`,
      date: localNowIso(),
      createdBy: actorEmail(ctx),
    });
    return {
      success: true,
      product: productNameOf(product),
      direction,
      quantity,
      newQuantity: resp?.data?.newQuantity ?? null,
    };
  },
};

const setProductActive: AgentActionExecutor = {
  describe: (args) =>
    `${parseIdList(args.productIds).length} ürünü ${args.isActive === true ? 'aktifleştir' : 'pasife al'}`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const ids = parseIdList(args.productIds);
    if (ids.length === 0) return { success: false, error: 'productIds zorunludur (virgülle ayrılmış Guid listesi).' };
    const resp = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_BULK_UPDATE_STATUS, {
      companyId,
      ids,
      isActive: args.isActive === true,
    });
    return { success: true, updatedCount: resp?.data?.count ?? ids.length, isActive: args.isActive === true };
  },
};

const toggleProductEcommerce: AgentActionExecutor = {
  describe: (args) =>
    `${parseIdList(args.productIds).length} ürünü e-ticarete ${args.enable === true ? 'aç' : 'kapat'}`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const ids = parseIdList(args.productIds);
    if (ids.length === 0) return { success: false, error: 'productIds zorunludur (virgülle ayrılmış Guid listesi).' };
    const resp = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_BULK_TOGGLE_ECOMMERCE, {
      companyId,
      ids,
      enable: args.enable === true,
    });
    return { success: true, updatedCount: resp?.data?.count ?? ids.length, enable: args.enable === true };
  },
};

const bulkUpdatePrices: AgentActionExecutor = {
  describe: (args) =>
    `${parseIdList(args.productIds).length} ürünün ${str(args.target) === 'purchase' ? 'alış' : 'satış'} fiyatını güncelle (${str(args.updateType) === 'fixed' ? `${args.value} TL` : `%${args.value}`})`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const ids = parseIdList(args.productIds);
    if (ids.length === 0) return { success: false, error: 'productIds zorunludur (virgülle ayrılmış Guid listesi).' };
    const value = Number(args.value);
    if (!Number.isFinite(value) || value === 0) return { success: false, error: 'value sıfırdan farklı bir sayı olmalıdır.' };
    const updateType = str(args.updateType);

    const resp = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_BULK_UPDATE_PRICE, {
      companyId,
      ids,
      priceUpdateType: updateType === 'fixed' ? 1 : 0, // 0=yüzde, 1=sabit tutar
      priceUpdateValue: value,
      priceUpdateTarget: str(args.target) === 'purchase' ? 'purchase' : 'sale',
    });
    return { success: true, updatedCount: resp?.data?.count ?? ids.length };
  },
};

const bulkUpdateVatRate: AgentActionExecutor = {
  describe: (args) => `${parseIdList(args.productIds).length} ürünün KDV oranını %${args.vatRate} yap`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const ids = parseIdList(args.productIds);
    if (ids.length === 0) return { success: false, error: 'productIds zorunludur (virgülle ayrılmış Guid listesi).' };
    const vatRate = Number(args.vatRate);
    if (!Number.isFinite(vatRate) || vatRate < 0 || vatRate > 100) {
      return { success: false, error: 'vatRate 0-100 arasında olmalıdır.' };
    }
    const resp = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_BULK_UPDATE_VAT_RATE, {
      companyId,
      ids,
      vatRate,
    });
    return { success: true, updatedCount: resp?.data?.count ?? ids.length, vatRate };
  },
};

const bulkUpdateDepartmentNo: AgentActionExecutor = {
  describe: (args) => `${parseIdList(args.productIds).length} ürünün ÖKC departman no'sunu ${args.departmentNo} yap`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const ids = parseIdList(args.productIds);
    if (ids.length === 0) return { success: false, error: 'productIds zorunludur (virgülle ayrılmış Guid listesi).' };
    const departmentNo = toIntId(args.departmentNo);
    if (!departmentNo || departmentNo < 1 || departmentNo > 100) {
      return { success: false, error: 'departmentNo 1-100 arasında olmalıdır.' };
    }
    const resp = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_BULK_UPDATE_DEPARTMENT_NO, {
      companyId,
      ids,
      departmentNo,
    });
    return { success: true, updatedCount: resp?.data?.count ?? ids.length, departmentNo };
  },
};

const bulkAssignWarehouse: AgentActionExecutor = {
  describe: (args) => `${parseIdList(args.productIds).length} ürünü depoya ata (${str(args.warehouseId).slice(0, 8)}…)`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const ids = parseIdList(args.productIds);
    if (ids.length === 0) return { success: false, error: 'productIds zorunludur (virgülle ayrılmış Guid listesi).' };
    const warehouseId = str(args.warehouseId);
    if (!warehouseId) return { success: false, error: 'warehouseId zorunludur (listWarehouses).' };
    const warehouse = (await fetchWarehouses()).find((w) => w.id === warehouseId);
    if (!warehouse) return { success: false, error: `Depo bulunamadı (id: ${warehouseId}).` };

    const resp = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEM_BULK_ASSIGN_WAREHOUSE, {
      productIds: ids, // DİKKAT: bu uç 'ids' değil 'productIds' bekler
      warehouseId,
      companyId,
      initialQuantity: 0,
    });
    return { success: true, assignedCount: resp?.data?.count ?? ids.length, warehouse: warehouse.name };
  },
};

const createCategory: AgentActionExecutor = {
  describe: (args) => `Yeni kategori oluştur: "${str(args.name)}"`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const name = str(args.name);
    if (!name) return { success: false, error: 'name zorunludur.' };
    const parentId = str(args.parentCategoryId) || null;

    if (parentId) {
      const parent = (await fetchCategories()).find((c: any) => str(c.id ?? c.Id) === parentId);
      if (!parent) return { success: false, error: `Üst kategori bulunamadı (id: ${parentId}).` };
      if ((Number(parent.level ?? parent.Level) || 1) >= 3) {
        return { success: false, error: 'En fazla 3 seviye kategori desteklenir — bu kategorinin altına alt kategori eklenemez.' };
      }
    }

    const created = unwrap(
      await accountingApi.post(ACCOUNTING_API.INVENTORY_CATEGORIES, {
        companyId,
        name,
        parentId,
        isEcommerceEnabled: false,
        orderIndex: 0,
        isActive: true,
      })
    );
    return { success: true, categoryId: created?.id ?? created?.Id ?? null, name };
  },
};

const updateCategory: AgentActionExecutor = {
  describe: (args) => `Kategoriyi güncelle (${str(args.categoryId).slice(0, 8)}…)`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const categoryId = str(args.categoryId);
    if (!categoryId) return { success: false, error: 'categoryId zorunludur (listCategories).' };
    const current = (await fetchCategories()).find((c: any) => str(c.id ?? c.Id) === categoryId);
    if (!current) return { success: false, error: `Kategori bulunamadı (id: ${categoryId}).` };

    const name = str(args.name);
    if (name && name !== str(current.name ?? current.Name)) {
      await accountingApi.post(`${ACCOUNTING_API.INVENTORY_CATEGORIES}/${categoryId}/update`, {
        companyId,
        id: categoryId,
        name,
        parentId: current.parentId ?? current.ParentId ?? null,
        isEcommerceEnabled: (current.isEcommerceEnabled ?? current.IsEcommerceEnabled) === true,
        orderIndex: Number(current.orderIndex ?? current.OrderIndex ?? 0) || 0,
        isActive: (current.isActive ?? current.IsActive ?? true) !== false,
        description: current.description ?? current.Description ?? undefined,
        imageUrl: current.imageUrl ?? current.ImageUrl ?? undefined,
        headerImageUrl: current.headerImageUrl ?? current.HeaderImageUrl ?? undefined,
      });
    }

    if (args.enableEcommerce !== undefined) {
      const target = args.enableEcommerce === true;
      const currentFlag = (current.isEcommerceEnabled ?? current.IsEcommerceEnabled) === true;
      if (target !== currentFlag) {
        await accountingApi.post(
          `${ACCOUNTING_API.INVENTORY_CATEGORIES}/${categoryId}/ecommerce/toggle?enable=${target}`
        );
      }
    }

    return { success: true, categoryId, name: name || str(current.name ?? current.Name) };
  },
};

const createBrand: AgentActionExecutor = {
  describe: (args) => `Yeni marka oluştur: "${str(args.name)}"`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const name = str(args.name);
    if (!name) return { success: false, error: 'name zorunludur.' };
    const brands = await fetchBrands();
    if (brands.some((b: any) => str(b.name ?? b.Name).toLocaleLowerCase('tr') === name.toLocaleLowerCase('tr'))) {
      return { success: false, error: `"${name}" adında bir marka zaten var.` };
    }

    const created = unwrap(
      await accountingApi.post(ACCOUNTING_API.INVENTORY_BRANDS, {
        companyId,
        name,
        description: str(args.description) || undefined,
        website: str(args.website) || undefined,
        isActive: true,
      })
    );
    return { success: true, brandId: created?.id ?? created?.Id ?? null, name };
  },
};

const updateBrand: AgentActionExecutor = {
  describe: (args) => `Markayı güncelle (${str(args.brandId).slice(0, 8)}…)`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const brandId = str(args.brandId);
    if (!brandId) return { success: false, error: 'brandId zorunludur (listBrands).' };
    const current = (await fetchBrands()).find((b: any) => str(b.id ?? b.Id) === brandId);
    if (!current) return { success: false, error: `Marka bulunamadı (id: ${brandId}).` };

    const name = str(args.name) || str(current.name ?? current.Name);
    await accountingApi.post(`${ACCOUNTING_API.INVENTORY_BRANDS}/${brandId}/update`, {
      companyId,
      id: brandId,
      name,
      description:
        args.description !== undefined ? str(args.description) || undefined : (current.description ?? current.Description ?? undefined),
      website: args.website !== undefined ? str(args.website) || undefined : (current.website ?? current.Website ?? undefined),
    });
    return { success: true, brandId, name };
  },
};

const createWarehouse: AgentActionExecutor = {
  describe: (args) => `Yeni depo oluştur: "${str(args.name)}"`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const name = str(args.name);
    if (!name) return { success: false, error: 'name zorunludur.' };
    const warehouses = await fetchWarehouses();
    if (warehouses.some((w) => w.name.toLocaleLowerCase('tr') === name.toLocaleLowerCase('tr'))) {
      return { success: false, error: `"${name}" adında bir depo zaten var.` };
    }

    const created = unwrap(
      await accountingApi.post(ACCOUNTING_API.INVENTORY_WAREHOUSES, {
        companyId,
        name,
        address: str(args.address) || undefined,
        description: str(args.description) || undefined,
        isDefault: false,
        branchesId: null,
        warehouseType: 'Normal',
      })
    );
    return { success: true, warehouseId: created?.id ?? created?.Id ?? null, name };
  },
};

const updateWarehouse: AgentActionExecutor = {
  describe: (args) => `Depoyu güncelle (${str(args.warehouseId).slice(0, 8)}…)`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const warehouseId = str(args.warehouseId);
    if (!warehouseId) return { success: false, error: 'warehouseId zorunludur (listWarehouses).' };
    const current = (await fetchWarehouses()).find((w) => w.id === warehouseId);
    if (!current) return { success: false, error: `Depo bulunamadı (id: ${warehouseId}).` };

    const isSmart = current.warehouseType === 'Smart';
    const name = str(args.name) || current.name;
    await accountingApi.post(`${ACCOUNTING_API.INVENTORY_WAREHOUSES}/${warehouseId}/update`, {
      companyId,
      id: warehouseId,
      name,
      address: args.address !== undefined ? str(args.address) || undefined : current.address || undefined,
      description: args.description !== undefined ? str(args.description) || undefined : current.description || undefined,
      isDefault: current.isDefault,
      isActive: true,
      branchesId: current.branchesId,
      warehouseType: current.warehouseType,
      locationModel: isSmart ? current.locationModel : undefined,
      defaultRoutingStrategy: isSmart ? current.defaultRoutingStrategy : undefined,
    });
    return { success: true, warehouseId, name };
  },
};

const transferStockBetweenWarehouses: AgentActionExecutor = {
  describe: (args) =>
    `Depolar arası transfer: ${args.quantity} adet (${str(args.sourceWarehouseId).slice(0, 8)}… → ${str(args.targetWarehouseId).slice(0, 8)}…)`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const productId = str(args.productId);
    const sourceId = str(args.sourceWarehouseId);
    const targetId = str(args.targetWarehouseId);
    if (!productId || !sourceId || !targetId) {
      return { success: false, error: 'productId, sourceWarehouseId ve targetWarehouseId zorunludur.' };
    }
    if (sourceId === targetId) return { success: false, error: 'Kaynak ve hedef depo aynı olamaz.' };
    const quantity = Number(args.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) return { success: false, error: 'quantity pozitif olmalıdır.' };

    const product = await getProduct(productId);
    if (!product) return { success: false, error: `Ürün bulunamadı (id: ${productId}).` };
    const sourceStock = warehouseStocksOf(product).find(
      (ws) => str(ws.warehouseId ?? ws.WarehouseId) === sourceId
    );
    const available = sourceStock ? Number(sourceStock.quantity ?? sourceStock.Quantity) || 0 : 0;
    if (quantity > available) {
      return { success: false, error: `Yetersiz stok: kaynak depoda ${available} var, ${quantity} transfer istendi.` };
    }

    // DİKKAT: transfer ucu PascalCase payload bekler
    await accountingApi.post(ACCOUNTING_API.INVENTORY_WAREHOUSE_TRANSFER_CREATE, {
      CompanyId: companyId,
      SourceWarehouseId: sourceId,
      TargetWarehouseId: targetId,
      Items: [
        {
          SourceItemId: productId,
          ItemName: productNameOf(product),
          StockCode: str(product.stockCode ?? product.StockCode) || null,
          Quantity: quantity,
          Unit: str(product.unit ?? product.Unit) || 'Adet',
        },
      ],
      Notes: `AI Asistan transferi (${actorEmail(ctx)})`,
    });
    return { success: true, product: productNameOf(product), quantity, remainingInSource: available - quantity };
  },
};

const setBranchPrice: AgentActionExecutor = {
  describe: (args) =>
    `Şube fiyatı ayarla: ${fmtAmount(args.salePrice)} (ürün ${str(args.productId).slice(0, 8)}…, şube ${str(args.branchId)})`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const productId = str(args.productId);
    const branchId = str(args.branchId);
    if (!productId || !branchId) {
      return { success: false, error: 'productId (searchProduct) ve branchId (listBranches) zorunludur.' };
    }
    const salePrice = Number(args.salePrice);
    if (!Number.isFinite(salePrice) || salePrice < 0) return { success: false, error: 'salePrice 0 veya pozitif olmalıdır.' };

    const product = await getProduct(productId);
    if (!product) return { success: false, error: `Ürün bulunamadı (id: ${productId}).` };
    const branches = await getBranches(companyId);
    const branch = branches.find((b) => b.id === branchId);
    if (!branch) return { success: false, error: `Şube bulunamadı (id: ${branchId}) — listBranches ile kontrol edin.` };

    const listPrice = args.listPrice !== undefined ? Number(args.listPrice) : undefined;
    const packagePrice = args.packagePrice !== undefined ? Number(args.packagePrice) : undefined;

    const existing = (await fetchBranchPrices(productId)).find(
      (bp: any) => str(bp.branchId ?? bp.BranchId) === branchId
    );

    if (existing) {
      // Güncelleme ucu PascalCase bekler (fiyat değişiklik logu için audit dahil)
      const bpId = existing.id ?? existing.Id;
      await accountingApi.post(`${API_BASE_URL}/api/InventoryBranchPrice/update/${bpId}`, {
        Id: bpId,
        CompanyId: companyId,
        SalePrice: salePrice,
        SaleCurrency: 'TRY',
        ListPrice: listPrice ?? existing.listPrice ?? existing.ListPrice ?? null,
        ListCurrency: (listPrice ?? existing.listPrice ?? existing.ListPrice) ? 'TRY' : null,
        PackagePrice: packagePrice ?? existing.packagePrice ?? existing.PackagePrice ?? null,
        UserId: null,
        UserName: actorEmail(ctx),
        BranchId: branchId,
        BranchName: branch.name,
      });
    } else {
      await accountingApi.post(`${API_BASE_URL}/api/InventoryBranchPrice`, {
        companyId,
        inventoryItemId: productId,
        branchId,
        branchName: branch.name,
        salePrice,
        saleCurrency: 'TRY',
        listPrice: listPrice ?? undefined,
        listCurrency: listPrice ? 'TRY' : undefined,
        packagePrice: packagePrice ?? undefined,
        createdBy: actorEmail(ctx),
      });
    }

    const pricingEnabled = (product.useBranchPricing ?? product.UseBranchPricing) === true;
    return {
      success: true,
      product: productNameOf(product),
      branch: branch.name,
      salePrice,
      mode: existing ? 'updated' : 'created',
      useBranchPricing: pricingEnabled,
      note: pricingEnabled
        ? undefined
        : 'DİKKAT: Bu ürünün şube fiyatlandırması KAPALI — fiyatın geçerli olması için toggleUseBranchPricing ile açılmalı.',
    };
  },
};

const toggleUseBranchPricing: AgentActionExecutor = {
  describe: (args) =>
    `Şube fiyatlandırmasını ${args.useBranchPricing === true ? 'aç' : 'kapat'} (ürün ${str(args.productId).slice(0, 8)}…)`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const productId = str(args.productId);
    if (!productId) return { success: false, error: 'productId zorunludur (searchProduct).' };
    const product = await getProduct(productId);
    if (!product) return { success: false, error: `Ürün bulunamadı (id: ${productId}).` };

    await accountingApi.post(`${API_BASE_URL}/api/InventoryBranchPrice/toggle-use-branch-pricing`, {
      companyId,
      inventoryItemId: productId,
      useBranchPricing: args.useBranchPricing === true,
    });
    return { success: true, product: productNameOf(product), useBranchPricing: args.useBranchPricing === true };
  },
};

const listBranchPricesByBranch: AgentActionExecutor = {
  describe: (args) => `Şubenin tüm ürün fiyatlarını listele (şube ${str(args.branchId)})`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const branchId = str(args.branchId);
    if (!branchId) return { success: false, error: 'branchId zorunludur (listBranches).' };
    const branch = (await getBranches(companyId)).find((b) => b.id === branchId);
    if (!branch) return { success: false, error: `Şube bulunamadı (id: ${branchId}) — listBranches ile kontrol edin.` };

    const prices = await fetchBranchPricesByBranch(branchId);
    return {
      success: true,
      branch: branch.name,
      totalCount: prices.length,
      branchPrices: prices.map((bp: any) => ({
        id: bp.id ?? bp.Id,
        productId: bp.inventoryItemId ?? bp.InventoryItemId ?? '',
        productName: bp.productName ?? bp.ProductName ?? '',
        stockCode: bp.stockCode ?? bp.StockCode ?? '',
        categoryId: bp.categoryId ?? bp.CategoryId ?? null,
        unit: bp.unit ?? bp.Unit ?? 'adet',
        salePrice: Number(bp.salePrice ?? bp.SalePrice) || 0,
        listPrice: Number(bp.listPrice ?? bp.ListPrice ?? 0) || 0,
        packagePrice: Number(bp.packagePrice ?? bp.PackagePrice ?? 0) || 0,
        currency: bp.saleCurrency ?? bp.SaleCurrency ?? 'TRY',
        useBranchPricing: (bp.useBranchPricing ?? bp.UseBranchPricing) === true,
      })),
    };
  },
};

const copyBranchPrices: AgentActionExecutor = {
  describe: (args) =>
    `Şube fiyatlarını kopyala: ${str(args.sourceBranchId)} → ${str(args.targetBranchId)}`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    const sourceBranchId = str(args.sourceBranchId);
    const targetBranchId = str(args.targetBranchId);
    if (!sourceBranchId || !targetBranchId) {
      return { success: false, error: 'sourceBranchId ve targetBranchId zorunludur (listBranches).' };
    }
    if (sourceBranchId === targetBranchId) {
      return { success: false, error: 'Kaynak ve hedef şube aynı olamaz.' };
    }

    const branches = await getBranches(companyId);
    const source = branches.find((b) => b.id === sourceBranchId);
    const target = branches.find((b) => b.id === targetBranchId);
    if (!source) return { success: false, error: `Kaynak şube bulunamadı (id: ${sourceBranchId}).` };
    if (!target) return { success: false, error: `Hedef şube bulunamadı (id: ${targetBranchId}).` };

    const sourcePrices = await fetchBranchPricesByBranch(sourceBranchId);
    if (sourcePrices.length === 0) {
      return { success: false, error: `"${source.name}" şubesinde tanımlı şube fiyatı yok — kopyalanacak bir şey bulunamadı.` };
    }

    // Hedefte zaten fiyatı olan ürünler atlanır (UI konvansiyonu: üzerine yazılmaz)
    const targetPrices = await fetchBranchPricesByBranch(targetBranchId);
    const existingItemIds = new Set(
      targetPrices.map((p: any) => str(p.inventoryItemId ?? p.InventoryItemId))
    );
    const toCopy = sourcePrices.filter(
      (p: any) => !existingItemIds.has(str(p.inventoryItemId ?? p.InventoryItemId))
    );
    const skipped = sourcePrices.length - toCopy.length;

    if (toCopy.length === 0) {
      return {
        success: true,
        created: 0,
        skipped,
        failed: 0,
        note: `"${target.name}" şubesinde tüm ürünlerin fiyatı zaten tanımlı — yeni kopyalanacak kayıt yok.`,
      };
    }

    // UI ile aynı: 10'arlı paralel chunk'larla hata toleranslı kopyalama
    const CHUNK_SIZE = 10;
    let created = 0;
    const failures: Array<{ productName: string; stockCode: string; message: string }> = [];

    for (let i = 0; i < toCopy.length; i += CHUNK_SIZE) {
      const chunk = toCopy.slice(i, i + CHUNK_SIZE);
      const results = await Promise.allSettled(
        chunk.map((src: any) =>
          accountingApi.post(`${API_BASE_URL}/api/InventoryBranchPrice`, {
            companyId,
            inventoryItemId: src.inventoryItemId ?? src.InventoryItemId,
            branchId: targetBranchId,
            branchName: target.name,
            salePrice: Number(src.salePrice ?? src.SalePrice) || 0,
            saleCurrency: src.saleCurrency ?? src.SaleCurrency ?? 'TRY',
            listPrice: src.listPrice ?? src.ListPrice ?? undefined,
            listCurrency: (src.listPrice ?? src.ListPrice) ? (src.listCurrency ?? src.ListCurrency ?? 'TRY') : undefined,
            packagePrice: src.packagePrice ?? src.PackagePrice ?? undefined,
            createdBy: actorEmail(ctx),
          })
        )
      );
      results.forEach((r, idx) => {
        if (r.status === 'fulfilled') {
          created += 1;
        } else {
          const src: any = chunk[idx];
          const err: any = r.reason;
          const status: number | undefined = err?.response?.status;
          const backendMessage: string | undefined =
            err?.response?.data?.message || err?.response?.data?.error || err?.message;
          failures.push({
            productName: str(src.productName ?? src.ProductName) || '(bilinmiyor)',
            stockCode: str(src.stockCode ?? src.StockCode),
            message:
              status === 404
                ? 'Ürün sistemde bulunamadı (silinmiş olabilir).'
                : backendMessage || 'Bilinmeyen hata.',
          });
        }
      });
    }

    return {
      success: true,
      sourceBranch: source.name,
      targetBranch: target.name,
      created,
      skipped,
      failed: failures.length,
      failures: failures.length > 0 ? failures.slice(0, 20) : undefined,
      note:
        skipped > 0
          ? `${skipped} ürünün hedef şubede zaten fiyatı olduğu için atlandı (üzerine yazılmadı).`
          : undefined,
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// FATURA (Invoice) MODÜLÜ
//
// Fatura OLUŞTURMA/DÜZENLEME arka planda yapılmaz: ajan formu ekranda açar
// (openInvoiceForm), cariyi seçer, kalemleri ekler/düzenler; kaydetme
// kullanıcı onayından geçen saveInvoiceForm ile yine form üzerinden yapılır.
// READ eylemleri (liste/detay/iade özeti) normal API çağrısıdır.
// ═══════════════════════════════════════════════════════════════════════════

/** Fatura listesi/raporu satırını kompakt hâle getirir (token maliyeti için). */
const mapInvoiceRow = (inv: any) => ({
  id: inv.id ?? inv.Id ?? '',
  invoiceNumber: inv.invoiceNumber ?? inv.InvoiceNumber ?? '',
  invoiceType: inv.invoiceType ?? inv.InvoiceType ?? '',
  accountName:
    inv.accountName ?? inv.AccountName ?? inv.account?.companyName ?? inv.Account?.CompanyName ?? '',
  invoiceDate: inv.invoiceDate ?? inv.InvoiceDate ?? inv.date ?? inv.Date ?? '',
  dueDate: inv.dueDate ?? inv.DueDate ?? '',
  grandTotal: Number(inv.grandTotal ?? inv.GrandTotal) || 0,
  currency: inv.currency ?? inv.Currency ?? 'TRY',
  status: inv.status ?? inv.Status ?? '',
  eInvoiceTypeCode: inv.eInvoiceTypeCode ?? inv.EInvoiceTypeCode ?? undefined,
  returnedInvoiceId: inv.returnedInvoiceId ?? inv.ReturnedInvoiceId ?? undefined,
  isDeleted: (inv.isDeleted ?? inv.IsDeleted) === true ? true : undefined,
});

const invoiceKindOf = (v: any): 'sales' | 'purchase' =>
  String(v ?? '').trim().toLowerCase() === 'purchase' ? 'purchase' : 'sales';

const listInvoices: AgentActionExecutor = {
  describe: (args) => {
    const kind = str(args.invoiceType).toUpperCase();
    const kindLabel = kind === 'PURCHASE' ? 'alış ' : kind === 'SALES' ? 'satış ' : '';
    return `${kindLabel}faturalarını listele${str(args.search) ? ` (ara: "${str(args.search)}")` : ''}`;
  },
  execute: async (args) => {
    const page = Math.max(1, Number(args.page) || 1);
    const pageSize = clampLimit(args.pageSize, 10, 50);
    const url = ACCOUNTING_API.INVOICE_BY_COMPANY({
      page,
      pageSize,
      search: str(args.search) || undefined,
      invoiceType: str(args.invoiceType) || undefined,
      sortColumn: 'invoiceDate',
      sortDirection: 'desc',
    });
    const resp = await accountingApi.get(url);
    const d = unwrap(resp);
    const rows: any[] = d?.items ?? d?.Items ?? (Array.isArray(d) ? d : []);
    return {
      success: true,
      totalCount: d?.totalCount ?? d?.TotalCount ?? rows.length,
      page,
      invoices: rows.map(mapInvoiceRow),
    };
  },
};

const getInvoiceDetails: AgentActionExecutor = {
  describe: (args) => `Fatura detayını getir (${str(args.invoiceId).slice(0, 8)}…)`,
  execute: async (args) => {
    const id = str(args.invoiceId);
    if (!id) return { success: false, error: 'invoiceId zorunlu.' };
    const resp = await accountingApi.get(ACCOUNTING_API.INVOICE_BY_ID(id));
    const inv = unwrap(resp);
    if (!inv) return { success: false, error: 'Fatura bulunamadı.' };
    const items: any[] = inv.items ?? inv.Items ?? [];
    return {
      success: true,
      invoice: {
        ...mapInvoiceRow(inv),
        notes: inv.notes ?? inv.Notes ?? '',
        subtotal: Number(inv.subtotal ?? inv.Subtotal) || 0,
        vatTotal: Number(inv.vatTotal ?? inv.VatTotal) || 0,
        discountTotal: Number(inv.discountTotal ?? inv.DiscountTotal) || 0,
        exchangeRate: Number(inv.exchangeRate ?? inv.ExchangeRate) || 1,
        items: items.map((i: any) => ({
          itemName: i.itemName ?? i.ItemName ?? '',
          quantity: Number(i.quantity ?? i.Quantity) || 0,
          unit: i.unit ?? i.Unit ?? '',
          unitPrice: Number(i.unitPrice ?? i.UnitPrice) || 0,
          vatRate: Number(i.vatRate ?? i.VatRate) || 0,
          discountRate: Number(i.discountRate ?? i.DiscountRate) || 0,
          grossTotal: Number(i.grossTotal ?? i.GrossTotal) || 0,
        })),
      },
    };
  },
};

const getInvoiceReturnSummary: AgentActionExecutor = {
  describe: (args) => `Fatura iade özetini getir (${str(args.invoiceId).slice(0, 8)}…)`,
  execute: async (args) => {
    const id = str(args.invoiceId);
    if (!id) return { success: false, error: 'invoiceId zorunlu.' };
    const resp = await accountingApi.get(ACCOUNTING_API.INVOICE_RETURN_SUMMARY(id));
    return { success: true, returnSummary: unwrap(resp) };
  },
};

const listInvoiceRelatedTransactions: AgentActionExecutor = {
  describe: (args) => `Faturaya bağlı hareketleri getir (${str(args.invoiceId).slice(0, 8)}…)`,
  execute: async (args) => {
    const id = str(args.invoiceId);
    if (!id) return { success: false, error: 'invoiceId zorunlu.' };
    const resp = await accountingApi.get(ACCOUNTING_API.INVOICE_RELATED_TRANSACTIONS(id));
    return { success: true, relatedTransactions: unwrap(resp) };
  },
};

const listInvoiceReports: AgentActionExecutor = {
  describe: (args) => `Fatura raporu: ${str(args.startDate)} → ${str(args.endDate)}`,
  execute: async (args) => {
    const startDate = str(args.startDate);
    const endDate = str(args.endDate);
    if (!startDate || !endDate) {
      return { success: false, error: 'startDate ve endDate zorunlu (YYYY-MM-DD).' };
    }
    const url = ACCOUNTING_API.INVOICE_REPORTS_PAGED({
      startDate,
      endDate,
      page: Math.max(1, Number(args.page) || 1),
      pageSize: clampLimit(args.pageSize, 20, 100),
      includeDeleted: args.includeDeleted === true ? 'true' : undefined,
      invoiceType: str(args.invoiceType) || undefined,
      searchTerm: str(args.search) || undefined,
    });
    const resp = await accountingApi.get(url);
    const d = unwrap(resp);
    const rows: any[] = d?.items ?? d?.Items ?? (Array.isArray(d) ? d : []);
    return {
      success: true,
      totalCount: d?.totalCount ?? d?.TotalCount ?? rows.length,
      invoices: rows.map(mapInvoiceRow),
    };
  },
};

// ── Fatura formu UI eylemleri (uiBridge üzerinden ekrandaki formu doldurur) ──

const INVOICE_FORM_ROUTES: Record<'sales' | 'purchase', { path: string; label: string }> = {
  sales: { path: '/fatura/satis/yeni', label: 'Yeni Satış Faturası' },
  purchase: { path: '/fatura/alis/yeni', label: 'Yeni Alış Faturası' },
};

const openInvoiceForm: AgentActionExecutor = {
  describe: (args) =>
    `${invoiceKindOf(args.invoiceKind) === 'purchase' ? 'Alış' : 'Satış'} faturası formunu aç`,
  execute: async (args, ctx) => {
    const kind = invoiceKindOf(args.invoiceKind);
    const route = INVOICE_FORM_ROUTES[kind];
    ctx.navigate(route.path);
    // Sayfanın mount olup köprüyü kaydetmesini bekle — hazır olduğu doğrulanmış döner
    await waitForInvoiceFormApi(kind, 15000);
    return {
      success: true,
      message: `${route.label} formu ekranda açıldı.`,
      nextSteps:
        'Sırasıyla: setInvoiceFormAccount ile cari seç, addInvoiceFormItem ile kalemleri ekle, ' +
        'gerekirse setInvoiceFormFields/updateInvoiceFormItem ile düzenle. Kaydetme KULLANICI isterse saveInvoiceForm ile yapılır.',
    };
  },
};

const setInvoiceFormAccount: AgentActionExecutor = {
  describe: (args) => `Fatura formunda cari seç: "${str(args.accountName)}"`,
  execute: async (args) => {
    const api = await waitForInvoiceFormApi();
    return api.setAccountByName(str(args.accountName));
  },
};

const setInvoiceFormFields: AgentActionExecutor = {
  describe: (args) => {
    const keys = Object.keys(args || {}).filter((k) => args[k] !== undefined && args[k] !== null);
    return `Fatura formu alanlarını ayarla (${keys.join(', ') || '—'})`;
  },
  execute: async (args) => {
    const api = await waitForInvoiceFormApi();
    const fields: InvoiceFormFields = {};
    if (args.invoiceDate !== undefined) fields.invoiceDate = str(args.invoiceDate);
    if (args.dueDate !== undefined) fields.dueDate = str(args.dueDate);
    if (args.invoiceNumber !== undefined) fields.invoiceNumber = str(args.invoiceNumber);
    if (args.notes !== undefined) fields.notes = str(args.notes);
    if (args.currency !== undefined) fields.currency = str(args.currency);
    if (args.exchangeRate !== undefined) fields.exchangeRate = Number(args.exchangeRate);
    if (args.paymentMethod !== undefined) fields.paymentMethod = str(args.paymentMethod);
    if (args.tevkifatType !== undefined) fields.tevkifatType = str(args.tevkifatType);
    if (args.stopajRate !== undefined) fields.stopajRate = Number(args.stopajRate);
    return api.setFields(fields);
  },
};

/** Ajan argümanlarını köprünün kalem girdisine çevirir (yalnızca verilen alanlar). */
const toItemInput = (args: Record<string, any>): InvoiceFormItemInput => {
  const input: InvoiceFormItemInput = {};
  if (args.quantity !== undefined) input.quantity = Number(args.quantity);
  if (args.unitPrice !== undefined) input.unitPrice = Number(args.unitPrice);
  if (args.priceType !== undefined) input.priceType = str(args.priceType) as any;
  if (args.vatRate !== undefined) input.vatRate = Number(args.vatRate);
  if (args.discountRate !== undefined) input.discountRate = Number(args.discountRate);
  if (args.discountAmount !== undefined) input.discountAmount = Number(args.discountAmount);
  if (args.unit !== undefined) input.unit = str(args.unit);
  return input;
};

const addInvoiceFormItem: AgentActionExecutor = {
  describe: (args) =>
    `Fatura formuna kalem ekle: ${str(args.itemName)}${Number(args.quantity) > 0 ? ` x${Number(args.quantity)}` : ''}${args.isManual === true ? ' (stoksuz)' : ''}`,
  execute: async (args) => {
    const api = await waitForInvoiceFormApi();
    const input: InvoiceFormItemInput = {
      ...toItemInput(args),
      itemName: str(args.itemName),
      isManual: args.isManual === true,
    };
    if (str(args.warehouseName)) {
      const rows = (await fetchWarehouses()).filter((w: any) => w.isActive !== false);
      const match = matchByName(rows as any[], str(args.warehouseName));
      if (!match) {
        return {
          success: false,
          error: `Depo bulunamadı: "${str(args.warehouseName)}" — listWarehouses ile mevcut depoları gör.`,
        };
      }
      input.warehouseId = (match as any).id;
    }
    return api.addItem(input);
  },
};

const updateInvoiceFormItem: AgentActionExecutor = {
  describe: (args) =>
    `Fatura formunda kalem güncelle: ${args.line !== undefined ? `satır ${Number(args.line)}` : `"${str(args.itemName)}"`}`,
  execute: async (args) => {
    const api = await waitForInvoiceFormApi();
    const changes = toItemInput(args);
    if (str(args.newItemName)) changes.itemName = str(args.newItemName);
    return api.updateItem(
      { line: args.line !== undefined ? Number(args.line) : undefined, itemName: str(args.itemName) || undefined },
      changes
    );
  },
};

const removeInvoiceFormItem: AgentActionExecutor = {
  describe: (args) =>
    `Fatura formundan kalem çıkar: ${args.line !== undefined ? `satır ${Number(args.line)}` : `"${str(args.itemName)}"`}`,
  execute: async (args) => {
    const api = await waitForInvoiceFormApi();
    return api.removeItem({
      line: args.line !== undefined ? Number(args.line) : undefined,
      itemName: str(args.itemName) || undefined,
    });
  },
};

const getInvoiceFormSummary: AgentActionExecutor = {
  describe: () => 'Ekrandaki fatura formunun özetini getir',
  execute: async () => {
    const api = await waitForInvoiceFormApi();
    return api.getSummary();
  },
};

const saveInvoiceForm: AgentActionExecutor = {
  describe: () => 'Ekrandaki fatura formunu kaydet',
  execute: async () => {
    const api = await waitForInvoiceFormApi();
    return api.save();
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// E-FATURA MODÜLÜ (EDM + PavoPay — Finansbank bilinçli olarak desteklenmiyor)
//
// Aktif entegratör EINVOICE_INTEGRATION_GET ile tespit edilir:
//   - edm       → einvoiceApi (X-Company-Id GÖNDERMEZ; kimlik JWT claim'inden)
//   - finansbank→ desteklenmez (eski ham SOAP yolu, şifre query string'de) → yönlendirme mesajı
//   - kayıt yok → PavoPay entegrasyonları denenir (accountingApi proxy üzerinden)
// Kabul/Red GİB'e resmi ve GERİ ALINAMAZ yanıt gönderir → risk=write (onay kartı).
// ═══════════════════════════════════════════════════════════════════════════

type EInvoiceProvider = 'edm' | 'finansbank' | 'pavopay' | 'none';

const FINANSBANK_NOT_SUPPORTED =
  'Aktif e-fatura entegratörü QNB/Finansbank — bu entegratör AI ajanı tarafından desteklenmiyor. ' +
  'İşlemi E-Fatura sayfasından yapın (navigateToPage: e-invoice).';

/** Aktif e-fatura entegratörünü ve (PavoPay için) proxy şube bilgisini çözer. */
const resolveEInvoiceProvider = async (): Promise<{
  provider: EInvoiceProvider;
  /** PavoPay proxy'sinde kullanılacak entegrasyon listesi (aktifler) */
  pavoIntegrations: Array<{ id: any; branchId: string | null; branchName: string }>;
}> => {
  // 1) EDM / Finansbank aktif entegrasyonu var mı?
  try {
    const resp = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_GET);
    const sqlData = resp?.data?.data;
    const p = str(sqlData?.providerType ?? sqlData?.ProviderType).toLowerCase();
    if (p === 'edm') return { provider: 'edm', pavoIntegrations: [] };
    if (p) return { provider: 'finansbank', pavoIntegrations: [] };
  } catch {
    // 404/hata → EDM-Finansbank entegrasyonu yok, PavoPay'e düş
  }

  // 2) PavoPay entegrasyonları (sayfadaki fallback ile aynı sıra)
  try {
    const resp = await accountingApi.get(ACCOUNTING_API.PAVOPAY_INTEGRATION_LIST);
    const list: any[] = resp?.data?.data ?? resp?.data?.Data ?? [];
    const actives = (Array.isArray(list) ? list : [])
      .filter((i) => (i.isActive ?? i.IsActive ?? true) !== false)
      .map((i) => ({
        id: i.id ?? i.Id,
        branchId: (i.branchId ?? i.BranchId) || null,
        branchName: str(i.branchName ?? i.BranchName),
      }));
    if (actives.length > 0) return { provider: 'pavopay', pavoIntegrations: actives };
  } catch {
    // liste alınamadı → entegrasyon yok kabul et
  }

  return { provider: 'none', pavoIntegrations: [] };
};

const NO_EINVOICE_INTEGRATION =
  'Aktif bir e-fatura entegrasyonu bulunamadı (EDM veya PavoPay). Önce E-Fatura sayfasından entegrasyon ayarlarını yapın.';

/** PavoPay proxy'si için şube seçimi: global (şubesiz) varsa o, yoksa ada göre / tek aktif. */
const pickPavoBranch = (
  integrations: Array<{ id: any; branchId: string | null; branchName: string }>,
  branchName?: string
): { ok: true; branchId: string | null } | { ok: false; error: string } => {
  if (str(branchName)) {
    const q = str(branchName).toLocaleLowerCase('tr');
    const match = integrations.find((i) => i.branchName.toLocaleLowerCase('tr').includes(q));
    if (!match) {
      return {
        ok: false,
        error: `"${str(branchName)}" şubesi için PavoPay entegrasyonu yok. Mevcut: ${integrations.map((i) => i.branchName || '(global)').join(', ')}`,
      };
    }
    return { ok: true, branchId: match.branchId };
  }
  const global = integrations.find((i) => !i.branchId);
  if (global) return { ok: true, branchId: null };
  if (integrations.length === 1) return { ok: true, branchId: integrations[0].branchId };
  return {
    ok: false,
    error: `Birden fazla şube bazlı PavoPay entegrasyonu var — branchName ver. Mevcut: ${integrations.map((i) => i.branchName).join(', ')}`,
  };
};

/** YYYY-MM-DD → ISO (yerel gün başı/sonu). Boşsa bugünden offsetGun geriye gider. */
const dayIso = (v: any, endOfDay: boolean, fallbackOffsetDays: number): string => {
  const s = str(v);
  const base = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00`) : (() => {
    const d = new Date();
    d.setDate(d.getDate() - fallbackOffsetDays);
    d.setHours(0, 0, 0, 0);
    return d;
  })();
  if (endOfDay) base.setHours(23, 59, 59, 0);
  return base.toISOString();
};

/** EDM gelen kutusu satırından kompakt + yanıt durumu türetilmiş kayıt (sayfadaki mantıkla aynı). */
const mapEdmInboxItem = (it: any) => {
  const profile = str(it.profileId).toUpperCase();
  const isTicari = profile === 'TICARIFATURA';
  const blob = [it.responseDescription, it.responseCode, it.gibStatusDescription, it.statusDescription, it.status]
    .filter(Boolean)
    .join(' ')
    .toUpperCase();
  const isRejected = blob.includes('REDDEDİL') || blob.includes('REDDEDIL') || blob.includes('REJECT');
  const isAccepted = blob.includes('KABUL') || blob.includes('ACCEPT');
  const responseStatus = !isTicari
    ? 'YANIT_GEREKMEZ' // Temel fatura
    : isRejected
      ? 'REDDEDILDI'
      : isAccepted
        ? 'KABUL_EDILDI'
        : 'YANIT_BEKLIYOR';
  return {
    ettn: it.uuid || it.id || '',
    belgeNo: it.id || '',
    issueDate: it.issueDate || '',
    senderVkn: it.senderVkn || '',
    senderTitle: it.senderTitle || '',
    payableAmount: it.payableAmount != null ? Number(it.payableAmount) : null,
    currency: it.currency || 'TRY',
    profileId: it.profileId || '',
    responseStatus,
    statusDescription: it.responseDescription || it.gibStatusDescription || it.status || '',
    isRead: it.isRead === true,
  };
};

const listEInvoiceInbox: AgentActionExecutor = {
  describe: (args) =>
    `Gelen e-faturaları listele${str(args.startDate) ? ` (${str(args.startDate)} → ${str(args.endDate) || 'bugün'})` : ' (son 30 gün)'}${args.unreadOnly === true ? ', yalnızca okunmamış' : ''}`,
  execute: async (args, ctx) => {
    const { provider, pavoIntegrations } = await resolveEInvoiceProvider();
    if (provider === 'finansbank') return { success: false, error: FINANSBANK_NOT_SUPPORTED };
    if (provider === 'none') return { success: false, error: NO_EINVOICE_INTEGRATION };

    const startIso = dayIso(args.startDate, false, 30);
    const endIso = dayIso(args.endDate, true, 0);
    const limit = clampLimit(args.limit, 50, 200);

    if (provider === 'edm') {
      // Şube filtresi: ad verildiyse id'ye çevir
      let branchId: string | null = null;
      if (str(args.branchName)) {
        const branches = await getBranches(requireCompanyId(ctx));
        const rows = (branches || []).map((b: any) => ({ id: str(b.id ?? b.Id), name: str(b.name ?? b.Name) }));
        const match = matchByName(rows, str(args.branchName));
        if (!match) return { success: false, error: `Şube bulunamadı: "${str(args.branchName)}" — listBranches kullan.` };
        branchId = match.id;
      }
      const resp = await einvoiceApi.post(EINVOICE_API.EDM_INBOX, {
        startDate: startIso,
        endDate: endIso,
        readIncluded: args.unreadOnly !== true,
        limit,
        branchId,
      });
      const data = resp.data;
      if (!data?.success) return { success: false, error: data?.error || 'Gelen kutusu (EDM) alınamadı.' };
      const items: any[] = Array.isArray(data.items) ? data.items : [];
      return {
        success: true,
        provider: 'edm',
        count: items.length,
        invoices: items.map(mapEdmInboxItem),
        note:
          'responseStatus=YANIT_BEKLIYOR olan ticari faturalara respondEInvoice ile kabul/red yanıtı verilebilir; ' +
          'içeri aktarmak için importEInvoiceToPurchase kullan.',
      };
    }

    // PavoPay
    const pick = pickPavoBranch(pavoIntegrations, str(args.branchName) || undefined);
    if (!pick.ok) return { success: false, error: pick.error };
    const qs =
      `sort=&pageSize=${limit}&page=1&group=&filter=&startDate=${startIso}&endDate=${endIso}` +
      `&filterStatus=1,2,3,4,5,6,7,8,9,12,17,23,24&taxId=&byIssueDate=true&isArchived=&profileType=&invoiceType=`;
    const proxyResp = await accountingApi.post(ACCOUNTING_API.PAVOPAY_INTEGRATION_PROXY, {
      Path: '/api/EInvoice/IncomingInvoice/SearchInvoices',
      QueryString: qs,
      Method: 'GET',
      BranchId: pick.branchId,
    });
    const rows: any[] = proxyResp.data?.Data ?? proxyResp.data?.data ?? [];
    return {
      success: true,
      provider: 'pavopay',
      count: Array.isArray(rows) ? rows.length : 0,
      invoices: (Array.isArray(rows) ? rows : []).map((item: any) => ({
        ettn: item.UUID || item.Ettn || '',
        belgeNo: item.InvoiceReference || item.InvoiceNumber || '',
        issueDate: item.IssueDate || item.CreateDate || '',
        senderVkn: item.SenderTaxOrIdNo || '',
        senderTitle: item.SenderPartyName || '',
        payableAmount: Number(item.TotalPayableAmount ?? item.TotalTaxInclusiveAmount) || 0,
        currency: item.DocCurrencyCode || 'TRY',
        invoiceType: item.InvoiceTypeName || 'FATURA',
      })),
      note: 'PavoPay entegrasyonunda kabul/red yanıtı ve okundu işaretleme ajan üzerinden desteklenmez; E-Fatura sayfasını kullanın.',
    };
  },
};

const getEInvoiceStatus: AgentActionExecutor = {
  describe: (args) => `E-fatura durumunu sorgula (${str(args.ettn).slice(0, 12)}…)`,
  execute: async (args) => {
    const ettn = str(args.ettn);
    const belgeNo = str(args.belgeNo);
    if (!ettn && !belgeNo) return { success: false, error: 'ettn veya belgeNo zorunlu.' };

    const { provider } = await resolveEInvoiceProvider();
    if (provider === 'finansbank') return { success: false, error: FINANSBANK_NOT_SUPPORTED };
    if (provider === 'pavopay') {
      return {
        success: false,
        error: 'PavoPay entegrasyonunda ayrı durum sorgusu yok — durum bilgisi listEInvoiceInbox sonucunda gelir.',
      };
    }
    if (provider === 'none') return { success: false, error: NO_EINVOICE_INTEGRATION };

    const resp = await einvoiceApi.post(EINVOICE_API.EDM_INBOX_STATUS, {
      uuid: ettn || null,
      id: belgeNo || null,
      branchId: null,
    });
    const data = resp.data;
    if (!data || data.success === false) return { success: false, error: data?.error || 'Durum sorgulanamadı.' };
    if (!data.found) return { success: true, found: false, message: 'Fatura EDM üzerinde bulunamadı.' };
    return { success: true, found: true, status: data };
  },
};

const queryEInvoiceMukellef: AgentActionExecutor = {
  describe: (args) => `E-fatura mükellef sorgula: VKN/TCKN ${str(args.vknTckn)}`,
  execute: async (args) => {
    const vkn = str(args.vknTckn).replace(/[^0-9]/g, '');
    if (vkn.length !== 10 && vkn.length !== 11) {
      return { success: false, error: 'Geçerli bir VKN (10 hane) veya TCKN (11 hane) verin.' };
    }
    const { provider } = await resolveEInvoiceProvider();
    if (provider !== 'edm') {
      return {
        success: false,
        error: provider === 'finansbank' ? FINANSBANK_NOT_SUPPORTED : 'Mükellef sorgusu yalnızca EDM entegrasyonunda desteklenir.',
      };
    }
    const resp = await einvoiceApi.post(EINVOICE_API.EDM_MUKELLEF_SORGULA, { vknTckn: vkn });
    const data = resp.data;
    const isUser = data?.isEInvoiceUser === true;
    const title = Array.isArray(data?.users) && data.users.length > 0 ? str(data.users[0]?.title) : '';
    return {
      success: true,
      vknTckn: vkn,
      isEInvoiceUser: isUser,
      title: title || undefined,
      note: isUser
        ? 'E-Fatura mükellefi — bu firmaya E-FATURA düzenlenir.'
        : 'E-fatura mükellefi DEĞİL — bu firmaya E-ARŞİV fatura düzenlenir.',
    };
  },
};

const getEInvoiceKontor: AgentActionExecutor = {
  describe: () => 'Kalan e-fatura kontörünü sorgula',
  execute: async () => {
    const { provider } = await resolveEInvoiceProvider();
    if (provider !== 'edm') {
      return {
        success: false,
        error: provider === 'finansbank' ? FINANSBANK_NOT_SUPPORTED : 'Kontör kavramı EDM entegrasyonuna özgüdür (aktif EDM entegrasyonu yok).',
      };
    }
    const resp = await einvoiceApi.post(EINVOICE_API.EDM_KONTOR_SORGULA, {});
    const data = resp.data;
    if (!data?.success) return { success: false, error: data?.error || 'Kontör sorgulanamadı.' };
    return { success: true, kalanKontor: data.kalanKontor };
  },
};

const listEInvoiceIntegrations: AgentActionExecutor = {
  describe: () => 'E-fatura entegrasyonlarını listele',
  execute: async () => {
    const result: Record<string, any> = { success: true };

    try {
      const resp = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_LIST(false));
      const list: any[] = resp?.data?.data ?? resp?.data ?? [];
      // Şifre/kimlik bilgileri ASLA döndürülmez
      result.einvoiceIntegrations = (Array.isArray(list) ? list : []).map((i: any) => ({
        providerType: str(i.providerType ?? i.ProviderType),
        companyName: str(i.companyName ?? i.CompanyName),
        taxNumber: str(i.taxNumber ?? i.TaxNumber),
        isActive: (i.isActive ?? i.IsActive) === true,
      }));
    } catch {
      result.einvoiceIntegrations = [];
    }

    try {
      const resp = await accountingApi.get(ACCOUNTING_API.PAVOPAY_INTEGRATION_LIST);
      const list: any[] = resp?.data?.data ?? resp?.data?.Data ?? [];
      result.pavoPayIntegrations = (Array.isArray(list) ? list : []).map((i: any) => ({
        branchName: str(i.branchName ?? i.BranchName) || '(global — tüm şirket)',
        isActive: (i.isActive ?? i.IsActive ?? true) !== false,
      }));
    } catch {
      result.pavoPayIntegrations = [];
    }

    const activeEdm = result.einvoiceIntegrations.find((i: any) => i.isActive);
    result.activeProvider = activeEdm
      ? str(activeEdm.providerType)
      : result.pavoPayIntegrations.some((i: any) => i.isActive)
        ? 'pavopay'
        : 'yok';
    return result;
  },
};

const testEInvoiceConnection: AgentActionExecutor = {
  describe: () => 'E-fatura entegrasyon bağlantısını test et',
  execute: async () => {
    const { provider, pavoIntegrations } = await resolveEInvoiceProvider();
    if (provider === 'finansbank') return { success: false, error: FINANSBANK_NOT_SUPPORTED };
    if (provider === 'none') return { success: false, error: NO_EINVOICE_INTEGRATION };

    if (provider === 'edm') {
      const resp = await einvoiceApi.post(EINVOICE_API.EDM_TEST_CONNECTION, {});
      const data = resp.data;
      return data?.success
        ? { success: true, provider: 'edm', message: 'EDM bağlantı testi başarılı.' }
        : { success: false, provider: 'edm', error: data?.error || 'EDM bağlantı testi başarısız.' };
    }

    // PavoPay: aktif entegrasyonların auth testini yap
    const results: any[] = [];
    for (const integ of pavoIntegrations) {
      try {
        const resp = await accountingApi.post(ACCOUNTING_API.PAVOPAY_INTEGRATION_AUTH_TEST, { Id: integ.id });
        const ok = resp?.data?.success !== false;
        results.push({ branch: integ.branchName || '(global)', ok });
      } catch (err: any) {
        results.push({
          branch: integ.branchName || '(global)',
          ok: false,
          error: err?.response?.data?.message || err?.message,
        });
      }
    }
    return { success: results.some((r) => r.ok), provider: 'pavopay', results };
  },
};

const respondEInvoice: AgentActionExecutor = {
  describe: (args) =>
    `Gelen e-faturayı ${str(args.response).toUpperCase() === 'RED' ? 'REDDET' : 'KABUL ET'} (${str(args.ettn).slice(0, 12)}…)`,
  execute: async (args) => {
    const ettn = str(args.ettn);
    const belgeNo = str(args.belgeNo);
    if (!ettn && !belgeNo) return { success: false, error: 'ettn veya belgeNo zorunlu.' };
    const responseType = str(args.response).toUpperCase();
    if (responseType !== 'KABUL' && responseType !== 'RED') {
      return { success: false, error: 'response parametresi KABUL veya RED olmalı.' };
    }
    const rejectReason = str(args.rejectReason);
    if (responseType === 'RED' && !rejectReason) {
      return { success: false, error: 'RED yanıtı için rejectReason (red nedeni) zorunlu.' };
    }

    const { provider } = await resolveEInvoiceProvider();
    if (provider !== 'edm') {
      return {
        success: false,
        error:
          provider === 'finansbank'
            ? FINANSBANK_NOT_SUPPORTED
            : 'Kabul/Red yanıtı yalnızca EDM entegrasyonunda desteklenir — E-Fatura sayfasını kullanın.',
      };
    }

    const resp = await einvoiceApi.post(EINVOICE_API.EDM_INBOX_RESPOND, {
      uuid: ettn || null,
      id: belgeNo || null,
      accept: responseType === 'KABUL',
      description: responseType === 'RED' ? rejectReason : null,
      branchId: null,
    });
    const data = resp.data;
    if (!data?.success) {
      return { success: false, error: data?.errorLong || data?.error || 'EDM yanıtı gönderilemedi.' };
    }
    return {
      success: true,
      message: `${responseType === 'KABUL' ? 'Kabul' : 'Red'} yanıtı GİB'e gönderildi (geri alınamaz).`,
      returnCode: data.returnCode ?? undefined,
      intlTxnId: data.intlTxnId ?? undefined,
    };
  },
};

const markEInvoiceRead: AgentActionExecutor = {
  describe: (args) =>
    `Gelen e-faturayı ${args.read === false ? 'okunmadı' : 'okundu'} işaretle (${str(args.ettn).slice(0, 12)}…)`,
  execute: async (args) => {
    const ettn = str(args.ettn);
    const belgeNo = str(args.belgeNo);
    if (!ettn && !belgeNo) return { success: false, error: 'ettn veya belgeNo zorunlu.' };

    const { provider } = await resolveEInvoiceProvider();
    if (provider !== 'edm') {
      return {
        success: false,
        error:
          provider === 'finansbank'
            ? FINANSBANK_NOT_SUPPORTED
            : 'Okundu işaretleme yalnızca EDM entegrasyonunda desteklenir.',
      };
    }

    const read = args.read !== false;
    const resp = await einvoiceApi.post(EINVOICE_API.EDM_INBOX_MARK, {
      uuid: ettn || null,
      id: belgeNo || null,
      read,
      branchId: null,
    });
    const data = resp.data;
    if (data?.success === false) return { success: false, error: data?.error || 'İşaretleme başarısız.' };
    return { success: true, message: `Fatura ${read ? 'okundu' : 'okunmadı'} olarak işaretlendi.` };
  },
};

const importEInvoiceToPurchase: AgentActionExecutor = {
  describe: (args) => `Gelen e-faturayı alış faturasına aktar (${str(args.ettn).slice(0, 12)}…)`,
  execute: async (args, ctx) => {
    const ettn = str(args.ettn);
    if (!ettn) return { success: false, error: 'ettn zorunlu (listEInvoiceInbox sonucundan al).' };

    // Zaten içeri aktarılmış mı? (sayfadaki kontrolle aynı: PURCHASE faturaların eInvoiceETTN seti)
    try {
      const resp = await accountingApi.get(
        ACCOUNTING_API.INVOICE_BY_COMPANY({ page: 1, pageSize: 500, invoiceType: 'PURCHASE' })
      );
      const d = unwrap(resp);
      const rows: any[] = d?.items ?? d?.Items ?? (Array.isArray(d) ? d : []);
      const existing = rows.find(
        (inv: any) => str(inv.eInvoiceETTN ?? inv.EInvoiceETTN).toLowerCase() === ettn.toLowerCase()
      );
      if (existing) {
        return {
          success: false,
          error: `Bu e-fatura zaten içeri aktarılmış (alış faturası no: ${existing.invoiceNumber ?? existing.InvoiceNumber ?? '?'}).`,
        };
      }
    } catch {
      // Kontrol başarısızsa aktarımı engelleme — hedef sayfa da doğrular
    }

    ctx.navigate(`/fatura/alis/yeni?ettn=${encodeURIComponent(ettn)}`);
    return {
      success: true,
      message: 'Alış faturası formu e-fatura içeriğiyle açılıyor — form yüklendikten sonra kullanıcı kontrol edip kaydedebilir.',
      note:
        'Form açıldıktan sonra getInvoiceFormSummary/updateInvoiceFormItem gibi form eylemleri kullanılabilir. ' +
        'DİKKAT: Ticari faturalarda yanıt bekleniyor/reddedildi durumundaysa aktarım hedef ekranda engellenebilir; ' +
        'önce respondEInvoice ile kabul yanıtı vermek gerekebilir.',
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// ÇEK / SENET MODÜLÜ (silme eylemleri bilinçli olarak yok)
// ═══════════════════════════════════════════════════════════════════════════

const CHECK_NOTE_BASE = `${API_BASE_URL}/api/accounting/CheckNote`;

const CHECK_NOTE_STATUSES = new Set([
  'PORTFOLIO', 'IN_COLLECTION', 'COLLECTED', 'PARTIAL_COLLECTED', 'ENDORSED',
  'COLLATERAL', 'COLLATERAL_RETURNED', 'BOUNCED', 'RETURNED', 'CANCELED',
  'GIVEN_PENDING', 'GIVEN_PAID', 'GIVEN_RETURNED',
]);

const mapCheckNote = (n: any) => ({
  id: n.id ?? n.Id,
  documentNumber: n.documentNumber ?? n.DocumentNumber ?? '',
  type: n.type ?? n.Type ?? '',
  direction: n.direction ?? n.Direction ?? '',
  accountId: n.accountId ?? n.AccountId ?? null,
  accountName: n.accountName ?? n.AccountName ?? '',
  bankName: n.bankName ?? n.BankName ?? '',
  checkNumber: n.checkNumber ?? n.CheckNumber ?? '',
  drawerName: n.drawerName ?? n.DrawerName ?? '',
  issueDate: n.issueDate ?? n.IssueDate ?? '',
  dueDate: n.dueDate ?? n.DueDate ?? '',
  amount: Number(n.amount ?? n.Amount ?? 0) || 0,
  currency: n.currency ?? n.Currency ?? 'TRY',
  amountTRY: Number(n.amountTRY ?? n.AmountTRY ?? n.amount ?? 0) || 0,
  status: n.status ?? n.Status ?? '',
  collectedAmount: Number(n.collectedAmount ?? n.CollectedAmount ?? 0) || 0,
  remainingAmount: Number(n.remainingAmount ?? n.RemainingAmount ?? 0) || 0,
  daysUntilDue: n.daysUntilDue ?? n.DaysUntilDue ?? null,
  isOverdue: (n.isOverdue ?? n.IsOverdue) === true,
  notes: n.notes ?? n.Notes ?? '',
  endorsedToAccountName: n.endorsedToAccountName ?? n.EndorsedToAccountName ?? undefined,
});

const mapCheckNoteSummary = (s: any) => {
  if (!s) return null;
  return {
    receivedCheckCount: Number(s.receivedCheckCount ?? s.ReceivedCheckCount ?? 0) || 0,
    receivedCheckTotalTRY: Number(s.receivedCheckTotalTRY ?? s.ReceivedCheckTotalTRY ?? 0) || 0,
    givenCheckCount: Number(s.givenCheckCount ?? s.GivenCheckCount ?? 0) || 0,
    givenCheckTotalTRY: Number(s.givenCheckTotalTRY ?? s.GivenCheckTotalTRY ?? 0) || 0,
    receivedNoteCount: Number(s.receivedNoteCount ?? s.ReceivedNoteCount ?? 0) || 0,
    receivedNoteTotalTRY: Number(s.receivedNoteTotalTRY ?? s.ReceivedNoteTotalTRY ?? 0) || 0,
    givenNoteCount: Number(s.givenNoteCount ?? s.GivenNoteCount ?? 0) || 0,
    givenNoteTotalTRY: Number(s.givenNoteTotalTRY ?? s.GivenNoteTotalTRY ?? 0) || 0,
    portfolioCount: Number(s.portfolioCount ?? s.PortfolioCount ?? 0) || 0,
    portfolioTotal: Number(s.portfolioTotal ?? s.PortfolioTotal ?? 0) || 0,
    inCollectionCount: Number(s.inCollectionCount ?? s.InCollectionCount ?? 0) || 0,
    collectedCount: Number(s.collectedCount ?? s.CollectedCount ?? 0) || 0,
    bouncedCount: Number(s.bouncedCount ?? s.BouncedCount ?? 0) || 0,
    overdueCount: Number(s.overdueCount ?? s.OverdueCount ?? 0) || 0,
    overdueTotal: Number(s.overdueTotal ?? s.OverdueTotal ?? 0) || 0,
    dueThisWeekCount: Number(s.dueThisWeekCount ?? s.DueThisWeekCount ?? 0) || 0,
    dueThisWeekTotal: Number(s.dueThisWeekTotal ?? s.DueThisWeekTotal ?? 0) || 0,
  };
};

/** Cari adı → hesap (tam → içerir). */
const findAccountByName = async (name: string): Promise<any | null> => {
  const q = name.trim();
  if (!q) return null;
  const rows = listOf(await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY));
  const mapped = rows.map((a: any) => ({
    raw: a,
    id: String(a.id ?? a.Id ?? ''),
    name: str(a.companyName ?? a.CompanyName),
  }));
  const match = matchByName(mapped, q);
  return match?.raw ?? null;
};

/** Banka hesabı adı → hesap (bankName / name). */
const findBankAccountByName = async (name: string): Promise<any | null> => {
  const q = name.trim();
  if (!q) return null;
  const rows = listOf(await accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY));
  const mapped = rows.map((b: any) => ({
    raw: b,
    id: String(b.id ?? b.Id ?? ''),
    name: str(b.bankName ?? b.BankName ?? b.name ?? b.Name),
  }));
  const match = matchByName(mapped, q);
  return match?.raw ?? null;
};

/** Kasa adı → kasa. */
const findCashboxByName = async (name: string): Promise<any | null> => {
  const q = name.trim();
  if (!q) return null;
  const rows = listOf(await accountingApi.get(ACCOUNTING_API.CASHBOX_BY_COMPANY));
  const mapped = rows.map((c: any) => ({
    raw: c,
    id: String(c.id ?? c.Id ?? ''),
    name: str(c.name ?? c.Name),
  }));
  const match = matchByName(mapped, q);
  return match?.raw ?? null;
};

const applyCheckNotesFilters: AgentActionExecutor = {
  describe: (args) => {
    const parts: string[] = [];
    if (args.clearAll === true) parts.push('filtreleri temizle');
    if (str(args.searchText)) parts.push(`ara: "${str(args.searchText)}"`);
    if (str(args.type)) parts.push(`tür: ${str(args.type)}`);
    if (str(args.direction)) parts.push(`yön: ${str(args.direction)}`);
    if (str(args.status)) parts.push(`durum: ${str(args.status)}`);
    if (str(args.dueDateFilter)) parts.push(`vade: ${str(args.dueDateFilter)}`);
    return `Çek/Senet sayfasında filtrele${parts.length ? ` (${parts.join(', ')})` : ''}`;
  },
  execute: async (args, ctx) => {
    const req: CheckNotesFilterRequest = {};
    const applied: string[] = [];
    if (args.clearAll === true) {
      req.clearAll = true;
      applied.push('tüm filtreler temizlendi');
    }
    if (args.searchText !== undefined && args.searchText !== null) {
      req.searchText = str(args.searchText);
      applied.push(req.searchText ? `arama: "${req.searchText}"` : 'arama temizlendi');
    }
    if (args.type !== undefined) {
      req.type = str(args.type) || 'all';
      applied.push(`tür: ${req.type}`);
    }
    if (args.direction !== undefined) {
      req.direction = str(args.direction) || 'all';
      applied.push(`yön: ${req.direction}`);
    }
    if (args.status !== undefined) {
      req.status = str(args.status) || 'all';
      applied.push(`durum: ${req.status}`);
    }
    if (args.dueDateFilter !== undefined) {
      req.dueDateFilter = str(args.dueDateFilter) || 'all';
      applied.push(`vade: ${req.dueDateFilter}`);
    }
    ctx.navigate('/cek-senet');
    requestCheckNotesFilters(req);
    return {
      success: true,
      applied,
      message: 'Çek/Senet sayfasında filtreler uygulandı.',
      note: 'Veri lazımsa listCheckNotes kullan; bu eylem yalnızca sayfa filtrelerini doldurur.',
    };
  },
};

const listCheckNotes: AgentActionExecutor = {
  describe: (args) => {
    const parts: string[] = [];
    if (str(args.type)) parts.push(str(args.type) === 'CHECK' ? 'çek' : 'senet');
    if (str(args.direction)) parts.push(str(args.direction) === 'RECEIVED' ? 'alınan' : 'verilen');
    if (str(args.status)) parts.push(str(args.status));
    if (str(args.search)) parts.push(`ara: "${str(args.search)}"`);
    return `Çek/senetleri listele${parts.length ? ` (${parts.join(', ')})` : ''}`;
  },
  execute: async (args) => {
    const page = Math.max(1, Number(args.page) || 1);
    const pageSize = clampLimit(args.pageSize, 20, 100);
    const params = new URLSearchParams();
    if (str(args.type) && str(args.type).toUpperCase() !== 'ALL') params.set('type', str(args.type).toUpperCase());
    if (str(args.direction) && str(args.direction).toUpperCase() !== 'ALL') params.set('direction', str(args.direction).toUpperCase());
    if (str(args.status) && str(args.status).toUpperCase() !== 'ALL') params.set('status', str(args.status).toUpperCase());
    if (str(args.search)) params.set('searchTerm', str(args.search));
    if (str(args.dueDateStart)) params.set('dueDateStart', str(args.dueDateStart));
    if (str(args.dueDateEnd)) params.set('dueDateEnd', str(args.dueDateEnd));
    if (args.accountId !== undefined) {
      const aid = toIntId(args.accountId);
      if (aid) params.set('accountId', String(aid));
    } else if (str(args.accountName)) {
      const acc = await findAccountByName(str(args.accountName));
      if (!acc) return { success: false, error: `Cari bulunamadı: "${str(args.accountName)}" — searchAccount kullan.` };
      params.set('accountId', String(acc.id ?? acc.Id));
    }
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    params.set('sortBy', 'dueDate');
    params.set('sortDirection', 'desc');

    const resp = await accountingApi.get(`${CHECK_NOTE_BASE}/by-company?${params.toString()}`);
    const data = unwrap(resp);
    const items: any[] = data?.items ?? data?.Items ?? [];
    return {
      success: true,
      totalCount: Number(data?.totalCount ?? data?.TotalCount ?? items.length) || items.length,
      page,
      summary: mapCheckNoteSummary(data?.summary ?? data?.Summary),
      items: items.map(mapCheckNote),
    };
  },
};

const getCheckNoteDetails: AgentActionExecutor = {
  describe: (args) => `Çek/senet detayı (#${args.checkNoteId})`,
  execute: async (args) => {
    const id = toIntId(args.checkNoteId);
    if (!id) return { success: false, error: 'checkNoteId zorunlu (listCheckNotes).' };
    const resp = await accountingApi.get(`${CHECK_NOTE_BASE}/${id}`);
    const n = unwrap(resp);
    if (!n) return { success: false, error: `Çek/senet bulunamadı (id: ${id}).` };
    return { success: true, checkNote: mapCheckNote(n) };
  },
};

const getCheckNoteDashboard: AgentActionExecutor = {
  describe: () => 'Çek/senet dashboard özetini getir',
  execute: async () => {
    const resp = await accountingApi.get(`${CHECK_NOTE_BASE}/dashboard`);
    const d = unwrap(resp);
    return {
      success: true,
      summary: mapCheckNoteSummary(d?.summary ?? d?.Summary ?? d),
      upcomingDueDates: (d?.upcomingDueDates ?? d?.UpcomingDueDates ?? []).slice(0, 14).map((u: any) => ({
        date: u.date ?? u.Date ?? '',
        checkCount: Number(u.checkCount ?? u.CheckCount ?? 0) || 0,
        checkTotal: Number(u.checkTotal ?? u.CheckTotal ?? 0) || 0,
        noteCount: Number(u.noteCount ?? u.NoteCount ?? 0) || 0,
        noteTotal: Number(u.noteTotal ?? u.NoteTotal ?? 0) || 0,
      })),
      recent: ((d?.recentTransactions ?? d?.RecentTransactions ?? []) as any[]).slice(0, 10).map(mapCheckNote),
    };
  },
};

const listCheckNoteTransactions: AgentActionExecutor = {
  describe: (args) => `Çek/senet işlem geçmişi (#${args.checkNoteId})`,
  execute: async (args) => {
    const id = toIntId(args.checkNoteId);
    if (!id) return { success: false, error: 'checkNoteId zorunlu (listCheckNotes).' };
    const resp = await accountingApi.get(`${CHECK_NOTE_BASE}/${id}/transactions`);
    const rows = listOf(resp);
    return {
      success: true,
      transactions: rows.map((t: any) => ({
        id: t.id ?? t.Id,
        transactionDate: t.transactionDate ?? t.TransactionDate ?? '',
        transactionType: t.transactionType ?? t.TransactionType ?? '',
        fromStatus: t.fromStatus ?? t.FromStatus ?? null,
        toStatus: t.toStatus ?? t.ToStatus ?? null,
        amount: Number(t.amount ?? t.Amount ?? 0) || 0,
        relatedAccountName: t.relatedAccountName ?? t.RelatedAccountName ?? '',
        bankAccountName: t.bankAccountName ?? t.BankAccountName ?? '',
        description: t.description ?? t.Description ?? '',
      })),
    };
  },
};

const listCheckNoteRelatedTransactions: AgentActionExecutor = {
  describe: (args) => `Çek/senete bağlı hareketler (#${args.checkNoteId})`,
  execute: async (args) => {
    const id = toIntId(args.checkNoteId);
    if (!id) return { success: false, error: 'checkNoteId zorunlu (listCheckNotes).' };
    const resp = await accountingApi.get(`${CHECK_NOTE_BASE}/${id}/related-transactions`);
    const d = unwrap(resp);
    return {
      success: true,
      totalAccountTransactions: Number(d?.totalAccountTransactions ?? 0) || 0,
      totalBankTransactions: Number(d?.totalBankTransactions ?? 0) || 0,
      totalCashboxTransactions: Number(d?.totalCashboxTransactions ?? 0) || 0,
      accountTransactions: (d?.accountTransactions ?? []).slice(0, 20),
      bankTransactions: (d?.bankTransactions ?? []).slice(0, 20),
      cashboxTransactions: (d?.cashboxTransactions ?? []).slice(0, 20),
    };
  },
};

const checkDuplicateCheckNumber: AgentActionExecutor = {
  describe: (args) => `Çek no mükerrer mi: ${str(args.checkNumber)}`,
  execute: async (args) => {
    const checkNumber = str(args.checkNumber);
    if (!checkNumber) return { success: false, error: 'checkNumber zorunlu.' };
    const params = new URLSearchParams({ checkNumber });
    if (str(args.type)) params.set('type', str(args.type).toUpperCase());
    if (args.excludeId !== undefined) {
      const ex = toIntId(args.excludeId);
      if (ex) params.set('excludeId', String(ex));
    }
    const resp = await accountingApi.get(`${CHECK_NOTE_BASE}/check-duplicate?${params.toString()}`);
    const d = unwrap(resp) ?? {};
    return {
      success: true,
      isDuplicate: (d.isDuplicate ?? d.IsDuplicate) === true,
      firstAvailableNumber: d.firstAvailableNumber ?? d.FirstAvailableNumber ?? null,
    };
  },
};

const createCheckNote: AgentActionExecutor = {
  describe: (args) => {
    const kind = str(args.type).toUpperCase() === 'PROMISSORY_NOTE' ? 'Senet' : 'Çek';
    const dir = str(args.direction).toUpperCase() === 'GIVEN' ? 'verilen' : 'alınan';
    return `${dir} ${kind.toLowerCase()} oluştur (${fmtAmount(args.amount, args.currency || 'TRY')})`;
  },
  execute: async (args, ctx) => {
    const type = str(args.type).toUpperCase();
    const direction = str(args.direction).toUpperCase();
    if (type !== 'CHECK' && type !== 'PROMISSORY_NOTE') {
      return { success: false, error: 'type CHECK veya PROMISSORY_NOTE olmalı.' };
    }
    if (direction !== 'RECEIVED' && direction !== 'GIVEN') {
      return { success: false, error: 'direction RECEIVED veya GIVEN olmalı.' };
    }
    const amount = Number(args.amount);
    if (!Number.isFinite(amount) || amount <= 0) return { success: false, error: 'amount > 0 olmalı.' };
    const dueDate = str(args.dueDate);
    const issueDate = str(args.issueDate) || new Date().toISOString().slice(0, 10);
    if (!dueDate) return { success: false, error: 'dueDate zorunlu (YYYY-MM-DD).' };

    const isGivenCheck = direction === 'GIVEN' && type === 'CHECK';
    let accountId: number | undefined;
    let accountName: string | undefined;
    if (!isGivenCheck || str(args.accountName)) {
      if (!str(args.accountName) && !isGivenCheck) {
        return { success: false, error: 'Alınan çek/senet için accountName zorunlu (searchAccount).' };
      }
      if (str(args.accountName)) {
        const acc = await findAccountByName(str(args.accountName));
        if (!acc) return { success: false, error: `Cari bulunamadı: "${str(args.accountName)}".` };
        accountId = Number(acc.id ?? acc.Id);
        accountName = accountNameOf(acc);
      }
    }

    let bankAccountId: number | undefined;
    let subAccountId: number | undefined;
    let checkBookId: number | undefined;
    if (isGivenCheck) {
      if (!str(args.bankAccountName) && !toIntId(args.bankAccountId)) {
        return { success: false, error: 'Verilen çek için bankAccountName (veya bankAccountId) zorunlu.' };
      }
      if (toIntId(args.bankAccountId)) {
        bankAccountId = toIntId(args.bankAccountId)!;
      } else {
        const bank = await findBankAccountByName(str(args.bankAccountName));
        if (!bank) return { success: false, error: `Banka hesabı bulunamadı: "${str(args.bankAccountName)}".` };
        bankAccountId = Number(bank.id ?? bank.Id);
      }
      if (toIntId(args.checkBookId)) {
        checkBookId = toIntId(args.checkBookId)!;
      } else if (str(args.checkBookSerial)) {
        const books = listOf(await accountingApi.get(ACCOUNTING_API.CHECK_BOOK_BY_COMPANY));
        const q = str(args.checkBookSerial).toLocaleLowerCase('tr');
        const book = books.find((b: any) =>
          str(b.serialNumber ?? b.SerialNumber).toLocaleLowerCase('tr').includes(q)
        );
        if (!book) return { success: false, error: `Çek defteri bulunamadı: "${str(args.checkBookSerial)}" — listCheckBooks kullan.` };
        checkBookId = Number(book.id ?? book.Id);
      } else {
        return { success: false, error: 'Verilen çek için checkBookId veya checkBookSerial zorunlu (listCheckBooks).' };
      }
    }

    const dto: Record<string, any> = {
      type,
      direction,
      accountId: accountId || undefined,
      accountName: accountName || undefined,
      bankName: str(args.bankName) || undefined,
      bankBranchName: str(args.bankBranchName) || undefined,
      bankBranchCode: str(args.bankBranchCode) || undefined,
      checkNumber: str(args.checkNumber) || undefined,
      checkAccountNumber: str(args.checkAccountNumber) || undefined,
      bankAccountId,
      subAccountId,
      checkBookId,
      drawerName: str(args.drawerName) || undefined,
      guarantorName: str(args.guarantorName) || undefined,
      guarantorIdentityNo: str(args.guarantorIdentityNo) || undefined,
      debtorIdentityNo: str(args.debtorIdentityNo) || undefined,
      debtorAddress: str(args.debtorAddress) || undefined,
      jurisdictionPlace: str(args.jurisdictionPlace) || undefined,
      issueDate,
      issuePlace: str(args.issuePlace) || undefined,
      dueDate,
      amount,
      currency: str(args.currency) || 'TRY',
      exchangeRate: Number(args.exchangeRate) || 1,
      notes: str(args.notes) || undefined,
      createdBy: actorEmail(ctx),
    };

    const resp = await accountingApi.post(`${CHECK_NOTE_BASE}/create`, dto);
    const created = unwrap(resp);
    if (!created) return { success: false, error: resp?.data?.message || 'Çek/senet oluşturulamadı.' };
    return { success: true, checkNote: mapCheckNote(created) };
  },
};

const updateCheckNote: AgentActionExecutor = {
  describe: (args) => `Çek/senet güncelle (#${args.checkNoteId})`,
  execute: async (args, ctx) => {
    const id = toIntId(args.checkNoteId);
    if (!id) return { success: false, error: 'checkNoteId zorunlu.' };
    const curResp = await accountingApi.get(`${CHECK_NOTE_BASE}/${id}`);
    const cur = unwrap(curResp);
    if (!cur) return { success: false, error: `Çek/senet bulunamadı (id: ${id}).` };

    let accountId = Number(cur.accountId ?? cur.AccountId) || undefined;
    let accountName = str(cur.accountName ?? cur.AccountName) || undefined;
    if (str(args.accountName)) {
      const acc = await findAccountByName(str(args.accountName));
      if (!acc) return { success: false, error: `Cari bulunamadı: "${str(args.accountName)}".` };
      accountId = Number(acc.id ?? acc.Id);
      accountName = accountNameOf(acc);
    }

    const dto: Record<string, any> = {
      id,
      type: cur.type ?? cur.Type,
      direction: cur.direction ?? cur.Direction,
      accountId,
      accountName,
      bankName: args.bankName !== undefined ? str(args.bankName) : (cur.bankName ?? cur.BankName),
      bankBranchName: args.bankBranchName !== undefined ? str(args.bankBranchName) : (cur.bankBranchName ?? cur.BankBranchName),
      checkNumber: args.checkNumber !== undefined ? str(args.checkNumber) : (cur.checkNumber ?? cur.CheckNumber),
      checkAccountNumber: args.checkAccountNumber !== undefined ? str(args.checkAccountNumber) : (cur.checkAccountNumber ?? cur.CheckAccountNumber),
      drawerName: args.drawerName !== undefined ? str(args.drawerName) : (cur.drawerName ?? cur.DrawerName),
      guarantorName: args.guarantorName !== undefined ? str(args.guarantorName) : (cur.guarantorName ?? cur.GuarantorName),
      issueDate: str(args.issueDate) || (cur.issueDate ?? cur.IssueDate),
      dueDate: str(args.dueDate) || (cur.dueDate ?? cur.DueDate),
      amount: args.amount !== undefined ? Number(args.amount) : Number(cur.amount ?? cur.Amount),
      currency: str(args.currency) || (cur.currency ?? cur.Currency) || 'TRY',
      exchangeRate: args.exchangeRate !== undefined ? Number(args.exchangeRate) : Number(cur.exchangeRate ?? cur.ExchangeRate ?? 1),
      notes: args.notes !== undefined ? str(args.notes) : (cur.notes ?? cur.Notes),
      updatedBy: actorEmail(ctx),
    };

    const resp = await accountingApi.post(`${CHECK_NOTE_BASE}/update`, dto);
    const updated = unwrap(resp);
    if (!updated) return { success: false, error: resp?.data?.message || 'Güncellenemedi.' };
    return { success: true, checkNote: mapCheckNote(updated) };
  },
};

const createBatchPromissoryNotes: AgentActionExecutor = {
  describe: (args) =>
    `Seri senet oluştur: ${Number(args.count) || '?'} adet × ${fmtAmount(args.amountPerNote || args.totalAmount, args.currency || 'TRY')}`,
  execute: async (args, ctx) => {
    const direction = str(args.direction).toUpperCase() || 'RECEIVED';
    if (direction !== 'RECEIVED' && direction !== 'GIVEN') {
      return { success: false, error: 'direction RECEIVED veya GIVEN olmalı.' };
    }
    if (!str(args.accountName)) return { success: false, error: 'accountName zorunlu.' };
    const acc = await findAccountByName(str(args.accountName));
    if (!acc) return { success: false, error: `Cari bulunamadı: "${str(args.accountName)}".` };
    const count = Math.min(60, Math.max(1, Number(args.count) || 0));
    if (!count) return { success: false, error: 'count 1–60 arası olmalı.' };
    const firstDueDate = str(args.firstDueDate);
    if (!firstDueDate) return { success: false, error: 'firstDueDate zorunlu (YYYY-MM-DD).' };
    const amountPerNote = Number(args.amountPerNote);
    const totalAmount = Number(args.totalAmount);
    if ((!Number.isFinite(amountPerNote) || amountPerNote <= 0) && (!Number.isFinite(totalAmount) || totalAmount <= 0)) {
      return { success: false, error: 'amountPerNote veya totalAmount > 0 olmalı.' };
    }

    const dto: Record<string, any> = {
      direction,
      accountId: Number(acc.id ?? acc.Id),
      accountName: accountNameOf(acc),
      drawerName: str(args.drawerName) || undefined,
      count,
      amountPerNote: Number.isFinite(amountPerNote) && amountPerNote > 0 ? amountPerNote : undefined,
      totalAmount: Number.isFinite(totalAmount) && totalAmount > 0 ? totalAmount : undefined,
      currency: str(args.currency) || 'TRY',
      exchangeRate: Number(args.exchangeRate) || 1,
      firstDueDate,
      dueDateIntervalDays: Math.max(1, Number(args.dueDateIntervalDays) || 30),
      serialPrefix: str(args.serialPrefix) || undefined,
      serialStartNumber: Number(args.serialStartNumber) || 1,
      description: str(args.description) || undefined,
      createdBy: actorEmail(ctx),
    };

    const resp = await accountingApi.post(`${CHECK_NOTE_BASE}/batch`, dto);
    const data = unwrap(resp);
    if (!data) return { success: false, error: resp?.data?.message || 'Seri senet oluşturulamadı.' };
    return {
      success: true,
      createdCount: Number(data.createdCount ?? data.CreatedCount ?? 0) || 0,
      totalAmount: Number(data.totalAmount ?? data.TotalAmount ?? 0) || 0,
      firstDueDate: data.firstDueDate ?? data.FirstDueDate,
      lastDueDate: data.lastDueDate ?? data.LastDueDate,
      notes: ((data.notes ?? data.Notes ?? []) as any[]).slice(0, 10).map(mapCheckNote),
    };
  },
};

const changeCheckNoteStatus: AgentActionExecutor = {
  describe: (args) => `Çek/senet durumu → ${str(args.newStatus)} (#${args.checkNoteId})`,
  execute: async (args, ctx) => {
    const id = toIntId(args.checkNoteId);
    if (!id) return { success: false, error: 'checkNoteId zorunlu (listCheckNotes).' };
    const newStatus = str(args.newStatus).toUpperCase();
    if (!CHECK_NOTE_STATUSES.has(newStatus)) {
      return { success: false, error: `Geçersiz newStatus: ${newStatus}. Geçerli: ${[...CHECK_NOTE_STATUSES].join(', ')}` };
    }

    const curResp = await accountingApi.get(`${CHECK_NOTE_BASE}/${id}`);
    const cur = unwrap(curResp);
    if (!cur) return { success: false, error: `Çek/senet bulunamadı (id: ${id}).` };
    const noteType = str(cur.type ?? cur.Type).toUpperCase();

    // Senetlerde çek-özel durumlar yok
    if (noteType === 'PROMISSORY_NOTE' && ['BOUNCED', 'PARTIAL_COLLECTED', 'IN_COLLECTION'].includes(newStatus)) {
      return { success: false, error: `${newStatus} yalnızca çeklerde geçerlidir (senet değil).` };
    }

    let bankAccountId: number | undefined;
    let bankSubAccountId: number | undefined;
    let cashboxId: number | undefined;
    let endorsedToAccountId: number | undefined;
    let endorsedToAccountName: string | undefined;

    const collectionMethod = str(args.collectionMethod).toUpperCase() || 'CASHBOX';
    const isCollection = newStatus === 'COLLECTED' || newStatus === 'PARTIAL_COLLECTED';
    const needsBank =
      newStatus === 'COLLATERAL' ||
      newStatus === 'IN_COLLECTION' ||
      newStatus === 'GIVEN_PAID' ||
      (isCollection && collectionMethod === 'BANK');

    if (newStatus === 'ENDORSED') {
      if (!str(args.endorsedToAccountName)) {
        return { success: false, error: 'Ciro için endorsedToAccountName zorunlu.' };
      }
      const acc = await findAccountByName(str(args.endorsedToAccountName));
      if (!acc) return { success: false, error: `Ciro edilecek cari bulunamadı: "${str(args.endorsedToAccountName)}".` };
      endorsedToAccountId = Number(acc.id ?? acc.Id);
      endorsedToAccountName = accountNameOf(acc);
    }

    if (isCollection && collectionMethod === 'CASHBOX') {
      if (!str(args.cashboxName) && !toIntId(args.cashboxId)) {
        return { success: false, error: 'Tahsilat için cashboxName (veya cashboxId) zorunlu — veya collectionMethod=BANK/HAND.' };
      }
      if (toIntId(args.cashboxId)) cashboxId = toIntId(args.cashboxId)!;
      else {
        const box = await findCashboxByName(str(args.cashboxName));
        if (!box) return { success: false, error: `Kasa bulunamadı: "${str(args.cashboxName)}".` };
        cashboxId = Number(box.id ?? box.Id);
      }
    } else if (needsBank) {
      if (!str(args.bankAccountName) && !toIntId(args.bankAccountId)) {
        return { success: false, error: 'Bu durum için bankAccountName (veya bankAccountId) zorunlu.' };
      }
      if (toIntId(args.bankAccountId)) bankAccountId = toIntId(args.bankAccountId)!;
      else {
        const bank = await findBankAccountByName(str(args.bankAccountName));
        if (!bank) return { success: false, error: `Banka hesabı bulunamadı: "${str(args.bankAccountName)}".` };
        bankAccountId = Number(bank.id ?? bank.Id);
      }
    }
    // HAND: ne kasa ne banka → backend hareket oluşturmaz

    let amount: number | undefined;
    if (isCollection) {
      amount = args.amount !== undefined ? Number(args.amount) : Number(cur.remainingAmount ?? cur.RemainingAmount ?? cur.amount ?? 0);
      if (!Number.isFinite(amount) || amount <= 0) {
        return { success: false, error: 'Tahsil tutarı (amount) > 0 olmalı.' };
      }
    }

    const requestBody = {
      Id: id,
      NewStatus: newStatus,
      BankAccountId: bankAccountId,
      BankSubAccountId: bankSubAccountId,
      CashboxId: cashboxId,
      EndorsedToAccountId: endorsedToAccountId,
      EndorsedToAccountName: endorsedToAccountName,
      TransactionDate: str(args.transactionDate) || localNowIso().slice(0, 10),
      Amount: amount,
      Description: str(args.description) || undefined,
      BouncedReason: str(args.bouncedReason) || undefined,
      CreatedBy: actorEmail(ctx),
    };

    const resp = await accountingApi.post(`${CHECK_NOTE_BASE}/change-status`, requestBody);
    const updated = unwrap(resp);
    if (!updated) return { success: false, error: resp?.data?.message || 'Durum değiştirilemedi.' };
    return {
      success: true,
      message: `Durum güncellendi: ${cur.status ?? cur.Status} → ${newStatus}`,
      checkNote: mapCheckNote(updated),
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// İRSALİYE (Waybill) MODÜLÜ
//
// Oluşturma/düzenleme arka planda değil: SalesInvoice mode='waybill' formu
// ekranda doldurulur (fatura form köprüsü, formType=waybill). Silme yok.
// ═══════════════════════════════════════════════════════════════════════════

const mapWaybillRow = (w: any) => ({
  id: w.id ?? w.Id ?? '',
  waybillNumber: w.waybillNumber ?? w.WaybillNumber ?? '',
  accountId: w.accountId ?? w.AccountId ?? '',
  accountName: w.accountName ?? w.AccountName ?? '',
  waybillDate: (w.waybillDate ?? w.WaybillDate ?? '').toString().split('T')[0],
  grandTotal: Number(w.grandTotal ?? w.GrandTotal ?? 0) || 0,
  currency: w.currency ?? w.Currency ?? 'TRY',
  showPrices: (w.showPrices ?? w.ShowPrices ?? true) !== false,
  itemCount: Number(w.itemCount ?? w.ItemCount ?? (w.items ?? w.Items)?.length ?? 0) || 0,
  status: w.status ?? w.Status ?? 'active',
  linkedInvoiceId: w.linkedInvoiceId ?? w.LinkedInvoiceId ?? null,
  linkedInvoiceNumber: w.linkedInvoiceNumber ?? w.LinkedInvoiceNumber ?? null,
  sourceOrderId: w.sourceOrderId ?? w.SourceOrderId ?? null,
  sourceOrderNumber: w.sourceOrderNumber ?? w.SourceOrderNumber ?? null,
});

const applyWaybillFilters: AgentActionExecutor = {
  describe: (args) =>
    `İrsaliye sayfasında ara${str(args.searchText) ? `: "${str(args.searchText)}"` : ''}`,
  execute: async (args, ctx) => {
    const req: WaybillFilterRequest = {};
    if (args.clearAll === true) req.clearAll = true;
    if (args.searchText !== undefined && args.searchText !== null) req.searchText = str(args.searchText);
    ctx.navigate('/irsaliyeler');
    requestWaybillFilters(req);
    return {
      success: true,
      message: 'İrsaliye sayfasında arama filtresi uygulandı.',
      note: 'Veri lazımsa listWaybills kullan.',
    };
  },
};

const listWaybills: AgentActionExecutor = {
  describe: (args) =>
    `İrsaliyeleri listele${str(args.search) ? ` (ara: "${str(args.search)}")` : ''}`,
  execute: async (args) => {
    const page = Math.max(1, Number(args.page) || 1);
    const pageSize = clampLimit(args.pageSize, 20, 100);
    const resp = await accountingApi.get(
      ACCOUNTING_API.WAYBILL_BY_COMPANY_PAGED({
        page,
        pageSize,
        searchTerm: str(args.search) || undefined,
        sortColumn: 'waybillDate',
        sortDirection: 'desc',
      })
    );
    const body = resp.data || {};
    const rows: any[] = body.data ?? body.Data ?? [];
    return {
      success: true,
      totalCount: Number(body.totalCount ?? body.TotalCount ?? rows.length) || rows.length,
      page,
      waybills: (Array.isArray(rows) ? rows : []).map(mapWaybillRow),
    };
  },
};

const getWaybillDetails: AgentActionExecutor = {
  describe: (args) => `İrsaliye detayı (${str(args.waybillId).slice(0, 8)}…)`,
  execute: async (args) => {
    const id = str(args.waybillId);
    if (!id) return { success: false, error: 'waybillId zorunlu (listWaybills).' };
    const resp = await accountingApi.get(ACCOUNTING_API.WAYBILL_BY_ID(id));
    const w = unwrap(resp);
    if (!w) return { success: false, error: 'İrsaliye bulunamadı.' };
    const items: any[] = w.items ?? w.Items ?? [];
    return {
      success: true,
      waybill: {
        ...mapWaybillRow(w),
        notes: w.notes ?? w.Notes ?? '',
        exchangeRate: Number(w.exchangeRate ?? w.ExchangeRate ?? 1) || 1,
        accountTaxNumber: w.accountTaxNumber ?? w.AccountTaxNumber ?? '',
        items: items.map((i: any) => ({
          itemName: i.itemName ?? i.ItemName ?? '',
          quantity: Number(i.quantity ?? i.Quantity) || 0,
          unit: i.unit ?? i.Unit ?? '',
          unitPrice: Number(i.unitPrice ?? i.UnitPrice) || 0,
          vatRate: Number(i.vatRate ?? i.VatRate) || 0,
          netTotal: Number(i.netTotal ?? i.NetTotal) || 0,
          grossTotal: Number(i.grossTotal ?? i.GrossTotal) || 0,
          stockId: i.stockId ?? i.StockId ?? null,
        })),
      },
    };
  },
};

const listWaybillsByAccount: AgentActionExecutor = {
  describe: (args) => `Cari irsaliyeleri: "${str(args.accountName)}"`,
  execute: async (args) => {
    let accountId = str(args.accountId);
    if (!accountId && str(args.accountName)) {
      const acc = await findAccountByName(str(args.accountName));
      if (!acc) return { success: false, error: `Cari bulunamadı: "${str(args.accountName)}".` };
      accountId = String(acc.id ?? acc.Id);
    }
    if (!accountId) return { success: false, error: 'accountName veya accountId zorunlu.' };
    const resp = await accountingApi.get(ACCOUNTING_API.WAYBILL_BY_ACCOUNT(accountId));
    const rows = listOf(resp);
    return { success: true, count: rows.length, waybills: rows.map(mapWaybillRow) };
  },
};

const getWaybillSummary: AgentActionExecutor = {
  describe: () => 'İrsaliye özet istatistiklerini getir',
  execute: async () => {
    const resp = await accountingApi.get(ACCOUNTING_API.WAYBILL_SUMMARY);
    return { success: true, summary: unwrap(resp) };
  },
};

const openWaybillForm: AgentActionExecutor = {
  describe: (args) =>
    str(args.waybillId)
      ? `İrsaliye formunu düzenle (${str(args.waybillId).slice(0, 8)}…)`
      : 'Yeni irsaliye formunu aç',
  execute: async (args, ctx) => {
    const id = str(args.waybillId);
    ctx.navigate(id ? `/irsaliye/duzenle/${id}` : '/irsaliye/yeni');
    await waitForInvoiceFormApi('waybill', 15000);
    return {
      success: true,
      message: id ? 'İrsaliye düzenleme formu açıldı.' : 'Yeni irsaliye formu ekranda açıldı.',
      nextSteps:
        'Sırasıyla: setWaybillFormAccount ile cari seç, addWaybillFormItem ile kalem ekle, ' +
        'gerekirse setWaybillFormFields ile tarih/showPrices ayarla. Kaydetme kullanıcı isterse saveWaybillForm.',
    };
  },
};

const setWaybillFormAccount: AgentActionExecutor = {
  describe: (args) => `İrsaliye formunda cari seç: "${str(args.accountName)}"`,
  execute: async (args) => {
    const api = await waitForInvoiceFormApi('waybill');
    return api.setAccountByName(str(args.accountName));
  },
};

const setWaybillFormFields: AgentActionExecutor = {
  describe: (args) => {
    const keys = Object.keys(args || {}).filter((k) => args[k] !== undefined && args[k] !== null);
    return `İrsaliye formu alanlarını ayarla (${keys.join(', ') || '—'})`;
  },
  execute: async (args) => {
    const api = await waitForInvoiceFormApi('waybill');
    const fields: InvoiceFormFields = {};
    if (args.waybillDate !== undefined || args.invoiceDate !== undefined) {
      fields.invoiceDate = str(args.waybillDate ?? args.invoiceDate);
    }
    if (args.waybillNumber !== undefined || args.invoiceNumber !== undefined) {
      fields.invoiceNumber = str(args.waybillNumber ?? args.invoiceNumber);
    }
    if (args.notes !== undefined) fields.notes = str(args.notes);
    if (args.currency !== undefined) fields.currency = str(args.currency);
    if (args.exchangeRate !== undefined) fields.exchangeRate = Number(args.exchangeRate);
    if (args.showPrices !== undefined) fields.showPrices = args.showPrices === true;
    return api.setFields(fields);
  },
};

const addWaybillFormItem: AgentActionExecutor = {
  describe: (args) =>
    `İrsaliye formuna kalem ekle: ${str(args.itemName)}${Number(args.quantity) > 0 ? ` x${Number(args.quantity)}` : ''}`,
  execute: async (args) => {
    const api = await waitForInvoiceFormApi('waybill');
    const input: InvoiceFormItemInput = {
      ...toItemInput(args),
      itemName: str(args.itemName),
      isManual: args.isManual === true,
    };
    if (str(args.warehouseName)) {
      const rows = (await fetchWarehouses()).filter((w: any) => w.isActive !== false);
      const match = matchByName(rows as any[], str(args.warehouseName));
      if (!match) {
        return { success: false, error: `Depo bulunamadı: "${str(args.warehouseName)}".` };
      }
      input.warehouseId = (match as any).id;
    }
    return api.addItem(input);
  },
};

const updateWaybillFormItem: AgentActionExecutor = {
  describe: (args) =>
    `İrsaliye kalemini güncelle: ${args.line !== undefined ? `satır ${Number(args.line)}` : `"${str(args.itemName)}"`}`,
  execute: async (args) => {
    const api = await waitForInvoiceFormApi('waybill');
    const changes = toItemInput(args);
    if (str(args.newItemName)) changes.itemName = str(args.newItemName);
    return api.updateItem(
      { line: args.line !== undefined ? Number(args.line) : undefined, itemName: str(args.itemName) || undefined },
      changes
    );
  },
};

const removeWaybillFormItem: AgentActionExecutor = {
  describe: (args) =>
    `İrsaliye kalemini çıkar: ${args.line !== undefined ? `satır ${Number(args.line)}` : `"${str(args.itemName)}"`}`,
  execute: async (args) => {
    const api = await waitForInvoiceFormApi('waybill');
    return api.removeItem({
      line: args.line !== undefined ? Number(args.line) : undefined,
      itemName: str(args.itemName) || undefined,
    });
  },
};

const getWaybillFormSummary: AgentActionExecutor = {
  describe: () => 'Ekrandaki irsaliye formunun özetini getir',
  execute: async () => {
    const api = await waitForInvoiceFormApi('waybill');
    return api.getSummary();
  },
};

const saveWaybillForm: AgentActionExecutor = {
  describe: () => 'Ekrandaki irsaliye formunu kaydet',
  execute: async () => {
    const api = await waitForInvoiceFormApi('waybill');
    return api.save();
  },
};

const convertWaybillToInvoice: AgentActionExecutor = {
  describe: (args) => `İrsaliyeyi satış faturasına dönüştür (${str(args.waybillId).slice(0, 8)}…)`,
  execute: async (args, ctx) => {
    const id = str(args.waybillId);
    if (!id) return { success: false, error: 'waybillId zorunlu (listWaybills).' };

    const resp = await accountingApi.get(ACCOUNTING_API.WAYBILL_BY_ID(id));
    const waybillData = unwrap(resp);
    if (!waybillData) return { success: false, error: 'İrsaliye bulunamadı.' };

    const status = str(waybillData.status ?? waybillData.Status).toLowerCase();
    const linkedId = waybillData.linkedInvoiceId ?? waybillData.LinkedInvoiceId;
    if (status === 'invoiced' || linkedId) {
      return {
        success: false,
        error: `Bu irsaliye zaten faturaya dönüştürülmüş${waybillData.linkedInvoiceNumber ?? waybillData.LinkedInvoiceNumber ? ` (fatura: ${waybillData.linkedInvoiceNumber ?? waybillData.LinkedInvoiceNumber})` : ''}.`,
      };
    }

    const waybillDataForInvoice = {
      waybillId: waybillData.id ?? waybillData.Id,
      waybillNumber: waybillData.waybillNumber ?? waybillData.WaybillNumber,
      waybillDate: (waybillData.waybillDate ?? waybillData.WaybillDate ?? '').toString().split('T')[0],
      accountId: (waybillData.accountId ?? waybillData.AccountId)?.toString() || '',
      account: {
        companyName: waybillData.accountName ?? waybillData.AccountName ?? '',
        taxNumber: waybillData.accountTaxNumber ?? waybillData.AccountTaxNumber ?? '',
        taxOffice: waybillData.accountTaxOffice ?? waybillData.AccountTaxOffice ?? '',
        address: waybillData.accountAddress ?? waybillData.AccountAddress ?? '',
        phone: waybillData.accountPhone ?? waybillData.AccountPhone ?? '',
        email: waybillData.accountEmail ?? waybillData.AccountEmail ?? '',
      },
      items: ((waybillData.items ?? waybillData.Items) || []).map((item: any) => ({
        id: item.id ?? item.Id,
        itemId: item.stockId ?? item.StockId ?? item.id,
        stockId: item.stockId ?? item.StockId ?? item.id,
        name: item.itemName ?? item.ItemName,
        itemName: item.itemName ?? item.ItemName,
        quantity: item.quantity ?? item.Quantity,
        unit: item.unit ?? item.Unit,
        unitPrice: item.unitPrice ?? item.UnitPrice,
        priceType: 'VAT_EXCLUDED',
        enteredPrice: item.unitPrice ?? item.UnitPrice,
        vatRate: item.vatRate ?? item.VatRate ?? 0,
        sctRate: 0,
        discountRate: 0,
        netTotal: item.netTotal ?? item.NetTotal ?? 0,
        vatAmount: (item.grossTotal ?? item.GrossTotal ?? 0) - (item.netTotal ?? item.NetTotal ?? 0),
        grossTotal: item.grossTotal ?? item.GrossTotal ?? 0,
        warehouseId: '',
      })),
      notes: waybillData.notes ?? waybillData.Notes
        ? `İrsaliye No: ${waybillData.waybillNumber ?? waybillData.WaybillNumber} - ${waybillData.notes ?? waybillData.Notes}`
        : `İrsaliye No: ${waybillData.waybillNumber ?? waybillData.WaybillNumber}`,
      currency: waybillData.currency ?? waybillData.Currency ?? 'TRY',
    };

    localStorage.setItem('waybillToInvoice', JSON.stringify(waybillDataForInvoice));
    ctx.navigate('/fatura/satis/yeni?fromWaybill=true');
    // Satış formu açılınca köprü hazır olsun
    try {
      await waitForInvoiceFormApi('sales', 15000);
    } catch {
      // form yüklenmesi gecikebilir; yine de yönlendirme başarılı
    }
    return {
      success: true,
      message: 'Satış faturası formu irsaliye içeriğiyle açıldı — kullanıcı kontrol edip kaydedebilir.',
      note: 'Form açıldıktan sonra fatura form eylemleri (getInvoiceFormSummary, updateInvoiceFormItem…) kullanılabilir. Kaydetme saveInvoiceForm veya ekrandaki Kaydet ile.',
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// SİPARİŞ (Order) MODÜLÜ
//
// Oluşturma/düzenleme arka planda değil: SalesInvoice/PurchaseInvoice
// mode='order' formu ekranda doldurulur. Silme yok.
// ═══════════════════════════════════════════════════════════════════════════

const orderFormTypeOf = (kind: string): 'orderSales' | 'orderPurchase' =>
  String(kind ?? '').trim().toLowerCase() === 'purchase' ? 'orderPurchase' : 'orderSales';

const mapOrderRow = (o: any) => {
  const orderType = str(o.orderType ?? o.OrderType).toUpperCase();
  const type =
    orderType.includes('PURCHASE') || str(o.type).toLowerCase() === 'purchase'
      ? 'purchase'
      : 'sales';
  return {
    id: o.id ?? o.Id ?? '',
    orderNumber: o.orderNumber ?? o.OrderNumber ?? '',
    orderType: orderType || (type === 'purchase' ? 'PURCHASE_ORDER' : 'SALES_ORDER'),
    type,
    accountName: o.accountName ?? o.AccountName ?? '',
    orderDate: (o.orderDate ?? o.OrderDate ?? '').toString().split('T')[0],
    deliveryDate: o.deliveryDate ?? o.DeliveryDate
      ? (o.deliveryDate ?? o.DeliveryDate).toString().split('T')[0]
      : null,
    grandTotal: Number(o.grandTotal ?? o.GrandTotal ?? 0) || 0,
    currency: o.currency ?? o.Currency ?? 'TRY',
    status: o.status ?? o.Status ?? '',
    processStock: (o.processStock ?? o.ProcessStock) === true,
    isDeliveryOverdue: (o.isDeliveryOverdue ?? o.IsDeliveryOverdue) === true,
    convertedInvoiceId: o.convertedInvoiceId ?? o.ConvertedInvoiceId ?? o.linkedInvoiceId ?? null,
    linkedInvoiceNumber: o.linkedInvoiceNumber ?? o.LinkedInvoiceNumber ?? o.convertedInvoiceNumber ?? null,
    linkedWaybillId: o.linkedWaybillId ?? o.LinkedWaybillId ?? null,
    linkedWaybillNumber: o.linkedWaybillNumber ?? o.LinkedWaybillNumber ?? null,
    sourceOfferId: o.sourceOfferId ?? o.SourceOfferId ?? null,
    sourceOfferNumber: o.sourceOfferNumber ?? o.SourceOfferNumber ?? null,
  };
};

const buildOrderPayloadForConvert = (orderData: any) => ({
  orderId: orderData.id ?? orderData.Id,
  orderNumber: orderData.orderNumber ?? orderData.OrderNumber,
  accountId: (orderData.accountId ?? orderData.AccountId)?.toString() || '',
  account: {
    companyName: orderData.accountName ?? orderData.AccountName ?? '',
    taxNumber: orderData.accountTaxNumber ?? orderData.AccountTaxNumber ?? '',
    taxOffice: orderData.accountTaxOffice ?? orderData.AccountTaxOffice ?? '',
    address: orderData.accountAddress ?? orderData.AccountAddress ?? '',
    phone: orderData.accountPhone ?? orderData.AccountPhone ?? '',
    email: orderData.accountEmail ?? orderData.AccountEmail ?? '',
  },
  items: ((orderData.items ?? orderData.Items) || []).map((item: any) => ({
    id: item.id ?? item.Id,
    itemId: item.itemId ?? item.ItemId ?? item.id,
    stockId: item.itemId ?? item.ItemId ?? item.id,
    name: item.itemName ?? item.ItemName,
    itemName: item.itemName ?? item.ItemName,
    quantity: item.quantity ?? item.Quantity,
    unit: item.unit ?? item.Unit,
    unitPrice: item.unitPrice ?? item.UnitPrice,
    priceType: (item.priceType ?? item.PriceType) || 'VAT_EXCLUDED',
    enteredPrice: item.enteredPrice ?? item.EnteredPrice ?? item.unitPrice ?? item.UnitPrice,
    vatRate: item.vatRate ?? item.VatRate ?? 0,
    sctRate: item.sctRate ?? item.SctRate ?? 0,
    discountRate: item.discountRate ?? item.DiscountRate ?? 0,
    netTotal: item.netTotal ?? item.NetTotal ?? 0,
    vatTotal: item.vatTotal ?? item.VatTotal ?? item.vatAmount ?? 0,
    vatAmount: item.vatAmount ?? item.VatAmount ?? item.vatTotal ?? 0,
    grossTotal: item.grossTotal ?? item.GrossTotal ?? 0,
    warehouseId: item.warehouseId ?? item.WarehouseId ?? '',
  })),
  notes: orderData.notes ?? orderData.Notes
    ? `Sipariş No: ${orderData.orderNumber ?? orderData.OrderNumber} - ${orderData.notes ?? orderData.Notes}`
    : `Sipariş No: ${orderData.orderNumber ?? orderData.OrderNumber}`,
  currency: orderData.currency ?? orderData.Currency ?? 'TRY',
});

const applyOrderFilters: AgentActionExecutor = {
  describe: (args) => {
    const parts: string[] = [];
    if (str(args.searchText)) parts.push(`ara: "${str(args.searchText)}"`);
    if (str(args.orderType)) parts.push(`tür: ${str(args.orderType)}`);
    if (str(args.status)) parts.push(`durum: ${str(args.status)}`);
    return `Sipariş sayfasında filtrele${parts.length ? ` (${parts.join(', ')})` : ''}`;
  },
  execute: async (args, ctx) => {
    const req: OrderFilterRequest = {};
    if (args.clearAll === true) req.clearAll = true;
    if (args.searchText !== undefined && args.searchText !== null) req.searchText = str(args.searchText);
    if (args.orderType !== undefined) req.orderType = str(args.orderType) || 'all';
    if (args.status !== undefined) req.status = str(args.status) || 'all';
    ctx.navigate('/siparisler');
    requestOrderFilters(req);
    return {
      success: true,
      message: 'Sipariş sayfasında filtreler uygulandı.',
      note: 'Veri lazımsa listOrders kullan.',
    };
  },
};

const listOrders: AgentActionExecutor = {
  describe: (args) => {
    const parts: string[] = [];
    if (str(args.orderType)) parts.push(str(args.orderType) === 'purchase' ? 'alış' : 'satış');
    if (str(args.status)) parts.push(str(args.status));
    if (str(args.search)) parts.push(`ara: "${str(args.search)}"`);
    return `Siparişleri listele${parts.length ? ` (${parts.join(', ')})` : ''}`;
  },
  execute: async (args) => {
    const page = Math.max(1, Number(args.page) || 1);
    const pageSize = clampLimit(args.pageSize, 20, 100);
    let orderType: string | undefined;
    const ot = str(args.orderType).toLowerCase();
    if (ot === 'sales') orderType = 'SALES_ORDER';
    else if (ot === 'purchase') orderType = 'PURCHASE_ORDER';
    else if (ot === 'sales_order' || ot === 'purchase_order') orderType = ot.toUpperCase();

    const resp = await accountingApi.get(
      ACCOUNTING_API.ORDER_BY_COMPANY_PAGED({
        page,
        pageSize,
        searchTerm: str(args.search) || undefined,
        sortColumn: 'createdAt',
        sortDirection: 'desc',
        orderType,
        status: str(args.status) && str(args.status).toLowerCase() !== 'all' ? str(args.status).toLowerCase() : undefined,
      })
    );
    const body = resp.data || {};
    const rows: any[] = body.data ?? body.Data ?? [];
    return {
      success: true,
      totalCount: Number(body.totalCount ?? body.TotalCount ?? rows.length) || rows.length,
      page,
      orders: (Array.isArray(rows) ? rows : []).map(mapOrderRow),
    };
  },
};

const getOrderDetails: AgentActionExecutor = {
  describe: (args) => `Sipariş detayı (${str(args.orderId).slice(0, 8)}…)`,
  execute: async (args) => {
    const id = str(args.orderId);
    if (!id) return { success: false, error: 'orderId zorunlu (listOrders).' };
    const resp = await accountingApi.get(ACCOUNTING_API.ORDER_BY_ID(id));
    const o = unwrap(resp);
    if (!o) return { success: false, error: 'Sipariş bulunamadı.' };
    const items: any[] = o.items ?? o.Items ?? [];
    return {
      success: true,
      order: {
        ...mapOrderRow(o),
        notes: o.notes ?? o.Notes ?? '',
        exchangeRate: Number(o.exchangeRate ?? o.ExchangeRate ?? 1) || 1,
        items: items.map((i: any) => ({
          itemName: i.itemName ?? i.ItemName ?? '',
          quantity: Number(i.quantity ?? i.Quantity) || 0,
          unit: i.unit ?? i.Unit ?? '',
          unitPrice: Number(i.unitPrice ?? i.UnitPrice) || 0,
          vatRate: Number(i.vatRate ?? i.VatRate) || 0,
          discountRate: Number(i.discountRate ?? i.DiscountRate) || 0,
          netTotal: Number(i.netTotal ?? i.NetTotal) || 0,
          grossTotal: Number(i.grossTotal ?? i.GrossTotal) || 0,
          itemId: i.itemId ?? i.ItemId ?? null,
        })),
      },
    };
  },
};

const getOrderStats: AgentActionExecutor = {
  describe: () => 'Sipariş istatistiklerini getir',
  execute: async () => {
    const resp = await accountingApi.get(ACCOUNTING_API.ORDER_STATS);
    return { success: true, stats: unwrap(resp) };
  },
};

const openOrderForm: AgentActionExecutor = {
  describe: (args) => {
    const kind = str(args.orderKind).toLowerCase() === 'purchase' ? 'alış' : 'satış';
    return str(args.orderId)
      ? `${kind} siparişi düzenle (${str(args.orderId).slice(0, 8)}…)`
      : `Yeni ${kind} siparişi formunu aç`;
  },
  execute: async (args, ctx) => {
    const formType = orderFormTypeOf(args.orderKind);
    const id = str(args.orderId);
    if (formType === 'orderPurchase') {
      ctx.navigate(id ? `/siparis/alis/duzenle/${id}` : '/siparis/alis/yeni');
    } else {
      ctx.navigate(id ? `/siparis/satis/duzenle/${id}` : '/siparis/satis/yeni');
    }
    await waitForInvoiceFormApi(formType, 15000);
    return {
      success: true,
      message: id
        ? `${formType === 'orderPurchase' ? 'Alış' : 'Satış'} siparişi düzenleme formu açıldı.`
        : `${formType === 'orderPurchase' ? 'Alış' : 'Satış'} siparişi formu ekranda açıldı.`,
      nextSteps:
        'Sırasıyla: setOrderFormAccount ile cari seç, addOrderFormItem ile kalem ekle, ' +
        'gerekirse setOrderFormFields ile teslim tarihi/durum/processStock ayarla. Kaydetme kullanıcı isterse saveOrderForm.',
    };
  },
};

const setOrderFormAccount: AgentActionExecutor = {
  describe: (args) => `Sipariş formunda cari seç: "${str(args.accountName)}"`,
  execute: async (args) => {
    // Hangi sipariş formu açıksa onu kullan
    const api = await waitForInvoiceFormApi();
    if (api.formType !== 'orderSales' && api.formType !== 'orderPurchase') {
      return {
        success: false,
        error: 'Açık form sipariş değil — önce openOrderForm ile satış/alış siparişi formunu aç.',
      };
    }
    return api.setAccountByName(str(args.accountName));
  },
};

const setOrderFormFields: AgentActionExecutor = {
  describe: (args) => {
    const keys = Object.keys(args || {}).filter((k) => args[k] !== undefined && args[k] !== null);
    return `Sipariş formu alanlarını ayarla (${keys.join(', ') || '—'})`;
  },
  execute: async (args) => {
    const api = await waitForInvoiceFormApi();
    if (api.formType !== 'orderSales' && api.formType !== 'orderPurchase') {
      return { success: false, error: 'Açık form sipariş değil — önce openOrderForm ile formu aç.' };
    }
    const fields: InvoiceFormFields = {};
    if (args.orderDate !== undefined || args.invoiceDate !== undefined) {
      fields.invoiceDate = str(args.orderDate ?? args.invoiceDate);
    }
    if (args.orderNumber !== undefined || args.invoiceNumber !== undefined) {
      fields.invoiceNumber = str(args.orderNumber ?? args.invoiceNumber);
    }
    if (args.notes !== undefined) fields.notes = str(args.notes);
    if (args.currency !== undefined) fields.currency = str(args.currency);
    if (args.exchangeRate !== undefined) fields.exchangeRate = Number(args.exchangeRate);
    if (args.deliveryDate !== undefined) fields.deliveryDate = str(args.deliveryDate);
    if (args.processStock !== undefined) fields.processStock = args.processStock === true;
    if (args.orderStatus !== undefined || args.status !== undefined) {
      fields.orderStatus = str(args.orderStatus ?? args.status).toLowerCase();
    }
    return api.setFields(fields);
  },
};

const addOrderFormItem: AgentActionExecutor = {
  describe: (args) =>
    `Sipariş formuna kalem ekle: ${str(args.itemName)}${Number(args.quantity) > 0 ? ` x${Number(args.quantity)}` : ''}`,
  execute: async (args) => {
    const api = await waitForInvoiceFormApi();
    if (api.formType !== 'orderSales' && api.formType !== 'orderPurchase') {
      return { success: false, error: 'Açık form sipariş değil — önce openOrderForm ile formu aç.' };
    }
    const input: InvoiceFormItemInput = {
      ...toItemInput(args),
      itemName: str(args.itemName),
      isManual: args.isManual === true,
    };
    if (str(args.warehouseName)) {
      const rows = (await fetchWarehouses()).filter((w: any) => w.isActive !== false);
      const match = matchByName(rows as any[], str(args.warehouseName));
      if (!match) return { success: false, error: `Depo bulunamadı: "${str(args.warehouseName)}".` };
      input.warehouseId = (match as any).id;
    }
    return api.addItem(input);
  },
};

const updateOrderFormItem: AgentActionExecutor = {
  describe: (args) =>
    `Sipariş kalemini güncelle: ${args.line !== undefined ? `satır ${Number(args.line)}` : `"${str(args.itemName)}"`}`,
  execute: async (args) => {
    const api = await waitForInvoiceFormApi();
    if (api.formType !== 'orderSales' && api.formType !== 'orderPurchase') {
      return { success: false, error: 'Açık form sipariş değil — önce openOrderForm ile formu aç.' };
    }
    const changes = toItemInput(args);
    if (str(args.newItemName)) changes.itemName = str(args.newItemName);
    return api.updateItem(
      { line: args.line !== undefined ? Number(args.line) : undefined, itemName: str(args.itemName) || undefined },
      changes
    );
  },
};

const removeOrderFormItem: AgentActionExecutor = {
  describe: (args) =>
    `Sipariş kalemini çıkar: ${args.line !== undefined ? `satır ${Number(args.line)}` : `"${str(args.itemName)}"`}`,
  execute: async (args) => {
    const api = await waitForInvoiceFormApi();
    if (api.formType !== 'orderSales' && api.formType !== 'orderPurchase') {
      return { success: false, error: 'Açık form sipariş değil — önce openOrderForm ile formu aç.' };
    }
    return api.removeItem({
      line: args.line !== undefined ? Number(args.line) : undefined,
      itemName: str(args.itemName) || undefined,
    });
  },
};

const getOrderFormSummary: AgentActionExecutor = {
  describe: () => 'Ekrandaki sipariş formunun özetini getir',
  execute: async () => {
    const api = await waitForInvoiceFormApi();
    if (api.formType !== 'orderSales' && api.formType !== 'orderPurchase') {
      return { success: false, error: 'Açık form sipariş değil — önce openOrderForm ile formu aç.' };
    }
    return api.getSummary();
  },
};

const saveOrderForm: AgentActionExecutor = {
  describe: () => 'Ekrandaki sipariş formunu kaydet',
  execute: async () => {
    const api = await waitForInvoiceFormApi();
    if (api.formType !== 'orderSales' && api.formType !== 'orderPurchase') {
      return { success: false, error: 'Açık form sipariş değil — önce openOrderForm ile formu aç.' };
    }
    return api.save();
  },
};

const convertOrderToInvoice: AgentActionExecutor = {
  describe: (args) => `Siparişi faturaya dönüştür (${str(args.orderId).slice(0, 8)}…)`,
  execute: async (args, ctx) => {
    const id = str(args.orderId);
    if (!id) return { success: false, error: 'orderId zorunlu (listOrders).' };
    const resp = await accountingApi.get(ACCOUNTING_API.ORDER_BY_ID(id));
    const orderData = unwrap(resp);
    if (!orderData) return { success: false, error: 'Sipariş bulunamadı.' };

    const mapped = mapOrderRow(orderData);
    if (mapped.status.toLowerCase() === 'invoiced' || mapped.convertedInvoiceId) {
      return {
        success: false,
        error: `Bu sipariş zaten faturaya dönüştürülmüş${mapped.linkedInvoiceNumber ? ` (fatura: ${mapped.linkedInvoiceNumber})` : ''}.`,
      };
    }

    localStorage.setItem('orderToInvoice', JSON.stringify(buildOrderPayloadForConvert(orderData)));
    const isPurchase = mapped.type === 'purchase';
    ctx.navigate(isPurchase ? '/fatura/alis/yeni?fromOrder=true' : '/fatura/satis/yeni?fromOrder=true');
    try {
      await waitForInvoiceFormApi(isPurchase ? 'purchase' : 'sales', 15000);
    } catch {
      // form gecikebilir
    }
    return {
      success: true,
      message: `${isPurchase ? 'Alış' : 'Satış'} faturası formu sipariş içeriğiyle açıldı — kullanıcı kontrol edip kaydedebilir.`,
      note: 'Kaydetme saveInvoiceForm veya ekrandaki Kaydet ile.',
    };
  },
};

const convertOrderToWaybill: AgentActionExecutor = {
  describe: (args) => `Siparişi irsaliyeye dönüştür (${str(args.orderId).slice(0, 8)}…)`,
  execute: async (args, ctx) => {
    const id = str(args.orderId);
    if (!id) return { success: false, error: 'orderId zorunlu (listOrders).' };
    const resp = await accountingApi.get(ACCOUNTING_API.ORDER_BY_ID(id));
    const orderData = unwrap(resp);
    if (!orderData) return { success: false, error: 'Sipariş bulunamadı.' };

    const mapped = mapOrderRow(orderData);
    if (mapped.linkedWaybillId) {
      return {
        success: false,
        error: `Bu sipariş zaten irsaliyeye dönüştürülmüş${mapped.linkedWaybillNumber ? ` (irsaliye: ${mapped.linkedWaybillNumber})` : ''}.`,
      };
    }

    localStorage.setItem('orderToWaybill', JSON.stringify(buildOrderPayloadForConvert(orderData)));
    ctx.navigate('/irsaliye/yeni?fromOrder=true');
    try {
      await waitForInvoiceFormApi('waybill', 15000);
    } catch {
      // form gecikebilir
    }
    return {
      success: true,
      message: 'İrsaliye formu sipariş içeriğiyle açıldı — kullanıcı kontrol edip kaydedebilir.',
      note: 'Kaydetme saveWaybillForm veya ekrandaki Kaydet ile.',
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// DÖVİZ (Currency) MODÜLÜ
//
// Kur sorularında ÖNCE TCMB'den canlı kur çekilir (sayfa state'ine güvenilmez).
// Silme yok. Ayar kaydı onay kartı ile.
// ═══════════════════════════════════════════════════════════════════════════

const CURRENCY_ALIASES: Record<string, string> = {
  dolar: 'USD',
  dollar: 'USD',
  usd: 'USD',
  'abd dolari': 'USD',
  'abd doları': 'USD',
  euro: 'EUR',
  eur: 'EUR',
  avro: 'EUR',
  sterlin: 'GBP',
  pound: 'GBP',
  gbp: 'GBP',
  'ingiliz sterlini': 'GBP',
  tl: 'TRY',
  try: 'TRY',
  lira: 'TRY',
  'turk lirasi': 'TRY',
  'türk lirası': 'TRY',
  frank: 'CHF',
  chf: 'CHF',
  yen: 'JPY',
  jpy: 'JPY',
  yuan: 'CNY',
  cny: 'CNY',
  ruble: 'RUB',
  rub: 'RUB',
  riyal: 'SAR',
  sar: 'SAR',
  aed: 'AED',
  dirhem: 'AED',
  cad: 'CAD',
  aud: 'AUD',
};

const normalizeCurrencyCode = (raw: any): string => {
  const s = str(raw).trim();
  if (!s) return '';
  const lower = s.toLocaleLowerCase('tr-TR');
  if (CURRENCY_ALIASES[lower]) return CURRENCY_ALIASES[lower];
  // "USD dolar" gibi karışık metinde bilinen alias ara
  for (const [alias, code] of Object.entries(CURRENCY_ALIASES)) {
    if (lower.includes(alias)) return code;
  }
  return s.toUpperCase();
};

const mapRateRow = (r: CurrencyRate) => ({
  code: r.code,
  name: r.name,
  unit: r.unit,
  buying: r.buying,
  selling: r.selling,
  effectiveDate: r.effectiveDate
    ? (r.effectiveDate instanceof Date
        ? r.effectiveDate.toISOString().split('T')[0]
        : String(r.effectiveDate).split('T')[0])
    : null,
  symbol: CurrencyService.getCurrencySymbol(r.code),
});

/** Her çağrıda TCMB proxy üzerinden taze kur çeker — cache/sayfa state yok. */
const fetchLiveRates = async (): Promise<CurrencyRate[]> => {
  const rates = await CurrencyService.fetchExchangeRates();
  if (!Array.isArray(rates) || rates.length === 0) {
    throw new Error('TCMB döviz kurları çekilemedi.');
  }
  return rates;
};

const convertWithRates = (
  amountValue: number,
  from: string,
  to: string,
  rateType: 'buying' | 'selling',
  rates: CurrencyRate[]
): { result: number; fromRate: number; toRate: number } => {
  const pick = (code: string) =>
    code === 'TRY' ? { buying: 1, selling: 1, unit: 1 } : rates.find((r) => r.code === code);

  const fromRateObj = pick(from);
  const toRateObj = pick(to);
  if (!fromRateObj || !toRateObj) {
    throw new Error(`Kur bulunamadı: ${!fromRateObj ? from : to}`);
  }

  const fromRate = rateType === 'buying' ? fromRateObj.buying : fromRateObj.selling;
  const toRate = rateType === 'buying' ? toRateObj.buying : toRateObj.selling;

  if (from === to) return { result: amountValue, fromRate, toRate };

  let result: number;
  if (from === 'TRY') result = amountValue / toRate;
  else if (to === 'TRY') result = amountValue * fromRate;
  else result = (amountValue * fromRate) / toRate;

  return { result, fromRate, toRate };
};

const listCurrencyRates: AgentActionExecutor = {
  describe: () => 'TCMB döviz kurlarını çek ve listele',
  execute: async (args) => {
    // Canlı çekim zorunlu — "dolar kaç?" sorusu için önkoşul
    const rates = await fetchLiveRates();
    const onlyActive = args.onlyActive === true;
    let settings: Record<string, boolean> | null = null;
    if (onlyActive) {
      const resp = await accountingApi.get(ACCOUNTING_API.CURRENCY_SETTINGS);
      const body = resp.data || {};
      settings = (body.data ?? body.Data ?? {}) as Record<string, boolean>;
    }
    const rows = rates
      .filter((r) => !onlyActive || r.code === 'TRY' || settings?.[r.code] === true)
      .map(mapRateRow);
    return {
      success: true,
      source: 'TCMB',
      fetchedAt: new Date().toISOString(),
      count: rows.length,
      rates: rows,
      note: 'Kurlar az önce TCMB üzerinden çekildi.',
    };
  },
};

const getCurrencyRate: AgentActionExecutor = {
  describe: (args) => `Döviz kurunu getir: ${normalizeCurrencyCode(args.code ?? args.currency) || '?'}`,
  execute: async (args) => {
    const code = normalizeCurrencyCode(args.code ?? args.currency);
    if (!code) return { success: false, error: 'currency/code zorunlu (örn. USD, dolar, EUR).' };
    if (code === 'TRY') {
      return {
        success: true,
        rate: { code: 'TRY', name: 'TÜRK LİRASI', unit: 1, buying: 1, selling: 1, symbol: '₺' },
        note: 'TRY baz para birimidir (1).',
      };
    }
    // Önce kurları çek, sonra cevapla
    const rates = await fetchLiveRates();
    const found = rates.find((r) => r.code === code);
    if (!found) {
      const suggestions = rates.slice(0, 12).map((r) => r.code);
      return {
        success: false,
        error: `Kur bulunamadı: "${code}". Önce listCurrencyRates ile mevcut kodlara bak.`,
        suggestions,
      };
    }
    return {
      success: true,
      source: 'TCMB',
      fetchedAt: new Date().toISOString(),
      rate: mapRateRow(found),
      note: `1 ${found.code} ≈ alış ${found.buying} / satış ${found.selling} TRY (TCMB, az önce çekildi).`,
    };
  },
};

const getCurrencySettings: AgentActionExecutor = {
  describe: () => 'Şirket para birimi ayarlarını getir',
  execute: async () => {
    const resp = await accountingApi.get(ACCOUNTING_API.CURRENCY_SETTINGS);
    const body = resp.data || {};
    const settings = (body.data ?? body.Data ?? { TRY: true }) as Record<string, boolean>;
    settings.TRY = true;
    const active = Object.entries(settings)
      .filter(([, v]) => v === true)
      .map(([k]) => k);
    return { success: true, settings, activeCodes: active };
  },
};

const listActiveCurrencies: AgentActionExecutor = {
  describe: () => 'Aktif para birimlerini listele',
  execute: async () => {
    const [rates, settingsResp] = await Promise.all([
      fetchLiveRates(),
      accountingApi.get(ACCOUNTING_API.CURRENCY_SETTINGS),
    ]);
    const settings = (settingsResp.data?.data ?? settingsResp.data?.Data ?? {}) as Record<string, boolean>;
    const active = [
      { code: 'TRY', name: 'TÜRK LİRASI', symbol: '₺', buying: 1, selling: 1 },
      ...rates
        .filter((r) => settings[r.code] === true)
        .map((r) => ({
          code: r.code,
          name: r.name,
          symbol: CurrencyService.getCurrencySymbol(r.code),
          buying: r.buying,
          selling: r.selling,
        })),
    ];
    return {
      success: true,
      source: 'TCMB+settings',
      fetchedAt: new Date().toISOString(),
      currencies: active,
    };
  },
};

const convertCurrency: AgentActionExecutor = {
  describe: (args) => {
    const from = normalizeCurrencyCode(args.from ?? args.fromCurrency) || '?';
    const to = normalizeCurrencyCode(args.to ?? args.toCurrency) || '?';
    return `Döviz çevir: ${Number(args.amount) || '?'} ${from} → ${to}`;
  },
  execute: async (args) => {
    const amountValue = Number(args.amount);
    if (!(amountValue > 0)) return { success: false, error: 'amount pozitif sayı olmalı.' };
    const from = normalizeCurrencyCode(args.from ?? args.fromCurrency) || 'TRY';
    const to = normalizeCurrencyCode(args.to ?? args.toCurrency) || 'TRY';
    const rateType: 'buying' | 'selling' =
      str(args.rateType).toLowerCase() === 'selling' ? 'selling' : 'buying';

    // Canlı kur çekmeden çeviri yapılamaz
    const rates = await fetchLiveRates();
    try {
      const { result, fromRate, toRate } = convertWithRates(amountValue, from, to, rateType, rates);
      return {
        success: true,
        source: 'TCMB',
        fetchedAt: new Date().toISOString(),
        amount: amountValue,
        from,
        to,
        rateType,
        fromRate,
        toRate,
        result,
        formatted: `${amountValue} ${from} ≈ ${Number(result.toFixed(4))} ${to} (${rateType === 'buying' ? 'alış' : 'satış'})`,
      };
    } catch (e: any) {
      return { success: false, error: String(e?.message || e) };
    }
  },
};

const parseCurrencyCodeList = (raw: any): string[] => {
  if (Array.isArray(raw)) return raw.map(normalizeCurrencyCode).filter(Boolean);
  const s = str(raw);
  if (!s) return [];
  return s
    .split(/[,;\s]+/)
    .map((x) => normalizeCurrencyCode(x))
    .filter(Boolean);
};

const updateCurrencySettings: AgentActionExecutor = {
  describe: (args) => {
    const enable = parseCurrencyCodeList(args.enableCodes ?? args.enable);
    const disable = parseCurrencyCodeList(args.disableCodes ?? args.disable);
    if (enable.length || disable.length) {
      return `Para birimi ayarı: +[${enable.join(',')}] -[${disable.join(',')}]`;
    }
    return 'Para birimi ayarlarını güncelle';
  },
  execute: async (args) => {
    const enable = parseCurrencyCodeList(args.enableCodes ?? args.enable);
    const disable = parseCurrencyCodeList(args.disableCodes ?? args.disable);
    if (enable.length === 0 && disable.length === 0) {
      return {
        success: false,
        error: 'enableCodes veya disableCodes ver (virgülle, örn. "USD,EUR").',
      };
    }

    const resp = await accountingApi.get(ACCOUNTING_API.CURRENCY_SETTINGS);
    const current = {
      ...((resp.data?.data ?? resp.data?.Data ?? {}) as Record<string, boolean>),
    };
    current.TRY = true;

    for (const code of enable) {
      if (code !== 'TRY') current[code] = true;
    }
    for (const code of disable) {
      if (code !== 'TRY') current[code] = false;
    }

    current.TRY = true;
    const save = await accountingApi.post(ACCOUNTING_API.CURRENCY_SETTINGS, { Settings: current });
    const body = save.data || {};
    if (body.success === false) {
      return { success: false, error: body.message || 'Para birimi ayarları kaydedilemedi.' };
    }
    const activeCodes = Object.entries(current)
      .filter(([, v]) => v === true)
      .map(([k]) => k);
    return {
      success: true,
      message: 'Para birimi ayarları kaydedildi.',
      settings: current,
      activeCodes,
    };
  },
};

const openCurrencyPage: AgentActionExecutor = {
  describe: (args) => {
    const parts: string[] = [];
    if (args.amount !== undefined) parts.push(`tutar: ${args.amount}`);
    const from = normalizeCurrencyCode(args.from ?? args.fromCurrency);
    const to = normalizeCurrencyCode(args.to ?? args.toCurrency);
    if (from) parts.push(`${from}`);
    if (to) parts.push(`→ ${to}`);
    return `Döviz sayfasını aç${parts.length ? ` (${parts.join(' ')})` : ''}`;
  },
  execute: async (args, ctx) => {
    const req: CurrencyPageRequest = {};
    if (args.amount !== undefined && args.amount !== null) req.amount = args.amount;
    const from = normalizeCurrencyCode(args.from ?? args.fromCurrency);
    const to = normalizeCurrencyCode(args.to ?? args.toCurrency);
    if (from) req.fromCurrency = from;
    if (to) req.toCurrency = to;
    if (args.rateType !== undefined) {
      req.rateType = str(args.rateType).toLowerCase() === 'selling' ? 'selling' : 'buying';
    }
    req.refreshRates = args.refreshRates !== false; // varsayılan: kurları yenile
    if (args.autoConvert === true) req.autoConvert = true;

    ctx.navigate('/doviz');
    requestCurrencyPage(req);
    return {
      success: true,
      message: 'Döviz sayfası açıldı.',
      note:
        'Kur bilgisini chat\'te vermek için getCurrencyRate / convertCurrency kullan (bunlar TCMB\'den canlı çeker). ' +
        'Bu eylem yalnızca ekranı açar / çeviriciyi doldurur.',
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// TEKLİF (Offer) MODÜLÜ
//
// Oluşturma/düzenleme arka planda değil: SalesInvoice/PurchaseInvoice
// mode='offer' formu ekranda doldurulur. Silme / PDF / şablon editörü yok.
// ═══════════════════════════════════════════════════════════════════════════

const offerFormTypeOf = (kind: string): 'offerSales' | 'offerPurchase' => {
  const k = String(kind ?? '').trim().toLowerCase();
  return k === 'purchase' || k === 'received' || k === 'alis' || k === 'alış'
    ? 'offerPurchase'
    : 'offerSales';
};

const mapOfferRow = (o: any) => {
  const offerType = str(o.offerType ?? o.OfferType).toUpperCase();
  const type =
    offerType.includes('PURCHASE') ||
    str(o.type).toLowerCase() === 'received' ||
    str(o.type).toLowerCase() === 'purchase'
      ? 'purchase'
      : 'sales';
  return {
    id: o.id ?? o.Id ?? '',
    offerNumber: o.offerNumber ?? o.OfferNumber ?? '',
    offerType: offerType || (type === 'purchase' ? 'PURCHASE_OFFER' : 'SALES_OFFER'),
    type,
    accountName: o.accountName ?? o.AccountName ?? o.account?.companyName ?? '',
    offerDate: (o.offerDate ?? o.OfferDate ?? o.date ?? '').toString().split('T')[0],
    validUntil: o.validUntil ?? o.ValidUntil
      ? (o.validUntil ?? o.ValidUntil).toString().split('T')[0]
      : null,
    grandTotal: Number(o.grandTotal ?? o.GrandTotal ?? o.amount ?? 0) || 0,
    currency: o.currency ?? o.Currency ?? 'TRY',
    status: o.status ?? o.Status ?? '',
    linkedInvoiceId: o.linkedInvoiceId ?? o.LinkedInvoiceId ?? o.convertedInvoiceId ?? null,
    linkedInvoiceNumber: o.linkedInvoiceNumber ?? o.LinkedInvoiceNumber ?? null,
    linkedOrderId: o.linkedOrderId ?? o.LinkedOrderId ?? null,
    linkedOrderNumber: o.linkedOrderNumber ?? o.LinkedOrderNumber ?? null,
  };
};

const buildOfferPayloadForConvert = (offerData: any) => ({
  offerId: offerData.id ?? offerData.Id,
  offerNumber: offerData.offerNumber ?? offerData.OfferNumber,
  accountId: (offerData.accountId ?? offerData.AccountId)?.toString() || '',
  account: {
    companyName: offerData.accountName ?? offerData.AccountName ?? '',
    taxNumber: offerData.accountTaxNumber ?? offerData.AccountTaxNumber ?? '',
    taxOffice: offerData.accountTaxOffice ?? offerData.AccountTaxOffice ?? '',
    address: offerData.accountAddress ?? offerData.AccountAddress ?? '',
    phone: offerData.accountPhone ?? offerData.AccountPhone ?? '',
    email: offerData.accountEmail ?? offerData.AccountEmail ?? '',
  },
  items: ((offerData.items ?? offerData.Items) || []).map((item: any) => ({
    id: item.id ?? item.Id,
    itemId: item.itemId ?? item.ItemId ?? item.id,
    stockId: item.itemId ?? item.ItemId ?? item.id,
    name: item.itemName ?? item.ItemName,
    itemName: item.itemName ?? item.ItemName,
    quantity: item.quantity ?? item.Quantity,
    unit: item.unit ?? item.Unit,
    unitPrice: item.unitPrice ?? item.UnitPrice,
    priceType: (item.priceType ?? item.PriceType) || 'VAT_EXCLUDED',
    enteredPrice: item.enteredPrice ?? item.EnteredPrice ?? item.unitPrice ?? item.UnitPrice,
    vatRate: item.vatRate ?? item.VatRate ?? 0,
    sctRate: item.sctRate ?? item.SctRate ?? 0,
    discountRate: item.discountRate ?? item.DiscountRate ?? 0,
    netTotal: item.netTotal ?? item.NetTotal ?? 0,
    vatTotal: item.vatTotal ?? item.VatTotal ?? item.vatAmount ?? 0,
    vatAmount: item.vatAmount ?? item.VatAmount ?? item.vatTotal ?? 0,
    grossTotal: item.grossTotal ?? item.GrossTotal ?? 0,
    warehouseId: item.warehouseId ?? item.WarehouseId ?? '',
  })),
  notes: offerData.notes ?? offerData.Notes
    ? `Teklif No: ${offerData.offerNumber ?? offerData.OfferNumber} - ${offerData.notes ?? offerData.Notes}`
    : `Teklif No: ${offerData.offerNumber ?? offerData.OfferNumber}`,
  currency: offerData.currency ?? offerData.Currency ?? 'TRY',
});

const applyOfferFilters: AgentActionExecutor = {
  describe: (args) => {
    const parts: string[] = [];
    if (str(args.searchText)) parts.push(`ara: "${str(args.searchText)}"`);
    if (str(args.offerType)) parts.push(`tür: ${str(args.offerType)}`);
    if (str(args.status)) parts.push(`durum: ${str(args.status)}`);
    return `Teklif sayfasında filtrele${parts.length ? ` (${parts.join(', ')})` : ''}`;
  },
  execute: async (args, ctx) => {
    const req: OfferFilterRequest = {};
    if (args.clearAll === true) req.clearAll = true;
    if (args.searchText !== undefined && args.searchText !== null) req.searchText = str(args.searchText);
    if (args.offerType !== undefined) req.offerType = str(args.offerType) || 'all';
    if (args.status !== undefined) req.status = str(args.status) || 'all';
    ctx.navigate('/teklifler');
    requestOfferFilters(req);
    return {
      success: true,
      message: 'Teklif sayfasında filtreler uygulandı.',
      note: 'Veri lazımsa listOffers kullan.',
    };
  },
};

const listOffers: AgentActionExecutor = {
  describe: (args) => {
    const parts: string[] = [];
    if (str(args.offerType)) {
      const t = str(args.offerType).toLowerCase();
      parts.push(t === 'purchase' || t === 'received' ? 'alış' : 'satış');
    }
    if (str(args.status)) parts.push(str(args.status));
    if (str(args.search)) parts.push(`ara: "${str(args.search)}"`);
    return `Teklifleri listele${parts.length ? ` (${parts.join(', ')})` : ''}`;
  },
  execute: async (args) => {
    const page = Math.max(1, Number(args.page) || 1);
    const pageSize = clampLimit(args.pageSize, 20, 100);
    let offerType: string | undefined;
    const ot = str(args.offerType).toLowerCase();
    if (ot === 'sales' || ot === 'given' || ot === 'sales_offer') offerType = 'SALES_OFFER';
    else if (ot === 'purchase' || ot === 'received' || ot === 'purchase_offer') offerType = 'PURCHASE_OFFER';

    const resp = await accountingApi.get(
      ACCOUNTING_API.OFFER_BY_COMPANY_PAGED({
        page,
        pageSize,
        searchTerm: str(args.search) || undefined,
        sortColumn: 'createdAt',
        sortDirection: 'desc',
        offerType,
        status: str(args.status) && str(args.status).toLowerCase() !== 'all' ? str(args.status).toLowerCase() : undefined,
      })
    );
    const body = resp.data || {};
    const rows: any[] = body.data ?? body.Data ?? [];
    return {
      success: true,
      totalCount: Number(body.totalCount ?? body.TotalCount ?? rows.length) || rows.length,
      page,
      offers: (Array.isArray(rows) ? rows : []).map(mapOfferRow),
    };
  },
};

const getOfferDetails: AgentActionExecutor = {
  describe: (args) => `Teklif detayı (${str(args.offerId).slice(0, 8)}…)`,
  execute: async (args) => {
    const id = str(args.offerId);
    if (!id) return { success: false, error: 'offerId zorunlu (listOffers).' };
    const resp = await accountingApi.get(ACCOUNTING_API.OFFER_BY_ID(id));
    if (resp.data?.success === false) {
      return { success: false, error: resp.data?.message || 'Teklif bulunamadı.' };
    }
    const o = unwrap(resp);
    if (!o) return { success: false, error: 'Teklif bulunamadı.' };
    const items: any[] = o.items ?? o.Items ?? [];
    return {
      success: true,
      offer: {
        ...mapOfferRow(o),
        notes: o.notes ?? o.Notes ?? '',
        exchangeRate: Number(o.exchangeRate ?? o.ExchangeRate ?? 1) || 1,
        items: items.map((i: any) => ({
          itemName: i.itemName ?? i.ItemName ?? '',
          quantity: Number(i.quantity ?? i.Quantity) || 0,
          unit: i.unit ?? i.Unit ?? '',
          unitPrice: Number(i.unitPrice ?? i.UnitPrice) || 0,
          vatRate: Number(i.vatRate ?? i.VatRate) || 0,
          discountRate: Number(i.discountRate ?? i.DiscountRate) || 0,
          netTotal: Number(i.netTotal ?? i.NetTotal) || 0,
          grossTotal: Number(i.grossTotal ?? i.GrossTotal) || 0,
          itemId: i.itemId ?? i.ItemId ?? null,
        })),
      },
    };
  },
};

const getOfferStats: AgentActionExecutor = {
  describe: () => 'Teklif istatistiklerini getir',
  execute: async () => {
    // Ayrı stats endpoint yok — son kayıtlar üzerinden özet
    const resp = await accountingApi.get(
      ACCOUNTING_API.OFFER_BY_COMPANY_PAGED({
        page: 1,
        pageSize: 100,
        sortColumn: 'createdAt',
        sortDirection: 'desc',
      })
    );
    const body = resp.data || {};
    const rows: any[] = (body.data ?? body.Data ?? []).map(mapOfferRow);
    const totalCount = Number(body.totalCount ?? body.TotalCount ?? rows.length) || rows.length;
    const pending = rows.filter((o) => ['pending', 'draft'].includes(String(o.status).toLowerCase()));
    const approved = rows.filter((o) => ['approved', 'invoiced', 'ordered'].includes(String(o.status).toLowerCase()));
    const rejected = rows.filter((o) => String(o.status).toLowerCase() === 'rejected');
    const invoiced = rows.filter((o) => String(o.status).toLowerCase() === 'invoiced');
    const nonDraft = rows.filter((o) => String(o.status).toLowerCase() !== 'draft');
    return {
      success: true,
      sampleSize: rows.length,
      totalCount,
      stats: {
        pendingCount: pending.length,
        pendingTotal: pending.reduce((s, o) => s + (o.grandTotal || 0), 0),
        approvedCount: approved.length,
        approvedTotal: approved.reduce((s, o) => s + (o.grandTotal || 0), 0),
        rejectedCount: rejected.length,
        invoicedCount: invoiced.length,
        conversionRate:
          nonDraft.length > 0 ? Math.round((invoiced.length / nonDraft.length) * 100) : 0,
      },
      note: rows.length < totalCount
        ? `Özet son ${rows.length} kayıt üzerinden; toplam ${totalCount} teklif var.`
        : undefined,
    };
  },
};

const openOfferForm: AgentActionExecutor = {
  describe: (args) => {
    const kind = offerFormTypeOf(args.offerKind) === 'offerPurchase' ? 'alış' : 'satış';
    return str(args.offerId)
      ? `${kind} teklifi düzenle (${str(args.offerId).slice(0, 8)}…)`
      : `Yeni ${kind} teklifi formunu aç`;
  },
  execute: async (args, ctx) => {
    const formType = offerFormTypeOf(args.offerKind);
    const id = str(args.offerId);
    if (formType === 'offerPurchase') {
      ctx.navigate(id ? `/teklifler/alis/duzenle/${id}` : '/teklifler/alis/yeni');
    } else {
      ctx.navigate(id ? `/teklifler/satis/duzenle/${id}` : '/teklifler/satis/yeni');
    }
    await waitForInvoiceFormApi(formType, 15000);
    return {
      success: true,
      message: id
        ? `${formType === 'offerPurchase' ? 'Alış' : 'Satış'} teklifi düzenleme formu açıldı.`
        : `${formType === 'offerPurchase' ? 'Alış' : 'Satış'} teklifi formu ekranda açıldı.`,
      nextSteps:
        'Sırasıyla: setOfferFormAccount ile cari seç, addOfferFormItem ile kalem ekle, ' +
        'setOfferFormFields ile validUntil (zorunlu) ve offerStatus ayarla. Kaydetme kullanıcı isterse saveOfferForm.',
    };
  },
};

const setOfferFormAccount: AgentActionExecutor = {
  describe: (args) => `Teklif formunda cari seç: "${str(args.accountName)}"`,
  execute: async (args) => {
    const api = await waitForInvoiceFormApi();
    if (api.formType !== 'offerSales' && api.formType !== 'offerPurchase') {
      return {
        success: false,
        error: 'Açık form teklif değil — önce openOfferForm ile satış/alış teklifi formunu aç.',
      };
    }
    return api.setAccountByName(str(args.accountName));
  },
};

const setOfferFormFields: AgentActionExecutor = {
  describe: (args) => {
    const keys = Object.keys(args || {}).filter((k) => args[k] !== undefined && args[k] !== null);
    return `Teklif formu alanlarını ayarla (${keys.join(', ') || '—'})`;
  },
  execute: async (args) => {
    const api = await waitForInvoiceFormApi();
    if (api.formType !== 'offerSales' && api.formType !== 'offerPurchase') {
      return { success: false, error: 'Açık form teklif değil — önce openOfferForm ile formu aç.' };
    }
    const fields: InvoiceFormFields = {};
    if (args.offerDate !== undefined || args.invoiceDate !== undefined) {
      fields.invoiceDate = str(args.offerDate ?? args.invoiceDate);
    }
    if (args.offerNumber !== undefined || args.invoiceNumber !== undefined) {
      fields.invoiceNumber = str(args.offerNumber ?? args.invoiceNumber);
    }
    if (args.notes !== undefined) fields.notes = str(args.notes);
    if (args.currency !== undefined) fields.currency = str(args.currency);
    if (args.exchangeRate !== undefined) fields.exchangeRate = Number(args.exchangeRate);
    if (args.validUntil !== undefined) fields.validUntil = str(args.validUntil);
    if (args.offerStatus !== undefined || args.status !== undefined) {
      fields.offerStatus = str(args.offerStatus ?? args.status).toLowerCase();
    }
    return api.setFields(fields);
  },
};

const addOfferFormItem: AgentActionExecutor = {
  describe: (args) =>
    `Teklif formuna kalem ekle: ${str(args.itemName)}${Number(args.quantity) > 0 ? ` x${Number(args.quantity)}` : ''}`,
  execute: async (args) => {
    const api = await waitForInvoiceFormApi();
    if (api.formType !== 'offerSales' && api.formType !== 'offerPurchase') {
      return { success: false, error: 'Açık form teklif değil — önce openOfferForm ile formu aç.' };
    }
    const input: InvoiceFormItemInput = {
      ...toItemInput(args),
      itemName: str(args.itemName),
      isManual: args.isManual === true,
    };
    if (str(args.warehouseName)) {
      const rows = (await fetchWarehouses()).filter((w: any) => w.isActive !== false);
      const match = matchByName(rows as any[], str(args.warehouseName));
      if (!match) return { success: false, error: `Depo bulunamadı: "${str(args.warehouseName)}".` };
      input.warehouseId = (match as any).id;
    }
    return api.addItem(input);
  },
};

const updateOfferFormItem: AgentActionExecutor = {
  describe: (args) =>
    `Teklif kalemini güncelle: ${args.line !== undefined ? `satır ${Number(args.line)}` : `"${str(args.itemName)}"`}`,
  execute: async (args) => {
    const api = await waitForInvoiceFormApi();
    if (api.formType !== 'offerSales' && api.formType !== 'offerPurchase') {
      return { success: false, error: 'Açık form teklif değil — önce openOfferForm ile formu aç.' };
    }
    const changes = toItemInput(args);
    if (str(args.newItemName)) changes.itemName = str(args.newItemName);
    return api.updateItem(
      { line: args.line !== undefined ? Number(args.line) : undefined, itemName: str(args.itemName) || undefined },
      changes
    );
  },
};

const removeOfferFormItem: AgentActionExecutor = {
  describe: (args) =>
    `Teklif kalemini çıkar: ${args.line !== undefined ? `satır ${Number(args.line)}` : `"${str(args.itemName)}"`}`,
  execute: async (args) => {
    const api = await waitForInvoiceFormApi();
    if (api.formType !== 'offerSales' && api.formType !== 'offerPurchase') {
      return { success: false, error: 'Açık form teklif değil — önce openOfferForm ile formu aç.' };
    }
    return api.removeItem({
      line: args.line !== undefined ? Number(args.line) : undefined,
      itemName: str(args.itemName) || undefined,
    });
  },
};

const getOfferFormSummary: AgentActionExecutor = {
  describe: () => 'Ekrandaki teklif formunun özetini getir',
  execute: async () => {
    const api = await waitForInvoiceFormApi();
    if (api.formType !== 'offerSales' && api.formType !== 'offerPurchase') {
      return { success: false, error: 'Açık form teklif değil — önce openOfferForm ile formu aç.' };
    }
    return api.getSummary();
  },
};

const saveOfferForm: AgentActionExecutor = {
  describe: () => 'Ekrandaki teklif formunu kaydet',
  execute: async () => {
    const api = await waitForInvoiceFormApi();
    if (api.formType !== 'offerSales' && api.formType !== 'offerPurchase') {
      return { success: false, error: 'Açık form teklif değil — önce openOfferForm ile formu aç.' };
    }
    return api.save();
  },
};

const convertOfferToInvoice: AgentActionExecutor = {
  describe: (args) => `Teklifi faturaya dönüştür (${str(args.offerId).slice(0, 8)}…)`,
  execute: async (args, ctx) => {
    const id = str(args.offerId);
    if (!id) return { success: false, error: 'offerId zorunlu (listOffers).' };
    const resp = await accountingApi.get(ACCOUNTING_API.OFFER_BY_ID(id));
    const raw = unwrap(resp);
    const offerData = raw?.data ?? raw?.Data ?? raw;
    if (!offerData || (!offerData.id && !offerData.Id)) {
      return { success: false, error: 'Teklif bulunamadı.' };
    }

    const mapped = mapOfferRow(offerData);
    if (mapped.linkedInvoiceId) {
      return {
        success: false,
        error: `Bu teklif zaten faturaya dönüştürülmüş${mapped.linkedInvoiceNumber ? ` (fatura: ${mapped.linkedInvoiceNumber})` : ''}.`,
      };
    }
    const st = mapped.status.toLowerCase();
    if (!['approved', 'invoiced', 'ordered'].includes(st)) {
      return {
        success: false,
        error: `Faturaya dönüştürmek için teklif durumu onaylı olmalı (şu an: ${mapped.status || '—'}).`,
      };
    }

    localStorage.setItem('offerToInvoice', JSON.stringify(buildOfferPayloadForConvert(offerData)));
    const isPurchase = mapped.type === 'purchase';
    ctx.navigate(isPurchase ? '/fatura/alis/yeni?fromOffer=true' : '/fatura/satis/yeni?fromOffer=true');
    try {
      await waitForInvoiceFormApi(isPurchase ? 'purchase' : 'sales', 15000);
    } catch {
      // form gecikebilir
    }
    return {
      success: true,
      message: `${isPurchase ? 'Alış' : 'Satış'} faturası formu teklif içeriğiyle açıldı — kullanıcı kontrol edip kaydedebilir.`,
      note: 'Kaydetme saveInvoiceForm veya ekrandaki Kaydet ile.',
    };
  },
};

const convertOfferToOrder: AgentActionExecutor = {
  describe: (args) => `Teklifi siparişe dönüştür (${str(args.offerId).slice(0, 8)}…)`,
  execute: async (args, ctx) => {
    const id = str(args.offerId);
    if (!id) return { success: false, error: 'offerId zorunlu (listOffers).' };
    const resp = await accountingApi.get(ACCOUNTING_API.OFFER_BY_ID(id));
    const raw = unwrap(resp);
    const offerData = raw?.data ?? raw?.Data ?? raw;
    if (!offerData || (!offerData.id && !offerData.Id)) {
      return { success: false, error: 'Teklif bulunamadı.' };
    }

    const mapped = mapOfferRow(offerData);
    if (mapped.linkedOrderId) {
      return {
        success: false,
        error: `Bu teklif zaten siparişe dönüştürülmüş${mapped.linkedOrderNumber ? ` (sipariş: ${mapped.linkedOrderNumber})` : ''}.`,
      };
    }
    const st = mapped.status.toLowerCase();
    if (!['approved', 'invoiced', 'ordered'].includes(st)) {
      return {
        success: false,
        error: `Siparişe dönüştürmek için teklif durumu onaylı olmalı (şu an: ${mapped.status || '—'}).`,
      };
    }

    localStorage.setItem('offerToOrder', JSON.stringify(buildOfferPayloadForConvert(offerData)));
    const isPurchase = mapped.type === 'purchase';
    ctx.navigate(isPurchase ? '/siparis/alis/yeni?fromOffer=true' : '/siparis/satis/yeni?fromOffer=true');
    try {
      await waitForInvoiceFormApi(isPurchase ? 'orderPurchase' : 'orderSales', 15000);
    } catch {
      // form gecikebilir
    }
    return {
      success: true,
      message: `${isPurchase ? 'Alış' : 'Satış'} siparişi formu teklif içeriğiyle açıldı — kullanıcı kontrol edip kaydedebilir.`,
      note: 'Kaydetme saveOrderForm veya ekrandaki Kaydet ile.',
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Satış Sistemi — yönetim sayfaları + ortak ayar modalı UI eylemleri
// (Hızlı Satış /hizli-satis · Restoran /restoran · Paket Sipariş /siparis-yonetimi)
//
// Ajan yalnızca YÖNETİCİ tarafında çalışır: ekran listeleme/oluşturma ve ayar
// modalı üzerinde kontrol (sekme, alan, kaydet). Üç mod da AYNI ayar modalını
// kullanır; modal eylemleri (setQuickSaleSettingsTab vb.) hepsinde çalışır.
// Operasyonel POS ekranları kapsam dışı (yalnızca navigasyonla açılabilir).
// Alan değişiklikleri EKRANDAKİ formda yapılır; kayıt saveQuickSaleSettings
// (write → onay kartı) ile kullanıcı onayından geçer.
// ═══════════════════════════════════════════════════════════════════════════

const SCREENS_PAGE_PATHS: Record<SalesScreensPageType, string> = {
  quickSale: '/hizli-satis',
  restaurant: '/restoran',
  package: '/siparis-yonetimi',
};

const QUICK_SALE_PATH = SCREENS_PAGE_PATHS.quickSale;

/** Sayfa açık değilse navigate edip sayfa API'sini bekler. */
const ensureScreensPage = async (ctx: AgentActionContext, pageType: SalesScreensPageType) => {
  if (!getSalesScreensPageApi(pageType)) {
    ctx.navigate(SCREENS_PAGE_PATHS[pageType]);
  }
  return waitForSalesScreensPageApi(pageType);
};

/** Ortak akış: yönetim sayfasında ada göre ayar modalını aç (+ opsiyonel sekme). */
const openSettingsOnScreensPage = async (
  ctx: AgentActionContext,
  pageType: SalesScreensPageType,
  args: Record<string, any>,
  listHint: string
): Promise<Record<string, any>> => {
  const screenName = str(args.screenName);
  if (!screenName) return { success: false, error: `screenName zorunlu (${listHint}).` };

  const pageApi = await ensureScreensPage(ctx, pageType);
  const openResult = pageApi.openSettings(screenName);
  if (openResult.success === false) return openResult;

  const settingsApi = await waitForQuickSaleSettingsApi(15000);

  const tab = str(args.tab);
  let tabResult: Record<string, any> | null = null;
  if (tab) tabResult = settingsApi.setTab(tab);

  return {
    success: true,
    message: `"${settingsApi.screenName}" ayar modalı açıldı.`,
    tabs: settingsApi.getTabs(),
    ...(tabResult ? { tabChange: tabResult } : {}),
  };
};

/** Şube adını id'ye çözer; bulunamazsa hata nesnesi döner. */
const resolveBranchByName = async (
  ctx: AgentActionContext,
  branchName: string
): Promise<{ branchId: string } | { error: Record<string, any> }> => {
  const branches = await getBranches(ctx.companyId || '');
  const wanted = branchName.toLowerCase();
  const branch =
    branches.find((b: any) => String(b.name || '').toLowerCase() === wanted) ||
    branches.find((b: any) => String(b.name || '').toLowerCase().includes(wanted));
  if (!branch) {
    return {
      error: {
        success: false,
        error: `"${branchName}" adında şube bulunamadı.`,
        availableBranches: branches.map((b: any) => b.name),
      },
    };
  }
  return { branchId: String(branch.id) };
};

/** Yönetim sayfası açıksa listesini yeniler, değilse sayfaya gider. */
const refreshOrNavigateScreensPage = async (ctx: AgentActionContext, pageType: SalesScreensPageType) => {
  const pageApi = getSalesScreensPageApi(pageType);
  if (pageApi) {
    try {
      await pageApi.refreshScreens();
    } catch {
      // liste yenileme kritik değil
    }
  } else {
    ctx.navigate(SCREENS_PAGE_PATHS[pageType]);
  }
};

const listQuickSaleScreens: AgentActionExecutor = {
  describe: () => 'Hızlı satış ekranlarını listele',
  execute: async () => {
    const screens = await salesService.getQuickSaleScreens();
    const active = screens.filter((s) => s.IsActive !== false);
    return {
      success: true,
      count: active.length,
      screens: active.map((s) => ({
        id: s.Id,
        name: s.Name,
        screenType: s.ScreenType || 'Standalone',
        branchId: s.BranchId ?? null,
        prepaidSystemEnabled: s.PrepaidSystemEnabled,
      })),
    };
  },
};

const openQuickSalePage: AgentActionExecutor = {
  describe: () => 'Hızlı satış yönetim sayfasını aç',
  execute: async (_args, ctx) => {
    ctx.navigate(QUICK_SALE_PATH);
    await waitForSalesScreensPageApi('quickSale');
    return { success: true, message: 'Hızlı satış yönetim sayfası açıldı.' };
  },
};

const createQuickSaleScreen: AgentActionExecutor = {
  describe: (args) => `Yeni hızlı satış ekranı oluştur: "${str(args.name)}"`,
  execute: async (args, ctx) => {
    const name = str(args.name);
    if (!name) return { success: false, error: 'name (ekran adı) zorunlu.' };

    // Aynı adda ekran varsa oluşturma
    const existing = await salesService.getQuickSaleScreens();
    if (existing.some((s) => s.IsActive !== false && s.Name.trim().toLowerCase() === name.toLowerCase())) {
      return { success: false, error: `"${name}" adında bir ekran zaten var.` };
    }

    // Opsiyonel şube: ada göre çözümle
    let branchId: string | null = null;
    const branchName = str(args.branchName);
    if (branchName) {
      const resolved = await resolveBranchByName(ctx, branchName);
      if ('error' in resolved) return resolved.error;
      branchId = resolved.branchId;
    }

    const created = await salesService.createQuickSaleScreen({
      Name: name,
      BranchId: branchId,
      ScreenType: 'Standalone',
    });

    await refreshOrNavigateScreensPage(ctx, 'quickSale');

    return {
      success: true,
      screenId: created?.Id,
      message: `"${name}" hızlı satış ekranı oluşturuldu.`,
      note: 'Ayarlarını düzenlemek için openQuickSaleSettings kullanılabilir.',
    };
  },
};

const openQuickSaleSettings: AgentActionExecutor = {
  describe: (args) =>
    `"${str(args.screenName)}" hızlı satış ekranının ayarlar modalını aç${str(args.tab) ? ` (${str(args.tab)} sekmesi)` : ''}`,
  execute: (args, ctx) => openSettingsOnScreensPage(ctx, 'quickSale', args, 'listQuickSaleScreens'),
};

const setQuickSaleSettingsTab: AgentActionExecutor = {
  describe: (args) => `Ayar modalında "${str(args.tabName)}" sekmesine geç`,
  execute: async (args) => {
    const tabName = str(args.tabName);
    if (!tabName) return { success: false, error: 'tabName zorunlu.' };
    const api = await waitForQuickSaleSettingsApi();
    return api.setTab(tabName);
  },
};

const getQuickSaleSettings: AgentActionExecutor = {
  describe: () => 'Açık ayar modalındaki ayarların özetini getir',
  execute: async () => {
    const api = await waitForQuickSaleSettingsApi();
    return { success: true, ...api.getSummary() };
  },
};

const updateQuickSaleSettings: AgentActionExecutor = {
  describe: (args) => {
    const keys = Object.keys(args).filter((k) => args[k] !== undefined && args[k] !== null);
    return `Ayar modalında alan değiştir: ${keys.join(', ') || '(alan yok)'}`;
  },
  execute: async (args) => {
    const api = await waitForQuickSaleSettingsApi();
    const fields: QuickSaleSettingsFields = {};

    // Ödeme yöntemi çifti: paymentOptionLabel + paymentOptionEnabled
    const payLabel = str(args.paymentOptionLabel);
    if (payLabel) {
      if (typeof args.paymentOptionEnabled !== 'boolean') {
        return { success: false, error: 'paymentOptionLabel verildiğinde paymentOptionEnabled (true/false) zorunlu.' };
      }
      fields.paymentOptions = { [payLabel]: args.paymentOptionEnabled };
    }

    // Depolar: virgülle ayrılmış ad/id listesi (mevcut seçimin yerine geçer)
    const warehousesStr = str(args.warehouses);
    if (warehousesStr) {
      fields.selectedWarehouses = warehousesStr.split(',').map((w) => w.trim()).filter(Boolean);
    }

    const boolKeys: Array<keyof QuickSaleSettingsFields> = [
      'useCashierSystem',
      'autoLockEnabled',
      'preventNegativeStock',
      'prepaidSystemEnabled',
      'showProductImages',
      'showCashChangeModal',
      'allowAccountCreationOnSale',
      'useKitchenScreen',
      'showScreenLayoutButton',
      'warnIfFiscalNotConnected',
      'useFiscalDeviceForCash',
    ];
    for (const key of boolKeys) {
      if (typeof args[key] === 'boolean') (fields as any)[key] = args[key];
    }
    if (args.autoLockTimeoutSeconds !== undefined && args.autoLockTimeoutSeconds !== null) {
      fields.autoLockTimeoutSeconds = Number(args.autoLockTimeoutSeconds);
    }
    if (str(args.printingMode)) fields.printingMode = str(args.printingMode);
    if (str(args.defaultReceiptPrinter)) fields.defaultReceiptPrinter = str(args.defaultReceiptPrinter);
    if (str(args.localServiceUrl)) fields.localServiceUrl = str(args.localServiceUrl);

    if (Object.keys(fields).length === 0) {
      return { success: false, error: 'Değiştirilecek en az bir alan belirt.' };
    }
    return api.setFields(fields);
  },
};

const saveQuickSaleSettings: AgentActionExecutor = {
  describe: () => 'Açık ayar modalındaki değişiklikleri kaydet',
  execute: async () => {
    const api = await waitForQuickSaleSettingsApi();
    return api.save();
  },
};

const closeQuickSaleSettings: AgentActionExecutor = {
  describe: () => 'Ayar modalını kapat (kaydetmeden)',
  execute: async () => {
    const api = await waitForQuickSaleSettingsApi(3000);
    return api.close();
  },
};

// ── Depo/Stok taslakları (ekranlar arası ürün aktarımı) ──
// Taslaklar localStorage'dadır (quickSaleWarehouseTemplates_{companyId}) —
// yalnızca aynı tarayıcıda görünür; eylem yanıtlarında bu not AI'ya iletilir.

const listWarehouseStockTemplates: AgentActionExecutor = {
  describe: () => 'Kayıtlı depo/stok taslaklarını listele',
  execute: async () => {
    const api = await waitForQuickSaleSettingsApi();
    const templates = api.listWarehouseTemplates();
    return {
      success: true,
      templates,
      note:
        templates.length === 0
          ? 'Bu tarayıcıda kayıtlı depo/stok taslağı yok. Taslak oluşturmak için kaynak ekranın ayarlarını açıp saveWarehouseStockTemplate kullan.'
          : 'Taslaklar tarayıcıda saklanır — yalnızca bu bilgisayarda görünür.',
    };
  },
};

const applyWarehouseStockTemplate: AgentActionExecutor = {
  describe: (args) => `"${str(args.templateName)}" depo/stok taslağını açık ayar modalına uygula`,
  execute: async (args) => {
    const templateName = str(args.templateName);
    if (!templateName) return { success: false, error: 'templateName zorunlu.' };
    const api = await waitForQuickSaleSettingsApi();
    return api.applyWarehouseTemplate(templateName);
  },
};

const saveWarehouseStockTemplate: AgentActionExecutor = {
  describe: (args) => `Açık modaldaki depo/stok seçimini "${str(args.templateName)}" taslağı olarak kaydet`,
  execute: async (args) => {
    const templateName = str(args.templateName);
    if (!templateName) return { success: false, error: 'templateName zorunlu.' };
    const api = await waitForQuickSaleSettingsApi();
    return api.saveWarehouseTemplate(templateName);
  },
};

// ── Ödeme yöntemleri (açık ayar modalı — Ödeme Yöntemleri sekmesi) ──

const listQuickSalePaymentBindings: AgentActionExecutor = {
  describe: () => 'Ödeme yöntemi bağlama seçeneklerini listele (kasalar + POS hesapları)',
  execute: async () => {
    const api = await waitForQuickSaleSettingsApi();
    return api.listPaymentBindings();
  },
};

const addQuickSalePaymentOption: AgentActionExecutor = {
  describe: (args) => {
    const type = str(args.type);
    const target =
      type === 'pos' ? str(args.bankAccountName) : str(args.cashboxName);
    return `Açık ayar modalına ödeme yöntemi ekle: ${str(args.label) || type}${target ? ` → ${target}` : ''}`;
  },
  execute: async (args) => {
    const api = await waitForQuickSaleSettingsApi();
    return api.addPaymentOption({
      type: str(args.type) as any,
      label: str(args.label) || undefined,
      cashboxName: str(args.cashboxName) || undefined,
      bankAccountName: str(args.bankAccountName) || undefined,
    });
  },
};

const removeQuickSalePaymentOption: AgentActionExecutor = {
  describe: (args) => `Açık ayar modalından "${str(args.label)}" ödeme yöntemini kaldır`,
  execute: async (args) => {
    const api = await waitForQuickSaleSettingsApi();
    return api.removePaymentOption(str(args.label));
  },
};

// ── Personel atama (açık ayar modalı — Personel Atama sekmesi) ──

const listQuickSaleAssignablePersonnel: AgentActionExecutor = {
  describe: () => 'Ekrana atanabilir personelleri ve mevcut atamaları listele',
  execute: async () => {
    const api = await waitForQuickSaleSettingsApi();
    return api.listAssignablePersonnel();
  },
};

const setQuickSaleAssignedPersonnel: AgentActionExecutor = {
  describe: (args) => {
    const mode = str(args.mode) || 'add';
    const label = mode === 'remove' ? 'çıkar' : mode === 'replace' ? 'atamaları değiştir' : 'ata';
    return `Açık ayar modalında personel ${label}: ${str(args.personnelNames)}`;
  },
  execute: async (args) => {
    const names = str(args.personnelNames)
      .split(',')
      .map((n) => n.trim())
      .filter(Boolean);
    if (names.length === 0) {
      return { success: false, error: 'personnelNames zorunlu (virgülle ayrılmış ad listesi).' };
    }
    const mode = (str(args.mode).toLowerCase() || 'add') as 'add' | 'remove' | 'replace';
    if (!['add', 'remove', 'replace'].includes(mode)) {
      return { success: false, error: "mode: 'add' | 'remove' | 'replace' olmalı." };
    }
    const api = await waitForQuickSaleSettingsApi();
    return api.setAssignedPersonnel(names, mode);
  },
};

// ── Mutfak entegrasyonu (açık ayar modalı — Ek Ayarlar sekmesi) ──

const setKitchenIntegration: AgentActionExecutor = {
  describe: (args) =>
    args.enabled === true
      ? `Mutfak entegrasyonunu aç ve "${str(args.qrMenuName)}" QR menüsünü bağla`
      : 'Mutfak entegrasyonunu kapat',
  execute: async (args) => {
    const api = await waitForQuickSaleSettingsApi();
    return api.setKitchenIntegration(args.enabled === true, str(args.qrMenuName) || undefined);
  },
};

// ── Ekran kasiyerleri (modal gerektirmez — salesService CRUD) ──

const CASHIER_PERMISSION_KEYS: Record<'QuickSale' | 'Restaurant', string[]> = {
  QuickSale: [
    'CanChangePrice', 'CanApplyDiscount', 'CanCancelSale', 'CanViewReports', 'CanViewEndOfDay',
    'CanWarehouseOperations', 'CanViewAccountBalance', 'CanListAccounts', 'CanQuickAddItem',
    'CanAddAccount', 'CanPosRefund', 'CanClearCart',
  ],
  Restaurant: [
    'CanCloseSale', 'CanCloseTable', 'CanApplyDiscount', 'CanAddProduct', 'CanRemoveProduct',
    'CanDeletePrintedItems', 'CanDeleteUnprintedItems', 'CanClearCart', 'CanPrintToKitchen',
    'CanTransferTable', 'CanPosRefund', 'CanCancelReceipt', 'CanCancelUnprintedReceipt',
    'CanPrintReceipt', 'CanAddProductAfterReceiptPrint', 'CanUsePavoP2P', 'CanViewEndOfDay',
  ],
};

// CashierSettingsTab resetForm ile aynı varsayılanlar: riskli yetkiler kapalı başlar
const CASHIER_DEFAULT_FALSE = new Set([
  'CanDeletePrintedItems', 'CanDeleteUnprintedItems', 'CanAddProductAfterReceiptPrint',
  'CanCancelReceipt', 'CanCancelUnprintedReceipt', 'CanPrintReceipt',
]);

const normalizeCashierScreenType = (raw: string): 'QuickSale' | 'Restaurant' | null => {
  const v = raw.toLocaleLowerCase('tr-TR').replace(/\s/g, '');
  if (['quicksale', 'hızlısatış', 'hizlisatis', 'paket', 'package', 'packageorder'].includes(v)) return 'QuickSale';
  if (['restaurant', 'restoran'].includes(v)) return 'Restaurant';
  return null;
};

/** Kasiyer eylemleri için ekranı ada göre çözer (TR-toleranslı). */
const resolveCashierScreen = async (
  screenName: string,
  screenType: 'QuickSale' | 'Restaurant'
): Promise<{ id: number; name: string } | { error: Record<string, any> }> => {
  const rows: any[] =
    screenType === 'Restaurant'
      ? (await restaurantScreenApi.getByCompanyId()).filter((s) => s.IsActive !== false)
      : (await salesService.getQuickSaleScreens()).filter((s) => s.IsActive !== false);
  const match = findByNameTr(rows, (s) => String(s.Name || ''), screenName);
  if (match.ambiguous) {
    return { error: { success: false, error: `Birden fazla ekran eşleşti: ${match.ambiguous.join(', ')} — tam adı belirt.` } };
  }
  if (!match.found) {
    return {
      error: {
        success: false,
        error: `"${screenName}" adında ${screenType === 'Restaurant' ? 'restoran' : 'hızlı satış'} ekranı bulunamadı.`,
        availableScreens: rows.map((s) => s.Name),
      },
    };
  }
  return { id: Number(match.found.Id), name: String(match.found.Name) };
};

const listScreenCashiers: AgentActionExecutor = {
  describe: (args) => `"${str(args.screenName)}" ekranının kasiyerlerini listele`,
  execute: async (args) => {
    const screenName = str(args.screenName);
    if (!screenName) return { success: false, error: 'screenName zorunlu.' };
    const screenType = normalizeCashierScreenType(str(args.screenType));
    if (!screenType) return { success: false, error: "screenType: 'QuickSale' veya 'Restaurant' olmalı." };

    const screen = await resolveCashierScreen(screenName, screenType);
    if ('error' in screen) return screen.error;

    const cashiers =
      screenType === 'QuickSale'
        ? await salesService.getCashiersByQuickSaleScreenId(screen.id)
        : await salesService.getCashiersByRestaurantScreenId(screen.id);
    return {
      success: true,
      screenName: screen.name,
      count: cashiers.length,
      cashiers: cashiers.map((c) => ({
        id: c.Id,
        name: c.CashierName,
        isActive: c.IsActive !== false,
        permissions: Object.fromEntries(
          CASHIER_PERMISSION_KEYS[screenType].map((key) => [key, (c as any)[key] === true])
        ),
      })),
      note: 'PIN güvenlik gereği listelenmez.',
    };
  },
};

const createScreenCashier: AgentActionExecutor = {
  describe: (args) =>
    `"${str(args.screenName)}" ekranına "${str(args.cashierName)}" kasiyerini ekle`,
  execute: async (args) => {
    const screenName = str(args.screenName);
    const cashierName = str(args.cashierName);
    const pin = str(args.pin);
    if (!screenName || !cashierName) return { success: false, error: 'screenName ve cashierName zorunlu.' };
    if (!/^\d{4,6}$/.test(pin)) return { success: false, error: 'pin 4-6 haneli rakam olmalı.' };
    const screenType = normalizeCashierScreenType(str(args.screenType));
    if (!screenType) return { success: false, error: "screenType: 'QuickSale' veya 'Restaurant' olmalı." };

    const screen = await resolveCashierScreen(screenName, screenType);
    if ('error' in screen) return screen.error;

    const existing =
      screenType === 'QuickSale'
        ? await salesService.getCashiersByQuickSaleScreenId(screen.id)
        : await salesService.getCashiersByRestaurantScreenId(screen.id);
    if (existing.some((c) => String(c.CashierName).toLocaleLowerCase('tr-TR') === cashierName.toLocaleLowerCase('tr-TR'))) {
      return { success: false, error: `"${cashierName}" adında kasiyer bu ekranda zaten var.` };
    }

    // Yetkiler: güvenli varsayılanlar + permissionsJson ile bilinçli değişiklikler
    const validKeys = CASHIER_PERMISSION_KEYS[screenType];
    const permissions: Record<string, boolean> = {};
    for (const key of validKeys) permissions[key] = !CASHIER_DEFAULT_FALSE.has(key);
    const permissionsJson = str(args.permissionsJson);
    if (permissionsJson) {
      let parsed: unknown;
      try {
        parsed = JSON.parse(permissionsJson);
      } catch {
        return { success: false, error: 'permissionsJson geçerli JSON değil. Örn: {"CanApplyDiscount":false}' };
      }
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return { success: false, error: 'permissionsJson bir nesne olmalı. Örn: {"CanApplyDiscount":false}' };
      }
      for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
        if (!validKeys.includes(key)) {
          return { success: false, error: `Bilinmeyen yetki: ${key}`, validPermissionKeys: validKeys };
        }
        if (typeof value !== 'boolean') {
          return { success: false, error: `${key}: true/false bekleniyor.` };
        }
        permissions[key] = value;
      }
    }

    const created = await salesService.createCashier({
      ScreenType: screenType,
      QuickSaleScreenId: screenType === 'QuickSale' ? screen.id : undefined,
      RestaurantScreenId: screenType === 'Restaurant' ? screen.id : undefined,
      CashierName: cashierName,
      CashierPin: pin,
      IsActive: true,
      ...permissions,
    });
    return {
      success: true,
      message: `"${cashierName}" kasiyeri "${screen.name}" ekranına eklendi${created?.Id ? ` (id: ${created.Id})` : ''}.`,
      permissions,
      note:
        'Kasiyer girişinin çalışması için ekranın "Kasiyer Sistemi" ayarı açık olmalı — ' +
        'kapalıysa ayar modalında useCashierSystem=true yapıp kaydet (updateQuickSaleSettings + saveQuickSaleSettings).',
    };
  },
};

// ─── Restoran yönetimi (/restoran) ──────────────────────────────────────────

/** TR-duyarlı ad eşleştirme: önce tam, sonra tek kısmi eşleşme. */
const findByNameTr = <T,>(
  items: T[],
  getName: (item: T) => string,
  wanted: string
): { found?: T; ambiguous?: string[] } => {
  const norm = (v: string) => v.toLocaleLowerCase('tr-TR').trim();
  const w = norm(wanted);
  const exact = items.find((i) => norm(getName(i)) === w);
  if (exact) return { found: exact };
  const partial = items.filter((i) => norm(getName(i)).includes(w));
  if (partial.length === 1) return { found: partial[0] };
  if (partial.length > 1) return { ambiguous: partial.map(getName) };
  return {};
};

const listRestaurantScreens: AgentActionExecutor = {
  describe: () => 'Restoran ekranlarını listele',
  execute: async () => {
    const screens = await restaurantScreenApi.getByCompanyId();
    const active = screens.filter((s) => s.IsActive !== false);
    return {
      success: true,
      count: active.length,
      screens: active.map((s) => ({
        id: s.Id,
        name: s.Name,
        branchId: s.BranchId ?? null,
        quickSaleScreenId: s.QuickSaleScreenId,
      })),
    };
  },
};

const openRestaurantSettings: AgentActionExecutor = {
  describe: (args) =>
    `"${str(args.screenName)}" restoran ekranının ayarlar modalını aç${str(args.tab) ? ` (${str(args.tab)} sekmesi)` : ''}`,
  execute: (args, ctx) => openSettingsOnScreensPage(ctx, 'restaurant', args, 'listRestaurantScreens'),
};

const createRestaurantScreen: AgentActionExecutor = {
  describe: (args) => `Yeni restoran ekranı oluştur: "${str(args.name)}"`,
  execute: async (args, ctx) => {
    const name = str(args.name);
    if (!name) return { success: false, error: 'name (ekran adı) zorunlu.' };

    const existing = await restaurantScreenApi.getByCompanyId();
    if (existing.some((s) => s.IsActive !== false && s.Name.trim().toLowerCase() === name.toLowerCase())) {
      return { success: false, error: `"${name}" adında bir restoran ekranı zaten var.` };
    }

    let branchId: string | null = null;
    const branchName = str(args.branchName);
    if (branchName) {
      const resolved = await resolveBranchByName(ctx, branchName);
      if ('error' in resolved) return resolved.error;
      branchId = resolved.branchId;
    }

    // AddRestaurantSetupModal ile aynı 3 adım: QuickSaleScreen (Restaurant) →
    // RestaurantScreen → QuickSaleScreen'e RestaurantScreenId bağla.
    const quickSale = await salesService.createQuickSaleScreen({
      Name: name,
      BranchId: branchId,
      ScreenType: 'Restaurant',
    });
    const restaurant = await restaurantScreenApi.create({
      Name: name,
      BranchId: branchId,
      QuickSaleScreenId: quickSale.Id,
      DefaultTableCapacity: 4,
      EnableTableTransfer: true,
      EnableSplitBill: true,
    });
    await salesService.updateQuickSaleScreen({ Id: quickSale.Id, RestaurantScreenId: restaurant.Id });

    await refreshOrNavigateScreensPage(ctx, 'restaurant');

    return {
      success: true,
      screenId: restaurant.Id,
      quickSaleScreenId: quickSale.Id,
      message: `"${name}" restoran ekranı oluşturuldu.`,
      note: 'Masa grupları/masalar ve diğer ayarlar openRestaurantSettings ile düzenlenebilir.',
    };
  },
};

const openRestaurantScreen: AgentActionExecutor = {
  describe: (args) => `"${str(args.screenName)}" restoran satış ekranını aç`,
  execute: async (args, ctx) => {
    const screenName = str(args.screenName);
    if (!screenName) return { success: false, error: 'screenName zorunlu (listRestaurantScreens).' };

    const screens = (await restaurantScreenApi.getByCompanyId()).filter((s) => s.IsActive !== false);
    const match = findByNameTr(screens, (s) => s.Name, screenName);
    if (match.ambiguous) {
      return { success: false, error: `Birden fazla eşleşme: ${match.ambiguous.join(', ')} — tam adı belirt.` };
    }
    if (!match.found) {
      return {
        success: false,
        error: `"${screenName}" adında restoran ekranı bulunamadı.`,
        availableScreens: screens.map((s) => s.Name),
      };
    }
    ctx.navigate(`/restaurant-screen/${match.found.Id}`);
    return {
      success: true,
      message: `"${match.found.Name}" restoran satış ekranı açıldı.`,
      note: 'Bu operasyonel ekranda AI sohbeti görünmez; yönetim için /restoran sayfasına dönülebilir.',
    };
  },
};

// ─── Paket sipariş yönetimi (/siparis-yonetimi) ─────────────────────────────

const listPackageScreens: AgentActionExecutor = {
  describe: () => 'Paket sipariş ekranlarını listele',
  execute: async () => {
    const screens = await salesService.getPackageScreens();
    return {
      success: true,
      count: screens.length,
      screens: screens.map((s) => ({
        id: s.Id,
        name: s.Name,
        branchId: s.BranchId ?? null,
        quickSaleScreenId: s.QuickSaleScreenId,
      })),
    };
  },
};

const openPackageSettings: AgentActionExecutor = {
  describe: (args) =>
    `"${str(args.screenName)}" paket ekranının ayarlar modalını aç${str(args.tab) ? ` (${str(args.tab)} sekmesi)` : ''}`,
  execute: (args, ctx) => openSettingsOnScreensPage(ctx, 'package', args, 'listPackageScreens'),
};

const createPackageScreen: AgentActionExecutor = {
  describe: (args) => `Yeni paket sipariş ekranı oluştur: "${str(args.name)}"`,
  execute: async (args, ctx) => {
    const name = str(args.name);
    if (!name) return { success: false, error: 'name (ekran adı) zorunlu.' };

    const existing = await salesService.getPackageScreens();
    if (existing.some((s) => s.Name.trim().toLowerCase() === name.toLowerCase())) {
      return { success: false, error: `"${name}" adında bir paket ekranı zaten var.` };
    }

    let branchId: string | null = null;
    const branchName = str(args.branchName);
    if (branchName) {
      const resolved = await resolveBranchByName(ctx, branchName);
      if ('error' in resolved) return resolved.error;
      branchId = resolved.branchId;
    }

    // AddPackageScreenModal ile aynı 3 adım: QuickSaleScreen (Package) →
    // PackageScreen → QuickSaleScreen'e PackageScreenId bağla.
    const quickSale = await salesService.createQuickSaleScreen({
      Name: name,
      BranchId: branchId,
      ScreenType: 'Package',
    });
    const packageScreen = await salesService.createPackageScreen({
      Name: name,
      BranchId: branchId,
      QuickSaleScreenId: quickSale.Id,
      DefaultDeliveryTime: 45,
      AutoPrintKitchen: false,
      ShowCallerIdPanel: true,
    });
    await salesService.updateQuickSaleScreen({ Id: quickSale.Id, PackageScreenId: packageScreen.Id });

    await refreshOrNavigateScreensPage(ctx, 'package');

    return {
      success: true,
      screenId: packageScreen.Id,
      quickSaleScreenId: quickSale.Id,
      message: `"${name}" paket sipariş ekranı oluşturuldu.`,
      note: 'Entegrasyonlar dahil ayarlar openPackageSettings ile düzenlenebilir.',
    };
  },
};

const openPackageOrderScreen: AgentActionExecutor = {
  describe: (args) => `"${str(args.screenName)}" paket sipariş ekranını aç`,
  execute: async (args, ctx) => {
    const screenName = str(args.screenName);
    if (!screenName) return { success: false, error: 'screenName zorunlu (listPackageScreens).' };

    const screens = await salesService.getPackageScreens();
    const match = findByNameTr(screens, (s) => s.Name, screenName);
    if (match.ambiguous) {
      return { success: false, error: `Birden fazla eşleşme: ${match.ambiguous.join(', ')} — tam adı belirt.` };
    }
    if (!match.found) {
      return {
        success: false,
        error: `"${screenName}" adında paket ekranı bulunamadı.`,
        availableScreens: screens.map((s) => s.Name),
      };
    }
    // Rota QuickSaleScreenId bekler (OrderManagementPage.handleGoToScreen ile aynı)
    ctx.navigate(`/paket-ekrani/${match.found.QuickSaleScreenId}`);
    return {
      success: true,
      message: `"${match.found.Name}" paket sipariş ekranı açıldı.`,
      note: 'Bu operasyonel ekranda AI sohbeti görünmez; yönetim için /siparis-yonetimi sayfasına dönülebilir.',
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Gün Sonu Raporu (/gun-sonu · /gun-sonlari)
//
// endOfDayDataService UI'dan bağımsız saf fonksiyonlar sunduğu için özet ve
// platform sağlaması doğrudan chat'e getirilebilir. "Gün Sonu Al" (kapanış)
// işlemi bilinçli olarak ajana VERİLMEZ — openEndOfDayReport sayfayı filtreli
// açar, butona basmak kullanıcıya kalır.
// ═══════════════════════════════════════════════════════════════════════════

/** 'YYYY-MM-DD' veya tam ISO tarih; geçersizse fallback. */
const parseEodDate = (v: any, fallback: Date, endOfDay: boolean): Date => {
  const s = str(v);
  if (!s) return fallback;
  const d = new Date(s.length === 10 ? `${s}T${endOfDay ? '23:59:59' : '00:00:00'}` : s);
  return isNaN(d.getTime()) ? fallback : d;
};

/** Gün sonu servisi için bağlam (şubeler + ad çözümü). */
const buildEodContext = async (ctx: AgentActionContext) => {
  const companyId = requireCompanyId(ctx);
  const branches = await getBranches(companyId);
  return {
    branches: branches.map((b: any) => ({ id: String(b.id), name: String(b.name || '') })),
    getBranchName: (id: string) =>
      String(branches.find((b: any) => String(b.id) === String(id))?.name || id),
    companyId,
  };
};

/** Ortak akış: tarih/şube argümanlarını çözüp tüm kanalların veri setini çeker. */
const runEndOfDayDataset = async (
  args: Record<string, any>,
  ctx: AgentActionContext
): Promise<
  | { ok: true; dataset: Awaited<ReturnType<typeof fetchEndOfDayDataset>>; startDate: Date; endDate: Date; branchIds: string[] }
  | { ok: false; error: Record<string, any> }
> => {
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const startDate = parseEodDate(args.startDate, todayStart, false);
  const endDate = parseEodDate(args.endDate, new Date(), true);

  let branchIds: string[] = [];
  const branchName = str(args.branchName);
  if (branchName) {
    const resolved = await resolveBranchByName(ctx, branchName);
    if ('error' in resolved) return { ok: false, error: resolved.error };
    branchIds = [resolved.branchId];
  }

  const eodCtx = await buildEodContext(ctx);
  const dataset = await fetchEndOfDayDataset(
    { startDate, endDate, branchIds, filterFiscalOnly: false },
    eodCtx
  );
  return { ok: true, dataset, startDate, endDate, branchIds };
};

/** Kanal satırlarından kompakt toplam üretir (iptal satırlar toplam dışı). */
const channelTotals = (rows: Array<{ grandTotal?: number; status?: string }>) => ({
  count: rows.length,
  total: Math.round(
    rows
      .filter((r) => r.status !== 'CANCELLED')
      .reduce((sum, r) => sum + (Number(r.grandTotal) || 0), 0) * 100
  ) / 100,
});

const getEndOfDaySummary: AgentActionExecutor = {
  describe: (args) =>
    `Gün sonu özetini getir${str(args.branchName) ? ` (${str(args.branchName)})` : ''}`,
  execute: async (args, ctx) => {
    const run = await runEndOfDayDataset(args, ctx);
    if (!run.ok) return run.error;
    const { dataset, startDate, endDate } = run;

    const reconciliationIssues = Object.entries(dataset.reconciliationData)
      .filter(([, r]) => r.warnings.length > 0 || r.unpaid.length > 0)
      .map(([platform, r]) => ({
        platform,
        warningCount: r.warnings.length,
        warningTotal: r.warningTotal,
        unpaidCount: r.unpaid.length,
        unpaidTotal: r.unpaidTotal,
      }));

    return {
      success: true,
      range: { startDate: startDate.toISOString(), endDate: endDate.toISOString() },
      summary: dataset.summary,
      channels: {
        restoran: channelTotals(dataset.restaurantData),
        hizliSatis: channelTotals(dataset.quickSaleData),
        kiosk: channelTotals(dataset.kioskData),
        qrMenu: channelTotals(dataset.qrMenuData),
        paketSiparis: channelTotals(dataset.packageOrderData),
        getir: channelTotals(dataset.getirOrderData),
        migros: channelTotals(dataset.migrosOrderData),
        yemeksepeti: channelTotals(dataset.ysOrderData),
        trendyol: channelTotals(dataset.trendyolOrderData),
      },
      openTableCount: dataset.activeTables.length,
      reconciliationIssues,
      note: 'Gün sonu KAPANIŞI buradan yapılmaz — rapor sayfasını openEndOfDayReport ile açıp "Gün Sonu Al" butonunu kullanıcı tıklar.',
    };
  },
};

const RECONCILIATION_PLATFORMS: Record<string, string> = {
  getir: 'Getir',
  migros: 'Migros',
  yemeksepeti: 'Yemeksepeti',
  trendyol: 'Trendyol',
};

const getPlatformReconciliation: AgentActionExecutor = {
  describe: (args) => `${str(args.platform)} platform sağlamasını getir`,
  execute: async (args, ctx) => {
    const platform = str(args.platform).toLowerCase();
    if (!RECONCILIATION_PLATFORMS[platform]) {
      return {
        success: false,
        error: `platform şunlardan biri olmalı: ${Object.keys(RECONCILIATION_PLATFORMS).join(', ')}`,
      };
    }
    const run = await runEndOfDayDataset(args, ctx);
    if (!run.ok) return run.error;
    const rec = run.dataset.reconciliationData[platform];
    if (!rec) return { success: false, error: `${platform} için sağlama verisi bulunamadı.` };

    const compactOrder = (o: any) => ({
      orderNumber: o.orderNumber,
      customerName: o.customerName,
      totalAmount: o.totalAmount,
      createdAt: o.createdAt,
      paymentStatus: o.paymentStatus,
      hasReceipt: o.hasReceipt === true,
      hasInvoice: o.hasInvoice === true,
    });

    return {
      success: true,
      platform: RECONCILIATION_PLATFORMS[platform],
      range: { startDate: run.startDate.toISOString(), endDate: run.endDate.toISOString() },
      paid: { count: rec.paid.length, total: rec.paidTotal },
      unpaid: { count: rec.unpaid.length, total: rec.unpaidTotal, orders: rec.unpaid.slice(0, 20).map(compactOrder) },
      cancelled: { count: rec.cancelled.length, total: rec.cancelledTotal },
      warnings: { count: rec.warnings.length, total: rec.warningTotal, orders: rec.warnings.slice(0, 20).map(compactOrder) },
    };
  },
};

const listSavedEndOfDayReports: AgentActionExecutor = {
  describe: () => 'Kayıtlı gün sonu raporlarını listele',
  execute: async (args, ctx) => {
    const limit = clampLimit(args.limit, 20, 50);
    const startDate = str(args.startDate) || undefined;
    const endDate = str(args.endDate) || undefined;
    const result = await endOfDayReportApi.getByCompanyId(startDate, endDate);
    const rows = result?.Data ?? [];

    const eodCtx = await buildEodContext(ctx);
    return {
      success: true,
      totalCount: result?.TotalCount ?? rows.length,
      reports: rows.slice(0, limit).map((r) => ({
        id: r.Id,
        screenType: r.ScreenType,
        startDate: r.StartDate,
        endDate: r.EndDate,
        balance: r.Balance,
        cashTotal: r.CashTotal,
        posTotal: r.PosTotal,
        otherTotal: r.OtherTotal,
        cancelledTotal: r.CancelledTotal,
        transactionCount: r.TransactionCount,
        closedBy: r.ClosedByName || r.ClosedByEmail || r.ClosedBy,
        branchName: r.BranchId ? eodCtx.getBranchName(r.BranchId) : 'Tüm Şubeler',
        createdAt: r.CreatedAt,
      })),
    };
  },
};

const openEndOfDayReport: AgentActionExecutor = {
  describe: (args) =>
    `Gün sonu raporu sayfasını filtreli aç${str(args.branchName) ? ` (${str(args.branchName)})` : ''}`,
  execute: async (args, ctx) => {
    let branchIds: string[] | undefined;
    const branchName = str(args.branchName);
    if (branchName) {
      const resolved = await resolveBranchByName(ctx, branchName);
      if ('error' in resolved) return resolved.error;
      branchIds = [resolved.branchId];
    } else if (args.allBranches === true) {
      branchIds = [];
    }

    requestEndOfDayFilters({
      startDate: str(args.startDate) || undefined,
      endDate: str(args.endDate) || undefined,
      branchIds,
      autoFilter: args.autoFilter !== false,
    });
    ctx.navigate('/gun-sonu');
    return {
      success: true,
      message: 'Gün sonu raporu sayfası açıldı; filtreler dolduruldu ve rapor çalıştırıldı.',
      note: '"Gün Sonu Al" (kapanış) butonuna kullanıcı kendisi basar.',
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// QR Menü + Mutfak Ekranı (/qr-menu-management · /qr-menu/orders ·
// /mutfak-ekrani-secimi · /mutfak-ekrani/:menuId)
//
// Mutfak ekranı QR menü altyapısını kullanır (aynı menü tanımları + aynı
// MasaSiparisi API'si); sipariş durumu değişikliği tek eylemde toplanır
// (setQrOrderStatus) ve mutfak siparişleri için de geçerlidir.
// ═══════════════════════════════════════════════════════════════════════════

/** QR menü tanımlarını çeker ({ success, data } sarmalı). */
const fetchQrMenus = async (): Promise<any[]> => {
  const resp = await mainApi.get('/api/QRMenu/menu-definitions');
  const result = resp.data;
  return result?.success && Array.isArray(result.data) ? result.data : [];
};

/** Menü adını kayda çözer; bulunamaz/muğlaksa hata nesnesi döner. */
const resolveQrMenuByName = async (
  menuName: string
): Promise<{ menu: any } | { error: Record<string, any> }> => {
  const menus = await fetchQrMenus();
  const match = findByNameTr(menus, (m: any) => String(m.name || ''), menuName);
  if (match.ambiguous) {
    return { error: { success: false, error: `Birden fazla eşleşme: ${match.ambiguous.join(', ')} — tam adı belirt.` } };
  }
  if (!match.found) {
    return {
      error: {
        success: false,
        error: `"${menuName}" adında QR menü bulunamadı.`,
        availableMenus: menus.map((m: any) => m.name),
      },
    };
  }
  return { menu: match.found };
};

/** Masa siparişlerini çeker (QR menü + mutfak aynı listeyi kullanır). */
const fetchTableOrders = async (): Promise<any[]> => {
  const resp = await mainApi.get('/api/MasaSiparisi/orders');
  const result = resp.data;
  return result?.success && Array.isArray(result.data) ? result.data : [];
};

const mapTableOrder = (o: any) => ({
  id: o.id,
  orderNo: o.siparisId,
  menuName: o.qrMenuName ?? null,
  table: o.masaAdi || (o.masaNumarasi != null ? `Masa ${o.masaNumarasi}` : null),
  customer: o.kullaniciAdi ?? null,
  total: Number(o.toplamTutar) || 0,
  status: o.siparisDurumu,
  createdAt: o.siparisOlusturmaTarihi,
  branchName: o.branchName ?? null,
});

/** Sipariş listesini duruma/menüye göre süzüp durum sayaçlarıyla döndürür. */
const buildOrdersResult = (orders: any[], args: Record<string, any>, excludeFinished: boolean) => {
  const norm = (v: any) => String(v || '').toLocaleLowerCase('tr-TR').trim();
  let rows = orders;

  if (excludeFinished) {
    rows = rows.filter((o) => !['teslim edildi', 'iptal'].includes(norm(o.siparisDurumu)));
  }
  const status = norm(args.status);
  if (status && status !== 'all') {
    rows = rows.filter((o) => norm(o.siparisDurumu).includes(status));
  }
  const menuName = norm(args.menuName);
  if (menuName) {
    rows = rows.filter((o) => norm(o.qrMenuName).includes(menuName));
  }

  const countsByStatus: Record<string, number> = {};
  for (const o of rows) {
    const s = String(o.siparisDurumu || 'Bilinmiyor');
    countsByStatus[s] = (countsByStatus[s] || 0) + 1;
  }

  const limit = clampLimit(args.limit, 20, 50);
  return {
    success: true,
    totalCount: rows.length,
    countsByStatus,
    orders: rows.slice(0, limit).map(mapTableOrder),
  };
};

const listQrMenus: AgentActionExecutor = {
  describe: () => 'QR menüleri listele',
  execute: async () => {
    const menus = await fetchQrMenus();
    return {
      success: true,
      count: menus.length,
      menus: menus.map((m: any) => ({
        id: m.id,
        name: m.name,
        branchName: m.branchName ?? null,
        template: m.template ?? 'standard',
      })),
    };
  },
};

const listQrOrders: AgentActionExecutor = {
  describe: () => 'QR menü siparişlerini listele',
  execute: async (args) => buildOrdersResult(await fetchTableOrders(), args, false),
};

const listQrTables: AgentActionExecutor = {
  describe: (args) => `"${str(args.menuName)}" menüsünün masalarını listele`,
  execute: async (args, ctx) => {
    const menuName = str(args.menuName);
    if (!menuName) return { success: false, error: 'menuName zorunlu (listQrMenus).' };
    const resolved = await resolveQrMenuByName(menuName);
    if ('error' in resolved) return resolved.error;

    const companyId = requireCompanyId(ctx);
    const resp = await mainApi.get(`/api/Table/company/${companyId}/qrmenu/${resolved.menu.id}/tables`);
    const result = resp.data;
    const tables: any[] = result?.success && Array.isArray(result.data) ? result.data : [];
    return {
      success: true,
      menuName: resolved.menu.name,
      count: tables.length,
      tables: tables.map((t: any) => ({
        id: t.id,
        tableNumber: t.tableNumber,
        tableName: t.tableName || `Masa ${t.tableNumber}`,
      })),
    };
  },
};

const getQrMenuPublicUrl: AgentActionExecutor = {
  describe: (args) => `"${str(args.menuName)}" menüsünün müşteri linkini getir`,
  execute: async (args) => {
    const menuName = str(args.menuName);
    if (!menuName) return { success: false, error: 'menuName zorunlu (listQrMenus).' };
    const resolved = await resolveQrMenuByName(menuName);
    if ('error' in resolved) return resolved.error;
    const menu = resolved.menu;

    // QRMenuPage.getPublicMenuUrl ile aynı öncelik: custom domain → prod hash rotası
    let customDomain = '';
    try {
      const resp = await mainApi.get('/api/QRMenu/custom-domain');
      const result = resp.data;
      if (result?.success && result.data) {
        customDomain =
          typeof result.data === 'string'
            ? result.data
            : result.data.customDomain || result.data.domainName || '';
      }
    } catch {
      // custom domain yoksa prod URL kullanılır
    }

    let isTicket = menu.template === 'ticket';
    try {
      const settings = typeof menu.settings === 'string' ? JSON.parse(menu.settings) : menu.settings;
      if (settings?.mode === 'ticket') isTicket = true;
    } catch {
      // settings parse edilemezse template alanına güvenilir
    }

    const url = customDomain
      ? `https://${customDomain}/${isTicket ? 'ticket' : 'menu'}/${menu.id}`
      : `${BRAND.urls.base}/#/${isTicket ? `ticket/${menu.id}` : `qr-menu/${menu.id}`}`;
    return { success: true, menuName: menu.name, url, isTicket };
  },
};

const openQrOrdersPage: AgentActionExecutor = {
  describe: () => 'QR menü siparişler sayfasını filtreli aç',
  execute: async (args, ctx) => {
    requestQrOrdersFilters({
      searchText: str(args.searchText) || undefined,
      status: str(args.status) || undefined,
    });
    ctx.navigate('/qr-menu/orders');
    return { success: true, message: 'QR menü siparişleri sayfası açıldı; filtreler uygulandı.' };
  },
};

const createQrMenu: AgentActionExecutor = {
  describe: (args) => `Yeni QR menü oluştur: "${str(args.name)}"`,
  execute: async (args, ctx) => {
    const name = str(args.name);
    if (!name) return { success: false, error: 'name (menü adı) zorunlu.' };

    const menus = await fetchQrMenus();
    if (menus.some((m: any) => String(m.name || '').trim().toLowerCase() === name.toLowerCase())) {
      return { success: false, error: `"${name}" adında bir QR menü zaten var.` };
    }

    let branchId: string | null = null;
    let branchLabel: string | null = null;
    const branchName = str(args.branchName);
    if (branchName) {
      const resolved = await resolveBranchByName(ctx, branchName);
      if ('error' in resolved) return resolved.error;
      branchId = resolved.branchId;
      branchLabel = branchName;
    }

    const isTicket = args.isTicket === true;
    // AddQRMenuModal ile aynı payload
    const payload = {
      firebaseId: `menu_${Date.now()}`,
      name,
      description: null,
      branchId,
      branchName: branchLabel,
      createdAt: new Date().toISOString(),
      productIds: null,
      template: isTicket ? 'ticket' : 'standard',
      subMenus: null,
      settings: JSON.stringify({
        mode: isTicket ? 'ticket' : 'qr',
        appearance: {
          template: 'standard',
          primaryColor: '#1976d2',
          fontFamily: 'Roboto',
          showPrices: true,
          showImages: true,
          showDescriptions: true,
          showLogo: true,
        },
      }),
    };
    await mainApi.post('/api/QRMenu/menu-definitions', payload);
    return {
      success: true,
      menuId: payload.firebaseId,
      message: `"${name}" QR menüsü oluşturuldu.`,
      note: 'Ürünler ve görünüm QR Menü Yönetimi sayfasındaki ayarlar modalından düzenlenir; masalar addQrTable ile eklenebilir.',
    };
  },
};

const addQrTable: AgentActionExecutor = {
  describe: (args) => `"${str(args.menuName)}" menüsüne ${Number(args.tableCount) || 1} masa ekle`,
  execute: async (args, ctx) => {
    const menuName = str(args.menuName);
    if (!menuName) return { success: false, error: 'menuName zorunlu (listQrMenus).' };
    const tableCount = Math.min(100, Math.max(1, Number(args.tableCount) || 1));

    const resolved = await resolveQrMenuByName(menuName);
    if ('error' in resolved) return resolved.error;

    const companyId = requireCompanyId(ctx);
    const resp = await mainApi.post(
      `/api/Table/company/${companyId}/qrmenu/${resolved.menu.id}/create-tables`,
      { tableCount, tablePrefix: str(args.tablePrefix) || 'Masa' }
    );
    const result = resp.data;
    if (result?.success === false) {
      return { success: false, error: result?.message || 'Masa oluşturulamadı.' };
    }
    return {
      success: true,
      message: `"${resolved.menu.name}" menüsüne ${tableCount} masa eklendi.`,
    };
  },
};

const setQrOrderStatus: AgentActionExecutor = {
  describe: (args) => `${str(args.orderId)} numaralı siparişi "${str(args.status)}" yap`,
  execute: async (args) => {
    const orderId = toIntId(args.orderId);
    if (!orderId) return { success: false, error: 'orderId zorunlu (listQrOrders / listKitchenOrders).' };
    const status = str(args.status);
    const allowed = ['Hazır', 'Teslim Edildi', 'İptal'];
    if (!allowed.includes(status)) {
      return { success: false, error: `status şunlardan biri olmalı: ${allowed.join(', ')}` };
    }
    const resp = await mainApi.post(`/api/MasaSiparisi/order/${orderId}/status`, {
      siparisDurumu: status,
    });
    const result = resp.data;
    if (result?.success === false) {
      return { success: false, error: result?.message || 'Sipariş durumu güncellenemedi.' };
    }
    return { success: true, message: `Sipariş #${orderId} "${status}" olarak güncellendi.` };
  },
};

// ─── Mutfak ekranı ───────────────────────────────────────────────────────────

const listKitchenMenus: AgentActionExecutor = {
  describe: () => 'Mutfak ekranı açılabilecek menüleri listele',
  execute: async () => {
    const menus = await fetchQrMenus();
    return {
      success: true,
      count: menus.length,
      menus: menus.map((m: any) => ({ id: m.id, name: m.name, branchName: m.branchName ?? null })),
      note: 'Canlı mutfak ekranı openKitchenScreen ile menü adına göre açılır.',
    };
  },
};

const listKitchenOrders: AgentActionExecutor = {
  describe: () => 'Mutfaktaki bekleyen siparişleri listele',
  execute: async (args) => {
    // Mutfak görünümü: teslim edilmiş/iptal siparişler varsayılan dışıdır
    const result = buildOrdersResult(await fetchTableOrders(), args, str(args.status) === '');
    return {
      ...result,
      note: 'Durum değiştirmek için setQrOrderStatus (Hazır / Teslim Edildi / İptal) kullanılır.',
    };
  },
};

const openKitchenScreen: AgentActionExecutor = {
  describe: (args) => `"${str(args.menuName)}" mutfak ekranını aç`,
  execute: async (args, ctx) => {
    const menuName = str(args.menuName);
    if (!menuName) return { success: false, error: 'menuName zorunlu (listKitchenMenus).' };
    const resolved = await resolveQrMenuByName(menuName);
    if ('error' in resolved) return resolved.error;
    ctx.navigate(`/mutfak-ekrani/${resolved.menu.id}`);
    return {
      success: true,
      message: `"${resolved.menu.name}" mutfak ekranı açıldı.`,
      note: 'Bu operasyonel ekranda AI sohbeti görünmez; seçim sayfası için navigateToPage kitchen-screen-selection.',
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Kiosk (/kiosk-menu · /kiosk-products)
//
// 10 sekmeli KioskSettingsModal ilk fazda kapsam dışı — kiosk oluşturma /
// temel alan güncelleme / ürün aç-kapa / cihaz yeniden başlatma desteklenir.
// ═══════════════════════════════════════════════════════════════════════════

/** Kiosk listesini çeker ({ success, data } sarmalı). */
const fetchKiosks = async (): Promise<any[]> => {
  const resp = await mainApi.get('/api/Kiosk');
  const result = resp.data;
  return result?.success && Array.isArray(result.data) ? result.data : [];
};

const resolveKioskByName = async (
  kioskName: string
): Promise<{ kiosk: any } | { error: Record<string, any> }> => {
  const kiosks = await fetchKiosks();
  const match = findByNameTr(kiosks, (k: any) => String(k.name || ''), kioskName);
  if (match.ambiguous) {
    return { error: { success: false, error: `Birden fazla eşleşme: ${match.ambiguous.join(', ')} — tam adı belirt.` } };
  }
  if (!match.found) {
    return {
      error: {
        success: false,
        error: `"${kioskName}" adında kiosk bulunamadı.`,
        availableKiosks: kiosks.map((k: any) => k.name),
      },
    };
  }
  return { kiosk: match.found };
};

const listKiosks: AgentActionExecutor = {
  describe: () => 'Kioskları listele',
  execute: async () => {
    const kiosks = await fetchKiosks();
    return {
      success: true,
      count: kiosks.length,
      kiosks: kiosks.map((k: any) => ({
        id: k.id,
        name: k.name,
        status: k.status,
        branchName: k.branchName ?? null,
        screenSize: k.screenSize,
        screenCode: k.screenCode ?? null,
        deviceStatus: k.deviceStatus ?? null,
        activeDeviceCount: k.activeDeviceCount ?? 0,
        lastDeviceSeen: k.lastDeviceSeen ?? null,
      })),
    };
  },
};

const listKioskProducts: AgentActionExecutor = {
  describe: () => 'Kiosk ürünlerini listele',
  execute: async (args) => {
    const resp = await accountingApi.get('/api/Inventory/kiosk-items');
    const result = resp.data;
    let items: any[] = result?.success && Array.isArray(result.data) ? result.data : [];

    const search = str(args.searchText).toLocaleLowerCase('tr-TR');
    if (search) {
      items = items.filter((p: any) =>
        String(p.name || '').toLocaleLowerCase('tr-TR').includes(search)
      );
    }
    const limit = clampLimit(args.limit, 25, 100);
    return {
      success: true,
      totalCount: items.length,
      products: items.slice(0, limit).map((p: any) => ({
        id: p.id,
        name: p.name,
        salePrice: Number(p.salePrice) || 0,
        unit: p.unit,
        categoryName: p.categoryName ?? null,
        isActive: p.isActive !== false,
      })),
    };
  },
};

const createKiosk: AgentActionExecutor = {
  describe: (args) => `Yeni kiosk oluştur: "${str(args.name)}"`,
  execute: async (args, ctx) => {
    const name = str(args.name);
    if (!name) return { success: false, error: 'name (kiosk adı) zorunlu.' };

    const existing = await fetchKiosks();
    if (existing.some((k: any) => String(k.name || '').trim().toLowerCase() === name.toLowerCase())) {
      return { success: false, error: `"${name}" adında bir kiosk zaten var.` };
    }

    let branchId: string | null = null;
    let branchLabel: string | null = null;
    const branchName = str(args.branchName);
    if (branchName) {
      const resolved = await resolveBranchByName(ctx, branchName);
      if ('error' in resolved) return resolved.error;
      branchId = resolved.branchId;
      branchLabel = branchName;
    }

    const screenSize = str(args.screenSize) || '24inch';
    const status = str(args.status) || 'active';
    const resp = await mainApi.post('/api/Kiosk', {
      name,
      description: str(args.description) || null,
      branchId,
      branchName: branchLabel,
      status,
      screenSize,
      deviceId: null,
      settings: null,
    });
    const result = resp.data;
    if (result?.success === false) {
      return { success: false, error: result?.message || 'Kiosk oluşturulamadı.' };
    }
    return {
      success: true,
      kioskId: result?.data?.id,
      message: `"${name}" kiosku oluşturuldu (${screenSize}, durum: ${status}).`,
    };
  },
};

const updateKiosk: AgentActionExecutor = {
  describe: (args) => `"${str(args.kioskName)}" kioskunu güncelle`,
  execute: async (args, ctx) => {
    const kioskName = str(args.kioskName);
    if (!kioskName) return { success: false, error: 'kioskName zorunlu (listKiosks).' };
    const resolved = await resolveKioskByName(kioskName);
    if ('error' in resolved) return resolved.error;
    const kiosk = resolved.kiosk;

    let branchId: string | null = kiosk.branchId ?? null;
    let branchLabel: string | null = kiosk.branchName ?? null;
    const branchName = str(args.branchName);
    if (branchName) {
      const rb = await resolveBranchByName(ctx, branchName);
      if ('error' in rb) return rb.error;
      branchId = rb.branchId;
      branchLabel = branchName;
    }

    // Update endpoint'i tam payload bekler — değişmeyen alanlar mevcut değerle gönderilir
    const payload = {
      name: str(args.newName) || kiosk.name,
      description: args.description !== undefined ? str(args.description) || null : kiosk.description ?? null,
      branchId,
      branchName: branchLabel,
      status: str(args.status) || kiosk.status,
      screenSize: str(args.screenSize) || kiosk.screenSize,
      deviceId: kiosk.deviceId ?? null,
      settings: kiosk.settings ?? null,
    };
    const resp = await mainApi.post(`/api/Kiosk/${kiosk.id}/update`, payload);
    const result = resp.data;
    if (result?.success === false) {
      return { success: false, error: result?.message || 'Kiosk güncellenemedi.' };
    }
    return { success: true, message: `"${kiosk.name}" kiosku güncellendi.`, kiosk: payload };
  },
};

const toggleKioskProduct: AgentActionExecutor = {
  describe: (args) =>
    `"${str(args.productName)}" ürününü kioskta ${args.enable === false ? 'kapat' : 'aç'}`,
  execute: async (args) => {
    const productName = str(args.productName);
    if (!productName) return { success: false, error: 'productName zorunlu.' };
    if (typeof args.enable !== 'boolean') {
      return { success: false, error: 'enable (true=kioskta sat, false=kiosktan kaldır) zorunlu.' };
    }

    // Önce kiosk listesinde ara (kapatma senaryosu); yoksa tüm stokta ara (açma senaryosu)
    let productId: string | null = null;
    let foundName = productName;

    const kioskResp = await accountingApi.get('/api/Inventory/kiosk-items');
    const kioskItems: any[] =
      kioskResp.data?.success && Array.isArray(kioskResp.data.data) ? kioskResp.data.data : [];
    const inKiosk = findByNameTr(kioskItems, (p: any) => String(p.name || ''), productName);
    if (inKiosk.ambiguous) {
      return { success: false, error: `Birden fazla eşleşme: ${inKiosk.ambiguous.join(', ')} — tam adı belirt.` };
    }
    if (inKiosk.found) {
      productId = String(inKiosk.found.id);
      foundName = String(inKiosk.found.name);
    } else {
      const searchResp = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEMS_PAGED, {
        pageNumber: 1,
        pageSize: 10,
        searchTerm: productName,
        isActive: true,
        excludeVariantChildren: true,
      });
      const items: any[] = searchResp.data?.items || searchResp.data?.Items || [];
      const match = findByNameTr(items, (p: any) => String(p.name ?? p.Name ?? ''), productName);
      if (match.ambiguous) {
        return { success: false, error: `Birden fazla eşleşme: ${match.ambiguous.join(', ')} — tam adı belirt.` };
      }
      if (!match.found) {
        return { success: false, error: `"${productName}" adında ürün bulunamadı (searchProduct ile arayabilirsin).` };
      }
      productId = String(match.found.id ?? match.found.Id);
      foundName = String(match.found.name ?? match.found.Name);
    }

    const resp = await accountingApi.post(`/api/Inventory/${productId}/toggle-kiosk`, {
      enable: args.enable,
    });
    const result = resp.data;
    if (result?.success === false) {
      return { success: false, error: result?.message || 'Kiosk ürün durumu değiştirilemedi.' };
    }
    return {
      success: true,
      message: `"${foundName}" ürünü kioskta ${args.enable ? 'satışa açıldı' : 'kapatıldı'}.`,
    };
  },
};

const restartKioskDevices: AgentActionExecutor = {
  describe: (args) =>
    `Kiosk cihazlarını yeniden başlat${str(args.branchName) ? ` (${str(args.branchName)})` : ''}`,
  execute: async (args, ctx) => {
    let branchId: string | null = null;
    const branchName = str(args.branchName);
    if (branchName) {
      const resolved = await resolveBranchByName(ctx, branchName);
      if ('error' in resolved) return resolved.error;
      branchId = resolved.branchId;
    }
    const resp = await remoteApi.post('/api/kiosk-management/restart-by-branch', {
      assignedCompanyId: requireCompanyId(ctx),
      assignedBranchId: branchId,
    });
    const result = resp.data;
    return {
      success: result?.success !== false,
      message: result?.message || 'Yeniden başlatma isteği gönderildi.',
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Kurye (/kurye)
//
// Kurye kaydetme/silme kapsam dışı (rol ve kimlik değiştiren çok adımlı
// işlemler); sipariş ayarları ve takip ekranı desteklenir.
// ═══════════════════════════════════════════════════════════════════════════

/** Kurye listesini çeker ({ success, data } sarmalı). */
const fetchCouriers = async (): Promise<any[]> => {
  const resp = await mainApi.get('/api/Couriers');
  const result = resp.data;
  return result?.success && Array.isArray(result.data) ? result.data : [];
};

const resolveCourierByName = async (
  courierName: string
): Promise<{ courier: any } | { error: Record<string, any> }> => {
  const couriers = await fetchCouriers();
  const match = findByNameTr(couriers, (c: any) => String(c.name || ''), courierName);
  if (match.ambiguous) {
    return { error: { success: false, error: `Birden fazla eşleşme: ${match.ambiguous.join(', ')} — tam adı belirt.` } };
  }
  if (!match.found) {
    return {
      error: {
        success: false,
        error: `"${courierName}" adında kurye bulunamadı.`,
        availableCouriers: couriers.map((c: any) => c.name),
      },
    };
  }
  return { courier: match.found };
};

/** Virgüllü/dizili branchIds değerini şube adlarına çevirir. */
const branchIdsToNames = (branchIds: any, branches: any[]): string[] => {
  const ids: string[] = Array.isArray(branchIds)
    ? branchIds.map((b: any) => String(b).trim())
    : String(branchIds || '').split(',').map((s) => s.trim()).filter(Boolean);
  return ids.map((id) => String(branches.find((b: any) => String(b.id) === id)?.name || id));
};

const listCouriers: AgentActionExecutor = {
  describe: () => 'Kuryeleri listele',
  execute: async (_args, ctx) => {
    const [couriers, branches] = await Promise.all([
      fetchCouriers(),
      getBranches(requireCompanyId(ctx)),
    ]);
    return {
      success: true,
      count: couriers.length,
      couriers: couriers.map((c: any) => ({
        name: c.name,
        status: c.status,
        totalDeliveries: c.totalDeliveries ?? 0,
        completedToday: c.completedToday ?? 0,
        rating: c.rating ?? null,
        branches: branchIdsToNames(c.branchIds, branches),
        restaurantOrders: c.restaurantOrders ?? true,
        ecommerceOrders: c.ecommerceOrders ?? true,
        isActive: c.isActive ?? true,
      })),
    };
  },
};

const getCourierOrderSettings: AgentActionExecutor = {
  describe: (args) => `"${str(args.courierName)}" kuryesinin sipariş ayarlarını getir`,
  execute: async (args, ctx) => {
    const courierName = str(args.courierName);
    if (!courierName) return { success: false, error: 'courierName zorunlu (listCouriers).' };
    const resolved = await resolveCourierByName(courierName);
    if ('error' in resolved) return resolved.error;

    const companyId = requireCompanyId(ctx);
    const resp = await mainApi.get(
      `/api/couriers/firebase/${resolved.courier.firebaseUid}?companyId=${companyId}`
    );
    const data = resp.data?.success ? resp.data.data : null;
    const branches = await getBranches(companyId);
    return {
      success: true,
      courierName: resolved.courier.name,
      branches: branchIdsToNames(data?.branchIds ?? resolved.courier.branchIds, branches),
      restaurantOrders: data?.restaurantOrders ?? resolved.courier.restaurantOrders ?? true,
      ecommerceOrders: data?.ecommerceOrders ?? resolved.courier.ecommerceOrders ?? true,
      packageOrders: data?.packageOrders ?? true,
    };
  },
};

const updateCourierOrderSettings: AgentActionExecutor = {
  describe: (args) => `"${str(args.courierName)}" kuryesinin sipariş ayarlarını güncelle`,
  execute: async (args, ctx) => {
    const courierName = str(args.courierName);
    if (!courierName) return { success: false, error: 'courierName zorunlu (listCouriers).' };
    const resolved = await resolveCourierByName(courierName);
    if ('error' in resolved) return resolved.error;
    const courier = resolved.courier;

    const companyId = requireCompanyId(ctx);

    // Mevcut ayarları çek (verilmeyen alanlar korunur)
    let current: any = null;
    try {
      const resp = await mainApi.get(`/api/couriers/firebase/${courier.firebaseUid}?companyId=${companyId}`);
      if (resp.data?.success) current = resp.data.data;
    } catch {
      // mevcut ayar yoksa kurye listesindeki değerlerle devam edilir
    }

    // Şubeler: virgüllü ad listesi → id listesi (verilirse mevcut seçimin YERİNE geçer)
    let branchIds: string[] = Array.isArray(current?.branchIds)
      ? current.branchIds
      : String(current?.branchIds ?? courier.branchIds ?? '')
          .split(',')
          .map((s: string) => s.trim())
          .filter(Boolean);
    const branchNames = str(args.branchNames);
    if (branchNames) {
      const wanted = branchNames.split(',').map((s) => s.trim()).filter(Boolean);
      const ids: string[] = [];
      for (const name of wanted) {
        const rb = await resolveBranchByName(ctx, name);
        if ('error' in rb) return rb.error;
        ids.push(rb.branchId);
      }
      branchIds = ids;
    }

    const restaurantOrders =
      typeof args.restaurantOrders === 'boolean'
        ? args.restaurantOrders
        : current?.restaurantOrders ?? courier.restaurantOrders ?? true;
    const ecommerceOrders =
      typeof args.ecommerceOrders === 'boolean'
        ? args.ecommerceOrders
        : current?.ecommerceOrders ?? courier.ecommerceOrders ?? true;
    const packageOrders =
      typeof args.packageOrders === 'boolean' ? args.packageOrders : current?.packageOrders ?? true;

    // CourierOrderSettingsModal.handleSave ile aynı payload
    const resp = await mainApi.post('/api/couriers/update-order-settings', {
      firebaseUid: courier.firebaseUid,
      companyId,
      branchIds,
      restaurantOrders,
      ecommerceOrders,
      packageOrders,
      orderSettings: { branchIds, restaurantOrders, ecommerceOrders, packageOrders },
    });
    const result = resp.data;
    if (result?.success === false) {
      return { success: false, error: result?.message || 'Sipariş ayarları güncellenemedi.' };
    }
    const branches = await getBranches(companyId);
    return {
      success: true,
      message: `"${courier.name}" kuryesinin sipariş ayarları güncellendi.`,
      branches: branchIdsToNames(branchIds, branches),
      restaurantOrders,
      ecommerceOrders,
      packageOrders,
    };
  },
};

const openCourierTracking: AgentActionExecutor = {
  describe: () => 'Kurye takip ekranını aç',
  execute: async (args, ctx) => {
    requestCourierTracking({
      searchText: str(args.searchText) || undefined,
      status: str(args.status) || undefined,
      dateFilter: str(args.dateFilter) || undefined,
    });
    ctx.navigate('/kurye');
    return { success: true, message: 'Kurye takip ekranı açıldı; filtreler uygulandı.' };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Entegrasyonlar (/entegrasyonlar)
//
// API anahtarı ekleme/silme ajana verilmez; anahtar/secret değerleri
// yanıtlarda ASLA döndürülmez (yalnızca hasCredentials bayrağı).
// ═══════════════════════════════════════════════════════════════════════════

/** Platform entegrasyon listesini çeker (anahtarlar maskeli döner). */
const fetchPlatformIntegrations = async (ctx: AgentActionContext): Promise<any[]> => {
  const companyId = requireCompanyId(ctx);
  const resp = await mainApi.get(
    `/api/platformintegration/list?companyId=${encodeURIComponent(companyId)}`
  );
  const result = resp.data;
  return result?.success && Array.isArray(result.data) ? result.data : [];
};

const mapPlatformIntegration = (i: any) => ({
  id: i.id,
  platform: i.platform,
  isEnabled: i.isEnabled === true,
  isConnected: i.isConnected === true,
  lastSyncAt: i.lastSyncAt ?? null,
  errorCount: i.errorCount ?? 0,
  // Güvenlik: apiKey/apiSecret değerleri asla ajana dönmez
  hasCredentials: Boolean(i.apiKey && i.apiSecret),
});

const listPlatformIntegrations: AgentActionExecutor = {
  describe: () => 'Platform entegrasyonlarını listele',
  execute: async (_args, ctx) => {
    const items = await fetchPlatformIntegrations(ctx);
    return { success: true, count: items.length, integrations: items.map(mapPlatformIntegration) };
  },
};

const listPaymentIntegrations: AgentActionExecutor = {
  describe: () => 'Ödeme sağlayıcı entegrasyonlarını listele',
  execute: async () => {
    const resp = await mainApi.get('/api/paymentproviderintegration/list?page=1&pageSize=100');
    const result = resp.data;
    const raw: any[] = result?.Data || result?.data || [];
    return {
      success: true,
      count: raw.length,
      integrations: raw.map((x: any) => ({
        provider: String(x.Provider ?? x.provider ?? '').toLowerCase(),
        isEnabled: Boolean(x.IsEnabled ?? x.isEnabled),
        isTestMode: Boolean(x.IsTestMode ?? x.isTestMode),
        is3DEnabled: Boolean(x.Is3DEnabled ?? x.is3DEnabled),
        branchId: x.BranchId ?? x.branchId ?? null,
        // Güvenlik: merchant anahtarları ajana dönmez
      })),
    };
  },
};

const getIntegrationHealth: AgentActionExecutor = {
  describe: () => 'Entegrasyon sağlık durumunu kontrol et',
  execute: async (_args, ctx) => {
    const items = await fetchPlatformIntegrations(ctx);
    const enabled = items.filter((i: any) => i.isEnabled === true);
    const issues = enabled
      .filter((i: any) => i.isConnected !== true || (i.errorCount ?? 0) > 0)
      .map(mapPlatformIntegration);
    return {
      success: true,
      totalCount: items.length,
      enabledCount: enabled.length,
      connectedCount: enabled.filter((i: any) => i.isConnected === true).length,
      issues,
      healthy: issues.length === 0,
    };
  },
};

/** Bağlantı testi olan platformlar → ilgili API istemcisi. */
const INTEGRATION_TEST_APIS: Record<string, any> = {
  trendyol: trendyolMpApi,
  n11: n11Api,
  hepsiburada: hepsiburadaApi,
  pazarama: pazaramaApi,
  pttavm: pttavmApi,
  hepsijet: hepsijetApi,
};

const testIntegrationConnection: AgentActionExecutor = {
  describe: (args) => `${str(args.platform)} bağlantı testini çalıştır`,
  execute: async (args, ctx) => {
    const platform = str(args.platform).toLowerCase();
    const testApi = INTEGRATION_TEST_APIS[platform];
    if (!testApi) {
      return {
        success: false,
        error: `platform şunlardan biri olmalı: ${Object.keys(INTEGRATION_TEST_APIS).join(', ')}`,
      };
    }

    const integrations = await fetchPlatformIntegrations(ctx);
    const record = integrations.find(
      (i: any) => String(i.platform || '').toLowerCase() === platform
    );
    if (!record) {
      return { success: false, error: `${platform} entegrasyonu tanımlı değil — önce Entegrasyonlar sayfasından eklenmeli.` };
    }

    let connected = false;
    let message: string | null = null;
    try {
      const resp = await testApi.post(`/api/${platform}/integration/test`);
      const payload = resp.data;
      connected = Boolean(payload?.Success ?? payload?.success);
      message = payload?.Message || payload?.message || null;
    } catch (err: any) {
      if (err?.response?.status === 404) {
        return { success: false, error: `${platform} test endpoint'i henüz mevcut değil.` };
      }
      connected = false;
      message = err?.response?.data?.Message || err?.response?.data?.message || err?.message || null;
    }

    // Test sonucunu kaydet (UI'daki durum rozeti buna göre güncellenir) — hepsijet hariç (UI ile aynı)
    if (platform !== 'hepsijet') {
      try {
        await mainApi.post('/api/platformintegration/update-connection', {
          id: record.id,
          isConnected: connected,
          errorMessage: connected ? null : message,
        });
      } catch {
        // durum kaydı kritik değil, test sonucu yine döner
      }
    }

    return {
      success: true,
      platform,
      connected,
      message: message || (connected ? 'Bağlantı testi başarılı.' : 'Bağlantı testi başarısız.'),
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Resim Kütüphanesi (/resim-kutuphanesi)
//
// Dosya yükleme tarayıcı güvenliği nedeniyle otomatikleştirilemez; silme
// kapsam dışı. Klasör oluşturma, taşıma ve yeniden adlandırma geri alınabilir
// işlemler olduğundan desteklenir.
// ═══════════════════════════════════════════════════════════════════════════

const resolveImageFolderByName = async (
  companyId: string,
  folderName: string
): Promise<{ folder: any } | { error: Record<string, any> }> => {
  const folders = await ImageLibraryFolderService.getAllFolders(companyId);
  const match = findByNameTr(folders, (f: any) => String(f.name || ''), folderName);
  if (match.ambiguous) {
    return { error: { success: false, error: `Birden fazla eşleşme: ${match.ambiguous.join(', ')} — tam adı belirt.` } };
  }
  if (!match.found) {
    return {
      error: {
        success: false,
        error: `"${folderName}" adında klasör bulunamadı.`,
        availableFolders: folders.map((f: any) => f.name),
      },
    };
  }
  return { folder: match.found };
};

const listImageFolders: AgentActionExecutor = {
  describe: () => 'Resim kütüphanesi klasörlerini listele',
  execute: async (_args, ctx) => {
    const folders = await ImageLibraryFolderService.getAllFolders(requireCompanyId(ctx));
    return {
      success: true,
      count: folders.length,
      folders: folders.map((f) => ({
        name: f.name,
        imageCount: f.imageCount ?? 0,
        color: f.color ?? null,
      })),
    };
  },
};

const listImages: AgentActionExecutor = {
  describe: (args) =>
    `Resimleri listele${str(args.folderName) ? ` ("${str(args.folderName)}" klasörü)` : ''}`,
  execute: async (args, ctx) => {
    const companyId = requireCompanyId(ctx);
    let images;
    let folderLabel: string | null = null;

    const folderName = str(args.folderName);
    if (folderName) {
      const resolved = await resolveImageFolderByName(companyId, folderName);
      if ('error' in resolved) return resolved.error;
      folderLabel = resolved.folder.name;
      images = await ImageLibraryImageService.getImagesByFolder(companyId, resolved.folder.id);
    } else {
      images = await ImageLibraryImageService.getAllImages(companyId);
    }

    const search = str(args.searchText).toLocaleLowerCase('tr-TR');
    if (search) {
      images = images.filter((i) =>
        String(i.name || '').toLocaleLowerCase('tr-TR').includes(search)
      );
    }
    const limit = clampLimit(args.limit, 25, 100);
    return {
      success: true,
      folder: folderLabel,
      totalCount: images.length,
      images: images.slice(0, limit).map((i) => ({
        name: i.name,
        mediaType: i.mediaType ?? i.type ?? null,
        url: i.url,
        createdAt: i.createdAt,
      })),
    };
  },
};

const createImageFolder: AgentActionExecutor = {
  describe: (args) => `Yeni klasör oluştur: "${str(args.name)}"`,
  execute: async (args, ctx) => {
    const name = str(args.name);
    if (!name) return { success: false, error: 'name (klasör adı) zorunlu.' };
    const companyId = requireCompanyId(ctx);

    const folders = await ImageLibraryFolderService.getAllFolders(companyId);
    if (folders.some((f) => String(f.name || '').trim().toLowerCase() === name.toLowerCase())) {
      return { success: false, error: `"${name}" adında bir klasör zaten var.` };
    }

    const created = await ImageLibraryFolderService.createFolder({
      companyId,
      name,
      color: str(args.color) || '#2196F3',
      createdBy: actorEmail(ctx),
    });
    return { success: true, folderId: created?.id, message: `"${name}" klasörü oluşturuldu.` };
  },
};

/** Resmi ada göre çözer (tüm kütüphanede arar). */
const resolveImageByName = async (
  companyId: string,
  imageName: string
): Promise<{ image: any } | { error: Record<string, any> }> => {
  const images = await ImageLibraryImageService.getAllImages(companyId);
  const match = findByNameTr(images, (i: any) => String(i.name || ''), imageName);
  if (match.ambiguous) {
    return {
      error: {
        success: false,
        error: `Birden fazla eşleşme: ${match.ambiguous.slice(0, 10).join(', ')} — tam adı belirt.`,
      },
    };
  }
  if (!match.found) {
    return { error: { success: false, error: `"${imageName}" adında resim bulunamadı (listImages ile arayabilirsin).` } };
  }
  return { image: match.found };
};

const moveImage: AgentActionExecutor = {
  describe: (args) =>
    `"${str(args.imageName)}" resmini ${str(args.targetFolderName) ? `"${str(args.targetFolderName)}" klasörüne` : 'ana dizine'} taşı`,
  execute: async (args, ctx) => {
    const imageName = str(args.imageName);
    if (!imageName) return { success: false, error: 'imageName zorunlu (listImages).' };
    const companyId = requireCompanyId(ctx);

    const resolvedImage = await resolveImageByName(companyId, imageName);
    if ('error' in resolvedImage) return resolvedImage.error;
    const image = resolvedImage.image;

    let targetFolderId: string | null = null;
    let targetLabel = 'ana dizin';
    const targetFolderName = str(args.targetFolderName);
    if (targetFolderName) {
      const resolvedFolder = await resolveImageFolderByName(companyId, targetFolderName);
      if ('error' in resolvedFolder) return resolvedFolder.error;
      targetFolderId = resolvedFolder.folder.id;
      targetLabel = `"${resolvedFolder.folder.name}" klasörü`;
    }

    await ImageLibraryImageService.updateImage(companyId, image.id, {
      name: image.name,
      folderId: targetFolderId as any, // null = ana dizin
    });
    return { success: true, message: `"${image.name}" resmi ${targetLabel} konumuna taşındı.` };
  },
};

const renameImage: AgentActionExecutor = {
  describe: (args) => `"${str(args.imageName)}" resmini "${str(args.newName)}" olarak adlandır`,
  execute: async (args, ctx) => {
    const imageName = str(args.imageName);
    const newName = str(args.newName);
    if (!imageName || !newName) return { success: false, error: 'imageName ve newName zorunlu.' };
    const companyId = requireCompanyId(ctx);

    const resolvedImage = await resolveImageByName(companyId, imageName);
    if ('error' in resolvedImage) return resolvedImage.error;
    const image = resolvedImage.image;

    await ImageLibraryImageService.updateImage(companyId, image.id, {
      name: newName,
      folderId: (image.folderId ?? null) as any, // klasör korunur
    });
    return { success: true, message: `Resim "${image.name}" → "${newName}" olarak yeniden adlandırıldı.` };
  },
};

const openImageLibrary: AgentActionExecutor = {
  describe: (args) =>
    `Resim kütüphanesini aç${str(args.folderName) ? ` ("${str(args.folderName)}" klasörü)` : ''}`,
  execute: async (args, ctx) => {
    requestImageLibrary({
      folderName: str(args.folderName) || undefined,
      filterType: str(args.filterType) || undefined,
    });
    ctx.navigate('/resim-kutuphanesi');
    return { success: true, message: 'Resim kütüphanesi açıldı.' };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// İnsan Kaynakları — Personel (/personnel)
//
// Kimlik: personelin kanonik kimliği Firebase UID'dir; tüm eylemler ad → uid
// çözümlemesi yapar (e-posta ile de aranabilir). Yeni hesap AÇMA kapsam dışı
// (şifrenin chat üzerinden geçmesi güvenlik riski — UI'daki modal kullanılır).
// Yetki YAZMA da ilk fazda kapsam dışı (screenType/screenId şeması karmaşık).
// ═══════════════════════════════════════════════════════════════════════════

const fetchPersonnelRows = async (
  ctx: AgentActionContext,
  includeDeleted = false
): Promise<personnelApi.Personnel[]> =>
  personnelApi.getPersonnel(requireCompanyId(ctx), { includeDeleted });

const personnelDisplayName = (p: personnelApi.Personnel): string =>
  str(p.name) || `${str(p.firstName)} ${str(p.lastName)}`.trim() || str(p.email);

/** Personeli ada (veya e-postaya) göre çözer; bulunamaz/muğlaksa hata nesnesi döner. */
const resolvePersonnelByName = async (
  ctx: AgentActionContext,
  wanted: string,
  includeDeleted = false
): Promise<{ person: personnelApi.Personnel } | { error: Record<string, any> }> => {
  const rows = await fetchPersonnelRows(ctx, includeDeleted);
  if (wanted.includes('@')) {
    const byEmail = rows.find((r) => str(r.email).toLowerCase() === wanted.toLowerCase());
    if (byEmail) return { person: byEmail };
  }
  const match = findByNameTr(rows, personnelDisplayName, wanted);
  if (match.ambiguous) {
    return { error: { success: false, error: `Birden fazla eşleşme: ${match.ambiguous.join(', ')} — tam adı veya e-postayı belirt.` } };
  }
  if (!match.found) {
    return {
      error: {
        success: false,
        error: `"${wanted}" adında personel bulunamadı.`,
        availablePersonnel: rows.slice(0, 30).map(personnelDisplayName),
      },
    };
  }
  return { person: match.found };
};

/** uid → görünen ad haritası (rapor eylemlerinde ad çözümü için). */
const buildPersonnelNameMap = async (ctx: AgentActionContext): Promise<Record<string, string>> => {
  const rows = await fetchPersonnelRows(ctx, true);
  const map: Record<string, string> = {};
  for (const p of rows) map[p.id] = personnelDisplayName(p);
  return map;
};

/** Departman adını kayda çözer. */
const resolveDepartmentByName = async (
  ctx: AgentActionContext,
  wanted: string
): Promise<{ department: personnelApi.Department } | { error: Record<string, any> }> => {
  const departments = await personnelApi.getDepartments(requireCompanyId(ctx));
  const match = findByNameTr(departments, (d) => str(d.name), wanted);
  if (match.ambiguous) {
    return { error: { success: false, error: `Birden fazla eşleşme: ${match.ambiguous.join(', ')} — tam adı belirt.` } };
  }
  if (!match.found) {
    return {
      error: {
        success: false,
        error: `"${wanted}" adında departman bulunamadı.`,
        availableDepartments: departments.map((d) => d.name),
      },
    };
  }
  return { department: match.found };
};

const listPersonnel: AgentActionExecutor = {
  describe: () => 'Personelleri listele',
  execute: async (args, ctx) => {
    const [rows, branches, departments] = await Promise.all([
      fetchPersonnelRows(ctx, args.includeDeleted === true),
      getBranches(requireCompanyId(ctx)),
      personnelApi.getDepartments(requireCompanyId(ctx)).catch(() => [] as personnelApi.Department[]),
    ]);
    const branchNameOf = (id: any) =>
      id ? String(branches.find((b: any) => String(b.id) === String(id))?.name || id) : null;
    const departmentNameOf = (id: any) =>
      id ? String(departments.find((d) => String(d.id) === String(id))?.name || id) : null;

    let filtered = rows;
    const search = str(args.searchText).toLocaleLowerCase('tr-TR');
    if (search) {
      filtered = filtered.filter(
        (p) =>
          personnelDisplayName(p).toLocaleLowerCase('tr-TR').includes(search) ||
          str(p.email).toLocaleLowerCase('tr-TR').includes(search)
      );
    }
    const role = str(args.role).toLowerCase();
    if (role) filtered = filtered.filter((p) => str(p.role).toLowerCase() === role);
    const branchName = str(args.branchName);
    if (branchName) {
      const resolved = await resolveBranchByName(ctx, branchName);
      if ('error' in resolved) return resolved.error;
      filtered = filtered.filter((p) => String(p.branchesId || '') === resolved.branchId);
    }

    const limit = clampLimit(args.limit, 25, 100);
    return {
      success: true,
      totalCount: filtered.length,
      personnel: filtered.slice(0, limit).map((p) => ({
        name: personnelDisplayName(p),
        email: p.email ?? null,
        phone: p.phone ?? null,
        role: p.role ?? null,
        branchName: branchNameOf(p.branchesId),
        departmentName: departmentNameOf(p.departmentId),
        jobTitle: p.jobTitle ?? null,
        isActive: p.isActive !== false,
        isDeleted: p.isDeleted === true,
      })),
    };
  },
};

const getPersonnelDetails: AgentActionExecutor = {
  describe: (args) => `"${str(args.personnelName)}" personelinin detaylarını getir`,
  execute: async (args, ctx) => {
    const personnelName = str(args.personnelName);
    if (!personnelName) return { success: false, error: 'personnelName zorunlu (listPersonnel).' };
    const resolved = await resolvePersonnelByName(ctx, personnelName, true);
    if ('error' in resolved) return resolved.error;

    // Liste satırı HR detay alanlarını içermeyebilir — tekil uçtan tam kaydı çek
    const full = (await personnelApi.getPersonnelByUid(resolved.person.id)) ?? resolved.person;
    const [branches, departments] = await Promise.all([
      getBranches(requireCompanyId(ctx)),
      personnelApi.getDepartments(requireCompanyId(ctx)).catch(() => [] as personnelApi.Department[]),
    ]);
    return {
      success: true,
      personnel: {
        name: personnelDisplayName(full),
        firstName: full.firstName ?? null,
        lastName: full.lastName ?? null,
        email: full.email ?? null,
        phone: full.phone ?? null,
        role: full.role ?? null,
        branchName: full.branchesId
          ? String(branches.find((b: any) => String(b.id) === String(full.branchesId))?.name || full.branchesId)
          : null,
        departmentName: full.departmentId
          ? String(departments.find((d) => String(d.id) === String(full.departmentId))?.name || full.departmentId)
          : null,
        jobTitle: full.jobTitle ?? null,
        startDate: full.startDate ?? null,
        birthDate: full.birthDate ?? null,
        address: full.address ?? null,
        emergencyContactName: full.emergencyContactName ?? null,
        emergencyContactPhone: full.emergencyContactPhone ?? null,
        notes: full.notes ?? null,
        isActive: full.isActive !== false,
        isDeleted: full.isDeleted === true,
      },
    };
  },
};

const listDepartments: AgentActionExecutor = {
  describe: () => 'Departmanları listele',
  execute: async (_args, ctx) => {
    const departments = await personnelApi.getDepartments(requireCompanyId(ctx));
    return {
      success: true,
      count: departments.length,
      departments: departments.map((d) => ({
        name: d.name,
        description: d.description ?? null,
        head: d.head ?? null,
      })),
    };
  },
};

const getPersonnelBalance: AgentActionExecutor = {
  describe: (args) => `"${str(args.personnelName)}" personelinin bakiyesini getir`,
  execute: async (args, ctx) => {
    const personnelName = str(args.personnelName);
    if (!personnelName) return { success: false, error: 'personnelName zorunlu (listPersonnel).' };
    const resolved = await resolvePersonnelByName(ctx, personnelName);
    if ('error' in resolved) return resolved.error;

    const balances = await personnelTxApi.getPersonnelBalances(resolved.person.id);
    return {
      success: true,
      personnelName: personnelDisplayName(resolved.person),
      balances,
      note: 'Pozitif bakiye = personel şirkete borçlu (şirketin alacağı); negatif = şirketin personele borcu (ödenmemiş maaş/prim vb.).',
    };
  },
};

const listPersonnelTransactions: AgentActionExecutor = {
  describe: (args) => `"${str(args.personnelName)}" personelinin hareketlerini listele`,
  execute: async (args, ctx) => {
    const personnelName = str(args.personnelName);
    if (!personnelName) return { success: false, error: 'personnelName zorunlu (listPersonnel).' };
    const resolved = await resolvePersonnelByName(ctx, personnelName);
    if ('error' in resolved) return resolved.error;

    const limit = clampLimit(args.limit, 20, 100);
    const result = await personnelTxApi.getPersonnelTransactions(resolved.person.id, 1, limit);
    return {
      success: true,
      personnelName: personnelDisplayName(resolved.person),
      totalCount: result.totalCount,
      transactions: result.data.map((t) => ({
        id: t.id,
        transactionType: t.transactionType,
        kind: t.kind,
        paymentMethod: t.paymentMethod,
        isAccrual: t.isAccrual,
        isPaid: t.isPaid,
        amount: t.amount,
        currency: t.currency,
        description: t.description,
        transactionDate: t.transactionDate,
        source: t.cashboxName || t.bankName || null,
        performedBy: t.performedBy,
      })),
    };
  },
};

const getPersonnelPermissions: AgentActionExecutor = {
  describe: (args) => `"${str(args.personnelName)}" personelinin ekran yetkilerini getir`,
  execute: async (args, ctx) => {
    const personnelName = str(args.personnelName);
    if (!personnelName) return { success: false, error: 'personnelName zorunlu (listPersonnel).' };
    const resolved = await resolvePersonnelByName(ctx, personnelName);
    if ('error' in resolved) return resolved.error;

    const permissions = await personnelApi.getScreenPermissions(resolved.person.id);
    return {
      success: true,
      personnelName: personnelDisplayName(resolved.person),
      count: permissions.length,
      permissions: permissions.map((p) => ({
        screenType: p.screenType,
        screenId: p.screenId ?? null,
        isEnabled: p.isEnabled,
        settings: typeof p.settings === 'string' ? p.settings : p.settings ?? null,
      })),
      note: 'Yetki değişikliği bu fazda ajana verilmez; Personeller sayfasından yapılır.',
    };
  },
};

const openPersonnelPage: AgentActionExecutor = {
  describe: () => 'Personeller sayfasını filtreli aç',
  execute: async (args, ctx) => {
    let branchId: string | null | undefined;
    const branchName = str(args.branchName);
    if (branchName) {
      const resolved = await resolveBranchByName(ctx, branchName);
      if ('error' in resolved) return resolved.error;
      branchId = resolved.branchId;
    }
    let departmentId: string | null | undefined;
    const departmentName = str(args.departmentName);
    if (departmentName) {
      const resolved = await resolveDepartmentByName(ctx, departmentName);
      if ('error' in resolved) return resolved.error;
      departmentId = String(resolved.department.id);
    }

    requestPersonnelFilters({
      searchText: str(args.searchText) || undefined,
      branchId,
      role: str(args.role) || undefined,
      status: str(args.status) || undefined,
      departmentId,
      showDeleted: typeof args.showDeleted === 'boolean' ? args.showDeleted : undefined,
    });
    ctx.navigate('/personnel');
    return { success: true, message: 'Personeller sayfası açıldı; filtreler uygulandı.' };
  },
};

const updatePersonnel: AgentActionExecutor = {
  describe: (args) => `"${str(args.personnelName)}" personelini güncelle`,
  execute: async (args, ctx) => {
    const personnelName = str(args.personnelName);
    if (!personnelName) return { success: false, error: 'personnelName zorunlu (listPersonnel).' };
    const resolved = await resolvePersonnelByName(ctx, personnelName);
    if ('error' in resolved) return resolved.error;

    const input: personnelApi.PersonnelUpdateInput = {};
    const textKeys: Array<keyof personnelApi.PersonnelUpdateInput> = [
      'firstName',
      'lastName',
      'phone',
      'jobTitle',
      'startDate',
      'birthDate',
      'address',
      'emergencyContactName',
      'emergencyContactPhone',
      'notes',
    ];
    for (const key of textKeys) {
      // "" gönderilirse backend alanı temizler — bilinçli olarak str() ile geçirilir
      if (args[key] !== undefined && args[key] !== null) (input as any)[key] = str(args[key]);
    }
    const role = str(args.role).toLowerCase();
    if (role) {
      if (!['manager', 'employee'].includes(role)) {
        return { success: false, error: "role yalnızca 'manager' veya 'employee' olabilir (admin ataması ajana kapalı)." };
      }
      input.role = role;
    }
    const departmentName = str(args.departmentName);
    if (departmentName) {
      const dep = await resolveDepartmentByName(ctx, departmentName);
      if ('error' in dep) return dep.error;
      input.departmentId = Number(dep.department.id) || null;
    } else if (args.clearDepartment === true) {
      input.departmentId = 0; // 0 = departmanı temizle (backend kuralı)
    }

    if (Object.keys(input).length === 0) {
      return { success: false, error: 'Değiştirilecek en az bir alan belirt.' };
    }
    await personnelApi.updatePersonnel(resolved.person.id, input);
    return {
      success: true,
      message: `"${personnelDisplayName(resolved.person)}" güncellendi.`,
      updatedFields: Object.keys(input),
    };
  },
};

const transferPersonnelBranch: AgentActionExecutor = {
  describe: (args) =>
    `"${str(args.personnelName)}" personelini ${args.removeBranch === true ? 'şubesiz yap' : `"${str(args.branchName)}" şubesine ata`}`,
  execute: async (args, ctx) => {
    const personnelName = str(args.personnelName);
    if (!personnelName) return { success: false, error: 'personnelName zorunlu (listPersonnel).' };
    const resolved = await resolvePersonnelByName(ctx, personnelName);
    if ('error' in resolved) return resolved.error;

    if (args.removeBranch === true) {
      await personnelApi.transferBranch(resolved.person.id, null);
      return { success: true, message: `"${personnelDisplayName(resolved.person)}" şube ataması kaldırıldı.` };
    }
    const branchName = str(args.branchName);
    if (!branchName) return { success: false, error: 'branchName zorunlu (veya removeBranch=true).' };
    const branch = await resolveBranchByName(ctx, branchName);
    if ('error' in branch) return branch.error;

    await personnelApi.transferBranch(resolved.person.id, branch.branchId);
    return {
      success: true,
      message: `"${personnelDisplayName(resolved.person)}" personeli "${branchName}" şubesine atandı.`,
    };
  },
};

const deletePersonnel: AgentActionExecutor = {
  describe: (args) => `"${str(args.personnelName)}" personelini sil (geri alınabilir)`,
  execute: async (args, ctx) => {
    const personnelName = str(args.personnelName);
    if (!personnelName) return { success: false, error: 'personnelName zorunlu (listPersonnel).' };
    const resolved = await resolvePersonnelByName(ctx, personnelName);
    if ('error' in resolved) return resolved.error;

    await personnelApi.deletePersonnel(resolved.person.id);
    return {
      success: true,
      message: `"${personnelDisplayName(resolved.person)}" silindi (soft-delete).`,
      note: 'Gerekirse restorePersonnel ile geri alınabilir.',
    };
  },
};

const restorePersonnel: AgentActionExecutor = {
  describe: (args) => `"${str(args.personnelName)}" personelini geri al`,
  execute: async (args, ctx) => {
    const personnelName = str(args.personnelName);
    if (!personnelName) return { success: false, error: 'personnelName zorunlu.' };

    // Silinenler arasında ara
    const rows = (await fetchPersonnelRows(ctx, true)).filter((p) => p.isDeleted === true);
    const match = findByNameTr(rows, personnelDisplayName, personnelName);
    if (match.ambiguous) {
      return { success: false, error: `Birden fazla eşleşme: ${match.ambiguous.join(', ')} — tam adı belirt.` };
    }
    if (!match.found) {
      return {
        success: false,
        error: `Silinmiş personeller arasında "${personnelName}" bulunamadı.`,
        deletedPersonnel: rows.map(personnelDisplayName),
      };
    }
    await personnelApi.restorePersonnel(match.found.id);
    return { success: true, message: `"${personnelDisplayName(match.found)}" geri alındı.` };
  },
};

const createPersonnel: AgentActionExecutor = {
  describe: (args) =>
    `Yeni personel oluştur: "${str(args.firstName)} ${str(args.lastName)}" (${str(args.role).toLowerCase() || 'employee'}${str(args.branchName) ? `, ${str(args.branchName)}` : ''})`,
  execute: async (args, ctx) => {
    const firstName = str(args.firstName);
    const lastName = str(args.lastName);
    const email = str(args.email).toLowerCase();
    const password = args.password === undefined || args.password === null ? '' : String(args.password);
    if (!firstName || !lastName || !email || !password) {
      return { success: false, error: 'firstName, lastName, email ve password zorunludur.' };
    }
    if (!email.includes('@')) return { success: false, error: 'Geçerli bir e-posta adresi ver.' };
    if (password.length < 6) return { success: false, error: 'Şifre en az 6 karakter olmalı.' };

    const role = str(args.role).toLowerCase() || 'employee';
    if (!['employee', 'manager', 'courier'].includes(role)) {
      return {
        success: false,
        error: "role yalnızca 'employee', 'manager' veya 'courier' olabilir (admin/superadmin ataması ajana kapalı).",
      };
    }

    let branchesId: string | null = null;
    const branchName = str(args.branchName);
    if (branchName) {
      const resolved = await resolveBranchByName(ctx, branchName);
      if ('error' in resolved) return resolved.error;
      branchesId = resolved.branchId;
    }

    // CreatePersonnelModal ile aynı uç: Firebase Auth + SQL tek transaction'da
    const response = await authApi.post('/api/auth/create-personnel-by-admin', {
      firstName,
      lastName,
      email,
      password,
      phone: str(args.phone) || null,
      role,
      branchesId,
      departmentId: null,
    });
    const result = response.data ?? {};
    return {
      success: true,
      message: str(result.message) || `"${firstName} ${lastName}" personeli oluşturuldu.`,
      email,
      role,
      ...(branchName ? { branchName } : {}),
      note: 'Personel bu e-posta ve şifreyle giriş yapabilir. Şifre chat geçmişinde görünür — istenirse personel ilk girişte değiştirmeli.',
    };
  },
};

const createPersonnelAccrual: AgentActionExecutor = {
  describe: (args) =>
    `"${str(args.personnelName)}" için ${str(args.kind) === 'BONUS' ? 'prim' : 'maaş'} hakedişi oluştur: ${fmtAmount(args.amount, args.currency)}`,
  execute: async (args, ctx) => {
    const personnelName = str(args.personnelName);
    if (!personnelName) return { success: false, error: 'personnelName zorunlu (listPersonnel).' };
    const kind = str(args.kind).toUpperCase();
    if (!['SALARY', 'BONUS'].includes(kind)) {
      return { success: false, error: "kind 'SALARY' (maaş) veya 'BONUS' (prim) olmalı." };
    }
    const amount = parseAmountLoose(args.amount);
    if (amount <= 0) return { success: false, error: 'amount pozitif bir tutar olmalı.' };
    const resolved = await resolvePersonnelByName(ctx, personnelName);
    if ('error' in resolved) return resolved.error;

    await personnelTxApi.createAccrual({
      personnelFirebaseUid: resolved.person.id,
      personnelName: personnelDisplayName(resolved.person),
      amount,
      currency: str(args.currency).toUpperCase() || 'TRY',
      description: str(args.description) || undefined,
      kind: kind as 'SALARY' | 'BONUS',
      transactionDate: str(args.transactionDate) || undefined,
      performedBy: actorEmail(ctx),
    });
    return {
      success: true,
      message: `"${personnelDisplayName(resolved.person)}" için ${kind === 'SALARY' ? 'maaş' : 'prim'} hakedişi oluşturuldu (${fmtAmount(amount, args.currency)}).`,
      note: 'Hakediş para hareketi yaratmaz; ödeme createPersonnelTransaction (PAYMENT, kasa/banka) ile yapılır.',
    };
  },
};

const TX_TYPE_LABELS: Record<string, string> = {
  DEBT: 'borç',
  RECEIVABLE: 'alacak',
  PAYMENT: 'ödeme',
  COLLECTION: 'tahsilat',
};

const createPersonnelTransaction: AgentActionExecutor = {
  describe: (args) =>
    `"${str(args.personnelName)}" için ${TX_TYPE_LABELS[str(args.transactionType).toUpperCase()] || 'hareket'} kaydet: ${fmtAmount(args.amount, args.currency)}${args.isAdvance === true ? ' (avans)' : ''}`,
  execute: async (args, ctx) => {
    const personnelName = str(args.personnelName);
    if (!personnelName) return { success: false, error: 'personnelName zorunlu (listPersonnel).' };
    const transactionType = str(args.transactionType).toUpperCase();
    if (!TX_TYPE_LABELS[transactionType]) {
      return { success: false, error: 'transactionType DEBT | RECEIVABLE | PAYMENT | COLLECTION olmalı.' };
    }
    const amount = parseAmountLoose(args.amount);
    if (amount <= 0) return { success: false, error: 'amount pozitif bir tutar olmalı.' };

    const paymentMethod = (str(args.paymentMethod).toLowerCase() || 'accrual') as 'accrual' | 'cash' | 'bank';
    if (!['accrual', 'cash', 'bank'].includes(paymentMethod)) {
      return { success: false, error: 'paymentMethod accrual | cash | bank olmalı.' };
    }

    let cashboxId: number | null = null;
    let bankAccountId: number | null = null;
    let sourceLabel = '';
    if (paymentMethod === 'cash') {
      const cashboxName = str(args.cashboxName);
      if (!cashboxName) return { success: false, error: 'paymentMethod=cash için cashboxName zorunlu (listCashboxes).' };
      const cashbox = await findCashboxByName(cashboxName);
      if (!cashbox) return { success: false, error: `"${cashboxName}" adında kasa bulunamadı.` };
      cashboxId = toIntId(cashbox.id ?? cashbox.Id);
      sourceLabel = ` — kasa: ${cashboxNameOf(cashbox)}`;
    } else if (paymentMethod === 'bank') {
      const bankAccountName = str(args.bankAccountName);
      if (!bankAccountName) return { success: false, error: 'paymentMethod=bank için bankAccountName zorunlu (listBankAccounts).' };
      const bank = await findBankAccountByName(bankAccountName);
      if (!bank) return { success: false, error: `"${bankAccountName}" adında banka hesabı bulunamadı.` };
      bankAccountId = toIntId(bank.id ?? bank.Id);
      sourceLabel = ` — banka: ${bankLabel(bank)}`;
    }

    const resolved = await resolvePersonnelByName(ctx, personnelName);
    if ('error' in resolved) return resolved.error;

    await personnelTxApi.createPersonnelTransaction({
      personnelFirebaseUid: resolved.person.id,
      personnelName: personnelDisplayName(resolved.person),
      transactionType: transactionType as 'DEBT' | 'RECEIVABLE' | 'PAYMENT' | 'COLLECTION',
      amount,
      currency: str(args.currency).toUpperCase() || 'TRY',
      description: str(args.description) || undefined,
      transactionDate: str(args.transactionDate) || undefined,
      paymentMethod,
      cashboxId,
      bankAccountId,
      performedBy: actorEmail(ctx),
      ...(args.isAdvance === true ? { kind: 'ADVANCE' as const } : {}),
    });
    return {
      success: true,
      message: `"${personnelDisplayName(resolved.person)}" için ${TX_TYPE_LABELS[transactionType]} kaydedildi: ${fmtAmount(amount, args.currency)}${sourceLabel}.`,
      ...(paymentMethod !== 'accrual'
        ? { note: 'Kasa/banka hareketi atomik olarak birlikte oluşturuldu.' }
        : {}),
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// İnsan Kaynakları — Vardiya (/shifts)
//
// Model: tarih bazlı takvim planlaması YOK; her personel bir vardiya şablonuna
// bağlanır (enforceSingleShift). Giriş-çıkış kayıtları attendance uçlarından
// okunur. İzin (leave) modülü backend'de olmadığından kapsam dışı.
// ═══════════════════════════════════════════════════════════════════════════

const resolveShiftByName = async (
  ctx: AgentActionContext,
  wanted: string
): Promise<{ shift: shiftApi.Shift } | { error: Record<string, any> }> => {
  const shifts = await shiftApi.getShifts(requireCompanyId(ctx));
  const match = findByNameTr(shifts, (s) => str(s.name), wanted);
  if (match.ambiguous) {
    return { error: { success: false, error: `Birden fazla eşleşme: ${match.ambiguous.join(', ')} — tam adı belirt.` } };
  }
  if (!match.found) {
    return {
      error: {
        success: false,
        error: `"${wanted}" adında vardiya bulunamadı.`,
        availableShifts: shifts.map((s) => s.name),
      },
    };
  }
  return { shift: match.found };
};

const HHMM_RE = /^([01]?\d|2[0-3]):[0-5]\d$/;

const listShifts: AgentActionExecutor = {
  describe: () => 'Vardiyaları listele',
  execute: async (_args, ctx) => {
    const [shifts, branches] = await Promise.all([
      shiftApi.getShifts(requireCompanyId(ctx)),
      getBranches(requireCompanyId(ctx)),
    ]);
    return {
      success: true,
      count: shifts.length,
      shifts: shifts.map((s) => ({
        name: s.name,
        startTime: s.startTime,
        endTime: s.endTime,
        lateToleranceMinutes: s.lateTolerance,
        earlyExitToleranceMinutes: s.earlyExitTolerance,
        isNextDayExit: s.isNextDayExit === true,
        attendanceMode: s.attendanceMode,
        branchName: s.branchesId
          ? String(branches.find((b: any) => String(b.id) === String(s.branchesId))?.name || s.branchesId)
          : null,
        assignedCount: s.assignedCount ?? 0,
        isActive: s.isActive !== false,
      })),
    };
  },
};

const getShiftPersonnel: AgentActionExecutor = {
  describe: (args) => `"${str(args.shiftName)}" vardiyasına atanan personelleri getir`,
  execute: async (args, ctx) => {
    const shiftName = str(args.shiftName);
    if (!shiftName) return { success: false, error: 'shiftName zorunlu (listShifts).' };
    const resolved = await resolveShiftByName(ctx, shiftName);
    if ('error' in resolved) return resolved.error;

    const [uids, nameMap] = await Promise.all([
      shiftApi.getShiftPersonnel(resolved.shift.id),
      buildPersonnelNameMap(ctx),
    ]);
    return {
      success: true,
      shiftName: resolved.shift.name,
      count: uids.length,
      personnel: uids.map((uid) => nameMap[uid] || uid),
    };
  },
};

const getPersonnelShift: AgentActionExecutor = {
  describe: (args) => `"${str(args.personnelName)}" personelinin vardiyasını getir`,
  execute: async (args, ctx) => {
    const personnelName = str(args.personnelName);
    if (!personnelName) return { success: false, error: 'personnelName zorunlu (listPersonnel).' };
    const resolved = await resolvePersonnelByName(ctx, personnelName);
    if ('error' in resolved) return resolved.error;

    const shift = await shiftApi.getShiftByPersonnel(resolved.person.id);
    if (!shift) {
      return {
        success: true,
        found: false,
        message: `"${personnelDisplayName(resolved.person)}" herhangi bir vardiyaya atanmamış.`,
      };
    }
    return {
      success: true,
      found: true,
      personnelName: personnelDisplayName(resolved.person),
      shift: {
        name: shift.name,
        startTime: shift.startTime,
        endTime: shift.endTime,
        isNextDayExit: shift.isNextDayExit === true,
        attendanceMode: shift.attendanceMode,
      },
    };
  },
};

/** yyyy-MM-dd biçimine indirger (geçersizse fallback). */
const toDayString = (v: any, fallback: Date): string => {
  const s = str(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = s ? new Date(s) : fallback;
  const use = isNaN(d.getTime()) ? fallback : d;
  return `${use.getFullYear()}-${pad2(use.getMonth() + 1)}-${pad2(use.getDate())}`;
};

const listAttendance: AgentActionExecutor = {
  describe: () => 'Giriş-çıkış kayıtlarını listele',
  execute: async (args, ctx) => {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const from = toDayString(args.startDate, weekAgo);
    const to = toDayString(args.endDate, now);

    let personnelUid: string | undefined;
    let personnelLabel: string | undefined;
    const personnelName = str(args.personnelName);
    if (personnelName) {
      const resolved = await resolvePersonnelByName(ctx, personnelName);
      if ('error' in resolved) return resolved.error;
      personnelUid = resolved.person.id;
      personnelLabel = personnelDisplayName(resolved.person);
    }

    const limit = clampLimit(args.limit, 25, 100);
    const [result, nameMap] = await Promise.all([
      shiftApi.getAttendanceList({
        from,
        to,
        personnelUid,
        status: str(args.status) || undefined,
        page: 1,
        pageSize: limit,
      }),
      buildPersonnelNameMap(ctx),
    ]);
    return {
      success: true,
      range: { from, to },
      ...(personnelLabel ? { personnelName: personnelLabel } : {}),
      totalCount: result.total,
      statusLegend: 'Giriş: early=erken, onTime=zamanında, late=geç · Çıkış: earlyExit=erken çıktı, complete=tamamladı',
      records: result.items.map((e) => ({
        date: e.attendanceDate,
        personnelName: nameMap[e.personnelUid] || e.personnelUid,
        shiftName: e.shiftName ?? null,
        checkInTime: e.checkInTime ?? null,
        checkInStatus: e.checkInStatus ?? null,
        checkOutTime: e.checkOutTime ?? null,
        checkOutStatus: e.checkOutStatus ?? null,
        workedMinutes: e.workedMinutes ?? null,
        isManuallyEdited: e.isManuallyEdited === true,
        notes: e.notes ?? null,
      })),
    };
  },
};

const getWorkDurationSummary: AgentActionExecutor = {
  describe: () => 'Toplam çalışma süresi özetini getir',
  execute: async (args, ctx) => {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const from = toDayString(args.startDate, weekAgo);
    const to = toDayString(args.endDate, now);

    let personnelUid: string | undefined;
    const personnelName = str(args.personnelName);
    if (personnelName) {
      const resolved = await resolvePersonnelByName(ctx, personnelName);
      if ('error' in resolved) return resolved.error;
      personnelUid = resolved.person.id;
    }

    const [items, nameMap] = await Promise.all([
      shiftApi.getToplamSure({ from, to, personnelUid }),
      buildPersonnelNameMap(ctx),
    ]);
    return {
      success: true,
      range: { from, to },
      count: items.length,
      summary: items.map((i) => ({
        personnelName: nameMap[i.personnelUid] || i.personnelUid,
        totalMinutes: i.totalMinutes,
        totalHours: Math.round((i.totalMinutes / 60) * 10) / 10,
        dayCount: i.dayCount,
      })),
    };
  },
};

const createShift: AgentActionExecutor = {
  describe: (args) =>
    `Yeni vardiya oluştur: "${str(args.name)}" (${str(args.startTime)}-${str(args.endTime)})`,
  execute: async (args, ctx) => {
    const name = str(args.name);
    if (!name) return { success: false, error: 'name (vardiya adı) zorunlu.' };
    const startTime = str(args.startTime);
    const endTime = str(args.endTime);
    if (!HHMM_RE.test(startTime) || !HHMM_RE.test(endTime)) {
      return { success: false, error: "startTime ve endTime 'HH:mm' biçiminde olmalı (örn. 09:00)." };
    }

    const existing = await shiftApi.getShifts(requireCompanyId(ctx));
    if (existing.some((s) => str(s.name).toLowerCase() === name.toLowerCase())) {
      return { success: false, error: `"${name}" adında bir vardiya zaten var.` };
    }

    let branchesId: string | null = null;
    const branchName = str(args.branchName);
    if (branchName) {
      const resolved = await resolveBranchByName(ctx, branchName);
      if ('error' in resolved) return resolved.error;
      branchesId = resolved.branchId;
    }

    const shiftId = await shiftApi.createShift({
      name,
      startTime,
      endTime,
      lateTolerance: args.lateTolerance !== undefined ? Number(args.lateTolerance) || 0 : 10,
      earlyExitTolerance: args.earlyExitTolerance !== undefined ? Number(args.earlyExitTolerance) || 0 : 10,
      isNextDayExit: args.isNextDayExit === true,
      branchesId,
      attendanceMode: str(args.attendanceMode) === 'dual' ? 'dual' : 'single',
    });
    return {
      success: true,
      shiftId,
      message: `"${name}" vardiyası oluşturuldu (${startTime}-${endTime}).`,
      note: 'Personel ataması assignShiftPersonnel ile yapılabilir.',
    };
  },
};

const updateShift: AgentActionExecutor = {
  describe: (args) => `"${str(args.shiftName)}" vardiyasını güncelle`,
  execute: async (args, ctx) => {
    const shiftName = str(args.shiftName);
    if (!shiftName) return { success: false, error: 'shiftName zorunlu (listShifts).' };
    const resolved = await resolveShiftByName(ctx, shiftName);
    if ('error' in resolved) return resolved.error;

    const input: Partial<shiftApi.ShiftWriteInput> & { isActive?: boolean } = {};
    if (str(args.newName)) input.name = str(args.newName);
    for (const key of ['startTime', 'endTime'] as const) {
      const v = str(args[key]);
      if (v) {
        if (!HHMM_RE.test(v)) return { success: false, error: `${key} 'HH:mm' biçiminde olmalı.` };
        input[key] = v;
      }
    }
    if (args.lateTolerance !== undefined && args.lateTolerance !== null) {
      input.lateTolerance = Number(args.lateTolerance) || 0;
    }
    if (args.earlyExitTolerance !== undefined && args.earlyExitTolerance !== null) {
      input.earlyExitTolerance = Number(args.earlyExitTolerance) || 0;
    }
    if (typeof args.isNextDayExit === 'boolean') input.isNextDayExit = args.isNextDayExit;
    if (typeof args.isActive === 'boolean') input.isActive = args.isActive;
    if (str(args.attendanceMode)) {
      input.attendanceMode = str(args.attendanceMode) === 'dual' ? 'dual' : 'single';
    }
    const branchName = str(args.branchName);
    if (branchName) {
      const branch = await resolveBranchByName(ctx, branchName);
      if ('error' in branch) return branch.error;
      input.branchesId = branch.branchId;
    }

    if (Object.keys(input).length === 0) {
      return { success: false, error: 'Değiştirilecek en az bir alan belirt.' };
    }
    await shiftApi.updateShift(resolved.shift.id, input);
    return {
      success: true,
      message: `"${resolved.shift.name}" vardiyası güncellendi.`,
      updatedFields: Object.keys(input),
    };
  },
};

const deleteShift: AgentActionExecutor = {
  describe: (args) => `"${str(args.shiftName)}" vardiyasını sil`,
  execute: async (args, ctx) => {
    const shiftName = str(args.shiftName);
    if (!shiftName) return { success: false, error: 'shiftName zorunlu (listShifts).' };
    const resolved = await resolveShiftByName(ctx, shiftName);
    if ('error' in resolved) return resolved.error;

    // UI ile aynı kural: personel atanmış vardiya silinemez
    if ((resolved.shift.assignedCount ?? 0) > 0) {
      return {
        success: false,
        error: `"${resolved.shift.name}" vardiyasına ${resolved.shift.assignedCount} personel atanmış — önce atamaları kaldır (assignShiftPersonnel, mode=replace boş liste ile veya personelleri başka vardiyaya taşıyarak).`,
      };
    }
    await shiftApi.deleteShift(resolved.shift.id);
    return { success: true, message: `"${resolved.shift.name}" vardiyası silindi.` };
  },
};

const assignShiftPersonnel: AgentActionExecutor = {
  describe: (args) =>
    `"${str(args.shiftName)}" vardiyası personel ataması (${str(args.mode) || 'add'}): ${str(args.personnelNames)}`,
  execute: async (args, ctx) => {
    const shiftName = str(args.shiftName);
    if (!shiftName) return { success: false, error: 'shiftName zorunlu (listShifts).' };
    const mode = (str(args.mode).toLowerCase() || 'add') as 'add' | 'remove' | 'replace';
    if (!['add', 'remove', 'replace'].includes(mode)) {
      return { success: false, error: 'mode add | remove | replace olmalı.' };
    }
    const names = str(args.personnelNames).split(',').map((s) => s.trim()).filter(Boolean);
    if (names.length === 0 && mode !== 'replace') {
      return { success: false, error: 'personnelNames zorunlu (virgülle ayrılmış ad listesi).' };
    }

    const resolved = await resolveShiftByName(ctx, shiftName);
    if ('error' in resolved) return resolved.error;

    const wantedUids: string[] = [];
    for (const name of names) {
      const person = await resolvePersonnelByName(ctx, name);
      if ('error' in person) return person.error;
      wantedUids.push(person.person.id);
    }

    const current = await shiftApi.getShiftPersonnel(resolved.shift.id);
    let finalUids: string[];
    if (mode === 'add') finalUids = Array.from(new Set([...current, ...wantedUids]));
    else if (mode === 'remove') finalUids = current.filter((uid) => !wantedUids.includes(uid));
    else finalUids = wantedUids;

    await shiftApi.assignShiftPersonnel(resolved.shift.id, finalUids, true);

    const nameMap = await buildPersonnelNameMap(ctx);
    return {
      success: true,
      message: `"${resolved.shift.name}" vardiyası ataması güncellendi (${finalUids.length} personel).`,
      assignedPersonnel: finalUids.map((uid) => nameMap[uid] || uid),
      note: 'Bir personel tek vardiyada olabilir — eklenen personel başka vardiyadaysa oradan otomatik çıkarılır.',
    };
  },
};

const editAttendance: AgentActionExecutor = {
  describe: (args) =>
    `"${str(args.personnelName)}" ${str(args.date)} giriş-çıkış kaydını düzenle`,
  execute: async (args, ctx) => {
    const personnelName = str(args.personnelName);
    if (!personnelName) return { success: false, error: 'personnelName zorunlu (listPersonnel).' };
    const date = str(args.date);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return { success: false, error: "date 'YYYY-MM-DD' biçiminde olmalı." };
    }
    const checkInTime = str(args.checkInTime);
    const checkOutTime = str(args.checkOutTime);
    if (!checkInTime && !checkOutTime && !str(args.notes)) {
      return { success: false, error: 'checkInTime, checkOutTime veya notes alanlarından en az biri gerekli.' };
    }
    if ((checkInTime && !HHMM_RE.test(checkInTime)) || (checkOutTime && !HHMM_RE.test(checkOutTime))) {
      return { success: false, error: "checkInTime/checkOutTime 'HH:mm' biçiminde olmalı (örn. 18:00)." };
    }

    const resolved = await resolvePersonnelByName(ctx, personnelName);
    if ('error' in resolved) return resolved.error;

    const input: shiftApi.AttendanceEditInput = {
      uid: resolved.person.id,
      date,
    };
    // Zamanlar TR yerel ISO gönderilir; backend UTC'ye çevirip saklar
    if (checkInTime) {
      input.setCheckIn = true;
      input.checkInTime = `${date}T${checkInTime}:00`;
    }
    if (checkOutTime) {
      input.setCheckOut = true;
      input.checkOutTime = `${date}T${checkOutTime}:00`;
    }
    if (args.notes !== undefined && args.notes !== null) input.notes = str(args.notes);

    const updated = await shiftApi.editAttendance(input);
    return {
      success: true,
      message: `"${personnelDisplayName(resolved.person)}" ${date} kaydı güncellendi (elle düzenlendi olarak işaretlenir).`,
      record: {
        date: updated?.attendanceDate ?? date,
        checkInTime: updated?.checkInTime ?? null,
        checkOutTime: updated?.checkOutTime ?? null,
        workedMinutes: updated?.workedMinutes ?? null,
      },
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Şubeler (/branches)
//
// Kimlik: kanonik şube id'si = FirebaseId (SQL integer Id DEĞİL). Yönetici
// modeli iki katmanlı: birincil yönetici = personelin role='manager' +
// branchesId=<şube>; kişi zaten başka şubenin birincil yöneticisiyse ikinci
// şubeye BranchPersonnelAssignments tablosuyla EK atama yapılır (UI ile aynı
// üç senaryo). Şube SİLME bilinçli olarak kapsam dışı (UI'da da yok).
// Ajan personeli daima SQL'den taze okur — Branches sayfasındaki eski önbellek
// bug'ından (bkz. fetchAvailablePersonnel yorumu) etkilenmez.
// ═══════════════════════════════════════════════════════════════════════════

/** Şubeyi ada göre tam kayıt olarak çözer (resolveBranchByName yalnız id döner). */
const resolveBranchRecordByName = async (
  ctx: AgentActionContext,
  wanted: string
): Promise<{ branch: branchApi.Branch } | { error: Record<string, any> }> => {
  const branches = await getBranches(requireCompanyId(ctx));
  const match = findByNameTr(branches, (b) => str(b.name), wanted);
  if (match.ambiguous) {
    return { error: { success: false, error: `Birden fazla eşleşme: ${match.ambiguous.join(', ')} — tam adı belirt.` } };
  }
  if (!match.found) {
    return {
      error: {
        success: false,
        error: `"${wanted}" adında şube bulunamadı.`,
        availableBranches: branches.map((b) => b.name),
      },
    };
  }
  return { branch: match.found };
};

const getBranchDetails: AgentActionExecutor = {
  describe: (args) => `"${str(args.branchName)}" şubesinin detaylarını getir`,
  execute: async (args, ctx) => {
    const branchName = str(args.branchName);
    if (!branchName) return { success: false, error: 'branchName zorunlu (listBranches).' };
    const resolved = await resolveBranchRecordByName(ctx, branchName);
    if ('error' in resolved) return resolved.error;
    const b = resolved.branch;
    return {
      success: true,
      branch: {
        name: b.name,
        address: b.address ?? null,
        district: b.district ?? null,
        city: b.city ?? null,
        postalCode: b.postalCode ?? null,
        country: b.country ?? null,
        phone: b.phone ?? null,
        email: b.email ?? null,
        website: b.website ?? null,
        openingDate: b.openingDate ?? null,
        isHeadquarter: b.isHeadquarter === true,
        isActive: b.isActive !== false,
        isFranchise: b.isFranchise === true,
        firmInfo: str(b.firmName)
          ? {
              firmName: b.firmName ?? null,
              firmTaxNumber: b.firmTaxNumber ?? null,
              firmTaxOffice: b.firmTaxOffice ?? null,
              firmAddress: b.firmAddress ?? null,
              firmPhone: b.firmPhone ?? null,
              firmEmail: b.firmEmail ?? null,
              firmWebsite: b.firmWebsite ?? null,
              firmMersisNo: b.firmMersisNo ?? null,
              firmTradeRegistryNo: b.firmTradeRegistryNo ?? null,
            }
          : null,
      },
    };
  },
};

const listBranchManagers: AgentActionExecutor = {
  describe: (args) => `"${str(args.branchName)}" şubesinin yöneticilerini getir`,
  execute: async (args, ctx) => {
    const branchName = str(args.branchName);
    if (!branchName) return { success: false, error: 'branchName zorunlu (listBranches).' };
    const resolved = await resolveBranchRecordByName(ctx, branchName);
    if ('error' in resolved) return resolved.error;
    const branchId = resolved.branch.id;

    const [personnel, assignments] = await Promise.all([
      fetchPersonnelRows(ctx),
      branchAssignApi.getAssignmentsByBranch(branchId),
    ]);
    const nameOf = (uid: string) => {
      const p = personnel.find((row) => row.id === uid);
      return p ? personnelDisplayName(p) : uid;
    };

    const primary = personnel
      .filter((p) => str(p.role) === 'manager' && String(p.branchesId || '') === branchId && p.isDeleted !== true)
      .map((p) => ({
        name: personnelDisplayName(p),
        email: p.email ?? null,
        phone: p.phone ?? null,
        assignmentType: 'birincil' as const,
      }));
    const primaryUids = new Set(
      personnel
        .filter((p) => str(p.role) === 'manager' && String(p.branchesId || '') === branchId && p.isDeleted !== true)
        .map((p) => p.id)
    );
    const additional = assignments
      .filter((a) => !primaryUids.has(a.userId))
      .map((a) => ({
        name: nameOf(a.userId),
        assignedRole: a.assignedRole,
        assignmentType: 'ek' as const,
      }));

    return {
      success: true,
      branchName: resolved.branch.name,
      count: primary.length + additional.length,
      managers: [...primary, ...additional],
      note: 'birincil = personelin ana şubesi bu şube; ek = başka şubenin birincil yöneticisiyken bu şubeye de atanmış.',
    };
  },
};

// ManagerPermissionsModal ile birebir aynı modül/alt izin kataloğu.
// Yalnızca genişletilebilir modüllerin (alt listesi dolu olanlar) alt izinleri kaydedilir.
const MANAGER_PERMISSION_MODULES: Record<string, string[]> = {
  dashboard: [
    'dashboard_stat_cards',
    'dashboard_task_status',
    'dashboard_weekly_task_status',
    'dashboard_monthly_task_status',
    'dashboard_personnel_performance',
    'dashboard_weekly_personnel_performance',
    'dashboard_monthly_personnel_performance',
    'dashboard_survey_charts',
    'dashboard_missed_tasks',
    'dashboard_completed_tasks',
    'dashboard_worst_performers',
    'dashboard_best_performers',
    'dashboard_task_locations',
  ],
  tasks: [],
  personnel: [],
  shifts: [],
  reports: [],
  messages: [],
  surveys: [],
  qrmenu: [
    'qrmenu_view',
    'qrmenu_create',
    'qrmenu_edit',
    'qrmenu_delete',
    'qrmenu_orders',
    'qrmenu_kitchen',
    'qrmenu_tables',
    'qrmenu_customers',
    'qrmenu_daily_closing',
  ],
  salesReports: ['salesReports_endOfDay', 'salesReports_view', 'salesReports_export'],
  accountingDashboard: [
    'accountingDashboard_view',
    'accountingDashboard_invoices',
    'accountingDashboard_eInvoices',
    'accountingDashboard_branchPrices',
  ],
  current: [],
  bank: [],
  cash: [],
};

/** Alt izin kimliği → ana modül kimliği haritası. */
const MANAGER_SUB_TO_MODULE: Record<string, string> = Object.fromEntries(
  Object.entries(MANAGER_PERMISSION_MODULES).flatMap(([mod, subs]) => subs.map((s) => [s, mod]))
);

const getManagerPermissions: AgentActionExecutor = {
  describe: (args) => `"${str(args.personnelName)}" yöneticisinin panel yetkilerini getir`,
  execute: async (args, ctx) => {
    const personnelName = str(args.personnelName);
    if (!personnelName) return { success: false, error: 'personnelName zorunlu (listBranchManagers).' };
    const resolved = await resolvePersonnelByName(ctx, personnelName);
    if ('error' in resolved) return resolved.error;

    const panel = await personnelApi.getManagerPanel(resolved.person.id);
    if (!panel.found || !panel.settings) {
      return {
        success: true,
        personnelName: personnelDisplayName(resolved.person),
        configured: false,
        message: 'Bu yönetici için henüz panel yetkisi tanımlanmamış (tüm modüller kapalı).',
        availableModules: Object.keys(MANAGER_PERMISSION_MODULES),
      };
    }

    const modules = Object.keys(MANAGER_PERMISSION_MODULES).map((mod) => {
      const subs = MANAGER_PERMISSION_MODULES[mod];
      const subSettings = panel.settings[`${mod}_subPermissions`] || {};
      return {
        module: mod,
        enabled: panel.settings[mod] === true,
        ...(subs.length
          ? {
              enabledSubPermissions: subs.filter((s) => subSettings[s] === true),
              disabledSubPermissions: subs.filter((s) => subSettings[s] !== true),
            }
          : {}),
      };
    });
    return {
      success: true,
      personnelName: personnelDisplayName(resolved.person),
      configured: true,
      panelEnabled: panel.isEnabled,
      enabledModules: modules.filter((m) => m.enabled).map((m) => m.module),
      modules,
    };
  },
};

const createBranch: AgentActionExecutor = {
  describe: (args) => `Yeni şube oluştur: "${str(args.name)}"`,
  execute: async (args, ctx) => {
    const name = str(args.name);
    const address = str(args.address);
    const phone = str(args.phone);
    // UI form validasyonuyla aynı zorunlular
    if (!name || !address || !phone) {
      return { success: false, error: 'name, address ve phone zorunludur (UI kuralı).' };
    }

    const branches = await getBranches(requireCompanyId(ctx));
    if (branches.some((b) => str(b.name).toLocaleLowerCase('tr-TR') === name.toLocaleLowerCase('tr-TR'))) {
      return { success: false, error: `"${name}" adında bir şube zaten var.` };
    }

    const branchId = await branchApi.createBranch({
      name,
      address,
      phone,
      district: str(args.district) || undefined,
      city: str(args.city) || undefined,
      postalCode: str(args.postalCode) || undefined,
      country: str(args.country) || 'Türkiye',
      email: str(args.email) || undefined,
      website: str(args.website) || undefined,
      openingDate: str(args.openingDate) || null,
      isFranchise: args.isFranchise === true,
    });
    return {
      success: true,
      branchId,
      message: `"${name}" şubesi oluşturuldu${args.isFranchise === true ? ' (franchise)' : ''}.`,
      note: 'Yönetici ataması assignBranchManager, franchise firma bilgisi updateBranchFirmInfo ile yapılabilir.',
    };
  },
};

const updateBranch: AgentActionExecutor = {
  describe: (args) => `"${str(args.branchName)}" şubesini güncelle`,
  execute: async (args, ctx) => {
    const branchName = str(args.branchName);
    if (!branchName) return { success: false, error: 'branchName zorunlu (listBranches).' };
    const resolved = await resolveBranchRecordByName(ctx, branchName);
    if ('error' in resolved) return resolved.error;
    const b = resolved.branch;

    // Backend update tüm kolonları yazar → verilmeyen alanlar mevcut değerle korunur
    const pick = (key: string, current: any) =>
      args[key] !== undefined && args[key] !== null ? str(args[key]) : current;

    const updated: string[] = [];
    for (const key of ['newName', 'address', 'district', 'city', 'postalCode', 'country', 'phone', 'email', 'website', 'openingDate']) {
      if (args[key] !== undefined && args[key] !== null) updated.push(key);
    }
    if (typeof args.isFranchise === 'boolean') updated.push('isFranchise');
    if (updated.length === 0) return { success: false, error: 'Değiştirilecek en az bir alan belirt.' };

    const newName = str(args.newName) || b.name;
    if (
      str(args.newName) &&
      newName.toLocaleLowerCase('tr-TR') !== str(b.name).toLocaleLowerCase('tr-TR')
    ) {
      const branches = await getBranches(requireCompanyId(ctx));
      if (branches.some((row) => row.id !== b.id && str(row.name).toLocaleLowerCase('tr-TR') === newName.toLocaleLowerCase('tr-TR'))) {
        return { success: false, error: `"${newName}" adında başka bir şube zaten var.` };
      }
    }

    await branchApi.updateBranch(b.id, {
      name: newName,
      address: pick('address', b.address),
      district: pick('district', b.district),
      city: pick('city', b.city),
      postalCode: pick('postalCode', b.postalCode),
      country: pick('country', b.country),
      phone: pick('phone', b.phone),
      email: pick('email', b.email),
      website: pick('website', b.website),
      openingDate: args.openingDate !== undefined && args.openingDate !== null ? str(args.openingDate) : b.openingDate ?? null,
      isFranchise: typeof args.isFranchise === 'boolean' ? args.isFranchise : b.isFranchise === true,
    });
    return {
      success: true,
      message: `"${b.name}" şubesi güncellendi.`,
      updatedFields: updated,
    };
  },
};

const updateBranchFirmInfo: AgentActionExecutor = {
  describe: (args) => `"${str(args.branchName)}" şubesinin firma bilgilerini güncelle`,
  execute: async (args, ctx) => {
    const branchName = str(args.branchName);
    if (!branchName) return { success: false, error: 'branchName zorunlu (listBranches).' };
    const resolved = await resolveBranchRecordByName(ctx, branchName);
    if ('error' in resolved) return resolved.error;
    const b = resolved.branch;

    const firmKeys = [
      'firmName',
      'firmTaxNumber',
      'firmTaxOffice',
      'firmAddress',
      'firmPhone',
      'firmEmail',
      'firmWebsite',
      'firmMersisNo',
      'firmTradeRegistryNo',
    ] as const;

    // Kısmi güncelleme: verilmeyen alanlar mevcut değerlerle korunur
    const firm: branchApi.BranchFirmInfo = {};
    const updated: string[] = [];
    for (const key of firmKeys) {
      if (args[key] !== undefined && args[key] !== null) {
        firm[key] = str(args[key]);
        updated.push(key);
      } else {
        firm[key] = (b[key] as string | undefined) ?? '';
      }
    }
    if (updated.length === 0) return { success: false, error: 'Değiştirilecek en az bir firma alanı belirt.' };

    await branchApi.updateBranchFirmInfo(requireCompanyId(ctx), b.id, firm);
    return {
      success: true,
      message: `"${b.name}" şubesinin firma bilgileri güncellendi.`,
      updatedFields: updated,
      note: 'Firma bilgisi yalnızca franchise şubelerde fatura/fişte satıcı olarak kullanılır.',
    };
  },
};

const assignBranchManager: AgentActionExecutor = {
  describe: (args) =>
    `"${str(args.personnelName)}" personelini "${str(args.branchName)}" şubesine yönetici ata`,
  execute: async (args, ctx) => {
    const personnelName = str(args.personnelName);
    const branchName = str(args.branchName);
    if (!personnelName) return { success: false, error: 'personnelName zorunlu (listPersonnel).' };
    if (!branchName) return { success: false, error: 'branchName zorunlu (listBranches).' };

    const [personResolved, branchResolved] = await Promise.all([
      resolvePersonnelByName(ctx, personnelName),
      resolveBranchRecordByName(ctx, branchName),
    ]);
    if ('error' in personResolved) return personResolved.error;
    if ('error' in branchResolved) return branchResolved.error;
    const person = personResolved.person;
    const branch = branchResolved.branch;
    const displayName = personnelDisplayName(person);

    if (str(person.role) === 'admin') {
      return {
        success: false,
        error: `"${displayName}" admin rolünde — yönetici ataması rolünü 'manager'a düşürür; bu işlem ajan üzerinden yapılmaz.`,
      };
    }

    const currentBranchId = String(person.branchesId || '');
    // Senaryo 3: zaten bu şubenin birincil yöneticisi
    if (str(person.role) === 'manager' && currentBranchId === branch.id) {
      return {
        success: true,
        message: `"${displayName}" zaten "${branch.name}" şubesinin birincil yöneticisi.`,
        assignmentType: 'birincil',
      };
    }
    // Senaryo 2: başka şubenin birincil yöneticisi → EK atama (birincil şubesi korunur)
    if (str(person.role) === 'manager' && currentBranchId) {
      const result = await branchAssignApi.assignManagerToBranch(branch.id, person.id);
      return {
        success: true,
        message: result.alreadyExists
          ? `"${displayName}" zaten ek olarak "${branch.name}" şubesine atanmış.`
          : `"${displayName}" ek olarak "${branch.name}" şubesine yönetici atandı (birincil şubesi korundu).`,
        assignmentType: 'ek',
      };
    }
    // Senaryo 1: henüz yönetici değil → rolü manager yap + birincil şubeyi ata
    await personnelApi.updatePersonnel(person.id, { role: 'manager', branchesId: branch.id });
    return {
      success: true,
      message: `"${displayName}" personeli "${branch.name}" şubesine birincil yönetici olarak atandı (rolü 'manager' yapıldı).`,
      assignmentType: 'birincil',
      note: 'Panel yetkileri setManagerPermissions ile tanımlanabilir — tanımlanmazsa yönetici panelde hiçbir modül göremez.',
    };
  },
};

const removeBranchManager: AgentActionExecutor = {
  describe: (args) =>
    `"${str(args.personnelName)}" yöneticisini "${str(args.branchName)}" şubesinden kaldır`,
  execute: async (args, ctx) => {
    const personnelName = str(args.personnelName);
    const branchName = str(args.branchName);
    if (!personnelName) return { success: false, error: 'personnelName zorunlu (listBranchManagers).' };
    if (!branchName) return { success: false, error: 'branchName zorunlu (listBranches).' };

    const [personResolved, branchResolved] = await Promise.all([
      resolvePersonnelByName(ctx, personnelName),
      resolveBranchRecordByName(ctx, branchName),
    ]);
    if ('error' in personResolved) return personResolved.error;
    if ('error' in branchResolved) return branchResolved.error;
    const person = personResolved.person;
    const branch = branchResolved.branch;
    const displayName = personnelDisplayName(person);

    const actions: string[] = [];
    // Bu şubenin birincil yöneticisiyse rolü düşür + şube bağını temizle (UI ile aynı)
    if (str(person.role) === 'manager' && String(person.branchesId || '') === branch.id) {
      await personnelApi.updatePersonnel(person.id, { role: 'employee', branchesId: '' });
      actions.push("birincil yöneticilik kaldırıldı (rol 'employee' yapıldı, şube bağı temizlendi)");
    }
    // Ek atama varsa onu da sil
    const removeResult = await branchAssignApi.removeAssignment(branch.id, person.id);
    if (removeResult.removedCount > 0) actions.push('ek şube ataması kaldırıldı');

    if (actions.length === 0) {
      return { success: false, error: `"${displayName}" zaten "${branch.name}" şubesinde yönetici değil.` };
    }
    return {
      success: true,
      message: `"${displayName}" — "${branch.name}" şubesinden kaldırıldı: ${actions.join(', ')}.`,
    };
  },
};

const setManagerPermissions: AgentActionExecutor = {
  describe: (args) => {
    const parts: string[] = [];
    if (args.enableAll === true) parts.push('tümünü aç');
    if (args.disableAll === true) parts.push('tümünü kapat');
    if (str(args.enableModules)) parts.push(`aç: ${str(args.enableModules)}`);
    if (str(args.disableModules)) parts.push(`kapat: ${str(args.disableModules)}`);
    return `"${str(args.personnelName)}" yöneticisinin panel yetkilerini düzenle${parts.length ? ` (${parts.join(' · ')})` : ''}`;
  },
  execute: async (args, ctx) => {
    const personnelName = str(args.personnelName);
    if (!personnelName) return { success: false, error: 'personnelName zorunlu (listBranchManagers).' };
    if (args.enableAll === true && args.disableAll === true) {
      return { success: false, error: 'enableAll ve disableAll birlikte kullanılamaz.' };
    }
    const enableTokens = str(args.enableModules).split(',').map((s) => s.trim()).filter(Boolean);
    const disableTokens = str(args.disableModules).split(',').map((s) => s.trim()).filter(Boolean);
    if (!enableTokens.length && !disableTokens.length && args.enableAll !== true && args.disableAll !== true) {
      return { success: false, error: 'enableModules, disableModules, enableAll veya disableAll belirt.' };
    }

    // Geçersiz kimlikleri baştan reddet (whitelist)
    const invalid = [...enableTokens, ...disableTokens].filter(
      (t) => !(t in MANAGER_PERMISSION_MODULES) && !(t in MANAGER_SUB_TO_MODULE)
    );
    if (invalid.length) {
      return {
        success: false,
        error: `Geçersiz izin kimliği: ${invalid.join(', ')}`,
        validModules: Object.keys(MANAGER_PERMISSION_MODULES),
        validSubPermissions: Object.keys(MANAGER_SUB_TO_MODULE),
      };
    }

    const resolved = await resolvePersonnelByName(ctx, personnelName);
    if ('error' in resolved) return resolved.error;
    const person = resolved.person;
    if (str(person.role) !== 'manager') {
      return {
        success: false,
        error: `"${personnelDisplayName(person)}" yönetici değil (rol: ${person.role || 'bilinmiyor'}) — önce assignBranchManager ile yönetici ata.`,
      };
    }

    // Mevcut ayarları oku ve tam katalog üzerine normalize et (kısmi güncelleme)
    const panel = await personnelApi.getManagerPanel(person.id);
    const currentSettings: Record<string, any> = (panel.found && panel.settings) || {};
    const settings: Record<string, any> = {};
    for (const [mod, subs] of Object.entries(MANAGER_PERMISSION_MODULES)) {
      settings[mod] = currentSettings[mod] === true;
      if (subs.length) {
        const curSubs = currentSettings[`${mod}_subPermissions`] || {};
        settings[`${mod}_subPermissions`] = Object.fromEntries(subs.map((s) => [s, curSubs[s] === true]));
      }
    }

    const setModule = (mod: string, value: boolean, includeSubs: boolean) => {
      settings[mod] = value;
      const subs = MANAGER_PERMISSION_MODULES[mod];
      if (includeSubs && subs.length) {
        settings[`${mod}_subPermissions`] = Object.fromEntries(subs.map((s) => [s, value]));
      }
    };

    if (args.enableAll === true) {
      for (const mod of Object.keys(MANAGER_PERMISSION_MODULES)) setModule(mod, true, true);
    }
    if (args.disableAll === true) {
      for (const mod of Object.keys(MANAGER_PERMISSION_MODULES)) setModule(mod, false, true);
    }

    // Önce kapatmalar, sonra açmalar uygulanır (çakışmada açma kazanır)
    for (const token of disableTokens) {
      if (token in MANAGER_PERMISSION_MODULES) {
        setModule(token, false, true);
      } else {
        const parent = MANAGER_SUB_TO_MODULE[token];
        settings[`${parent}_subPermissions`][token] = false;
      }
    }
    // Aynı çağrıda modülün alt izni de belirtilmişse modül açılırken TÜM altları açma
    const explicitSubParents = new Set(enableTokens.filter((t) => t in MANAGER_SUB_TO_MODULE).map((t) => MANAGER_SUB_TO_MODULE[t]));
    for (const token of enableTokens) {
      if (token in MANAGER_PERMISSION_MODULES) {
        // Alt izinsiz açılan genişletilebilir modül panelde boş görünür → altları da aç
        setModule(token, true, !explicitSubParents.has(token));
      } else {
        const parent = MANAGER_SUB_TO_MODULE[token];
        settings[parent] = true;
        settings[`${parent}_subPermissions`][token] = true;
      }
    }

    await personnelApi.setManagerPanel(person.id, true, settings);
    const enabledModules = Object.keys(MANAGER_PERMISSION_MODULES).filter((mod) => settings[mod] === true);
    return {
      success: true,
      message: `"${personnelDisplayName(person)}" yöneticisinin panel yetkileri güncellendi.`,
      enabledModules,
    };
  },
};

// Her seferde paralel çalışacak istek sayısı — CopyBranchPricesModal ile aynı denge.
const BRANCH_PRICE_COPY_CHUNK_SIZE = 10;

/**
 * Şube fiyatlarını başka şubeye kopyalar — CopyBranchPricesModal mantığının ajan
 * karşılığı. Toplu backend ucu olmadığından ürün başına POST, 10'luk paralel
 * parçalarla atılır; hedefte fiyatı olan ürünler ATLANIR (üzerine yazılmaz).
 */
const transferBranchPrices: AgentActionExecutor = {
  describe: (args) =>
    `"${str(args.sourceBranchName)}" şubesinin fiyatlarını "${str(args.targetBranchName)}" şubesine kopyala (mevcutlar atlanır)`,
  execute: async (args, ctx) => {
    const sourceBranchName = str(args.sourceBranchName);
    const targetBranchName = str(args.targetBranchName);
    if (!sourceBranchName || !targetBranchName) {
      return { success: false, error: 'sourceBranchName ve targetBranchName zorunlu (listBranches).' };
    }

    const [sourceResolved, targetResolved] = await Promise.all([
      resolveBranchRecordByName(ctx, sourceBranchName),
      resolveBranchRecordByName(ctx, targetBranchName),
    ]);
    if ('error' in sourceResolved) return sourceResolved.error;
    if ('error' in targetResolved) return targetResolved.error;
    const source = sourceResolved.branch;
    const target = targetResolved.branch;
    if (source.id === target.id) {
      return { success: false, error: 'Kaynak ve hedef şube aynı olamaz.' };
    }

    const companyId = requireCompanyId(ctx);
    const fetchPrices = async (branchId: string): Promise<any[]> => {
      try {
        return listOf(await accountingApi.get(`/api/InventoryBranchPrice/branch/${branchId}`));
      } catch (err: any) {
        if (err?.response?.status === 404) return []; // kayıt yok
        throw err;
      }
    };

    const [sourcePrices, targetPrices] = await Promise.all([
      fetchPrices(source.id),
      fetchPrices(target.id),
    ]);
    if (sourcePrices.length === 0) {
      return { success: false, error: `"${source.name}" şubesinde kopyalanacak fiyat kaydı yok.` };
    }

    // Hedefte fiyatı olan ürünler atlanır — UI kuralı: mevcut fiyatların üzerine yazılmaz
    const existingItemIds = new Set(targetPrices.map((p: any) => p.inventoryItemId));
    const toCopy = sourcePrices.filter((p: any) => !existingItemIds.has(p.inventoryItemId));
    const skippedCount = sourcePrices.length - toCopy.length;
    if (toCopy.length === 0) {
      return {
        success: true,
        copiedCount: 0,
        skippedCount,
        failedCount: 0,
        message: `Kaynak şubedeki tüm ürünlerin fiyatı "${target.name}" şubesinde zaten mevcut — yeni eklenen kayıt yok.`,
      };
    }

    let copiedCount = 0;
    let failedCount = 0;
    const failedSamples: Array<{ productName?: string; stockCode?: string; message: string }> = [];

    for (let i = 0; i < toCopy.length; i += BRANCH_PRICE_COPY_CHUNK_SIZE) {
      const chunk = toCopy.slice(i, i + BRANCH_PRICE_COPY_CHUNK_SIZE);
      const results = await Promise.allSettled(
        chunk.map((src: any) =>
          accountingApi.post('/api/InventoryBranchPrice', {
            companyId,
            inventoryItemId: src.inventoryItemId,
            branchId: target.id,
            branchName: target.name,
            salePrice: src.salePrice,
            saleCurrency: src.saleCurrency || 'TRY',
            listPrice: src.listPrice,
            listCurrency: src.listCurrency,
            packagePrice: src.packagePrice,
            createdBy: ctx.userEmail,
          })
        )
      );
      results.forEach((r, idx) => {
        if (r.status === 'fulfilled') {
          copiedCount += 1;
          return;
        }
        failedCount += 1;
        const err: any = (r as PromiseRejectedResult).reason;
        const status: number | undefined = err?.response?.status;
        // 404 = ürünü silinmiş "yetim" şube fiyat kaydı
        const message =
          status === 404
            ? 'Ürün sistemde bulunamadı (silinmiş olabilir).'
            : err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Bilinmeyen hata.';
        if (failedSamples.length < 10) {
          const src = chunk[idx] as any;
          failedSamples.push({ productName: src.productName, stockCode: src.stockCode, message });
        }
      });
    }

    return {
      success: failedCount === 0 || copiedCount > 0,
      copiedCount,
      skippedCount,
      failedCount,
      ...(failedSamples.length > 0 ? { failedSamples } : {}),
      message: `"${source.name}" → "${target.name}": ${copiedCount} fiyat kopyalandı, ${skippedCount} atlandı (hedefte zaten vardı)${failedCount > 0 ? `, ${failedCount} başarısız` : ''}.`,
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// AI Plan Modu — plan CRUD eylemleri
// Plan modunda AI, kullanıcıyla konuşarak kurduğu görev planını bu eylemlerle
// panele kaydeder. Yürütme aiPlanService üzerinden (kullanıcının oturumuyla);
// her yazma sonrası notifyAiPlanChanged ile panel canlı tazelenir.
// ═══════════════════════════════════════════════════════════════════════════

/** Plan eylemleri oturum sahibinin uid'sini ister (plan sahipliği CompanyId+UserId). */
const requirePlanUserId = (ctx: AgentActionContext): string => {
  const uid = str(ctx.userId);
  if (!uid) throw new Error('Oturum bilgisi bulunamadı — lütfen sayfayı yenileyip tekrar deneyin.');
  return uid;
};

const listAiPlans: AgentActionExecutor = {
  describe: () => 'Görev planlarını listele',
  execute: async (_args, ctx) => {
    const plans = await aiPlanService.getPlans(requirePlanUserId(ctx));
    return {
      success: true,
      count: plans.length,
      plans: plans.map((p) => ({
        id: p.id,
        title: p.title,
        status: p.status,
        doneTasks: p.doneCount,
        totalTasks: p.taskCount,
        updatedAt: p.updatedAt,
      })),
    };
  },
};

const getAiPlan: AgentActionExecutor = {
  describe: (args) => `Plan detayını getir (#${str(args.planId)})`,
  execute: async (args, ctx) => {
    const planId = toIntId(args.planId);
    if (!planId || planId <= 0) return { success: false, error: 'Geçerli bir planId verilmedi (listAiPlans ile bul).' };
    const plan = await aiPlanService.getPlan(planId, requirePlanUserId(ctx));
    return {
      success: true,
      plan: {
        id: plan.id,
        title: plan.title,
        status: plan.status,
        contextVars: plan.contextVars,
        tasks: plan.tasks.map((t) => ({
          id: t.id,
          order: t.sortOrder,
          title: t.title,
          status: t.status,
          prompt: t.promptTemplate,
          resultSummary: t.resultSummary,
        })),
      },
    };
  },
};

/** createAiPlan tasksJson doğrulaması: [{title, prompt}] dizisi, 1-20 görev. */
const parsePlanTasksJson = (
  raw: any
): { tasks: Array<{ title: string; prompt: string }> } | { error: string } => {
  let parsed: any;
  try {
    parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return { error: 'tasksJson geçerli bir JSON değil. Beklenen biçim: [{"title":"...","prompt":"..."}]' };
  }
  if (!Array.isArray(parsed) || parsed.length === 0)
    return { error: 'tasksJson boş olmayan bir JSON dizisi olmalı: [{"title":"...","prompt":"..."}]' };
  if (parsed.length > 20) return { error: 'En fazla 20 görev eklenebilir.' };

  const tasks: Array<{ title: string; prompt: string }> = [];
  for (let i = 0; i < parsed.length; i++) {
    const title = str(parsed[i]?.title);
    const prompt = str(parsed[i]?.prompt);
    if (!title || !prompt)
      return { error: `Görev ${i + 1} eksik: her görevde "title" ve "prompt" alanları dolu olmalı.` };
    tasks.push({ title, prompt });
  }
  return { tasks };
};

const createAiPlan: AgentActionExecutor = {
  describe: (args) => `"${str(args.title)}" adlı görev planını oluştur`,
  execute: async (args, ctx) => {
    const userId = requirePlanUserId(ctx);
    const title = str(args.title);
    if (!title) return { success: false, error: 'Plan başlığı (title) zorunludur.' };

    const parsedTasks = parsePlanTasksJson(args.tasksJson);
    if ('error' in parsedTasks) return { success: false, error: parsedTasks.error };

    // contextJson: {degisken: deger} — görev prompt'larındaki {degisken} yer tutucularını doldurur
    let contextVars: Record<string, string> | undefined;
    if (str(args.contextJson)) {
      try {
        const parsed = JSON.parse(String(args.contextJson));
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          contextVars = {};
          Object.entries(parsed).forEach(([k, v]) => {
            if (v !== null && v !== undefined) contextVars![k] = String(v);
          });
        }
      } catch {
        return { success: false, error: 'contextJson geçerli bir JSON nesnesi değil. Örnek: {"branchName":"Kadıköy"}' };
      }
    }

    const plan = await aiPlanService.createPlan(
      {
        title,
        contextVars,
        tasks: parsedTasks.tasks.map((t, i) => ({
          title: t.title,
          promptTemplate: t.prompt,
          sortOrder: i,
        })),
      },
      userId
    );

    notifyAiPlanChanged(true); // panel açılır ve yeni plan görünür
    return {
      success: true,
      planId: plan.id,
      taskCount: plan.tasks.length,
      message: `"${plan.title}" planı ${plan.tasks.length} görevle oluşturuldu. Kullanıcı görevleri plan panelindeki "Şimdi yap" butonlarıyla başlatabilir.`,
    };
  },
};

const updateAiPlan: AgentActionExecutor = {
  describe: (args) => `Planı güncelle (#${str(args.planId)})`,
  execute: async (args, ctx) => {
    const userId = requirePlanUserId(ctx);
    const planId = toIntId(args.planId);
    if (!planId || planId <= 0) return { success: false, error: 'Geçerli bir planId verilmedi (listAiPlans ile bul).' };

    const title = str(args.title) || undefined;
    const status = ['active', 'completed', 'archived'].includes(str(args.status))
      ? (str(args.status) as 'active' | 'completed' | 'archived')
      : undefined;

    let contextVars: Record<string, string> | undefined;
    if (str(args.contextJson)) {
      try {
        const parsed = JSON.parse(String(args.contextJson));
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          contextVars = {};
          Object.entries(parsed).forEach(([k, v]) => {
            if (v !== null && v !== undefined) contextVars![k] = String(v);
          });
        }
      } catch {
        return { success: false, error: 'contextJson geçerli bir JSON nesnesi değil. Örnek: {"branchName":"Kadıköy"}' };
      }
    }

    // tasksJson: TAM görev listesi — aynı başlıklı görevler durumunu koruyarak
    // güncellenir, listede olmayanlar silinir, yeniler pending olarak eklenir.
    let tasks: Array<{ title: string; promptTemplate: string; sortOrder: number }> | undefined;
    if (args.tasksJson !== undefined && args.tasksJson !== null && str(args.tasksJson)) {
      const parsedTasks = parsePlanTasksJson(args.tasksJson);
      if ('error' in parsedTasks) return { success: false, error: parsedTasks.error };
      tasks = parsedTasks.tasks.map((t, i) => ({
        title: t.title,
        promptTemplate: t.prompt,
        sortOrder: i,
      }));
    }

    if (!title && !status && !contextVars && !tasks)
      return { success: false, error: 'Değiştirilecek alan verilmedi (title, contextJson, tasksJson veya status).' };

    await aiPlanService.updatePlan(planId, userId, { title, contextVars, status, tasks });
    notifyAiPlanChanged();
    return {
      success: true,
      ...(tasks ? { taskCount: tasks.length } : {}),
      message: tasks
        ? `Plan güncellendi: görev listesi ${tasks.length} görevle yeniden yazıldı (aynı başlıklı görevlerin durumu korundu).`
        : 'Plan güncellendi.',
    };
  },
};

const AI_PLAN_TASK_STATUSES = ['done', 'needsReview', 'pending', 'failed'] as const;

/**
 * Plan görevini işaretler. Ana kullanım: görev yeniden çalıştırıldığında iş zaten
 * yapılmışsa AI yeni eylem çağırmadan bununla görevi kapatır — aksi halde görev
 * panelde 'çalışıyor' asılı kalır ve "tümünü yap" akışı ilerleyemez.
 * (AiChatWindow bu eylemin başarısını görünce aktif görev kilidini de çözer.)
 */
const completeAiPlanTask: AgentActionExecutor = {
  describe: (args) => `Plan görevini işaretle (#${str(args.taskId)} → ${str(args.status) || 'done'})`,
  execute: async (args, ctx) => {
    const userId = requirePlanUserId(ctx);
    const taskId = toIntId(args.taskId);
    if (!taskId || taskId <= 0)
      return { success: false, error: "Geçerli bir taskId verilmedi (görev komutundaki taskId'yi kullan veya getAiPlan ile bul)." };

    const status = (AI_PLAN_TASK_STATUSES as readonly string[]).includes(str(args.status))
      ? (str(args.status) as (typeof AI_PLAN_TASK_STATUSES)[number])
      : 'done';
    const summary = str(args.resultSummary) || null;

    await aiPlanService.updateTask(taskId, userId, status, summary);
    notifyAiPlanChanged();
    return { success: true, taskId, status, message: `Görev #${taskId} '${status}' olarak işaretlendi.` };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Proje Yönetim Sistemi (PYS) — proje bazlı kârlılık ve nakit akışı
// Avans/tahsilat Net Nakit'i artırır, Kâr'ı değiştirmez (kâr = faturalaşan tutar).
// ═══════════════════════════════════════════════════════════════════════════

const listProjects: AgentActionExecutor = {
  describe: (args) =>
    `Projeleri listele${str(args.status) ? ` (${str(args.status)})` : ''}${str(args.search) ? `: "${str(args.search)}"` : ''}`,
  execute: async (args) => {
    const result = await projectApi.getProjectsPaged({
      page: 1,
      pageSize: clampLimit(args.limit, 20, 100),
      search: str(args.search) || undefined,
      status: str(args.status) || undefined,
      includeSummary: true,
    });
    return {
      success: true,
      totalCount: result.totalCount,
      projects: result.data.map((p) => ({
        id: p.id,
        name: p.name,
        code: p.code,
        status: p.status,
        accountId: p.accountId,
        startDate: p.startDate,
        endDate: p.endDate,
        revenue: p.summary?.revenue ?? 0,
        cost: p.summary?.cost ?? 0,
        profit: p.summary?.profit ?? 0,
        netCash: p.summary?.netCash ?? 0,
        itemCount: p.summary?.itemCount ?? 0,
      })),
    };
  },
};

const getProjectSummary: AgentActionExecutor = {
  describe: (args) => `Proje özetini getir (#${args.projectId})`,
  execute: async (args) => {
    const id = toIntId(args.projectId);
    if (!id) return { success: false, error: 'projectId zorunludur (listProjects).' };

    const summary = await projectApi.getProjectSummary(id);
    return {
      success: true,
      projectId: id,
      revenue: summary.revenue,
      cost: summary.cost,
      profit: summary.profit,
      cashIn: summary.cashIn,
      cashOut: summary.cashOut,
      netCash: summary.netCash,
      remainingReceivable: summary.remainingReceivable,
      itemCount: summary.itemCount,
      note: 'Avans/tahsilat yalnız Net Nakit\'i artırır, Kâr\'ı değiştirmez; Kâr = Gelir − Maliyet.',
    };
  },
};

const createProject: AgentActionExecutor = {
  describe: (args) => `Yeni proje oluştur: "${str(args.name)}"`,
  execute: async (args, ctx) => {
    const name = str(args.name);
    if (!name) return { success: false, error: 'name zorunludur.' };

    // İdempotency: aynı adda proje varsa yeniden oluşturulmaz
    const existing = await projectApi.getProjectsPaged({ page: 1, pageSize: 50, search: name });
    const match = existing.data.find(
      (p) => p.name.toLocaleLowerCase('tr') === name.toLocaleLowerCase('tr')
    );
    if (match) {
      return {
        success: true,
        alreadyExists: true,
        projectId: match.id,
        name: match.name,
        status: match.status,
      };
    }

    const created = await projectApi.createProject({
      name,
      code: str(args.code) || null,
      accountId: toIntId(args.accountId),
      description: str(args.description) || null,
      startDate: str(args.startDate) || null,
      endDate: str(args.endDate) || null,
      createdBy: actorEmail(ctx),
    });
    return { success: true, projectId: created.id, name: created.name, status: created.status };
  },
};

const setProjectStatus: AgentActionExecutor = {
  describe: (args) => `Proje durumunu değiştir (#${args.projectId} → ${str(args.status)})`,
  execute: async (args) => {
    const id = toIntId(args.projectId);
    if (!id) return { success: false, error: 'projectId zorunludur (listProjects).' };

    const status = str(args.status).toLowerCase();
    if (!['active', 'completed', 'cancelled'].includes(status)) {
      return { success: false, error: 'status yalnız active | completed | cancelled olabilir.' };
    }

    const updated = await projectApi.updateProjectStatus(id, status as projectApi.ProjectStatus);
    return { success: true, projectId: id, name: updated.name, status: updated.status };
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Kayıt
// ═══════════════════════════════════════════════════════════════════════════

/**
 * API hatalarını AI'nin anlayabileceği { success:false, error } biçimine çevirir.
 * (Axios'un genel "Request failed..." mesajı yerine backend'in message alanı.)
 */
const withApiErrorHandling = (executor: AgentActionExecutor): AgentActionExecutor => ({
  describe: executor.describe,
  execute: async (args, ctx) => {
    try {
      return await executor.execute(args, ctx);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Eylem yürütülemedi.';
      return { success: false, error: String(msg) };
    }
  },
});

/** Eylem adı → yürütücü kaydı (backend kataloğuyla aynı adlar). */
const RAW_EXECUTORS: Record<string, AgentActionExecutor> = {
  navigateToPage,
  searchProduct,
  applyInventoryFilters,
  // Cari Hesaplar — okuma
  searchAccount,
  getAccountDetails,
  listAccountTransactions,
  listCompanyTransactions,
  listTransactionLogs,
  listAccountGroups,
  listShippingAddresses,
  listCashboxes,
  listBankAccounts,
  listCreditCards,
  // Cari Hesaplar — yazma (onay kartı gerektirir)
  createAccount,
  updateAccount,
  deleteAccount,
  deactivateAccount,
  activateAccount,
  bulkCreateAccounts,
  collectPayment,
  makePayment,
  debitAccount,
  deleteTransaction,
  changeTransactionPaymentMethod,
  transferBetweenAccounts,
  createAccountGroup,
  deleteAccountGroup,
  addShippingAddress,
  deleteShippingAddress,
  // Banka — okuma
  getBankSummary,
  listBankTransactions,
  listBankAccountTransactions,
  listBankTransactionLogs,
  listCreditCardTransactions,
  listCheckBooks,
  listCheckBookLeaves,
  listLoans,
  getLoanDetail,
  listLoanPayments,
  // Banka — yazma (onay kartı gerektirir; silme eylemleri bilinçli olarak yok)
  createBankAccount,
  updateBankAccount,
  createBankSubAccount,
  updateBankSubAccount,
  bankDeposit,
  bankWithdraw,
  bankToBankTransfer,
  bankToCashTransfer,
  bankVirman,
  createCreditCard,
  updateCreditCard,
  creditCardAddUsage,
  creditCardPayment,
  createCheckBook,
  updateCheckBook,
  createLoan,
  updateLoan,
  payLoanInstallment,
  // Kasa — okuma
  getCashboxSummary,
  listCashboxTransactions,
  listCashboxTransactionLogs,
  // Kasa — yazma (onay kartı gerektirir; silme eylemleri bilinçli olarak yok)
  createCashbox,
  updateCashbox,
  cashboxDeposit,
  cashboxWithdraw,
  cashboxToCashboxTransfer,
  cashboxToBankTransfer,
  // Stok — okuma
  getProductDetails,
  listCategories,
  listBrands,
  listWarehouses,
  listStockMovements,
  listPriceChangeLogs,
  listBranches,
  listBranchPrices,
  // Stok — yazma (onay kartı gerektirir; silme eylemleri bilinçli olarak yok)
  createProduct,
  updateProduct,
  updateProductPrice,
  setProductStock,
  stockMovement,
  setProductActive,
  toggleProductEcommerce,
  bulkUpdatePrices,
  bulkUpdateVatRate,
  bulkUpdateDepartmentNo,
  bulkAssignWarehouse,
  createCategory,
  updateCategory,
  createBrand,
  updateBrand,
  createWarehouse,
  updateWarehouse,
  transferStockBetweenWarehouses,
  setBranchPrice,
  toggleUseBranchPricing,
  listBranchPricesByBranch,
  copyBranchPrices,
  // Fatura — okuma
  listInvoices,
  getInvoiceDetails,
  getInvoiceReturnSummary,
  listInvoiceRelatedTransactions,
  listInvoiceReports,
  // Fatura — form UI eylemleri (ekrandaki formu doldurur; kaydetme onay gerektirir)
  openInvoiceForm,
  setInvoiceFormAccount,
  setInvoiceFormFields,
  addInvoiceFormItem,
  updateInvoiceFormItem,
  removeInvoiceFormItem,
  getInvoiceFormSummary,
  saveInvoiceForm,
  // E-Fatura — okuma (EDM + PavoPay)
  listEInvoiceInbox,
  getEInvoiceStatus,
  queryEInvoiceMukellef,
  getEInvoiceKontor,
  listEInvoiceIntegrations,
  testEInvoiceConnection,
  // E-Fatura — yazma (onay kartı gerektirir)
  respondEInvoice,
  markEInvoiceRead,
  // E-Fatura — UI/navigasyon
  importEInvoiceToPurchase,
  // Çek/Senet — okuma + UI filtre
  applyCheckNotesFilters,
  listCheckNotes,
  getCheckNoteDetails,
  getCheckNoteDashboard,
  listCheckNoteTransactions,
  listCheckNoteRelatedTransactions,
  checkDuplicateCheckNumber,
  // Çek/Senet — yazma (onay kartı; silme yok)
  createCheckNote,
  updateCheckNote,
  createBatchPromissoryNotes,
  changeCheckNoteStatus,
  // İrsaliye — okuma + UI filtre
  applyWaybillFilters,
  listWaybills,
  getWaybillDetails,
  listWaybillsByAccount,
  getWaybillSummary,
  // İrsaliye — form UI (ekrandaki formu doldurur; kaydetme onay gerektirir)
  openWaybillForm,
  setWaybillFormAccount,
  setWaybillFormFields,
  addWaybillFormItem,
  updateWaybillFormItem,
  removeWaybillFormItem,
  getWaybillFormSummary,
  saveWaybillForm,
  convertWaybillToInvoice,
  // Sipariş — okuma + UI filtre
  applyOrderFilters,
  listOrders,
  getOrderDetails,
  getOrderStats,
  // Sipariş — form UI (ekrandaki formu doldurur; kaydetme onay gerektirir)
  openOrderForm,
  setOrderFormAccount,
  setOrderFormFields,
  addOrderFormItem,
  updateOrderFormItem,
  removeOrderFormItem,
  getOrderFormSummary,
  saveOrderForm,
  convertOrderToInvoice,
  convertOrderToWaybill,
  // Döviz — okuma (her seferinde TCMB canlı çekim) + UI + ayar yazma
  listCurrencyRates,
  getCurrencyRate,
  getCurrencySettings,
  listActiveCurrencies,
  convertCurrency,
  updateCurrencySettings,
  openCurrencyPage,
  // Teklif — okuma + UI filtre
  applyOfferFilters,
  listOffers,
  getOfferDetails,
  getOfferStats,
  // Teklif — form UI (ekrandaki formu doldurur; kaydetme onay gerektirir)
  openOfferForm,
  setOfferFormAccount,
  setOfferFormFields,
  addOfferFormItem,
  updateOfferFormItem,
  removeOfferFormItem,
  getOfferFormSummary,
  saveOfferForm,
  convertOfferToInvoice,
  convertOfferToOrder,
  // Hızlı Satış — yönetim sayfası + ortak ayar modalı UI (POS ekranı kapsam dışı)
  listQuickSaleScreens,
  openQuickSalePage,
  createQuickSaleScreen,
  openQuickSaleSettings,
  setQuickSaleSettingsTab,
  getQuickSaleSettings,
  updateQuickSaleSettings,
  saveQuickSaleSettings,
  closeQuickSaleSettings,
  // Depo/stok taslakları (ekranlar arası ürün aktarımı; taslaklar tarayıcıda saklanır)
  listWarehouseStockTemplates,
  applyWarehouseStockTemplate,
  saveWarehouseStockTemplate,
  // Ödeme yöntemleri + personel atama + mutfak (açık ayar modalı köprüsü)
  listQuickSalePaymentBindings,
  addQuickSalePaymentOption,
  removeQuickSalePaymentOption,
  listQuickSaleAssignablePersonnel,
  setQuickSaleAssignedPersonnel,
  setKitchenIntegration,
  // Ekran kasiyerleri (modal gerektirmez)
  listScreenCashiers,
  createScreenCashier,
  // Restoran — yönetim sayfası + ortak ayar modalı + ekrana git
  listRestaurantScreens,
  openRestaurantSettings,
  createRestaurantScreen,
  openRestaurantScreen,
  // Paket Sipariş — yönetim sayfası + ortak ayar modalı + ekrana git
  listPackageScreens,
  openPackageSettings,
  createPackageScreen,
  openPackageOrderScreen,
  // Gün Sonu — okuma + sayfayı filtreli açma (kapanış işlemi ajana verilmez)
  getEndOfDaySummary,
  getPlatformReconciliation,
  listSavedEndOfDayReports,
  openEndOfDayReport,
  // QR Menü — okuma + yazma (menü/masa oluşturma, sipariş durumu)
  listQrMenus,
  listQrOrders,
  listQrTables,
  getQrMenuPublicUrl,
  openQrOrdersPage,
  createQrMenu,
  addQrTable,
  setQrOrderStatus,
  // Mutfak Ekranı — QR menü altyapısını kullanır
  listKitchenMenus,
  listKitchenOrders,
  openKitchenScreen,
  // Kiosk — yönetim + ürün aç/kapa + cihaz yeniden başlatma
  listKiosks,
  listKioskProducts,
  createKiosk,
  updateKiosk,
  toggleKioskProduct,
  restartKioskDevices,
  // Kurye — sipariş ayarları + takip ekranı (kurye kaydetme/silme kapsam dışı)
  listCouriers,
  getCourierOrderSettings,
  updateCourierOrderSettings,
  openCourierTracking,
  // Entegrasyonlar — okuma + bağlantı testi (anahtar ekleme/silme kapsam dışı)
  listPlatformIntegrations,
  listPaymentIntegrations,
  getIntegrationHealth,
  testIntegrationConnection,
  // Resim Kütüphanesi — okuma + klasör/taşıma/yeniden adlandırma (yükleme/silme kapsam dışı)
  listImageFolders,
  listImages,
  createImageFolder,
  moveImage,
  renameImage,
  openImageLibrary,
  // İK / Personel — okuma + güncelleme + hakediş/hareket (hesap AÇMA ve yetki YAZMA kapsam dışı)
  listPersonnel,
  getPersonnelDetails,
  listDepartments,
  getPersonnelBalance,
  listPersonnelTransactions,
  getPersonnelPermissions,
  openPersonnelPage,
  updatePersonnel,
  transferPersonnelBranch,
  deletePersonnel,
  restorePersonnel,
  createPersonnel,
  createPersonnelAccrual,
  createPersonnelTransaction,
  // İK / Vardiya — tanım + atama + giriş-çıkış raporu/düzeltme
  listShifts,
  getShiftPersonnel,
  getPersonnelShift,
  listAttendance,
  getWorkDurationSummary,
  createShift,
  updateShift,
  deleteShift,
  assignShiftPersonnel,
  editAttendance,
  // Şubeler — oluşturma/güncelleme + yönetici atama + panel yetkileri (şube silme kapsam dışı)
  getBranchDetails,
  listBranchManagers,
  getManagerPermissions,
  createBranch,
  updateBranch,
  updateBranchFirmInfo,
  assignBranchManager,
  removeBranchManager,
  setManagerPermissions,
  transferBranchPrices,
  // Proje Yönetim Sistemi (PYS) — proje listesi/özeti + oluşturma (idempotent) + durum
  listProjects,
  getProjectSummary,
  createProject,
  setProjectStatus,
  // AI Plan Modu — plan CRUD (Plan modunda AI kullanıcıyla konuşarak plan kurar)
  listAiPlans,
  getAiPlan,
  createAiPlan,
  updateAiPlan,
  completeAiPlanTask,
};

const EXECUTORS: Record<string, AgentActionExecutor> = Object.fromEntries(
  Object.entries(RAW_EXECUTORS).map(([name, ex]) => [name, withApiErrorHandling(ex)])
);

export const getActionExecutor = (name: string): AgentActionExecutor | null =>
  EXECUTORS[name] ?? null;
